/// <reference path="./declaration.d.ts" />
import { Observer } from '@playcanvas/observer';
import {
    ADDRESS_CLAMP_TO_EDGE,
    BLENDMODE_ONE,
    BLENDMODE_ZERO,
    BLENDEQUATION_ADD,
    EVENT_KEYDOWN,
    FILTER_NEAREST,
    INTERPOLATION_LINEAR,
    KEY_F,
    KEY_R,
    LAYERID_DEPTH,
    LAYERID_SKYBOX,
    PIXELFORMAT_DEPTH,
    PIXELFORMAT_RGBA8,
    PRIMITIVE_POINTS,
    PRIMITIVE_LINELOOP,
    PRIMITIVE_LINES,
    PRIMITIVE_LINESTRIP,
    PRIMITIVE_TRIANGLES,
    PRIMITIVE_TRISTRIP,
    PRIMITIVE_TRIFAN,
    SKYTYPE_BOX,
    SKYTYPE_DOME,
    SKYTYPE_INFINITE,
    SORTMODE_BACK2FRONT,
    TEXTURETYPE_DEFAULT,
    SEMANTIC_POSITION,
    TYPE_INT8,
    TYPE_UINT8,
    TYPE_INT16,
    TYPE_UINT16,
    TYPE_INT32,
    TYPE_UINT32,
    TEXTURETYPE_RGBM,
    TONEMAP_NONE,
    TONEMAP_LINEAR,
    TONEMAP_NEUTRAL,
    TONEMAP_FILMIC,
    TONEMAP_HEJL,
    TONEMAP_ACES,
    TONEMAP_ACES2,
    math,
    path,
    ShaderChunks,
    AnimCurve,
    AnimEvents,
    AnimSnapshot,
    AnimTrack,
    Asset,
    BlendState,
    BoundingBox,
    Color,
    ContainerResource,
    Entity,
    EnvLighting,
    GraphicsDevice,
    GraphNode,
    GSplatComponent,
    GSplatData,
    GSplatResource,
    GSplatResourceBase,
    Keyboard,
    Mat4,
    Mesh,
    MeshInstance,
    MorphInstance,
    MorphTarget,
    Mouse,
    MiniStats,
    Quat,
    RenderComponent,
    RenderTarget,
    Skin,
    SkinInstance,
    StandardMaterial,
    Texture,
    TouchDevice,
    Vec3,
    Vec2,
} from 'playcanvas';
// @ts-ignore
import { CameraControls } from 'playcanvas/scripts/esm/camera-controls.mjs';

import { computeAircraftSilhouette, type AircraftSilhouette, type SilhouetteMeshInput } from './aircraft-silhouette';
import { App } from './app';
import { DebugLines } from './debug-lines';
import { CreateDropHandler } from './drop-handler';
import { addEventListenerOnClickOnly } from './helpers';
import { Multiframe } from './multiframe';
import { Picker } from './picker';
import { PngExporter } from './png-exporter';
import { ShadowCatcher } from './shadow-catcher';
import arCloseImage from './svg/ar-close.svg';
import arModeImage from './svg/ar-mode.svg';
import { File, HierarchyNode, MorphTargetData } from './types';
import { XRObjectPlacementController } from './xr-mode';
import { MeshoptDecoder } from '../lib/meshopt_decoder.module.js';

// model filename extensions
const modelExtensions = ['gltf', 'glb', 'vox'];
const defaultSceneBounds = new BoundingBox(new Vec3(0, 1, 0), new Vec3(1, 1, 1));
const maxSkinJoints = 64;
const touchedTranslation = 1 << 0;
const touchedRotation = 1 << 1;
const touchedScale = 1 << 2;

const vec = new Vec3();
const bbox = new BoundingBox();

type RestLocalTransform = {
    position: Vec3;
    rotation: Quat;
    scale: Vec3;
};

type AnimationChannelMetadata = {
    displayName: string;
    sourceAnimation: string;
    targetProperty: string;
};

const FOCUS_FOV = 75;

// override global pick to pack depth instead of meshInstance id
const pickDepthGlsl = /* glsl */ `
vec4 packFloat(float depth) {
    uvec4 u = (uvec4(floatBitsToUint(depth)) >> uvec4(0u, 8u, 16u, 24u)) & 0xffu;
    return vec4(u) / 255.0;
}
vec4 getPickOutput() {
    return packFloat(gl_FragCoord.z);
}
`;

const pickDepthWgsl = /* wgsl */ `
    fn packFloat(depth: f32) -> vec4f {
        let u: vec4<u32> = (vec4<u32>(bitcast<u32>(depth)) >> vec4<u32>(0u, 8u, 16u, 24u)) & vec4<u32>(0xffu);
        return vec4f(u) / 255.0;
    }

    fn getPickOutput() -> vec4f {
        return packFloat(pcPosition.z);
    }
`;

// Custom shader chunk for decoding runtime-packed normal maps (XXXY format).
// We avoid PlayCanvas template placeholders ($UV / $CH) because material-level
// chunks are not templated. Instead, we directly sample the normal map using
// vUv0/vUv1 and reconstruct Z.
const normalMapXXXYGlsl = /* glsl */ `
#ifdef STD_NORMAL_TEXTURE
    uniform float material_bumpiness;
#endif
#ifdef STD_NORMALDETAIL_TEXTURE
    uniform float material_normalDetailMapBumpiness;
    vec3 blendNormals(vec3 n1, vec3 n2) {
        n1 += vec3(0, 0, 1);
        n2 *= vec3(-1, -1, 1);
        return n1 * dot(n1, n2) / n1.z - n2;
    }
#endif

void getNormal() {
#ifdef STD_NORMAL_TEXTURE
    vec4 normalSample = texture2DBias({STD_NORMAL_TEXTURE_NAME}, {STD_NORMAL_TEXTURE_UV}, textureBias);

    // Decode XXXY packed format: R=X, G=X, B=X, A=Y
    float normalX = normalSample.r * 2.0 - 1.0;
    float normalY = normalSample.a * 2.0 - 1.0;
    float normalZ = sqrt(max(0.0, 1.0 - normalX * normalX - normalY * normalY));

    vec3 normalMap = vec3(normalX, normalY, normalZ);
    normalMap = mix(vec3(0.0, 0.0, 1.0), normalMap, material_bumpiness);

    #ifdef STD_NORMALDETAIL_TEXTURE
        vec3 normalDetailMap = {STD_NORMALDETAIL_TEXTURE_DECODE}(texture2DBias({STD_NORMALDETAIL_TEXTURE_NAME}, {STD_NORMALDETAIL_TEXTURE_UV}, textureBias));
        normalDetailMap = mix(vec3(0.0, 0.0, 1.0), normalDetailMap, material_normalDetailMapBumpiness);
        normalMap = blendNormals(normalMap, normalDetailMap);
    #endif

    dNormalW = normalize(dTBN * normalMap);
#else
    dNormalW = dVertexNormalW;
#endif
}
`;

class Viewer {
    canvas: HTMLCanvasElement;

    app: App;

    // Global flag to track if any input field is focused
    private isAnyInputFocused = false;

    // Track whether altitude offset has been applied to avoid double application
    private altitudeOffsetApplied = false;

    // Track whether pitch offset has been applied to avoid double application
    private pitchOffsetApplied = false;

    // Store original transforms to avoid compounding offsets
    private originalSceneRootPosition: Vec3 | null = null;
    private originalSceneRootRotation: Vec3 | null = null;
    private originalEntityTransforms: Array<{ position: Vec3; rotation: Vec3 }> = [];

    // Store hidden mesh instances for screenshot preparation
    private hiddenMeshInstancesForScreenshot: Set<any> = new Set();

    // Store directory handle for batch screenshot saving (File System Access API)
    private screenshotDirectoryHandle: any = null;

    skyboxUrls: Map<string, string>;

    controlEventKeys: string[] = null;

    pngExporter: PngExporter = null;

    prevCameraMat: Mat4;

    camera: Entity;

    initialCameraPosition: Vec3 | null;

    initialCameraFocus: Vec3 | null;

    light: Entity;

    sceneRoot: Entity;

    debugRoot: Entity;

    entities: Array<Entity>;

    entityAssets: Array<{ entity: Entity; asset: Asset }>;

    assets: Array<Asset>;

    meshInstances: Array<MeshInstance>;

    wireframeMeshInstances: Array<MeshInstance>;

    wireframeMaterial: StandardMaterial;

    animTracks: Array<AnimTrack>;

    animationMap: Record<string, string>;

    animationProgresses: Map<string, number>;

    animationSnapshots: Map<AnimTrack, AnimSnapshot>;

    individualAnimationChannels: Map<string, AnimationChannelMetadata>;

    restLocalTransforms: Map<GraphNode, RestLocalTransform>;

    // Store raw glTF animation data for node targeting
    gltfAnimationNodeMap: Map<string, Array<{ nodeIndex: number; property: string }>>;

    // Store glTF node names array for proper index mapping
    gltfNodeNames: Array<string>;

    firstFrame: boolean;

    skyboxLoaded: boolean;

    generatedSkyboxUrl: string | null = null;

    animSpeed: number;

    animTransition: number;

    animLoops: number;

    showWireframe: boolean;

    showBounds: boolean;

    showSkeleton: boolean;

    showAxes: boolean;

    showGrid: boolean;

    normalLength: number;

    dirtyWireframe: boolean;

    dirtyBounds: boolean;

    dirtySkeleton: boolean;

    dirtyGrid: boolean;

    dirtyNormals: boolean;

    sceneBounds: BoundingBox;

    dynamicSceneBounds: BoundingBox;

    debugBounds: DebugLines;

    debugSkeleton: DebugLines;

    debugGrid: DebugLines;

    debugNormals: DebugLines;

    miniStats: MiniStats;

    observer: Observer;

    suppressAnimationProgressUpdate: boolean;

    selectedNode: GraphNode | null;

    multiframe: Multiframe | null;

    multiframeBusy = false;

    picker: Picker = null;

    cursorWorld = new Vec3();

    loadTimestamp?: number = null;

    shadowCatcher: ShadowCatcher = null;

    xrMode: XRObjectPlacementController;

    canvasResize = true;

    cameraControls: CameraControls;

    cameraCenterMouseHandlers: {
        onMouseDown: (event: MouseEvent) => void;
        onMouseMove: (event: MouseEvent) => void;
        onMouseUp: (event: MouseEvent) => void;
        onWheel: (event: WheelEvent) => void;
        onKeyDown?: (event: KeyboardEvent) => void;
        onKeyUp?: (event: KeyboardEvent) => void;
        updateMovement?: (deltaTime: number) => void;
        speedMultiplier?: number;
    } | null = null;

    // Livery system properties
    baseTextures: Map<string, Asset> = new Map(); // filename -> texture asset
    liveryTextures: Map<string, Map<string, Asset>> = new Map(); // livery -> (filename -> texture asset)
    materialTextureMap: Map<StandardMaterial, Map<string, string>> = new Map(); // material -> (texture property -> filename)
    liveryIdMap: Map<string, string> = new Map(); // livery directory name -> livery ID (from .json filename)
    preloadedTextures: Set<Asset> = new Set(); // Track which textures have been preloaded

    // Treat any livery name containing "shared" as a shared package that should not be user-selectable
    private isSharedLiveryName(name: string) {
        return name && name.toLowerCase().includes('shared');
    }

    // Filter out shared and sort alphabetically with Generic first
    private normalizeLiveryList(list: string[]): string[] {
        const filtered = list.filter((name) => name && !this.isSharedLiveryName(name));
        const unique = Array.from(new Set(filtered));
        // Sort all liveries alphabetically, including Generic
        return unique.sort((a, b) => a.localeCompare(b));
    }

    // Treat "Default" as an alias of Generic (shared textures)
    private resolveLiveryAlias(name: string) {
        return name === 'Default' ? 'Generic' : name;
    }

    constructor(
        canvas: HTMLCanvasElement,
        graphicsDevice: GraphicsDevice,
        observer: Observer,
        skyboxUrls: Map<string, string>
    ) {
        this.canvas = canvas;

        // create the application
        const app = new App(canvas, {
            mouse: new Mouse(canvas),
            touch: new TouchDevice(canvas),
            keyboard: new Keyboard(window),
            graphicsDevice: graphicsDevice,
        });
        this.app = app;
        this.skyboxUrls = skyboxUrls;

        // global override depth
        ShaderChunks.get(this.app.graphicsDevice, 'glsl').set('pickPS', pickDepthGlsl);
        ShaderChunks.get(this.app.graphicsDevice, 'wgsl').set('pickPS', pickDepthWgsl);

        // NOTE: Normal maps use XXXY packing; we apply a custom decode shader per material
        // Users can toggle normal maps on/off in the Debug panel
        console.log('[NORMAL MAP] XXXY decode shader enabled for normal maps');

        // clustered not needed and has faster startup on windows
        this.app.scene.clusteredLightingEnabled = false;

        // monkeypatch the mouse and touch input devices to ignore touch events
        // when they don't originate from the canvas.
        const origMouseHandler = app.mouse._moveHandler;
        app.mouse.detach();
        app.mouse._moveHandler = (event: MouseEvent) => {
            if (event.target === canvas) {
                origMouseHandler(event);
            }
        };
        app.mouse.attach(canvas);

        const origTouchHandler = app.touch._moveHandler;
        app.touch.detach();
        app.touch._moveHandler = (event: MouseEvent) => {
            if (event.target === canvas) {
                origTouchHandler(event);
            }
        };
        app.touch.attach(canvas);

        // @ts-ignore
        const multisampleSupported = app.graphicsDevice.maxSamples > 1;
        observer.set('camera.multisampleSupported', multisampleSupported);
        observer.set(
            'camera.multisample',
            multisampleSupported && observer.get('camera.multisample')
        );

        // create the exporter
        this.pngExporter = new PngExporter();

        // create drop handler
        CreateDropHandler(
            document.getElementById('app'),
            (files: Array<File>, resetScene: boolean) => {
                // Check if multiple aircraft folders are being dropped
                const aircraftGroups = this.groupFilesByAircraft(files);

                if (aircraftGroups.size > 1) {
                    // Multiple aircraft detected - trigger automatic batch processing
                    console.log(`🎯 Detected ${aircraftGroups.size} aircraft folders!`);
                    console.log('Starting automatic multi-aircraft batch screenshot process...');
                    console.log('Please wait, this may take several minutes...');
                    this.processBatchScreenshotsMultipleAircraft(aircraftGroups);
                } else {
                    // Single aircraft or no aircraft config - normal loading
                    this.loadFiles(files, resetScene);
                }
            }
        );

        // observe canvas size changes
        new ResizeObserver(() => {
            if (this.xrMode && !this.xrMode.active) {
                this.canvasResize = true;
                this.renderNextFrame();
            }
        }).observe(window.document.getElementById('canvas-wrapper'));

        // Depth layer is where the framebuffer is copied to a texture to be used in the following layers.
        // Move the depth layer to take place after World and Skydome layers, to capture both of them.
        const depthLayer = app.scene.layers.getLayerById(LAYERID_DEPTH);
        app.scene.layers.remove(depthLayer);
        app.scene.layers.insertOpaque(depthLayer, 2);

        // create the camera
        const camera = new Entity('Camera');
        camera.setPosition(0, 1, 10);
        this.app.root.addChild(camera);
        camera.addComponent('camera', {
            fov: 75,
            frustumCulling: true,
            clearColor: new Color(0, 0, 0, 0),
        });
        camera.addComponent('script');
        camera.script.create(CameraControls, {
            properties: {
                zoomMin: 0.001,
                zoomMax: 100, // Increased from 10 to allow zooming out much further
                pitchRange: new Vec2(-90, 90),
            },
        });

        this.cameraControls = (camera.script as any).cameraControls as CameraControls;

        camera.camera.requestSceneColorMap(true);

        // Setup global input focus tracking to disable ALL keyboard inputs when typing
        this.setupGlobalInputFocusTracking();

        // Handle Shift+WASD for aircraft rotation in target mode
        // Regular WASD is allowed to pass through to PlayCanvas CameraControls for camera movement
        window.addEventListener(
            'keydown',
            (e) => {
                // Don't process if typing in an input field
                if (this.isAnyInputFocused) {
                    return;
                }

                // Only intercept Shift+WASD for aircraft rotation in target mode
                const currentOrbitMode = this.observer.get('camera.orbitMode');
                if (
                    currentOrbitMode === 'target' &&
                    e.shiftKey &&
                    ['KeyW', 'KeyA', 'KeyS', 'KeyD'].includes(e.code)
                ) {
                    // Handle Shift+WASD for aircraft rotation
                    const currentPitch = this.observer.get('aircraftRotation.pitch') || 0;
                    const currentRoll = this.observer.get('aircraftRotation.roll') || 0;

                    if (e.code === 'KeyW') {
                        this.observer.set(
                            'aircraftRotation.pitch',
                            Math.min(90, currentPitch + 5)
                        );
                    } else if (e.code === 'KeyS') {
                        this.observer.set(
                            'aircraftRotation.pitch',
                            Math.max(-90, currentPitch - 5)
                        );
                    } else if (e.code === 'KeyA') {
                        this.observer.set(
                            'aircraftRotation.roll',
                            Math.max(-180, currentRoll - 5)
                        );
                    } else if (e.code === 'KeyD') {
                        this.observer.set(
                            'aircraftRotation.roll',
                            Math.min(180, currentRoll + 5)
                        );
                    }

                    // Block Shift+WASD from reaching CameraControls (we handled it for aircraft rotation)
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    e.stopPropagation();
                }
                // Regular WASD (without Shift) passes through to PlayCanvas CameraControls
            },
            true
        ); // Use capture phase to intercept before other handlers

        window.addEventListener(
            'keyup',
            (e) => {
                // Don't block if typing in an input field
                if (this.isAnyInputFocused) {
                    return;
                }

                // Only block Shift+WASD keyup in target mode (to match keydown handling)
                const currentOrbitMode = this.observer.get('camera.orbitMode');
                if (
                    currentOrbitMode === 'target' &&
                    e.shiftKey &&
                    ['KeyW', 'KeyA', 'KeyS', 'KeyD'].includes(e.code)
                ) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    e.stopPropagation();
                }
                // Regular WASD keyup passes through to PlayCanvas CameraControls
            },
            true
        ); // Use capture phase to intercept before other handlers

        app.keyboard.on(EVENT_KEYDOWN, (event) => {
            // Ignore keyboard shortcuts when typing in any input field
            if (this.isAnyInputFocused) {
                return;
            }

            const key = event.key;
            switch (key) {
                case KEY_F: {
                    this.focusSelection(false);
                    // @ts-ignore - resetZoom may not exist on all versions
                    if (typeof this.cameraControls.resetZoom === 'function') {
                        this.cameraControls.resetZoom(this.getZoomDist());
                    }
                    event.event.preventDefault();
                    break;
                }
                case KEY_R: {
                    this.cameraControls.focus(Vec3.ZERO, new Vec3(2, 2, 2));
                    event.event.preventDefault();
                    break;
                }
            }

            // Handle +/- keys for FOV adjustment (check both event.key and event.event.key)
            // + zooms in (decreases FOV), - zooms out (increases FOV)
            const keyString = typeof key === 'string' ? key : event.event?.key || '';
            const isShiftPressed = event.event?.shiftKey || false;

            if (keyString === '+' || keyString === '=' || keyString === 'NumpadAdd') {
                const currentFov = this.observer.get('camera.fov') || 40;
                const newFov = Math.max(35, currentFov - 5); // Min 35, decrement FOV (zoom in)
                this.observer.set('camera.fov', newFov);
                if (event.event) event.event.preventDefault();
                return;
            }
            if (keyString === '-' || keyString === '_' || keyString === 'NumpadSubtract') {
                const currentFov = this.observer.get('camera.fov') || 40;
                const newFov = Math.min(150, currentFov + 5); // Max 150, increment FOV (zoom out)
                this.observer.set('camera.fov', newFov);
                if (event.event) event.event.preventDefault();
                return;
            }

            // Handle Shift+WASD for aircraft pitch/roll
            // Check both event.key and event.code for compatibility
            const keyCode = event.code || event.event?.code || '';
            const shiftKey = event.event?.shiftKey || false;

            // Check if this is a WASD key
            const isWASDKey =
                keyCode === 'KeyW' ||
                keyCode === 'KeyA' ||
                keyCode === 'KeyS' ||
                keyCode === 'KeyD' ||
                keyString.toLowerCase() === 'w' ||
                keyString.toLowerCase() === 'a' ||
                keyString.toLowerCase() === 's' ||
                keyString.toLowerCase() === 'd';

            // Only handle Shift+WASD here for aircraft pitch/roll
            if (isWASDKey && shiftKey) {
                // Prevent default behavior for Shift+WASD keys
                if (event.event) {
                    event.event.preventDefault();
                    event.event.stopPropagation();
                }

                if (keyCode === 'KeyW' || keyString.toLowerCase() === 'w') {
                    const currentPitch = this.observer.get('aircraftRotation.pitch') || 0;
                    const newPitch = Math.min(90, currentPitch + 5); // Increase pitch (nose up)
                    this.observer.set('aircraftRotation.pitch', newPitch);
                } else if (keyCode === 'KeyS' || keyString.toLowerCase() === 's') {
                    const currentPitch = this.observer.get('aircraftRotation.pitch') || 0;
                    const newPitch = Math.max(-90, currentPitch - 5); // Decrease pitch (nose down)
                    this.observer.set('aircraftRotation.pitch', newPitch);
                } else if (keyCode === 'KeyA' || keyString.toLowerCase() === 'a') {
                    const currentRoll = this.observer.get('aircraftRotation.roll') || 0;
                    const newRoll = Math.max(-180, currentRoll - 5); // Roll left (negative roll)
                    this.observer.set('aircraftRotation.roll', newRoll);
                } else if (keyCode === 'KeyD' || keyString.toLowerCase() === 'd') {
                    const currentRoll = this.observer.get('aircraftRotation.roll') || 0;
                    const newRoll = Math.min(180, currentRoll + 5); // Roll right (positive roll)
                    this.observer.set('aircraftRotation.roll', newRoll);
                }
                return; // Prevent camera movement when Shift is pressed
            }
        });

        // create the light
        const light = new Entity();
        light.addComponent('light', {
            type: 'directional',
            shadowBias: 0.2,
            shadowResolution: 2048,
        });
        app.root.addChild(light);

        // disable autorender
        app.autoRender = false;
        this.prevCameraMat = new Mat4();
        app.on('update', this.update, this);
        app.on('framerender', this.onFrameRender, this);
        app.on('prerender', this.onPrerender, this);
        app.on('postrender', this.onPostrender, this);
        app.on('frameend', this.onFrameend, this);

        // create the scene and debug root nodes
        const sceneRoot = new Entity('sceneRoot', app);
        app.root.addChild(sceneRoot);

        const debugRoot = new Entity('debugRoot', app);
        app.root.addChild(debugRoot);

        // store app things
        this.camera = camera;
        this.initialCameraPosition = null;
        this.initialCameraFocus = null;
        this.light = light;
        this.sceneRoot = sceneRoot;
        this.debugRoot = debugRoot;
        this.entities = [];
        this.entityAssets = [];
        this.assets = [];
        this.meshInstances = [];
        this.wireframeMeshInstances = [];

        const material = new StandardMaterial();
        material.blendState = new BlendState(
            true,
            BLENDEQUATION_ADD,
            BLENDMODE_ONE,
            BLENDMODE_ZERO,
            BLENDEQUATION_ADD,
            BLENDMODE_ZERO,
            BLENDMODE_ONE
        );
        material.useLighting = false;
        material.useSkybox = false;
        material.ambient = new Color(0, 0, 0);
        material.diffuse = new Color(0, 0, 0);
        material.specular = new Color(0, 0, 0);
        material.emissive = new Color(1, 1, 1);
        material.update();
        this.wireframeMaterial = material;

        this.animTracks = [];
        this.animationMap = {};
        this.animationProgresses = new Map();
        this.animationSnapshots = new Map();
        this.individualAnimationChannels = new Map();
        this.restLocalTransforms = new Map();
        this.gltfAnimationNodeMap = new Map();
        this.gltfNodeNames = [];
        this.firstFrame = false;
        this.skyboxLoaded = false;

        this.animSpeed = observer.get('animation.speed');
        this.animTransition = observer.get('animation.transition');
        this.animLoops = observer.get('animation.loops');
        this.showWireframe = observer.get('debug.wireframe');
        this.showBounds = observer.get('debug.bounds');
        this.showSkeleton = observer.get('debug.skeleton');
        this.showAxes = observer.get('debug.axes');
        this.normalLength = observer.get('debug.normals');
        this.setTonemapping(observer.get('camera.tonemapping'));
        this.setCameraExposure(observer.get('camera.exposure'));
        this.setBackgroundColor(observer.get('skybox.backgroundColor'));
        this.setLightColor(observer.get('light.color'));
        this.setAmbientLightIntensity(observer.get('light.ambientIntensity'));
        this.setWireframeColor(observer.get('debug.wireframeColor'));

        this.dirtyWireframe = false;
        this.dirtyBounds = false;
        this.dirtySkeleton = false;
        this.dirtyGrid = false;
        this.dirtyNormals = false;

        this.sceneBounds = new BoundingBox();
        this.dynamicSceneBounds = new BoundingBox();

        this.debugBounds = new DebugLines(app, camera);
        this.debugSkeleton = new DebugLines(app, camera);
        this.debugGrid = new DebugLines(app, camera, false);
        this.debugNormals = new DebugLines(app, camera, false);

        // construct ministats, default off
        this.miniStats = new MiniStats(app);
        this.miniStats.enabled = observer.get('debug.stats');

        // Set observer BEFORE initializing UI camera mode (it needs observer.get)
        this.observer = observer;

        // Initialize UI camera mode (separate from internal orbit mode)
        // Note: setOrbitMode is kept for screenshot logic compatibility
        this.setUICameraMode(observer.get('camera.uiCameraMode') || 'orbit');

        const device = this.app.graphicsDevice;

        // render frame after device restored
        device.on('devicelost', () => {
            this.deviceLost = true;
            console.warn('[Viewer] Graphics device lost');
        });

        device.on('devicerestored', () => {
            this.deviceLost = false;
            this.renderNextFrame();
        });

        // multiframe
        this.multiframe = new Multiframe(device, this.camera.camera);

        // dynamic shadow catcher
        this.shadowCatcher = new ShadowCatcher(
            app,
            this.camera.camera,
            this.debugRoot,
            this.sceneRoot
        );

        // xr support
        this.initXrMode();

        // initialize control events
        this.bindControlEvents();

        // load initial settings
        this.reloadSettings();

        // construct the depth reader
        this.picker = new Picker(app, camera);
        this.cursorWorld = new Vec3();

        // double click handler
        canvas.addEventListener('dblclick', async (event) => {
            const result = await this.picker.pick(event.offsetX, event.offsetY);
            if (result) {
                this.cameraControls.focus(result, this.camera.getPosition());
            }
        });

        // single click handler for mesh selection
        const clickHandler = async (event: MouseEvent) => {
            const result = await this.picker.pick(event.offsetX, event.offsetY);
            if (result) {
                const closestMesh = this.findClosestMeshInstance(result);
                const closestMeshNode = closestMesh
                    ? this.getMeshInstanceDisplayNode(closestMesh)
                    : null;
                if (closestMesh && closestMeshNode) {
                    const nodePath = closestMeshNode.path;
                    const materialInfo = this.getMeshMaterialInfo(closestMesh);

                    // Extract mesh name from path
                    // For nodes like "node_688", the mesh name is the parent
                    // For other nodes, use the node name itself as mesh name
                    const pathParts = nodePath.split('/');
                    let meshName = '';
                    if (closestMeshNode.name && closestMeshNode.name.match(/^node_\d+$/)) {
                        // If node name is like "node_688", use parent as mesh name
                        meshName = pathParts.length > 1 ? pathParts[pathParts.length - 2] : '';
                    } else {
                        // Otherwise, use the node name itself (the node IS the mesh)
                        meshName = closestMeshNode.name || '';
                    }

                    console.log('Selected mesh:', {
                        nodeName: closestMeshNode.name,
                        nodePath: nodePath,
                        worldPosition: result.toString(),
                        meshBounds: closestMesh.aabb,
                        materialName: materialInfo.materialName,
                        textures: materialInfo.textures,
                    });

                    // Check for Ctrl+Click for multi-selection
                    if (event.ctrlKey || event.metaKey) {
                        this.toggleMultiSelectNode(closestMeshNode.name, nodePath, meshName);
                    } else {
                        // Regular click - clear multi-selection and select single node
                        this.clearMultiSelection();
                        this.setSelectedNode(nodePath, {
                            meshName: meshName,
                            meshBounds: closestMesh.aabb
                                ? `center: [${closestMesh.aabb.center.x.toFixed(
                                      4
                                  )}, ${closestMesh.aabb.center.y.toFixed(
                                      4
                                  )}, ${closestMesh.aabb.center.z.toFixed(
                                      4
                                  )}], halfExtents: [${closestMesh.aabb.halfExtents.x.toFixed(
                                      4
                                  )}, ${closestMesh.aabb.halfExtents.y.toFixed(
                                      4
                                  )}, ${closestMesh.aabb.halfExtents.z.toFixed(4)}]`
                                : 'N/A',
                            materialName: materialInfo.materialName,
                        });
                    }
                } else {
                    console.log('No mesh found at clicked position');
                    this.clearSelection();
                }
            } else {
                console.log('No surface found at clicked position');
                this.clearSelection();
            }
        };

        const escapeHandler = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return;
            if (this.isAnyInputFocused) return;

            this.clearSelection();
        };

        // Use the helper function to only trigger on actual clicks (not drags)
        const removeClickListener = addEventListenerOnClickOnly(canvas, clickHandler, 2);
        window.addEventListener('keydown', escapeHandler, true);

        this.app.scene.layers.getLayerByName('World').transparentSortMode = SORTMODE_BACK2FRONT;

        // start the application
        app.start();
    }

    private initXrMode() {
        const xr = this.app.xr;

        this.xrMode = new XRObjectPlacementController({
            xr: xr,
            camera: this.camera,
            content: this.sceneRoot,
            showUI: true,
            startArImgSrc: arModeImage.src,
            stopArImgSrc: arCloseImage.src,
        });

        const events = this.xrMode.events;

        events.on('xr:started', () => {
            // prepare scene settings for AR mode
            this.setShadowCatcherEnabled(true);
            this.setShadowCatcherIntensity(0.3);
            this.setDebugGrid(false);
            this.setDebugBounds(false);
            this.setLightEnabled(true);
            this.setLightShadow(true);
            this.setLightFollow(false);
            this.setCenterScene(true);

            this.setSkyboxBackground('None');
            this.setSkyboxExposure(0);
            this.setBackgroundColor(Color.BLACK);
            this.app.scene.layers.getLayerById(LAYERID_SKYBOX).enabled = false;

            this.multiframe.blend = 0.5;

            // Clean up any camera center controls and detach camera controls for XR
            this.removeCameraCenterControls();
            this.cameraControls.enabled = false;
        });

        events.on('xr:initial-place', () => {
            this.multiframe.blend = 1.0;
        });

        events.on('xr:ended', () => {
            // reload all user options
            this.reloadSettings();

            // background color isn't correctly restored
            this.setBackgroundColor(this.observer.get('skybox.backgroundColor'));

            // Restore the orbit mode that was active before XR
            this.setOrbitMode(this.observer.get('camera.orbitMode'));

            this.multiframe.blend = 1.0;
        });
    }

    private getSelectedMeshInstances() {
        // Check for multi-selected nodes first
        const multiSelectedNodes: Array<{ name: string; path: string }> =
            this.observer.get('scene.multiSelectedNodes') || [];
        if (multiSelectedNodes.length > 0) {
            const allMeshInstances: MeshInstance[] = [];
            for (const nodeInfo of multiSelectedNodes) {
                const graphNode = this.app.root.findByPath(nodeInfo.path);
                if (graphNode) {
                    const meshes = this.collectMeshInstances(graphNode as Entity);
                    allMeshInstances.push(...meshes);
                }
            }
            return allMeshInstances;
        }

        // Fall back to single selection
        return this.selectedNode
            ? this.collectMeshInstances(this.selectedNode as Entity)
            : this.meshInstances;
    }

    private getMeshInstanceDisplayNode(meshInstance: MeshInstance): GraphNode | null {
        return ((meshInstance as any).__ifOriginalNode as GraphNode) || meshInstance.node || null;
    }

    // extract material name and texture information from a mesh instance
    private getMeshMaterialInfo(
        meshInstance: MeshInstance
    ): { materialName: string; textures: Array<{ property: string; textureName: string }> } {
        const material = meshInstance.material as StandardMaterial;

        if (!material) {
            return { materialName: 'No Material', textures: [] };
        }

        const materialName = material.name || 'Unnamed Material';
        const textures: Array<{ property: string; textureName: string }> = [];

        // Check common texture properties
        const textureProperties = [
            'diffuseMap',
            'normalMap',
            'specularMap',
            'glossMap',
            'metalnessMap',
            'emissiveMap',
            'aoMap',
            'heightMap',
            'opacityMap',
            'lightMap',
        ];

        textureProperties.forEach((prop) => {
            const texture = (material as any)[prop];

            if (texture && texture.name) {
                // Extract just the filename from the texture name (remove any livery path)
                const fullTextureName = texture.name;
                const textureName = fullTextureName.includes('/')
                    ? fullTextureName.split('/').pop()
                    : fullTextureName;
                textures.push({ property: prop, textureName: textureName });
            }
        });

        return { materialName, textures };
    }

    // find the mesh instance closest to a world coordinate point
    private findClosestMeshInstance(worldPos: Vec3): MeshInstance | null {
        let closestMesh: MeshInstance | null = null;
        let closestDistance = Infinity;

        for (const meshInstance of this.meshInstances) {
            // Check if the point is inside the mesh's AABB
            if (meshInstance.aabb.containsPoint(worldPos)) {
                const center = meshInstance.aabb.center;
                const distance = center.distance(worldPos);

                if (distance < closestDistance) {
                    closestDistance = distance;
                    closestMesh = meshInstance;
                }
            }
        }

        // If no mesh AABB contains the point, find the closest AABB
        if (!closestMesh) {
            for (const meshInstance of this.meshInstances) {
                const center = meshInstance.aabb.center;
                const distance = center.distance(worldPos);

                if (distance < closestDistance) {
                    closestDistance = distance;
                    closestMesh = meshInstance;
                }
            }
        }

        return closestMesh;
    }

    // Top-view outline of the loaded aircraft in its own model frame, for the
    // client's pushback plan. Geometry is read from the render components of the
    // loaded entities and expressed relative to the scene root, so the viewer's
    // centering, altitude and pitch offsets do not leak into the result.
    /**
     * KHR_mesh_quantization models store positions as normalized integers that the node's
     * scale maps back to metres. `Mesh.getPositions` returns the raw integers, so they
     * have to be brought back to [-1, 1] before the node transform is applied.
     */
    private static quantizedPositionScale(mesh: Mesh): number {
        const element = mesh.vertexBuffer?.format?.elements?.find(
            (candidate) => candidate.name === SEMANTIC_POSITION
        );
        if (!element || !element.normalize) {
            return 1;
        }
        switch (element.dataType) {
            case TYPE_INT8:
                return 1 / 127;
            case TYPE_UINT8:
                return 1 / 255;
            case TYPE_INT16:
                return 1 / 32767;
            case TYPE_UINT16:
                return 1 / 65535;
            case TYPE_INT32:
                return 1 / 2147483647;
            case TYPE_UINT32:
                return 1 / 4294967295;
            default:
                return 1;
        }
    }

    /**
     * Renders the loaded aircraft from straight above with an orthographic camera and
     * returns a square coverage mask of it: white where the model is, transparent
     * elsewhere, with the alpha edges the renderer produced. Layout contract shared with
     * the client: the mask's left edge is model +X and its top edge is the nose (model
     * −Z); it spans `size * metersPerPixel` metres centred on (centerX, centerZ) in the
     * model frame. `extents` is the planform's bounding box from the traced silhouette.
     */
    async captureAircraftSilhouetteMask(
        extents: { minX: number; maxX: number; minZ: number; maxZ: number },
        size = 256
    ): Promise<{ blob: Blob; size: number; metersPerPixel: number; centerX: number; centerZ: number } | null> {
        const camera = this.camera;
        const cameraComponent = camera.camera;
        const rt = cameraComponent.renderTarget;
        if (!rt || !rt.colorBuffer) {
            console.warn('[SilhouetteMask] No render target');
            return null;
        }
        const width = rt.colorBuffer.width;
        const height = rt.colorBuffer.height;
        const spanX = Math.max(extents.maxX - extents.minX, 1);
        const spanZ = Math.max(extents.maxZ - extents.minZ, 1);
        const centerX = (extents.minX + extents.maxX) * 0.5;
        const centerZ = (extents.minZ + extents.maxZ) * 0.5;
        // Half of the square the camera frames vertically, with room for what the trace
        // may have opened away (antennas, fences).
        const half = Math.max(spanX, spanZ) * 0.5 * 1.08;

        const bounds = new BoundingBox();
        this.calcSceneBounds(bounds);
        const rootWorld = this.sceneRoot.getWorldTransform();
        const portAxis = rootWorld.transformVector(new Vec3(1, 0, 0)).normalize();
        const noseAxis = rootWorld.transformVector(new Vec3(0, 0, -1)).normalize();
        const upAxis = rootWorld.transformVector(new Vec3(0, 1, 0)).normalize();
        const focus = rootWorld.transformPoint(new Vec3(centerX, 0, centerZ));
        const clearance = bounds.halfExtents.length() + 20;
        const eye = focus.clone().add(upAxis.clone().mulScalar(clearance));

        const saved = this.saveCameraState();
        const renderingState = this.saveRenderingState();
        try {
            // Skybox and background off, alpha clear: the read-back alpha is the coverage.
            this.setupTransparentBackground();
            camera.setPosition(eye);
            camera.lookAt(focus, noseAxis);
            cameraComponent.projection = 1; // PROJECTION_ORTHOGRAPHIC
            cameraComponent.orthoHeight = half;
            cameraComponent.nearClip = 0.1;
            cameraComponent.farClip = clearance * 2 + 10;

            this.app.renderNextFrame = true;
            this.app.render();
            if (rt.samples > 1) {
                rt.resolve();
            }
            await new Promise((resolve) => requestAnimationFrame(resolve));

            const readOptions =
                rt.samples > 1 ? { immediate: true } : { renderTarget: rt, immediate: true };
            const typedArray = await rt.colorBuffer.read(0, 0, width, height, readOptions);
            const pixels = new Uint32Array(typedArray.buffer.slice(0));

            // Metres per source pixel: the ortho height spans the target's height.
            const metersPerSource = (2 * half) / height;
            // Screen right is the camera's +X, which is model +X (port) here; the
            // contract wants +X on the left, so columns are mirrored on the way out.
            const alpha = new Uint8Array(width * height);
            let minCol = width, maxCol = -1, minRow = height, maxRow = -1;
            for (let row = 0; row < height; row++) {
                const srcRow = (height - 1 - row) * width; // the read-back is bottom-up
                for (let col = 0; col < width; col++) {
                    const a = (pixels[srcRow + col] >>> 24) & 0xff;
                    if (a > 16) {
                        alpha[row * width + col] = a;
                        if (col < minCol) minCol = col;
                        if (col > maxCol) maxCol = col;
                        if (row < minRow) minRow = row;
                        if (row > maxRow) maxRow = row;
                    }
                }
            }
            if (maxCol < 0) {
                console.warn('[SilhouetteMask] Nothing rendered');
                return null;
            }

            // The square around the covered pixels, two pixels of margin.
            const pad = 2;
            const boxW = maxCol - minCol + 1 + 2 * pad;
            const boxH = maxRow - minRow + 1 + 2 * pad;
            const side = Math.max(boxW, boxH);
            const boxLeft = (minCol + maxCol + 1) / 2 - side / 2;
            const boxTop = (minRow + maxRow + 1) / 2 - side / 2;
            const scale = size / side;

            // Model-frame centre of that square: columns run toward −X, rows toward +Z.
            const boxCenterCol = boxLeft + side / 2;
            const boxCenterRow = boxTop + side / 2;
            const maskCenterX = centerX - (boxCenterCol - width / 2) * metersPerSource;
            const maskCenterZ = centerZ + (boxCenterRow - height / 2) * metersPerSource;
            const metersPerPixel = (side * metersPerSource) / size;

            const source = document.createElement('canvas');
            source.width = width;
            source.height = height;
            const sourceContext = source.getContext('2d');
            const target = document.createElement('canvas');
            target.width = size;
            target.height = size;
            const targetContext = target.getContext('2d');
            if (!sourceContext || !targetContext) {
                return null;
            }
            const image = sourceContext.createImageData(width, height);
            for (let i = 0; i < width * height; i++) {
                image.data[i * 4] = 255;
                image.data[i * 4 + 1] = 255;
                image.data[i * 4 + 2] = 255;
                image.data[i * 4 + 3] = alpha[i];
            }
            sourceContext.putImageData(image, 0, 0);
            targetContext.imageSmoothingEnabled = true;
            targetContext.imageSmoothingQuality = 'high';
            // Mirror horizontally so model +X lands on the left edge.
            targetContext.translate(size, 0);
            targetContext.scale(-1, 1);
            targetContext.drawImage(source, boxLeft, boxTop, side, side, 0, 0, size, size);
            targetContext.setTransform(1, 0, 0, 1, 0, 0);
            source.width = 0;
            source.height = 0;

            const blob = await new Promise<Blob | null>((resolve) => target.toBlob(resolve, 'image/png'));
            if (!blob) {
                return null;
            }
            console.log(
                `[SilhouetteMask] ${size}px, ${(size * metersPerPixel).toFixed(1)} m square centred at (${maskCenterX.toFixed(2)}, ${maskCenterZ.toFixed(2)}), ${blob.size} bytes`
            );
            return { blob, size, metersPerPixel, centerX: maskCenterX, centerZ: maskCenterZ };
        } catch (error) {
            console.error('[SilhouetteMask] Capture failed', error);
            return null;
        } finally {
            this.restoreRenderingState(renderingState);
            this.restoreCameraState(saved);
            this.renderNextFrame();
        }
    }

    computeAircraftSilhouette(): AircraftSilhouette | null {
        const meshes: SilhouetteMeshInput[] = [];
        const rootInverse = this.sceneRoot.getWorldTransform().clone().invert();
        const local = new Mat4();
        for (const entity of this.entities) {
            const components = entity.findComponents('render') as RenderComponent[];
            for (const render of components) {
                if (!render.meshInstances) continue;
                for (const meshInstance of render.meshInstances) {
                    const mesh = meshInstance.mesh;
                    if (!mesh) continue;
                    const positions: number[] = [];
                    if (mesh.getPositions(positions) === 0) continue;
                    const quantizedScale = Viewer.quantizedPositionScale(mesh);
                    if (quantizedScale !== 1) {
                        for (let i = 0; i < positions.length; i++) {
                            positions[i] *= quantizedScale;
                        }
                    }
                    const indices: number[] = [];
                    const indexCount = mesh.getIndices(indices);
                    local.mul2(rootInverse, meshInstance.node.getWorldTransform());
                    meshes.push({
                        positions,
                        indices: indexCount > 0 ? indices : null,
                        transform: Array.from(local.data)
                    });
                }
            }
        }
        const selectedLOD = this.observer.get('lods.selected');
        return computeAircraftSilhouette(meshes, { source: typeof selectedLOD === 'string' ? selectedLOD : '' });
    }

    // collects all mesh instances from entity hierarchy
    private collectMeshInstances(entity: Entity) {
        const meshInstances: Array<MeshInstance> = [];
        if (entity) {
            const components = entity.findComponents('render');
            for (let i = 0; i < components.length; i++) {
                const render = components[i] as RenderComponent;
                if (render.meshInstances) {
                    for (let m = 0; m < render.meshInstances.length; m++) {
                        const meshInstance = render.meshInstances[m];
                        meshInstances.push(meshInstance);
                    }
                }
            }

            const gsplatComponents = entity.findComponents('gsplat');
            for (let i = 0; i < gsplatComponents.length; i++) {
                const gsplat = gsplatComponents[i] as GSplatComponent;
                if (gsplat.instance) {
                    meshInstances.push(gsplat.instance.meshInstance);
                }
            }
        }
        return meshInstances;
    }

    // calculate the bounding box of the given mesh
    private static calcMeshBoundingBox(result: BoundingBox, meshInstances: Array<MeshInstance>) {
        if (meshInstances.length > 0) {
            result.copy(meshInstances[0].aabb);
            for (let i = 1; i < meshInstances.length; ++i) {
                result.add(meshInstances[i].aabb);
            }
        }
    }

    // calculate the bounding box of the graph-node hierarchy
    private static calcHierBoundingBox(result: BoundingBox, rootNode: Entity) {
        const position = rootNode.getPosition();
        let min_x = position.x;
        let min_y = position.y;
        let min_z = position.z;
        let max_x = position.x;
        let max_y = position.y;
        let max_z = position.z;

        const recurse = (node: GraphNode) => {
            const p = node.getPosition();
            min_x = Math.min(min_x, p.x);
            min_y = Math.min(min_y, p.y);
            min_z = Math.min(min_z, p.z);

            max_x = Math.max(max_x, p.x);
            max_y = Math.max(max_y, p.y);
            max_z = Math.max(max_z, p.z);

            for (let i = 0; i < node.children.length; ++i) {
                recurse(node.children[i]);
            }
        };
        recurse(rootNode);

        result.setMinMax(new Vec3(min_x, min_y, min_z), new Vec3(max_x, max_y, max_z));
    }

    // construct the controls interface and initialize controls
    private bindControlEvents() {
        const controlEvents: Record<string, (...args: any[]) => void> = {
            // ui
            'ui.fullscreen:set': (fullscreen: boolean) => {
                if (fullscreen) {
                    document.body.requestFullscreen();
                } else {
                    if (document.fullscreenElement) {
                        document.exitFullscreen();
                    }
                }
            },

            // camera
            'camera.fov': this.setFov.bind(this),
            'camera.tonemapping': this.setTonemapping.bind(this),
            'camera.exposure': this.setCameraExposure.bind(this),
            'camera.pixelScale': () => {
                this.canvasResize = true;
                this.renderNextFrame();
            },
            'camera.multisample': () => {
                this.destroyRenderTargets();
                this.renderNextFrame();
            },
            'camera.hq': (enabled: boolean) => {
                this.multiframe.enabled = enabled;
                this.renderNextFrame();
            },
            'camera.orbitMode': this.setOrbitMode.bind(this),
            'camera.uiCameraMode': this.setUICameraMode.bind(this),

            // skybox
            'skybox.value': (value: string) => {
                if (value === 'Basic Skybox') {
                    // Generate the basic skybox
                    this.generateBasicSkybox();
                } else if (this.skyboxUrls.has(value)) {
                    const url = this.skyboxUrls.get(value);
                    this.loadFiles([{ url, filename: url }]);
                } else if (value === 'None') {
                    this.clearSkybox();
                } else {
                    this.loadFiles([{ url: value, filename: value }]);
                }
            },
            'skybox.exposure': this.setSkyboxExposure.bind(this),
            'skybox.rotation': this.setSkyboxRotation.bind(this),
            'skybox.blur': this.setSkyboxBlur.bind(this),
            'skybox.background': this.setSkyboxBackground.bind(this),
            'skybox.backgroundColor': this.setBackgroundColor.bind(this),
            'skybox.domeProjection.domeRadius': this.setSkyboxDomeRadius.bind(this),
            'skybox.domeProjection.tripodOffset': this.setSkyboxTripodOffset.bind(this),

            // light
            'light.enabled': this.setLightEnabled.bind(this),
            'light.intensity': this.setLightIntensity.bind(this),
            'light.color': this.setLightColor.bind(this),
            'light.ambientIntensity': this.setAmbientLightIntensity.bind(this),
            'light.follow': this.setLightFollow.bind(this),
            'light.shadow': this.setLightShadow.bind(this),

            // shadow catcher
            'shadowCatcher.enabled': this.setShadowCatcherEnabled.bind(this),
            'shadowCatcher.intensity': this.setShadowCatcherIntensity.bind(this),

            // aircraft rotation
            'aircraftRotation.pitch': this.setAircraftRotation.bind(this),
            'aircraftRotation.roll': this.setAircraftRotation.bind(this),

            // debug
            'debug.stats': this.setDebugStats.bind(this),
            'debug.wireframe': this.setDebugWireframe.bind(this),
            'debug.wireframeColor': this.setWireframeColor.bind(this),
            'debug.bounds': this.setDebugBounds.bind(this),
            'debug.skeleton': this.setDebugSkeleton.bind(this),
            'debug.axes': this.setDebugAxes.bind(this),
            'debug.grid': this.setDebugGrid.bind(this),
            'debug.normals': this.setNormalLength.bind(this),
            'debug.normalMaps': this.setNormalMapsEnabled.bind(this),
            'debug.renderMode': this.setRenderMode.bind(this),

            // animation
            'animation.playing': (playing: boolean) => {
                if (playing) {
                    this.play();
                } else {
                    this.stop();
                }
            },
            'animation.selectedTrack': this.setSelectedTrack.bind(this),
            'animation.speed': this.setSpeed.bind(this),
            'animation.transition': this.setTransition.bind(this),
            'animation.loops': this.setLoops.bind(this),
            'animation.progress': this.setAnimationProgress.bind(this),

            'scene.selectedNode.path': this.setSelectedNode.bind(this),
            'scene.variant.selected': this.setSelectedVariant.bind(this),

            // livery switching
            'liveries.selected': (liveryName: string) => {
                // Only apply livery if material maps are ready
                // Otherwise, it will be handled by onPostrender after material maps are built
                if (this.materialTextureMap.size > 0 || this.meshInstances.length > 0) {
                    this.setSelectedLivery(liveryName);
                } else {
                    console.log(
                        '[Viewer] Deferring livery selection - material maps not ready yet. Will be handled by onPostrender.'
                    );
                }
            },

            // LOD switching
            'lods.selected': (lodName: string) => {
                this.setSelectedLOD(lodName);
            },

            centerScene: this.setCenterScene.bind(this),
        };

        // store control event keys
        this.controlEventKeys = Object.keys(controlEvents);

        // register control events
        this.controlEventKeys.forEach((e) => {
            this.observer.on(`${e}:set`, controlEvents[e]);
        });

        // Handle individual animation controls dynamically
        this.observer.on('*:set', (path: string, value: any) => {
            if (path.startsWith('animations.')) {
                const parts = path.split('.');
                if (parts.length === 3) {
                    const animationName = parts[1];
                    const property = parts[2];

                    if (property === 'progress') {
                        this.setIndividualAnimationProgress(animationName, value);
                    } else if (property === 'playing') {
                        this.setIndividualAnimationPlaying(animationName, value);
                    }
                }
            }
        });
    }

    // Set individual animation playing state
    setIndividualAnimationPlaying(animationName: string, playing: boolean) {
        const trackPath = this.animationMap[animationName];

        if (trackPath) {
            if (playing && this.individualAnimationChannels.has(animationName)) {
                this.animationProgresses.clear();
                this.restoreAnimatedLocalTransforms();
            }

            this.entities.forEach((entity) => {
                if (entity.anim && entity.anim.baseLayer) {
                    if (playing) {
                        entity.anim.baseLayer.transition(trackPath);
                        entity.anim.playing = true;
                        entity.anim.baseLayer.play();
                    } else {
                        // Stop this specific animation - always pause, regardless of active state
                        entity.anim.playing = false;
                        entity.anim.baseLayer.pause();
                    }
                }
            });

            // Update the individual animation states - when one plays, stop all others
            const animations = this.observer.get('animations') || {};
            Object.keys(animations).forEach((key) => {
                if (key === animationName) {
                    animations[key].playing = playing;
                } else if (playing) {
                    // If we're starting this animation, stop all others
                    animations[key].playing = false;
                    if (this.individualAnimationChannels.has(animationName)) {
                        animations[key].progress = 0;
                    }
                }
            });

            this.observer.suspendEvents = true;
            this.observer.set('animations', animations);
            this.observer.suspendEvents = false;

            this.refreshSelectedNodeTransform();
        }
    }

    // Set individual animation progress
    setIndividualAnimationProgress(animationName: string, progress: number) {
        const trackPath = this.animationMap[animationName];
        if (trackPath) {
            this.entities.forEach((entity) => {
                if (entity.anim?.baseLayer) {
                    entity.anim.playing = false;
                    entity.anim.baseLayer.pause();
                }
            });

            if (this.individualAnimationChannels.has(animationName)) {
                this.animationProgresses.clear();
                this.restoreAnimatedLocalTransforms();
            }

            this.animationProgresses.set(animationName, progress);
            this.applyActiveAnimationProgressPose();

            // Update the individual animation states - set all to paused when scrubbing
            const animations = this.observer.get('animations') || {};
            Object.keys(animations).forEach((key) => {
                if (key === animationName) {
                    animations[key].progress = progress;
                } else if (this.individualAnimationChannels.has(animationName)) {
                    animations[key].progress = 0;
                }
                animations[key].playing = false; // Scrubbing pauses all animations
            });

            this.observer.suspendEvents = true;
            this.observer.set('animations', animations);
            this.observer.suspendEvents = false;

            this.refreshSelectedNodeTransform();
            this.renderNextFrame();
        } else {
            console.log(`  ✗ Track path not found for animation "${animationName}"`);
            console.log(`  Available animations in map:`, Object.keys(this.animationMap));
        }
    }

    private reloadSettings() {
        this.controlEventKeys.forEach((e) => {
            this.observer.set(e, this.observer.get(e), false, false, true);
        });
    }

    private clearSkybox() {
        this.app.scene.envAtlas = null;
        this.app.scene.setSkybox(null);
        this.renderNextFrame();
        this.skyboxLoaded = false;
    }

    // initialize the faces and prefiltered lighting data from the given
    // skybox texture, which is either a cubemap or equirect texture.
    private initSkybox(source: Texture) {
        const skybox = EnvLighting.generateSkyboxCubemap(source);
        const lighting = EnvLighting.generateLightingSource(source);
        // The second options parameter should not be necessary but the TS declarations require it for now
        const envAtlas = EnvLighting.generateAtlas(lighting, {});
        lighting.destroy();
        this.app.scene.envAtlas = envAtlas;
        this.app.scene.skybox = skybox;

        this.renderNextFrame();
    }

    // generate a basic skybox with light blue sky and gray ground
    generateBasicSkybox() {
        // Create a canvas to draw the equirectangular texture
        // Standard equirectangular format is 2:1 aspect ratio (width:height)
        const width = 2048;
        const height = 1024;
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
            console.error('Failed to get canvas context');
            return;
        }

        // Create gradient from white (sky) to target gray (ground)
        // Target gray: RGB(128, 128, 128) - 50% neutral gray
        // Sky: White RGB(255, 255, 255)
        // Use smooth transitions to:
        //   1. Avoid hard shadow line on aircraft fuselages (horizon transition)
        //   2. Reduce intense shine on top-down views (softer zenith)
        const targetGray = 128;
        const groundColor = { r: targetGray, g: targetGray, b: targetGray };
        const skyColor = { r: 255, g: 255, b: 255 };
        // Softer zenith to reduce shine on top-down views
        const zenithColor = { r: 225, g: 225, b: 225 };
        // Intermediate blend colors for smooth horizon transition
        const lightBlend = { r: 220, g: 220, b: 220 };
        const darkBlend = { r: 160, g: 160, b: 160 };

        // Create vertical gradient (top = sky, bottom = ground) with smooth transitions
        // - Zenith (0.0) is slightly softer to reduce top-down view shine
        // - Sky brightens toward horizon for good side-view lighting
        // - Gradual horizon blend zone (~30%) eliminates hard shadow lines in IBL lighting
        const gradient = ctx.createLinearGradient(0, 0, 0, height);
        gradient.addColorStop(0, `rgb(${zenithColor.r}, ${zenithColor.g}, ${zenithColor.b})`); // Softer zenith for top-down views
        gradient.addColorStop(0.15, `rgb(${skyColor.r}, ${skyColor.g}, ${skyColor.b})`); // Brighten toward horizon
        gradient.addColorStop(0.35, `rgb(${skyColor.r}, ${skyColor.g}, ${skyColor.b})`); // Keep sky color in upper portion
        gradient.addColorStop(0.45, `rgb(${lightBlend.r}, ${lightBlend.g}, ${lightBlend.b})`); // Begin smooth horizon transition
        gradient.addColorStop(0.55, `rgb(${darkBlend.r}, ${darkBlend.g}, ${darkBlend.b})`); // Continue transition
        gradient.addColorStop(0.65, `rgb(${groundColor.r}, ${groundColor.g}, ${groundColor.b})`); // Complete transition to ground
        gradient.addColorStop(1, `rgb(${groundColor.r}, ${groundColor.g}, ${groundColor.b})`);

        // Fill the canvas with the gradient
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, width, height);

        // Convert canvas to blob URL and load as texture asset
        canvas.toBlob((blob) => {
            if (!blob) {
                console.error('Failed to create blob from canvas');
                return;
            }

            // Revoke old URL if it exists
            if (this.generatedSkyboxUrl) {
                URL.revokeObjectURL(this.generatedSkyboxUrl);
            }

            const blobUrl = URL.createObjectURL(blob);
            this.generatedSkyboxUrl = blobUrl; // Store for viewing

            const textureAsset = new Asset('skybox_basic', 'texture', {
                url: blobUrl,
                filename: 'basic-skybox.png',
            });

            textureAsset.ready(() => {
                const texture = textureAsset.resource as Texture;
                // Don't use RGBM encoding for generated skybox - it causes white scene
                // Keep as TEXTURETYPE_DEFAULT for proper display
                // RGBM is meant for HDR images, not simple gradients
                this.initSkybox(texture);

                // Update observer to reflect that Basic Skybox is now active
                this.observer.set('skybox.value', 'Basic Skybox');

                // Clean up asset but keep blob URL for viewing
                textureAsset.unload();
                this.app.assets.remove(textureAsset);
                // Don't revoke blob URL here - we need it for viewing

                this.skyboxLoaded = true;
            });

            this.app.assets.add(textureAsset);
            this.app.assets.load(textureAsset);
        }, 'image/png');
    }

    // View the current skybox texture in a new window
    viewCurrentSkybox() {
        const currentSkybox = this.observer.get('skybox.value');

        if (currentSkybox === 'Basic Skybox' && this.generatedSkyboxUrl) {
            // Open generated skybox in new window
            window.open(this.generatedSkyboxUrl, '_blank');
        } else if (
            currentSkybox &&
            currentSkybox !== 'None' &&
            this.skyboxUrls.has(currentSkybox)
        ) {
            // Open regular skybox URL (relative paths will resolve correctly)
            const url = this.skyboxUrls.get(currentSkybox);
            window.open(url, '_blank');
        } else {
            console.warn('No skybox available to view');
        }
    }

    // load the image files into the skybox. this function supports loading a single equirectangular
    // skybox image or 6 cubemap faces.
    private loadSkybox(files: Array<File>) {
        const app = this.app;

        if (files.length !== 6) {
            // load equirectangular skybox
            const textureAsset = new Asset('skybox_equi', 'texture', {
                url: files[0].url,
                filename: files[0].filename,
            });
            textureAsset.ready(() => {
                const texture = textureAsset.resource as Texture;
                if (texture.type === TEXTURETYPE_DEFAULT && texture.format === PIXELFORMAT_RGBA8) {
                    // assume RGBA data (pngs) are RGBM
                    texture.type = TEXTURETYPE_RGBM;
                }
                this.initSkybox(texture);

                // if we don't unload the texture asset and user selects it a second time, the
                // brightness is completely wrong.
                textureAsset.unload();
                app.assets.remove(textureAsset);
            });
            app.assets.add(textureAsset);
            app.assets.load(textureAsset);
        } else {
            // sort files into the correct order based on filename
            const names = [
                ['posx', 'negx', 'posy', 'negy', 'posz', 'negz'],
                ['px', 'nx', 'py', 'ny', 'pz', 'nz'],
                ['right', 'left', 'up', 'down', 'front', 'back'],
                ['right', 'left', 'top', 'bottom', 'forward', 'backward'],
                ['0', '1', '2', '3', '4', '5'],
            ];

            const getOrder = (filename: string) => {
                const fn = filename.toLowerCase();
                for (let i = 0; i < names.length; ++i) {
                    const nameList = names[i];
                    for (let j = 0; j < nameList.length; ++j) {
                        if (fn.indexOf(`${nameList[j]}.`) !== -1) {
                            return j;
                        }
                    }
                }
                return 0;
            };

            const sortPred = (first: File, second: File) => {
                const firstOrder = getOrder(first.filename);
                const secondOrder = getOrder(second.filename);
                return firstOrder < secondOrder ? -1 : secondOrder < firstOrder ? 1 : 0;
            };

            files.sort(sortPred);

            // construct an asset for each cubemap face
            const faceAssets = files.map((file, index) => {
                const faceAsset = new Asset(`skybox_face${index}`, 'texture', file);
                app.assets.add(faceAsset);
                app.assets.load(faceAsset);
                return faceAsset;
            });

            // construct the cubemap asset
            const cubemapAsset = new Asset('skybox_cubemap', 'cubemap', null, {
                textures: faceAssets.map((faceAsset) => faceAsset.id),
            });
            cubemapAsset.loadFaces = true;
            cubemapAsset.on('load', () => {
                this.initSkybox(cubemapAsset.resource as Texture);
            });
            app.assets.add(cubemapAsset);
            app.assets.load(cubemapAsset);
        }
        this.skyboxLoaded = true;
    }

    private getCanvasSize() {
        const s = this.canvas.getBoundingClientRect();
        return {
            width: s.width,
            height: s.height,
        };
    }

    private getFocusPosition(bbox: BoundingBox) {
        const focus = new Vec3();
        if (this.initialCameraFocus) {
            focus.copy(this.initialCameraFocus);
            this.initialCameraFocus = null;
        } else {
            const entityAsset = this.entityAssets[0];
            const splatData = (entityAsset?.asset?.resource as GSplatResource)
                ?.gsplatData as GSplatData;
            if (splatData) {
                splatData.calcFocalPoint(focus, () => true);
                entityAsset.entity.getWorldTransform().transformPoint(focus, focus);
            } else {
                focus.copy(bbox.center);
            }
        }
        return focus;
    }

    private getZoomDist() {
        const camera = this.camera.camera;
        const d1 = Math.tan(0.5 * FOCUS_FOV * math.DEG_TO_RAD);
        const d2 = Math.tan(0.5 * camera.fov * math.DEG_TO_RAD);

        const scale = (d1 / d2) * (1 / camera.aspectRatio);
        return scale * this.cameraControls.sceneSize + this.cameraControls.sceneSize;
    }

    private focusSelection(calcStart = true) {
        // calculate scene bounding box
        this.calcSceneBounds(bbox, this.selectedNode as Entity);
        this.cameraControls.sceneSize = bbox.halfExtents.length();

        // calculate the camera focus point
        const focus = this.getFocusPosition(bbox);

        let start: Vec3 | null = null;
        if (calcStart) {
            start = new Vec3();
            if (this.initialCameraPosition) {
                start.copy(this.initialCameraPosition);
                this.initialCameraPosition = null;
            } else {
                start.copy(focus);
                start.z += this.getZoomDist();
            }

            // set initial camera position
            this.camera.setPosition(start);

            // refit camera clip planes
            this.fitCameraClipPlanes();
        }

        this.cameraControls.focus(focus, start);
    }

    destroyRenderTargets() {
        const rt = this.camera.camera.renderTarget;
        if (rt) {
            rt.colorBuffer?.destroy();
            rt.depthBuffer?.destroy();
            rt.destroy();
            this.camera.camera.renderTarget = null;
        }
    }

    rebuildRenderTargets() {
        const device = this.app.graphicsDevice;

        // get the canvas UI size
        const widthPixels = device.width;
        const heightPixels = device.height;

        const old = this.camera.camera.renderTarget;
        if (old && old.width === widthPixels && old.height === heightPixels) {
            return;
        }

        // out with the old
        this.destroyRenderTargets();

        const createTexture = (width: number, height: number, format: number) => {
            return new Texture(device, {
                name: 'viewer-rt-texture',
                width: width,
                height: height,
                format: format,
                mipmaps: false,
                minFilter: FILTER_NEAREST,
                magFilter: FILTER_NEAREST,
                addressU: ADDRESS_CLAMP_TO_EDGE,
                addressV: ADDRESS_CLAMP_TO_EDGE,
            });
        };

        // @ts-ignore
        const maxSamples = device.maxSamples;

        // in with the new
        const colorBuffer = createTexture(widthPixels, heightPixels, PIXELFORMAT_RGBA8);
        const depthBuffer = createTexture(widthPixels, heightPixels, PIXELFORMAT_DEPTH);
        const renderTarget = new RenderTarget({
            name: 'viewer-rt',
            colorBuffer: colorBuffer,
            depthBuffer: depthBuffer,
            flipY: false,
            samples: this.observer.get('camera.multisample') ? maxSamples : 1,
            autoResolve: false,
        });
        this.camera.camera.renderTarget = renderTarget;
    }

    // reset the viewer, unloading resources
    resetScene() {
        const app = this.app;

        this.entities.forEach((entity) => {
            this.sceneRoot.removeChild(entity);
            this.shadowCatcher.onEntityRemoved(entity);
            entity.destroy();
        });
        this.entities = [];

        // Reset altitude and pitch offset flags when clearing entities
        this.altitudeOffsetApplied = false;
        this.pitchOffsetApplied = false;

        // Clear stored original transforms
        this.originalSceneRootPosition = null;
        this.originalSceneRootRotation = null;
        this.originalEntityTransforms = [];

        // Clear any active WASD keys when resetting scene
        if (this.cameraCenterMouseHandlers) {
            const keysPressed = (this.cameraCenterMouseHandlers as any).keysPressed;
            if (keysPressed && keysPressed.clear) {
                keysPressed.clear();
            }
        }

        this.assets.forEach((asset) => {
            app.assets.remove(asset);
            asset.unload();
        });
        this.assets = [];

        this.entityAssets = [];

        this.meshInstances = [];
        this.resetWireframeMeshes();

        // reset animation state
        this.animTracks = [];
        this.animationMap = {};
        this.animationProgresses.clear();
        this.animationSnapshots.clear();
        this.individualAnimationChannels.clear();
        this.restLocalTransforms.clear();
        this.gltfAnimationNodeMap = new Map();
        this.gltfNodeNames = [];

        // reset files list
        this.observer.set('files.list', []);

        // reset livery system
        this.baseTextures.clear();
        this.liveryTextures.clear();
        this.materialTextureMap.clear();
        this.liveryIdMap.clear();
        this.observer.set('liveries.available', ['Generic']);
        this.observer.set('liveries.selected', 'Generic');
        this.observer.set('liveries.loading', false);

        // reset LOD system
        this.observer.set('lods.available', []);
        this.observer.set('lods.selected', '');
        this.observer.set('lods.loading', false);
    }

    updateSceneStats() {
        // Prevent infinite loops by checking if we're already updating
        if ((this as any)._updatingSceneStats) {
            return;
        }
        (this as any)._updatingSceneStats = true;

        let meshCount = 0;
        let meshVRAM = 0;
        let vertexCount = 0;
        let primitiveCount = 0;
        let materialCount = 0;
        let textureCount = 0;
        let textureVRAM = 0;
        let variants: string[] = [];
        const textureDetails: Array<{
            name: string;
            filename: string;
            width: number;
            height: number;
            format: string;
            size: number;
            type: string;
        }> = [];

        // update mesh stats
        this.assets.forEach((asset) => {
            if (asset.resource instanceof GSplatResourceBase) {
                const resource = asset.resource as GSplatResource;

                meshCount++;
                materialCount++;
                primitiveCount += resource.gsplatData.numSplats;
                vertexCount += resource.gsplatData.numSplats * 4;
                meshVRAM += resource.gsplatData.numSplats * 64; // 16 * float32
            } else if (asset.type === 'container') {
                // Only process container assets for scene stats (skip individual texture assets)
                const resource = asset.resource as any;

                variants = variants.concat(resource.getMaterialVariants() ?? []);

                resource.renders.forEach((renderAsset: Asset) => {
                    const res = renderAsset.resource as any;
                    meshCount += res.meshes.length;
                    res.meshes.forEach((mesh: Mesh) => {
                        vertexCount += mesh.vertexBuffer.getNumVertices();

                        const prim = mesh.primitive[0];
                        switch (prim.type) {
                            case PRIMITIVE_POINTS:
                                primitiveCount += prim.count;
                                break;
                            case PRIMITIVE_LINES:
                                primitiveCount += prim.count / 2;
                                break;
                            case PRIMITIVE_LINELOOP:
                                primitiveCount += prim.count;
                                break;
                            case PRIMITIVE_LINESTRIP:
                                primitiveCount += prim.count - 1;
                                break;
                            case PRIMITIVE_TRIANGLES:
                                primitiveCount += prim.count / 3;
                                break;
                            case PRIMITIVE_TRISTRIP:
                                primitiveCount += prim.count - 2;
                                break;
                            case PRIMITIVE_TRIFAN:
                                primitiveCount += prim.count - 2;
                                break;
                        }
                        meshVRAM +=
                            mesh.vertexBuffer.numBytes + (mesh.indexBuffer?.[0]?.numBytes ?? 0);
                    });
                });

                materialCount += resource.materials.length ?? 0;
                textureCount += resource.textures.length ?? 0;
                (resource.textures ?? []).forEach((textureAsset: Asset) => {
                    const texture = textureAsset.resource as Texture;
                    textureVRAM += texture.gpuSize;

                    // Collect detailed texture information
                    const formatNames: Record<number, string> = {
                        0: 'L8',
                        1: 'L8_A8',
                        2: 'R5_G6_B5',
                        3: 'R5_G5_B5_A1',
                        4: 'R4_G4_B4_A4',
                        5: 'R8_G8_B8',
                        6: 'R8_G8_B8_A8',
                        7: 'DXT1',
                        8: 'DXT3',
                        9: 'DXT5',
                        10: 'RGB16F',
                        11: 'RGBA16F',
                        12: 'RGB32F',
                        13: 'RGBA32F',
                        14: 'R32F',
                        15: 'DEPTH',
                        16: 'ETC1',
                        17: 'ETC2_RGB',
                        18: 'ETC2_RGBA',
                        19: 'PVRTC_2BPP_RGB_1',
                        20: 'PVRTC_2BPP_RGBA_1',
                        21: 'PVRTC_4BPP_RGB_1',
                        22: 'PVRTC_4BPP_RGBA_1',
                        23: 'ASTC_4x4',
                        24: 'ATC_RGB',
                        25: 'ATC_RGBA',
                    };

                    const typeNames: Record<number, string> = {
                        0: '2D',
                        1: 'CUBE',
                        2: '3D',
                        3: 'RGBM',
                    };

                    textureDetails.push({
                        name: textureAsset.name,
                        filename: (textureAsset.file as any)?.filename || textureAsset.name,
                        width: texture.width,
                        height: texture.height,
                        format:
                            typeof texture.format === 'number'
                                ? formatNames[texture.format] || `Format ${texture.format}`
                                : String(texture.format),
                        size: texture.gpuSize,
                        type:
                            typeof texture.type === 'number'
                                ? typeNames[texture.type] || `Type ${texture.type}`
                                : String(texture.type),
                    });
                });
            }
            // Skip individual texture assets - they don't have scene statistics
        });

        const mapChildren = function (node: GraphNode): Array<HierarchyNode> {
            return node.children.map((child: GraphNode) => ({
                name: child.name,
                path: child.path,
                children: mapChildren(child),
            }));
        };

        const graph: Array<HierarchyNode> = this.entities.map((entity) => {
            return {
                name: entity.name,
                path: entity.path,
                children: mapChildren(entity),
            };
        });

        // Collect material data with their texture relationships
        const materialDetails: Array<{
            name: string;
            id: string;
            textures: Array<{
                property: string;
                textureName: string;
                textureFilename: string;
            }>;
        }> = [];

        // Build material data from the materialTextureMap
        let materialId = 0;
        this.materialTextureMap.forEach((textureMap, material) => {
            const materialData = {
                name: material.name || `Material ${materialId}`,
                id: `material_${materialId}`,
                textures: Array.from(textureMap.entries()).map(([property, textureName]) => {
                    // Find the texture filename from textureDetails
                    const textureDetail = textureDetails.find(
                        (t) => t.name === textureName || t.filename === textureName
                    );
                    return {
                        property: property,
                        textureName: textureName,
                        textureFilename: textureDetail?.filename || textureName,
                    };
                }),
            };
            materialDetails.push(materialData);
            materialId++;
        });

        // Batch all observer updates to avoid triggering multiple events
        this.observer.suspendEvents = true;

        // hierarchy
        this.observer.set('scene.nodes', JSON.stringify(graph));

        // mesh stats
        this.observer.set('scene.meshCount', meshCount);
        this.observer.set('scene.materialCount', materialCount);
        this.observer.set('scene.textureCount', textureCount);
        this.observer.set('scene.vertexCount', vertexCount);
        this.observer.set('scene.primitiveCount', primitiveCount);
        this.observer.set('scene.textureVRAM', textureVRAM);
        this.observer.set('scene.meshVRAM', meshVRAM);

        // variant stats
        this.observer.set('scene.variants.list', JSON.stringify(variants));
        this.observer.set('scene.variant.selected', variants[0]);

        // texture details
        this.observer.set('scene.textures', textureDetails);

        // material details
        this.observer.set('scene.materials', materialDetails);

        // Resume events and clear the updating flag
        this.observer.suspendEvents = false;
        (this as any)._updatingSceneStats = false;
    }

    downloadPngScreenshot() {
        const texture = this.camera.camera.renderTarget.colorBuffer;
        texture.read(0, 0, texture.width, texture.height).then((typedArray: Uint32Array) => {
            // Generate timestamp for filename
            const now = new Date();
            const timestamp = now
                .toISOString()
                .replace(/:/g, '-')
                .replace(/\..+/, '')
                .replace('T', '_');
            const filename = `if-model-viewer_${timestamp}.png`;

            this.pngExporter.export(
                filename,
                new Uint32Array(typedArray.buffer.slice(0)),
                texture.width,
                texture.height
            );
        });
    }

    // Soften alpha channel edges to reduce aliasing artifacts
    // Improved algorithm: Uses Gaussian-like weighting and processes more edge pixels
    private softenAlphaEdges(
        data: Uint8ClampedArray,
        width: number,
        height: number,
        radius: number = 1
    ) {
        console.log(
            `[softenAlphaEdges] Starting edge softening: ${width}x${height}, radius: ${radius}`
        );
        const totalPixels = width * height;
        let processedPixels = 0;

        // Create a copy of alpha channel for reading (we'll read from original during processing)
        const alphaOriginal = new Uint8ClampedArray(width * height);
        for (let i = 0; i < width * height; i++) {
            alphaOriginal[i] = data[i * 4 + 3];
        }

        // Pre-calculate Gaussian-like weights for smoother blending
        const weights: number[] = [];
        const weightSum = (() => {
            let sum = 0;
            for (let dy = -radius; dy <= radius; dy++) {
                for (let dx = -radius; dx <= radius; dx++) {
                    const distSq = dx * dx + dy * dy;
                    if (distSq <= radius * radius) {
                        // Gaussian-like weight: e^(-dist^2 / (2 * sigma^2))
                        // Using sigma = radius / 2 for a smooth falloff
                        const sigma = radius / 2;
                        const weight = Math.exp(-distSq / (2 * sigma * sigma));
                        weights.push(weight);
                        sum += weight;
                    } else {
                        weights.push(0);
                    }
                }
            }
            return sum;
        })();

        // Apply edge softening using Gaussian-weighted blur on edge pixels
        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                const idx = (y * width + x) * 4;
                const alpha = alphaOriginal[y * width + x];

                // Process edge pixels (semi-transparent areas) - expanded range for better coverage
                // Changed from < 250 to < 255 to catch more edge pixels
                if (alpha > 0 && alpha < 255) {
                    let weightedSum = 0;
                    let weightSumActual = 0;
                    let weightIdx = 0;

                    // Sample surrounding pixels with Gaussian weighting
                    for (let dy = -radius; dy <= radius; dy++) {
                        for (let dx = -radius; dx <= radius; dx++) {
                            const nx = x + dx;
                            const ny = y + dy;

                            // Check bounds
                            if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
                                const nidx = ny * width + nx;
                                const weight = weights[weightIdx];
                                weightedSum += alphaOriginal[nidx] * weight;
                                weightSumActual += weight;
                            }
                            weightIdx++;
                        }
                    }

                    // Blend original alpha with weighted average for smoother edges
                    // Use 60% original, 40% blurred for better edge preservation
                    const avgAlpha = weightSumActual > 0 ? weightedSum / weightSumActual : alpha;
                    const blendedAlpha = Math.floor(alpha * 0.6 + avgAlpha * 0.4);
                    data[idx + 3] = Math.max(0, Math.min(255, blendedAlpha));
                    processedPixels++;
                }
            }
        }

        console.log(
            `[softenAlphaEdges] Edge softening complete. Processed ${processedPixels} edge pixels out of ${totalPixels} total (${(
                (processedPixels / totalPixels) *
                100
            ).toFixed(2)}%)`
        );
    }

    // Capture current view and save as WebP
    async captureCurrentViewAsWebP() {
        // Disable UI camera controls for programmatic camera control
        this.disableUICameraControls();

        // Check if we should preserve the current camera configuration
        const preserveCamera = this.observer.get('screenshot.preserveCamera');

        let originalOrbitMode = null;
        let cameraState = null;
        let originalState = null;
        let savedRotation = null;

        if (!preserveCamera) {
            // Save current orbit mode and force to "target" for screenshots
            originalOrbitMode = this.observer.get('camera.orbitMode');
            if (originalOrbitMode !== 'target') {
                this.setOrbitMode('target');
                // Wait a bit for orbit mode to take effect
                await new Promise((resolve) => setTimeout(resolve, 100));
            }

            // Save current camera state
            cameraState = this.saveCameraState();
        } else {
            // Save current aircraft rotation to restore after screenshot setup
            savedRotation = {
                pitch: this.observer.get('aircraftRotation.pitch') || 0,
                roll: this.observer.get('aircraftRotation.roll') || 0,
            };
        }

        // Save current rendering state
        originalState = this.saveRenderingState();

        try {
            // Always apply screenshot defaults (skybox, lighting, transparent background)
            this.applyScreenshotDefaults();

            // Wait for skybox to load
            await new Promise((resolve) => setTimeout(resolve, 500));

            // Remove offsets to return to normal pose
            this.removeOffsetsForScreenshot();

            // Hide high-speed spinning nodes (propeller blurs, etc.)
            this.hideHighSpeedSpinningNodes();

            if (!preserveCamera) {
                // Apply screenshot animation settings (only if not preserving)
                this.applyScreenshotAnimationSettings();
            } else {
                // Restore aircraft rotation AFTER removeOffsetsForScreenshot
                // because removeOffsetsForScreenshot resets the rotation
                if (savedRotation) {
                    console.log(
                        `[captureCurrentViewAsWebP] Restoring rotation after offsets removed: pitch=${savedRotation.pitch}, roll=${savedRotation.roll}`
                    );
                    this.observer.set('aircraftRotation.pitch', savedRotation.pitch);
                    this.observer.set('aircraftRotation.roll', savedRotation.roll);
                    // Force the rotation to apply immediately by calling the method directly
                    this.setAircraftRotation();
                    // Wait a bit for rotation to take effect
                    await new Promise((resolve) => setTimeout(resolve, 150));
                }
            }

            // Always setup transparent background
            this.setupTransparentBackground();

            // Force render cycles to ensure everything is updated
            for (let i = 0; i < 3; i++) {
                this.app.fire('prerender');
                this.app.renderNextFrame = true;
                this.app.render();
                this.app.fire('postrender');
                await new Promise((resolve) => requestAnimationFrame(resolve));
            }

            // Wait for render to complete
            await new Promise((resolve) => requestAnimationFrame(resolve));

            const rt = this.camera.camera.renderTarget;
            if (!rt) {
                console.error('[captureCurrentViewAsWebP] No render target available');
                return;
            }

            console.log(
                `[captureCurrentViewAsWebP] Render target: ${rt.width}x${rt.height}, samples: ${rt.samples}`
            );

            // CRITICAL: Resolve MSAA render target before reading
            if (rt.samples > 1) {
                console.log(
                    `[captureCurrentViewAsWebP] Render target has ${rt.samples} samples, resolving MSAA...`
                );
                rt.resolve();
                await new Promise((resolve) => requestAnimationFrame(resolve));
            }

            // Final render
            this.app.fire('prerender');
            this.app.renderNextFrame = true;
            this.app.render();
            this.app.fire('postrender');

            if (rt.samples > 1) {
                rt.resolve();
            }

            await new Promise((resolve) => requestAnimationFrame(resolve));

            const texture = rt.colorBuffer;
            if (!texture) {
                console.error('[captureCurrentViewAsWebP] No color buffer available');
                return;
            }

            console.log(
                `[captureCurrentViewAsWebP] Reading texture: ${texture.width}x${texture.height}`
            );

            // Read texture data
            const readOptions =
                rt.samples > 1 ? { immediate: true } : { renderTarget: rt, immediate: true };
            const typedArray = await texture.read(0, 0, texture.width, texture.height, readOptions);
            const imageData = new Uint32Array(typedArray.buffer.slice(0));

            // Convert to blob
            const canvas = document.createElement('canvas');
            canvas.width = texture.width;
            canvas.height = texture.height;
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                console.error('[captureCurrentViewAsWebP] Failed to get 2D context');
                return;
            }

            const imgData = ctx.createImageData(texture.width, texture.height);
            const data = imgData.data;

            // Flip vertically and convert ABGR to RGBA
            for (let y = 0; y < texture.height; y++) {
                const srcRow = (texture.height - 1 - y) * texture.width;
                const dstRow = y * texture.width;
                for (let x = 0; x < texture.width; x++) {
                    const srcIdx = srcRow + x;
                    const dstIdx = (dstRow + x) * 4;
                    const pixel = imageData[srcIdx];
                    data[dstIdx + 0] = (pixel >> 0) & 0xff; // R
                    data[dstIdx + 1] = (pixel >> 8) & 0xff; // G
                    data[dstIdx + 2] = (pixel >> 16) & 0xff; // B
                    data[dstIdx + 3] = (pixel >> 24) & 0xff; // A
                }
            }

            // Soften alpha edges to reduce aliasing artifacts (if enabled)
            const softenEdges = this.observer.get('screenshot.softenEdges');
            if (softenEdges) {
                const radius = this.observer.get('screenshot.softenEdgesRadius') || 1.5;
                this.softenAlphaEdges(data, texture.width, texture.height, radius);
            }

            ctx.putImageData(imgData, 0, 0);

            // Convert to WebP blob and download
            const blob = await new Promise<Blob>((resolve, reject) => {
                canvas.toBlob(
                    (blob) => {
                        if (blob) {
                            resolve(blob);
                        } else {
                            reject(new Error('Failed to create WebP blob'));
                        }
                    },
                    'image/webp',
                    0.95
                );
            });

            // Generate timestamp for filename
            const now = new Date();
            const timestamp = now
                .toISOString()
                .replace(/:/g, '-')
                .replace(/\..+/, '')
                .replace('T', '_');
            const filename = `screenshot_${timestamp}.webp`;

            // Download the blob
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);

            console.log(`[captureCurrentViewAsWebP] Screenshot saved: ${filename}`);
        } finally {
            // Always restore visibility of high-speed spinning nodes
            this.showHighSpeedSpinningNodes();

            // Restore offsets (unless preserving camera, which means we want to keep rotation)
            if (!preserveCamera) {
                this.restoreOffsetsAfterScreenshot();
            }

            // Restore camera state (only if not preserving)
            if (!preserveCamera && cameraState) {
                this.restoreCameraState(cameraState);
            }

            // Always restore rendering state
            if (originalState) {
                this.restoreRenderingState(originalState);
            }

            // Always restore original orbit mode if it was changed
            if (originalOrbitMode && originalOrbitMode !== 'target') {
                console.log(
                    `[captureCurrentViewAsWebP] Restoring orbit mode to "${originalOrbitMode}"`
                );
                this.setOrbitMode(originalOrbitMode);
            }

            // Re-enable UI camera controls
            this.enableUICameraControls();

            // Force render update
            this.renderNextFrame();
        }
    }

    // Save current camera state for special screenshots
    private saveCameraState() {
        const camera = this.camera;
        const cameraComponent = camera.camera;

        return {
            position: camera.getPosition().clone(),
            rotation: camera.getRotation().clone(),
            projection: cameraComponent.projection,
            fov: cameraComponent.fov,
            orthoHeight: cameraComponent.orthoHeight,
            nearClip: cameraComponent.nearClip,
            farClip: cameraComponent.farClip,
        };
    }

    // Restore camera state after special screenshot
    private restoreCameraState(state: any) {
        const camera = this.camera;
        const cameraComponent = camera.camera;

        camera.setPosition(state.position);
        camera.setRotation(state.rotation);
        cameraComponent.projection = state.projection;
        cameraComponent.fov = state.fov;
        cameraComponent.orthoHeight = state.orthoHeight;
        cameraComponent.nearClip = state.nearClip;
        cameraComponent.farClip = state.farClip;

        // Sync FOV with observer so UI reflects the restored value
        this.observer.set('camera.fov', state.fov);
    }

    testSideViewPosition() {
        // show callstack in the console
        console.log('Callstack:', new Error().stack);

        console.log('Testing side view camera position...');

        // Calculate side position
        this.calcSceneBounds(this.dynamicSceneBounds);
        const center = this.dynamicSceneBounds.center.clone();
        const size = this.dynamicSceneBounds.halfExtents.length() * 2;
        const distance = size * 3.0; // 2x farther than before (was 1.5, now 3.0)

        // Get the bounds for FOV calculation
        const bounds = this.dynamicSceneBounds;
        const min = bounds.getMin();
        const max = bounds.getMax();

        // For side view (camera on -X axis looking at +X), we care about:
        // - Width: Z-axis extent (depth when viewed from side)
        // - Height: Y-axis extent (height when viewed from side)
        const modelWidth = max.z - min.z; // Z extent = apparent width from side
        const modelHeight = max.y - min.y; // Y extent = apparent height from side


        // Set camera position manually (camera on -X axis for side view)
        const sidePos = new Vec3(center.x - distance, center.y, center.z);
        this.camera.setPosition(sidePos);

        // Use the built-in focus system to look at the scene
        this.cameraControls.focus(center, sidePos);

        // Calculate FOV to fit model with 10% margin
        this.calculateAndSetOptimalFOV(modelWidth, modelHeight, distance);
    }

    calculateAndSetOptimalFOV(modelWidth: number, modelHeight: number, distance: number) {
        // Add 10% margin (multiply by 1.1)
        const targetWidth = modelWidth * 1.1;
        const targetHeight = modelHeight * 1.1;

        // Get camera aspect ratio
        const camera = this.camera.camera;
        const aspectRatio = camera.aspectRatio;

        // Calculate FOV needed for width and height
        // FOV = 2 * atan(size / (2 * distance)) * (180/π)
        const fovForWidth = 2 * Math.atan(targetWidth / (2 * distance)) * (180 / Math.PI);
        const fovForHeight = 2 * Math.atan(targetHeight / (2 * distance)) * (180 / Math.PI);

        // The camera's FOV is typically the vertical FOV
        // We need to account for aspect ratio to ensure both width and height fit

        // Method 1: Use the larger of the two requirements
        let finalFOV = Math.max(fovForHeight, fovForWidth / aspectRatio);

        // Method 2: Alternative - ensure both dimensions fit by choosing the more restrictive constraint
        // If aspect ratio > 1 (wider than tall), width constraint is more restrictive
        // If aspect ratio < 1 (taller than wide), height constraint is more restrictive
        const widthConstrainedFOV = fovForWidth / aspectRatio;
        const heightConstrainedFOV = fovForHeight;

        // Use the larger FOV to ensure both dimensions fit
        finalFOV = Math.max(widthConstrainedFOV, heightConstrainedFOV);

        // Clamp FOV to reasonable limits (typically 10-120 degrees)
        finalFOV = Math.max(10, Math.min(120, finalFOV));

        // Set the new FOV
        camera.fov = finalFOV;

        // Update the observer if needed (assuming you track FOV in observer)
        this.observer.set('camera.fov', finalFOV);

        // Force a render to apply the new FOV
        this.renderNextFrame();
    }

    // Tighter FOV calculation for larger model appearance in screenshots
    calculateAndSetOptimalFOVTighter(modelWidth: number, modelHeight: number, distance: number) {
        // Use minimal margin (2% instead of 10%) or no margin for tighter framing
        const marginFactor = 1.02; // 2% margin instead of 10%
        const targetWidth = modelWidth * marginFactor;
        const targetHeight = modelHeight * marginFactor;

        // Get camera aspect ratio
        const camera = this.camera.camera;
        const aspectRatio = camera.aspectRatio;

        // Calculate FOV needed for width and height
        // FOV = 2 * atan(size / (2 * distance)) * (180/π)
        const fovForWidth = 2 * Math.atan(targetWidth / (2 * distance)) * (180 / Math.PI);
        const fovForHeight = 2 * Math.atan(targetHeight / (2 * distance)) * (180 / Math.PI);

        // Calculate width-constrained and height-constrained FOVs
        const widthConstrainedFOV = fovForWidth / aspectRatio;
        const heightConstrainedFOV = fovForHeight;

        // Use the larger FOV to ensure both dimensions fit
        let finalFOV = Math.max(widthConstrainedFOV, heightConstrainedFOV);

        // Clamp FOV to reasonable limits (typically 10-120 degrees)
        finalFOV = Math.max(10, Math.min(120, finalFOV));

        // Set the new FOV
        camera.fov = finalFOV;

        // Update the observer if needed
        this.observer.set('camera.fov', finalFOV);

        // Force a render to apply the new FOV
        this.renderNextFrame();
    }

    // Alternative method if the above doesn't work as expected
    calculateOptimalFOVAlternative(modelWidth: number, modelHeight: number, distance: number) {
        // Add 10% margin
        const marginedWidth = modelWidth * 1.1;
        const marginedHeight = modelHeight * 1.1;

        const camera = this.camera.camera;
        const aspectRatio = camera.aspectRatio;

        // Calculate the angular size needed
        // For horizontal: apparent_width = 2 * distance * tan(horizontal_fov / 2)
        // For vertical: apparent_height = 2 * distance * tan(vertical_fov / 2)

        // Calculate vertical FOV needed to fit the height
        const verticalFOVForHeight =
            2 * Math.atan(marginedHeight / (2 * distance)) * (180 / Math.PI);

        // Calculate vertical FOV needed to fit the width (considering aspect ratio)
        const horizontalFOVForWidth =
            2 * Math.atan(marginedWidth / (2 * distance)) * (180 / Math.PI);
        const verticalFOVForWidth = horizontalFOVForWidth / aspectRatio;

        // Use the larger FOV to ensure both fit
        const optimalFOV = Math.max(verticalFOVForHeight, verticalFOVForWidth);

        // Clamp to reasonable limits
        const clampedFOV = Math.max(10, Math.min(120, optimalFOV));

        return clampedFOV;
    }

    // Test function to restore normal camera controls
    restoreNormalView() {
        // Re-enable camera controls
        this.cameraControls.enabled = true;

        // Switch back to perspective projection
        this.camera.camera.projection = 0; // PROJECTION_PERSPECTIVE = 0
        this.camera.camera.fov = 75; // Default FOV

        // Focus on the scene normally
        this.focusSelection();
    }

    // Reset camera controls to working state - call this if camera appears locked
    resetCameraControls() {
        console.log('[resetCameraControls] ========== RESETTING CAMERA CONTROLS ==========');

        // Clear any stuck screenshot flags
        // @ts-ignore
        if (this.isCapturingScreenshot) {
            console.log('[resetCameraControls] Clearing stuck isCapturingScreenshot flag');
            // @ts-ignore
            this.isCapturingScreenshot = false;
        }

        // End batch mode if stuck
        if (this.isBatchScreenshotMode) {
            console.log('[resetCameraControls] Clearing stuck batch screenshot mode');
            this.isBatchScreenshotMode = false;
            this.batchScreenshotState = null;
        }

        // Clear input focus flag
        if (this.isAnyInputFocused) {
            console.log('[resetCameraControls] Clearing stuck input focus flag');
            this.isAnyInputFocused = false;
        }

        // Get current orbit mode
        const currentOrbitMode = this.observer.get('camera.orbitMode');
        console.log(`[resetCameraControls] Current orbit mode: ${currentOrbitMode}`);

        // Force re-apply orbit mode to reset controls
        if (currentOrbitMode === 'camera-center') {
            // Remove and re-setup camera center controls
            this.removeCameraCenterControls();
            this.setupCameraCenterControls();
            console.log('[resetCameraControls] Re-initialized camera-center controls');
        } else {
            // Force enable standard camera controls
            this.cameraControls.enabled = true;
            console.log('[resetCameraControls] Enabled standard camera controls');
        }

        // Reset multiframe if it exists
        if (this.multiframe) {
            this.multiframe.enabled = this.observer.get('camera.hq') || true;
            this.multiframe.moved();
            console.log('[resetCameraControls] Reset multiframe accumulation');
        }

        // Force a render
        this.renderNextFrame();

        console.log('[resetCameraControls] ========== CAMERA CONTROLS RESET COMPLETE ==========');
        console.log('[resetCameraControls] Camera controls enabled:', this.cameraControls.enabled);

        return {
            orbitMode: currentOrbitMode,
            cameraControlsEnabled: this.cameraControls.enabled,
            isCapturingScreenshot: (this as any).isCapturingScreenshot || false,
            isBatchScreenshotMode: this.isBatchScreenshotMode,
        };
    }

    // Get current camera control state for debugging
    getCameraControlState() {
        // @ts-ignore
        const zoomDist = this.cameraControls?._zoomDist || 0;
        const sceneSize = this.cameraControls?.sceneSize || 0;
        const camPos = this.camera?.getPosition();
        
        return {
            orbitMode: this.observer.get('camera.orbitMode'),
            cameraControlsEnabled: this.cameraControls?.enabled,
            isCapturingScreenshot: (this as any).isCapturingScreenshot || false,
            isBatchScreenshotMode: this.isBatchScreenshotMode,
            isAnyInputFocused: this.isAnyInputFocused,
            xrModeActive: this.xrMode?.active || false,
            zoomDist: zoomDist,
            sceneSize: sceneSize,
            cameraPosition: camPos ? { x: camPos.x, y: camPos.y, z: camPos.z } : null,
        };
    }
    
    // Zoom out the orbit camera to see the full model
    zoomOutCamera(multiplier: number = 3) {
        if (!this.cameraControls) {
            console.warn('[zoomOutCamera] cameraControls not available');
            return;
        }
        
        // Calculate proper zoom distance based on scene size
        const sceneSize = this.cameraControls.sceneSize || 1;
        const newZoomDist = sceneSize * multiplier;
        
        // @ts-ignore
        this.cameraControls._zoomDist = newZoomDist;
        // @ts-ignore
        if (this.cameraControls._targetDistance !== undefined) {
            // @ts-ignore
            this.cameraControls._targetDistance = newZoomDist;
        }
        
        // Focus on scene center
        this.focusSelection();
        
        console.log(`[zoomOutCamera] Set zoom distance to ${newZoomDist} (${multiplier}x scene size of ${sceneSize})`);
        this.renderNextFrame();
    }

    // Fixed downloadSideViewTransparentAdvanced with manual aspect ratio control
    async downloadSideViewTransparentAdvanced() {
        const outputFormat = this.observer.get('screenshot.outputFormat') || 'png';
        const resolution = this.observer.get('screenshot.resolution') || 'thumbnail';

        // If "all" is selected, export both sizes
        if (resolution === 'all') {
            await this.takeAdvancedScreenshotAtResolution('thumbnail');
            await this.delay(200);
            await this.takeAdvancedScreenshotAtResolution('large');
            return;
        }

        // Single resolution export
        await this.takeAdvancedScreenshotAtResolution(
            resolution as 'thumbnail' | 'large' | '4k' | '8k'
        );
    }

    // Internal helper for advanced screenshot at specific resolution
    private async takeAdvancedScreenshotAtResolution(
        resolution: 'thumbnail' | 'large' | '4k' | '8k'
    ): Promise<void> {
        const outputFormat = this.observer.get('screenshot.outputFormat') || 'png';

        // Calculate resolution dimensions based on setting
        const baseWidth = 512;
        const baseHeight = 184;
        const scale =
            resolution === 'large' ? 4 : resolution === '4k' ? 7.5 : resolution === '8k' ? 15 : 1;
        const targetWidth = baseWidth * scale;
        const targetHeight = baseHeight * scale;


        // Save current canvas and device dimensions
        const originalWidth = this.canvas.width;
        const originalHeight = this.canvas.height;
        const originalStyleWidth = this.canvas.style.width;
        const originalStyleHeight = this.canvas.style.height;
        const originalDeviceWidth = this.app.graphicsDevice.width;
        const originalDeviceHeight = this.app.graphicsDevice.height;

        // Save current camera aspect ratio settings
        const originalAspectRatioMode = this.camera.camera.aspectRatioMode;
        const originalAspectRatio = this.camera.camera.aspectRatio;

        // Save current state
        const originalState = this.saveRenderingState();

        console.log(`Original device resolution: ${originalDeviceWidth}x${originalDeviceHeight}`);
        console.log(
            `Original camera aspect ratio: ${originalAspectRatio} (mode: ${originalAspectRatioMode})`
        );
        console.log(`Setting device resolution to ${targetWidth}x${targetHeight}...`);

        // FIRST: Change resolution
        this.app.graphicsDevice.setResolution(targetWidth, targetHeight);

        // FORCE destroy and rebuild render targets
        this.destroyRenderTargets();
        this.forceRebuildRenderTargets(targetWidth, targetHeight);

        // CRITICAL: Set camera to manual aspect ratio mode and set the correct aspect ratio
        const targetAspectRatio = targetWidth / targetHeight;
        this.camera.camera.aspectRatioMode = 1; // ASPECT_MANUAL = 1, ASPECT_AUTO = 0
        this.camera.camera.aspectRatio = targetAspectRatio;

        // Debug: Check if aspect ratio was set correctly
        this.debugCameraAspectRatio();

        // THEN: Do all camera setup with the correct resolution (in async IIFE to handle await)
        (async () => {
            await this.setupSideViewCameraFor512x184();

            // Set up for transparent rendering
            this.setupTransparentBackground();

            // Force multiple render cycles to ensure camera position is applied
            for (let i = 0; i < 3; i++) {
                this.app.fire('prerender');
                this.app.renderNextFrame = true;
                this.app.render();
                this.app.fire('postrender');
            }

            // Small delay for one render frame (camera moves instantly with inertiaFactor=1.0)
            setTimeout(() => {
                const texture = this.camera.camera.renderTarget.colorBuffer;
                texture
                    .read(0, 0, texture.width, texture.height)
                    .then(async (typedArray: Uint32Array) => {
                        const now = new Date();
                        const timestamp = now
                            .toISOString()
                            .replace(/:/g, '-')
                            .replace(/\..+/, '')
                            .replace('T', '_');

                        // Add appropriate suffix based on resolution
                        const suffix = resolution === 'large' ? '_profile@2x' : '_profile';
                        const filename = `if-model-viewer_side-view-transparent_${timestamp}${suffix}.${outputFormat}`;

                        const imageData = new Uint32Array(typedArray.buffer.slice(0));

                        if (outputFormat === 'png') {
                            await this.pngExporter.export(
                                filename,
                                imageData,
                                texture.width,
                                texture.height
                            );
                        } else if (outputFormat === 'webp') {
                            // WebP export using Canvas API
                            await this.exportAsWebP(
                                filename,
                                imageData,
                                texture.width,
                                texture.height
                            );
                        }

                    })
                    .catch((error) => {
                        console.error('[Viewer] Error reading texture data:', error);
                    })
                    .finally(() => {
                        // Restore everything
                        this.canvas.width = originalWidth;
                        this.canvas.height = originalHeight;
                        this.canvas.style.width = originalStyleWidth;
                        this.canvas.style.height = originalStyleHeight;

                        this.app.graphicsDevice.setResolution(
                            originalDeviceWidth,
                            originalDeviceHeight
                        );
                        this.destroyRenderTargets();
                        this.forceRebuildRenderTargets(originalDeviceWidth, originalDeviceHeight);

                        // Restore original camera aspect ratio settings
                        this.camera.camera.aspectRatioMode = originalAspectRatioMode;
                        this.camera.camera.aspectRatio = originalAspectRatio;

                        this.restoreRenderingState(originalState);

                        // Restore visibility of high-speed spinning nodes
                        this.showHighSpeedSpinningNodes();

                        // Restore altitude and pitch offsets
                        this.restoreOffsetsAfterScreenshot();

                        // Restore original inertia factor
                        const originalInertia = (this as any)._originalInertia;
                        if (originalInertia !== undefined) {
                            // @ts-ignore
                            if (this.cameraControls.inertiaFactor !== undefined) {
                                // @ts-ignore
                                this.cameraControls.inertiaFactor = originalInertia;
                                console.log('Restored inertia factor to:', originalInertia);
                            }
                            delete (this as any)._originalInertia;
                        }

                        this.renderNextFrame();

                        console.log('Canvas and rendering state restored');
                    });
            }, 100); // Small delay for one render frame (camera moves instantly with inertiaFactor=1.0)
        })(); // Execute async IIFE
    }

    // Automated livery screenshot system - batch process all liveries
    // Capture screenshots for all liveries and return as blob URLs (for UI display)
    async captureSideViewTransparentAllLiveries(): Promise<
        Array<{ liveryId: string; liveryName: string; thumbnailUrl: string; largeUrl?: string }>
    > {
        console.log('========================================');
        console.log('Starting automated livery screenshot capture...');
        console.log('========================================');

        const results: Array<{
            liveryId: string;
            liveryName: string;
            thumbnailUrl: string;
            largeUrl?: string;
        }> = [];
        // Always capture both thumbnail and large sizes
        const captureLarge = true;

        // Get manifest data
        const manifestData = this.observer.get('manifestData');
        const aircraftConfig = this.observer.get('aircraftConfig');

        if (!manifestData?.loaded || !manifestData?.manifest) {
            console.error('✗ No manifest loaded. Please import a package manifest first.');
            return;
        }

        if (!aircraftConfig?.loaded || !aircraftConfig?.configuration) {
            console.error('✗ No aircraft configuration loaded.');
            return;
        }

        // Get aircraft ID
        const aircraftId = aircraftConfig.configuration.id || aircraftConfig.configuration.ID;
        if (!aircraftId) {
            console.error('✗ No aircraft ID found in configuration.');
            return;
        }

        console.log(`Aircraft ID: ${aircraftId}`);

        // Find matching liveries in manifest
        const manifest = manifestData.manifest;
        const manifestLiveries: any[] = [];

        if (manifest.liveryPackages) {
            for (const liveryPkg of manifest.liveryPackages) {
                if (
                    liveryPkg.modelID &&
                    liveryPkg.modelID.toLowerCase() === aircraftId.toLowerCase()
                ) {
                    manifestLiveries.push(liveryPkg);
                }
            }
        }

        if (manifestLiveries.length === 0) {
            console.error('✗ No liveries found in manifest for this aircraft.');
            return;
        }

        console.log(`Found ${manifestLiveries.length} liveries in manifest for this aircraft`);

        // Get available loaded liveries
        const availableLiveries = this.observer.get('liveries.available') as string[];
        const originalLivery = this.observer.get('liveries.selected') as string;

        console.log(`Loaded liveries in viewer: ${availableLiveries.length}`);
        console.log('Loaded liveries:', availableLiveries);
        console.log('');

        console.log('Manifest liveries:');
        manifestLiveries.forEach((liv, idx) => {
            console.log(`  [${idx + 1}] "${liv.name}" (${liv.id})`);
        });
        console.log('');

        let successCount = 0;
        let skipCount = 0;
        let errorCount = 0;

        // Process each livery from manifest
        for (let i = 0; i < manifestLiveries.length; i++) {
            const liveryPkg = manifestLiveries[i];
            const liveryName = liveryPkg.name;
            const liveryId = liveryPkg.id;
            const liveryPath = liveryPkg.path;

            console.log(`[${i + 1}/${manifestLiveries.length}] Processing: ${liveryName}`);
            console.log(`  ID: ${liveryId}`);
            console.log(`  Path: ${liveryPath}`);

            // Extract the actual directory name from the path (last segment after Liveries/)
            // e.g., "Content\\Aircraft\\A-10\\Liveries\\188th Wing" -> "188th Wing"
            let liveryDirName = liveryName; // Fallback to name
            if (liveryPath) {
                // Split by both forward and backslash
                const pathParts: string[] = liveryPath.split(/[/\\]/);
                // Find "Liveries" and get the next part
                const liveriesIndex = pathParts.findIndex(
                    (part) => part.toLowerCase() === 'liveries'
                );
                if (liveriesIndex !== -1 && liveriesIndex < pathParts.length - 1) {
                    liveryDirName = pathParts[liveriesIndex + 1];
                    console.log(`  Extracted directory name from path: "${liveryDirName}"`);
                }
            }

            // Check if this livery is loaded in the viewer
            let matchedLivery = null;

            // Try to match using the directory name extracted from path
            if (availableLiveries.includes(liveryDirName)) {
                matchedLivery = liveryDirName;
                console.log(`  ✓ Found exact match using directory name: "${matchedLivery}"`);
            } else {
                console.log(`  ✗ Directory name "${liveryDirName}" not found in loaded liveries`);
            }

            if (!matchedLivery) {
                console.log(`  ⊘ SKIPPED: Livery "${liveryName}" not found in loaded files`);
                console.log(
                    `     Loaded liveries: ${availableLiveries.slice(0, 3).join(', ')}${
                        availableLiveries.length > 3 ? '...' : ''
                    }`
                );
                skipCount++;
                console.log('');
                continue;
            }

            try {
                // Switch to the livery (use the matched name from loaded liveries)
                console.log(`  → Switching to livery: "${matchedLivery}"`);
                await this.switchToLiveryAndWait(matchedLivery);

                // Wait a bit more for rendering to settle
                await this.delay(200);

                // Capture screenshot as blob URL
                console.log(`  → Capturing screenshot for: ${liveryName}`);
                const thumbnailUrl = await this.captureSideViewTransparentAsBlob('thumbnail');

                let largeUrl: string | undefined;
                if (captureLarge) {
                    await this.delay(100);
                    largeUrl = await this.captureSideViewTransparentAsBlob('large');
                }

                results.push({
                    liveryId,
                    liveryName,
                    thumbnailUrl,
                    largeUrl,
                });

                // Wait before processing next livery
                await this.delay(500);

                console.log(`  ✓ SUCCESS`);
                successCount++;
            } catch (error) {
                console.error(`  ✗ ERROR: ${error}`);
                errorCount++;
            }
            console.log('');
        }

        console.log('========================================');
        console.log('=== Batch screenshot capture complete ===');
        console.log(`Total: ${manifestLiveries.length} liveries`);
        console.log(`  ✓ Success: ${successCount}`);
        console.log(`  ⊘ Skipped: ${skipCount} (not loaded)`);
        console.log(`  ✗ Errors: ${errorCount}`);
        console.log('========================================');

        // Restore original livery
        console.log(`Restoring original livery: ${originalLivery}`);
        await this.switchToLiveryAndWait(originalLivery);

        return results;
    }

    // Helper: Ensure Basic Skybox is generated and ready for screenshots
    private async ensureBasicSkyboxReady(): Promise<void> {
        const currentSkybox = this.observer.get('skybox.value');
        const skyboxName = 'Basic Skybox';

        // Check if Basic Skybox is already loaded and set in scene
        if (currentSkybox === skyboxName && this.skyboxLoaded && this.app.scene.skybox) {
            console.log('Basic Skybox already loaded and ready');
            return;
        }

        console.log('Generating Basic Skybox for screenshots...');

        // Set skybox to Basic Skybox (this will trigger generation via observer handler)
        // The observer handler will call generateBasicSkybox() automatically
        if (currentSkybox !== skyboxName) {
            this.observer.set('skybox.value', skyboxName);
        } else if (!this.skyboxLoaded) {
            // If already set but not loaded, generate it directly
            this.generateBasicSkybox();
        }

        // Wait for skybox to be loaded
        let attempts = 0;
        const maxAttempts = 100; // 10 seconds max wait
        while (!this.skyboxLoaded || !this.app.scene.skybox) {
            await this.delay(100);
            attempts++;
            if (attempts >= maxAttempts) {
                console.warn('Timeout waiting for Basic Skybox to load, proceeding anyway...');
                break;
            }
        }

        // Double-check skybox is set in the scene
        if (!this.app.scene.skybox && this.skyboxLoaded) {
            console.warn('Basic Skybox loaded but not set in scene, attempting to reinitialize...');
            // If we have the generated URL, we can try to reload it
            // But for now, just log a warning and proceed
        }

        console.log('Basic Skybox ready for screenshots');
    }

    // Automated livery screenshot download system - batch download screenshots for all liveries
    // Downloads screenshots for all liveries using the new screenshot system
    async downloadSideViewTransparentAllLiveries(): Promise<void> {
        console.log('========================================');
        console.log('Starting automated livery screenshot download...');
        console.log('========================================');

        // Ensure Basic Skybox is generated and ready before starting batch screenshots
        await this.ensureBasicSkyboxReady();

        // Get manifest data
        const manifestData = this.observer.get('manifestData');
        const aircraftConfig = this.observer.get('aircraftConfig');

        if (!manifestData?.loaded || !manifestData?.manifest) {
            console.error('✗ No manifest loaded. Please import a package manifest first.');
            return;
        }

        if (!aircraftConfig?.loaded || !aircraftConfig?.configuration) {
            console.error('✗ No aircraft configuration loaded.');
            return;
        }

        // Get aircraft ID
        const aircraftId = aircraftConfig.configuration.id || aircraftConfig.configuration.ID;
        if (!aircraftId) {
            console.error('✗ No aircraft ID found in configuration.');
            return;
        }

        console.log(`Aircraft ID: ${aircraftId}`);

        // Find matching liveries in manifest
        const manifest = manifestData.manifest;
        const manifestLiveries: any[] = [];

        if (manifest.liveryPackages) {
            for (const liveryPkg of manifest.liveryPackages) {
                if (
                    liveryPkg.modelID &&
                    liveryPkg.modelID.toLowerCase() === aircraftId.toLowerCase()
                ) {
                    manifestLiveries.push(liveryPkg);
                }
            }
        }

        if (manifestLiveries.length === 0) {
            console.error('✗ No liveries found in manifest for this aircraft.');
            return;
        }

        console.log(`Found ${manifestLiveries.length} liveries in manifest for this aircraft`);

        // Get available loaded liveries
        const availableLiveries = this.observer.get('liveries.available') as string[];
        const originalLivery = this.observer.get('liveries.selected') as string;

        console.log(`Loaded liveries in viewer: ${availableLiveries.length}`);
        console.log('Loaded liveries:', availableLiveries);
        console.log('');

        console.log('Manifest liveries:');
        manifestLiveries.forEach((liv, idx) => {
            console.log(`  [${idx + 1}] "${liv.name}" (${liv.id})`);
        });
        console.log('');

        let successCount = 0;
        let skipCount = 0;
        let errorCount = 0;

        // Process each livery from manifest
        for (let i = 0; i < manifestLiveries.length; i++) {
            const liveryPkg = manifestLiveries[i];
            const liveryName = liveryPkg.name;
            const liveryId = liveryPkg.id;
            const liveryPath = liveryPkg.path;

            console.log(`[${i + 1}/${manifestLiveries.length}] Processing: ${liveryName}`);
            console.log(`  ID: ${liveryId}`);
            console.log(`  Path: ${liveryPath}`);

            // Extract the actual directory name from the path (last segment after Liveries/)
            // e.g., "Content\\Aircraft\\A-10\\Liveries\\188th Wing" -> "188th Wing"
            let liveryDirName = liveryName; // Fallback to name
            if (liveryPath) {
                // Split by both forward and backslash
                const pathParts: string[] = liveryPath.split(/[/\\]/);
                // Find "Liveries" and get the next part
                const liveriesIndex = pathParts.findIndex(
                    (part) => part.toLowerCase() === 'liveries'
                );
                if (liveriesIndex !== -1 && liveriesIndex < pathParts.length - 1) {
                    liveryDirName = pathParts[liveriesIndex + 1];
                    console.log(`  Extracted directory name from path: "${liveryDirName}"`);
                }
            }

            // Check if this livery is loaded in the viewer
            let matchedLivery = null;

            // Try to match using the directory name extracted from path
            if (availableLiveries.includes(liveryDirName)) {
                matchedLivery = liveryDirName;
                console.log(`  ✓ Found exact match using directory name: "${matchedLivery}"`);
            } else {
                console.log(`  ✗ Directory name "${liveryDirName}" not found in loaded liveries`);
            }

            if (!matchedLivery) {
                console.log(`  ⊘ SKIPPED: Livery "${liveryName}" not found in loaded files`);
                console.log(
                    `     Loaded liveries: ${availableLiveries.slice(0, 3).join(', ')}${
                        availableLiveries.length > 3 ? '...' : ''
                    }`
                );
                skipCount++;
                console.log('');
                continue;
            }

            try {
                // Switch to the livery (use the matched name from loaded liveries)
                console.log(`  → Switching to livery: "${matchedLivery}"`);
                await this.switchToLiveryAndWait(matchedLivery);

                // Wait a bit more for rendering to settle
                await this.delay(200);

                // Download screenshot using the new screenshot system
                console.log(`  → Downloading screenshot for: ${liveryName}`);
                // Use livery ID as the filename base
                const filename = liveryId;
                await this.downloadSideViewTransparentWithFilename(filename);

                // Wait before processing next livery
                await this.delay(500);

                console.log(`  ✓ SUCCESS`);
                successCount++;
            } catch (error) {
                console.error(`  ✗ ERROR: ${error}`);
                errorCount++;
            }
            console.log('');
        }

        console.log('========================================');
        console.log('=== Batch screenshot download complete ===');
        console.log(`Total: ${manifestLiveries.length} liveries`);
        console.log(`  ✓ Success: ${successCount}`);
        console.log(`  ⊘ Skipped: ${skipCount} (not loaded)`);
        console.log(`  ✗ Errors: ${errorCount}`);
        console.log('========================================');

        // Restore original livery
        console.log(`Restoring original livery: ${originalLivery}`);
        await this.switchToLiveryAndWait(originalLivery);
    }

    // Helper: Switch to a livery and wait for it to load
    private async switchToLiveryAndWait(liveryName: string): Promise<void> {
        return new Promise((resolve, reject) => {
            console.log(`Switching to livery: ${liveryName}`);

            // Set up a listener for when loading completes
            const checkLoading = () => {
                const isLoading = this.observer.get('liveries.loading') as boolean;
                if (!isLoading) {
                    console.log(`Livery ${liveryName} loaded successfully`);
                    resolve();
                } else {
                    setTimeout(checkLoading, 50);
                }
            };

            // Start the livery switch
            this.observer.set('liveries.selected', liveryName);

            // Start checking for completion
            setTimeout(checkLoading, 100);

            // Add a timeout to prevent hanging forever
            setTimeout(() => {
                reject(new Error(`Timeout waiting for livery ${liveryName} to load`));
            }, 10000);
        });
    }

    // Helper: Simple delay function
    private delay(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    // Group files by aircraft folders (each folder should have AircraftConfiguration.json and a .gltf file)
    private groupFilesByAircraft(files: Array<File>): Map<string, Array<File>> {
        const aircraftMap = new Map<string, Array<File>>();

        // Find all AircraftConfiguration.json files
        const configFiles = files.filter((f) =>
            f.filename?.toLowerCase().endsWith('aircraftconfiguration.json')
        );

        if (configFiles.length === 0) {
            // No configuration files found, return all files as single group
            return new Map([['default', files]]);
        }

        // For each config file, find its base directory
        for (const configFile of configFiles) {
            const configPath = configFile.filename || '';
            // Get the directory containing the config file
            const lastSlash = Math.max(configPath.lastIndexOf('/'), configPath.lastIndexOf('\\'));
            const baseDir = lastSlash >= 0 ? configPath.substring(0, lastSlash) : '';

            // Find all files in this directory (including subdirectories)
            const aircraftFiles = files.filter((f) => {
                const filePath = f.filename || '';
                // File belongs to this aircraft if it's in the same directory or subdirectories
                if (baseDir === '') {
                    return true;
                }
                return (
                    filePath.startsWith(baseDir + '/') ||
                    filePath.startsWith(baseDir + '\\') ||
                    filePath === configPath
                );
            });

            // CRITICAL: Strip the aircraft folder prefix from filenames so GLTF can find referenced files
            // The GLTF references files like "Aircraft_LOD_0.bin" without the folder prefix
            const normalizedFiles = aircraftFiles.map((f) => {
                let normalizedFilename = f.filename || '';
                if (baseDir && normalizedFilename.startsWith(baseDir)) {
                    // Remove the baseDir prefix and leading slash
                    normalizedFilename = normalizedFilename.substring(baseDir.length);
                    if (normalizedFilename.startsWith('/') || normalizedFilename.startsWith('\\')) {
                        normalizedFilename = normalizedFilename.substring(1);
                    }
                }
                return {
                    url: f.url,
                    filename: normalizedFilename,
                };
            });

            // Use the aircraft folder name as the key
            const folderName = baseDir.split(/[/\\]/).pop() || 'default';
            aircraftMap.set(folderName, normalizedFiles);
        }

        return aircraftMap;
    }

    // Automated batch screenshot system for multiple aircraft
    async processBatchScreenshotsMultipleAircraft(aircraftGroups: Map<string, Array<File>>) {
        console.log('========================================');
        console.log('MULTI-AIRCRAFT BATCH SCREENSHOT SYSTEM');
        console.log('========================================');
        console.log(`Detected ${aircraftGroups.size} aircraft folder(s)`);
        console.log('');

        // Ask for output directory once
        let directoryHandle = null;
        if ('showDirectoryPicker' in window) {
            try {
                console.log('📁 Please select a folder to save ALL screenshots');
                console.log(
                    '   Recommended: Desktop, Documents, or a dedicated Screenshots folder'
                );
                // @ts-ignore - File System Access API
                directoryHandle = await window.showDirectoryPicker({
                    mode: 'readwrite',
                });
                console.log('✓ Directory selected:', directoryHandle.name);
                console.log('');
            } catch (error) {
                if (error.name === 'AbortError') {
                    console.log('⊘ Directory selection cancelled by user');
                    console.log('Batch process aborted.');
                    return;
                } else {
                    console.warn('⚠️  Directory selection failed:', error.message || error);
                    console.log('→ Using regular downloads instead');
                }
            }
        }

        let totalAircraftProcessed = 0;
        let totalLiveriesProcessed = 0;
        let totalErrors = 0;

        // Process each aircraft
        for (const [aircraftName, aircraftFiles] of aircraftGroups) {
            console.log('========================================');
            console.log(`Processing Aircraft: ${aircraftName}`);
            console.log(`Files: ${aircraftFiles.length}`);
            console.log('========================================');

            try {
                // Clear any previous errors
                this.observer.set('ui.error', null);

                // Load the aircraft
                console.log(`→ Loading aircraft...`);
                this.loadFiles(aircraftFiles, true);

                // Wait for the aircraft to load
                await this.waitForModelLoad();

                // Additional delay for textures and assets to settle
                // Note: Some textures may fail to load but we continue anyway
                await this.delay(3000);

                // Clear any texture loading errors (we can still take screenshots with missing textures)
                this.observer.set('ui.error', null);

                // Check if we actually have a model loaded
                if (!this.entities || this.entities.length === 0) {
                    console.error(`✗ No model entities loaded for ${aircraftName} - skipping`);
                    totalErrors++;
                    await this.delay(500);
                    continue;
                }

                console.log(`✓ Model loaded (${this.entities.length} entities)`);

                // Check if manifest and config are loaded
                const manifestData = this.observer.get('manifestData');
                const aircraftConfig = this.observer.get('aircraftConfig');

                if (!manifestData?.loaded || !aircraftConfig?.loaded) {
                    console.warn(
                        `⚠️  Aircraft ${aircraftName} does not have manifest or config loaded`
                    );
                    console.log('   Skipping livery screenshots for this aircraft');
                    console.log('');
                    continue;
                }

                // Get aircraft ID
                const aircraftId =
                    aircraftConfig.configuration.id || aircraftConfig.configuration.ID;
                if (!aircraftId) {
                    console.warn(`⚠️  No aircraft ID found for ${aircraftName}`);
                    console.log('   Skipping livery screenshots for this aircraft');
                    console.log('');
                    continue;
                }

                console.log(`✓ Aircraft loaded: ${aircraftId}`);

                // Find matching liveries in manifest
                const manifest = manifestData.manifest;
                const manifestLiveries: any[] = [];

                if (manifest.liveryPackages) {
                    for (const liveryPkg of manifest.liveryPackages) {
                        if (
                            liveryPkg.modelID &&
                            liveryPkg.modelID.toLowerCase() === aircraftId.toLowerCase()
                        ) {
                            manifestLiveries.push(liveryPkg);
                        }
                    }
                }

                if (manifestLiveries.length === 0) {
                    console.log(`ℹ️  No liveries found in manifest for ${aircraftName}`);
                    console.log('');
                    continue;
                }

                console.log(`Found ${manifestLiveries.length} liveries for ${aircraftName}`);
                console.log('');

                // Get available loaded liveries
                const availableLiveries = this.observer.get('liveries.available') as string[];
                const originalLivery = this.observer.get('liveries.selected') as string;

                let successCount = 0;
                let skipCount = 0;
                let errorCount = 0;

                // Process each livery
                for (let i = 0; i < manifestLiveries.length; i++) {
                    const liveryPkg = manifestLiveries[i];
                    const liveryName = liveryPkg.name;
                    const liveryId = liveryPkg.id;
                    const liveryPath = liveryPkg.path;

                    console.log(`[${i + 1}/${manifestLiveries.length}] ${liveryName}`);

                    // Extract directory name from path
                    let liveryDirName = liveryName;
                    if (liveryPath) {
                        const pathParts: string[] = liveryPath.split(/[/\\]/);
                        const liveriesIndex = pathParts.findIndex(
                            (part) => part.toLowerCase() === 'liveries'
                        );
                        if (liveriesIndex !== -1 && liveriesIndex < pathParts.length - 1) {
                            liveryDirName = pathParts[liveriesIndex + 1];
                        }
                    }

                    // Check if livery is loaded
                    let matchedLivery = null;
                    if (availableLiveries.includes(liveryDirName)) {
                        matchedLivery = liveryDirName;
                    }

                    if (!matchedLivery) {
                        console.log(`  ⊘ Skipped (not loaded)`);
                        skipCount++;
                        continue;
                    }

                    try {
                        // Switch to livery
                        await this.switchToLiveryAndWait(matchedLivery);
                        await this.delay(200);

                        // Take screenshot
                        const filename = `${liveryId}`;
                        await this.downloadSideViewTransparentWithFilename(
                            filename,
                            directoryHandle
                        );
                        await this.delay(500);

                        console.log(`  ✓ Success`);
                        successCount++;
                        totalLiveriesProcessed++;
                    } catch (error) {
                        console.error(`  ✗ Error: ${error}`);
                        errorCount++;
                        totalErrors++;
                    }
                }

                // Restore original livery
                if (originalLivery) {
                    await this.switchToLiveryAndWait(originalLivery);
                }

                console.log('');
                console.log(`Aircraft ${aircraftName} Complete:`);
                console.log(`  ✓ Success: ${successCount}`);
                console.log(`  ⊘ Skipped: ${skipCount}`);
                console.log(`  ✗ Errors: ${errorCount}`);
                console.log('');

                totalAircraftProcessed++;
            } catch (error) {
                console.error(`✗ Failed to process aircraft ${aircraftName}:`, error);
                totalErrors++;
            }
        }

        console.log('========================================');
        console.log('=== BATCH PROCESS COMPLETE ===');
        console.log(`Aircraft Processed: ${totalAircraftProcessed}/${aircraftGroups.size}`);
        console.log(`Total Liveries: ${totalLiveriesProcessed}`);
        console.log(`Total Errors: ${totalErrors}`);
        console.log('========================================');
    }

    // Helper: Wait for model to finish loading
    private waitForModelLoad(): Promise<void> {
        return new Promise((resolve) => {
            const checkLoading = () => {
                const isSpinning = this.observer.get('ui.spinner') as boolean;
                if (!isSpinning) {
                    resolve();
                } else {
                    setTimeout(checkLoading, 100);
                }
            };

            // Start checking after a brief delay
            setTimeout(checkLoading, 100);

            // Timeout after 30 seconds
            setTimeout(() => {
                resolve();
            }, 30000);
        });
    }

    // Helper: Wait for model to finish loading with error detection
    private waitForModelLoadWithErrors(): Promise<boolean> {
        return new Promise((resolve) => {
            let checkCount = 0;
            const maxChecks = 300; // 30 seconds max

            const checkLoading = () => {
                checkCount++;
                const isSpinning = this.observer.get('ui.spinner') as boolean;
                const error = this.observer.get('ui.error');

                if (error) {
                    console.error('Loading error detected:', error);
                    resolve(false);
                    return;
                }

                if (!isSpinning) {
                    // Loading complete - check if we actually loaded something
                    const hasEntities = this.entities && this.entities.length > 0;
                    resolve(hasEntities);
                    return;
                }

                if (checkCount >= maxChecks) {
                    console.error('Timeout waiting for model to load');
                    resolve(false);
                    return;
                }

                setTimeout(checkLoading, 100);
            };

            // Start checking after a brief delay
            setTimeout(checkLoading, 100);
        });
    }

    // Capture screenshot and return as blob URL (for UI display)
    async captureSideViewTransparentAsBlob(
        resolution: 'thumbnail' | 'large' | '4k' | '8k' = 'thumbnail',
        angle: 'left' | 'right' = 'left'
    ): Promise<string> {
        // Disable UI camera controls for programmatic camera control
        this.disableUICameraControls();

        // Always use WebP format
        const outputFormat = 'webp';

        // Ensure Basic Skybox is generated and ready before capturing
        await this.ensureBasicSkyboxReady();

        // Save current orbit mode and force to "target" for screenshots
        const originalOrbitMode = this.observer.get('camera.orbitMode');
        if (originalOrbitMode !== 'target') {
            console.log(
                `[captureSideViewTransparentAsBlob] Forcing orbit mode from "${originalOrbitMode}" to "target" for screenshot`
            );
            this.setOrbitMode('target');
            // Wait a bit for orbit mode to take effect
            await new Promise((resolve) => setTimeout(resolve, 100));
        }

        // Calculate resolution dimensions
        const baseWidth = 512;
        const baseHeight = 184;
        const scale = resolution === 'large' ? 4 : resolution === '4k' ? 7.5 : resolution === '8k' ? 15 : 1;
        const targetWidth = baseWidth * scale;
        const targetHeight = baseHeight * scale;

        // Save current state
        const originalWidth = this.canvas.width;
        const originalHeight = this.canvas.height;
        const originalStyleWidth = this.canvas.style.width;
        const originalStyleHeight = this.canvas.style.height;
        const originalDeviceWidth = this.app.graphicsDevice.width;
        const originalDeviceHeight = this.app.graphicsDevice.height;
        const originalAspectRatioMode = this.camera.camera.aspectRatioMode;
        const originalAspectRatio = this.camera.camera.aspectRatio;
        const originalState = this.saveRenderingState();

        try {
            // Change resolution
            this.app.graphicsDevice.setResolution(targetWidth, targetHeight);
            this.destroyRenderTargets();
            this.forceRebuildRenderTargets(targetWidth, targetHeight);

            // Set camera aspect ratio
            const targetAspectRatio = targetWidth / targetHeight;
            this.camera.camera.aspectRatioMode = 1;
            this.camera.camera.aspectRatio = targetAspectRatio;

            // Wait for render targets to be fully initialized and force a render cycle
            // This ensures the camera controls are ready before positioning
            await new Promise((resolve) => requestAnimationFrame(resolve));
            this.app.fire('prerender');
            this.app.renderNextFrame = true;
            this.app.render();
            this.app.fire('postrender');
            await new Promise((resolve) => setTimeout(resolve, 100));

            // Ensure camera controls are in the correct state before positioning
            // Force camera controls to update to clear any stale state
            // @ts-ignore
            if (this.cameraControls && typeof this.cameraControls.update === 'function') {
                // @ts-ignore
                this.cameraControls.update(0);
            }

            // Setup camera and rendering based on angle
            console.log(`[captureSideViewTransparentAsBlob] Setting up camera for angle: ${angle}`);
            if (angle === 'right') {
                await this.setupRightSideViewCameraFor512x184();
            } else {
                await this.setupSideViewCameraFor512x184();
            }

            // CRITICAL: Wait for camera controls to fully apply the position
            // Camera controls may need time to update their internal state
            await new Promise((resolve) => setTimeout(resolve, 200));

            // Verify camera position immediately after setup
            const cameraPosAfterSetup = this.camera.getPosition();
            const cameraRotAfterSetup = this.camera.getEulerAngles();
            console.log(
                `[captureSideViewTransparentAsBlob] Camera position immediately after setup: x=${cameraPosAfterSetup.x.toFixed(
                    2
                )}, y=${cameraPosAfterSetup.y.toFixed(2)}, z=${cameraPosAfterSetup.z.toFixed(2)}`
            );
            console.log(
                `[captureSideViewTransparentAsBlob] Camera rotation immediately after setup: x=${cameraRotAfterSetup.x.toFixed(
                    2
                )}, y=${cameraRotAfterSetup.y.toFixed(2)}, z=${cameraRotAfterSetup.z.toFixed(2)}`
            );

            // Force camera controls to update immediately
            // @ts-ignore
            if (this.cameraControls && typeof this.cameraControls.update === 'function') {
                // @ts-ignore
                this.cameraControls.update(0);
            }

            // Force multiple render cycles after camera setup to ensure position is fully applied
            console.log(
                `[captureSideViewTransparentAsBlob] Forcing render cycles to apply camera position...`
            );
            for (let i = 0; i < 5; i++) {
                await new Promise((resolve) => requestAnimationFrame(resolve));
                // Force camera controls update on each render cycle
                // @ts-ignore
                if (this.cameraControls && typeof this.cameraControls.update === 'function') {
                    // @ts-ignore
                    this.cameraControls.update(0);
                }
                this.app.fire('prerender');
                this.app.renderNextFrame = true;
                this.app.render();
                this.app.fire('postrender');
            }
            await new Promise((resolve) => setTimeout(resolve, 150));

            // Verify camera position again after render cycles
            const verifyPos = this.camera.getPosition();
            const verifyRot = this.camera.getEulerAngles();
            console.log(
                `[captureSideViewTransparentAsBlob] Camera position after render cycles: x=${verifyPos.x.toFixed(
                    2
                )}, y=${verifyPos.y.toFixed(2)}, z=${verifyPos.z.toFixed(2)}`
            );
            console.log(
                `[captureSideViewTransparentAsBlob] Camera rotation after render cycles: x=${verifyRot.x.toFixed(
                    2
                )}, y=${verifyRot.y.toFixed(2)}, z=${verifyRot.z.toFixed(2)}`
            );

            // Check if camera position changed (shouldn't have)
            if (
                Math.abs(verifyPos.x - cameraPosAfterSetup.x) > 0.1 ||
                Math.abs(verifyPos.z - cameraPosAfterSetup.z) > 0.1
            ) {
                console.warn(
                    `[captureSideViewTransparentAsBlob] WARNING: Camera position changed during render cycles!`
                );
                console.warn(
                    `[captureSideViewTransparentAsBlob] Before: x=${cameraPosAfterSetup.x.toFixed(
                        2
                    )}, z=${cameraPosAfterSetup.z.toFixed(2)}`
                );
                console.warn(
                    `[captureSideViewTransparentAsBlob] After: x=${verifyPos.x.toFixed(
                        2
                    )}, z=${verifyPos.z.toFixed(2)}`
                );

                // If camera was reset, reposition it now
                console.warn(`[captureSideViewTransparentAsBlob] Repositioning camera...`);
                this.calcSceneBounds(this.dynamicSceneBounds);
                const center = this.dynamicSceneBounds.center.clone();
                const bounds = this.dynamicSceneBounds;
                const min = bounds.getMin();
                const max = bounds.getMax();
                const modelWidth = max.z - min.z;
                const modelHeight = max.y - min.y;
                const modelDepth = max.x - min.x;
                const maxVisibleDimension = Math.max(modelWidth, modelHeight, modelDepth);
                const distance = maxVisibleDimension * 2.2;

                if (angle === 'right') {
                    const sidePos = new Vec3(center.x + distance, center.y, center.z);
                    this.camera.setPosition(sidePos);
                    this.cameraControls.focus(center, sidePos);
                } else {
                    const sidePos = new Vec3(center.x - distance, center.y, center.z);
                    this.camera.setPosition(sidePos);
                    this.cameraControls.focus(center, sidePos);
                }

                // Force camera controls to update
                // @ts-ignore
                if (this.cameraControls._zoomDist !== undefined) {
                    // @ts-ignore
                    this.cameraControls._zoomDist = distance;
                }

                // Force render cycles again
                for (let i = 0; i < 3; i++) {
                    await new Promise((resolve) => requestAnimationFrame(resolve));
                    this.app.fire('prerender');
                    this.app.renderNextFrame = true;
                    this.app.render();
                    this.app.fire('postrender');
                }
                await new Promise((resolve) => setTimeout(resolve, 100));
            }

            // Verify camera forward vector to ensure it's looking at the model
            const cameraForward = this.camera.forward.clone();
            this.calcSceneBounds(this.dynamicSceneBounds);
            const center = this.dynamicSceneBounds.center.clone();
            const cameraPos = this.camera.getPosition();
            const toCenter = center.sub(cameraPos).normalize();
            const dotProduct = cameraForward.dot(toCenter);
            console.log(
                `[captureSideViewTransparentAsBlob] Camera forward vector: x=${cameraForward.x.toFixed(
                    3
                )}, y=${cameraForward.y.toFixed(3)}, z=${cameraForward.z.toFixed(3)}`
            );
            console.log(
                `[captureSideViewTransparentAsBlob] Direction to center: x=${toCenter.x.toFixed(
                    3
                )}, y=${toCenter.y.toFixed(3)}, z=${toCenter.z.toFixed(3)}`
            );
            console.log(
                `[captureSideViewTransparentAsBlob] Dot product (should be close to 1.0): ${dotProduct.toFixed(
                    3
                )}`
            );

            if (dotProduct < 0.5) {
                console.warn(
                    `[captureSideViewTransparentAsBlob] WARNING: Camera is not looking at the model! Dot product: ${dotProduct.toFixed(
                        3
                    )}`
                );
                console.warn(
                    `[captureSideViewTransparentAsBlob] Repositioning camera to look at model...`
                );

                // Reposition camera to look at center
                const bounds = this.dynamicSceneBounds;
                const min = bounds.getMin();
                const max = bounds.getMax();
                const modelWidth = max.z - min.z;
                const modelHeight = max.y - min.y;
                const modelDepth = max.x - min.x;
                const maxVisibleDimension = Math.max(modelWidth, modelHeight, modelDepth);
                const distance = maxVisibleDimension * 2.2;

                if (angle === 'right') {
                    const sidePos = new Vec3(center.x + distance, center.y, center.z);
                    this.camera.setPosition(sidePos);
                    this.camera.lookAt(center);
                } else {
                    const sidePos = new Vec3(center.x - distance, center.y, center.z);
                    this.camera.setPosition(sidePos);
                    this.camera.lookAt(center);
                }

                // Force render cycles
                for (let i = 0; i < 3; i++) {
                    await new Promise((resolve) => requestAnimationFrame(resolve));
                    this.app.fire('prerender');
                    this.app.renderNextFrame = true;
                    this.app.render();
                    this.app.fire('postrender');
                }
                await new Promise((resolve) => setTimeout(resolve, 100));
            }

            this.setupTransparentBackground();

            // Verify camera position after setupTransparentBackground (in case it was reset)
            const verifyPos2 = this.camera.getPosition();
            console.log(
                `[captureSideViewTransparentAsBlob] Camera position after setupTransparentBackground: x=${verifyPos2.x.toFixed(
                    2
                )}, y=${verifyPos2.y.toFixed(2)}, z=${verifyPos2.z.toFixed(2)}`
            );

            // If camera was reset, reposition it
            if (
                Math.abs(verifyPos2.x - verifyPos.x) > 0.1 ||
                Math.abs(verifyPos2.z - verifyPos.z) > 0.1
            ) {
                console.warn(
                    `[captureSideViewTransparentAsBlob] Camera was reset! Repositioning...`
                );
                // Recalculate position
                this.calcSceneBounds(this.dynamicSceneBounds);
                const center = this.dynamicSceneBounds.center.clone();
                const bounds = this.dynamicSceneBounds;
                const min = bounds.getMin();
                const max = bounds.getMax();
                const modelWidth = max.z - min.z;
                const modelHeight = max.y - min.y;
                const modelDepth = max.x - min.x;
                const maxVisibleDimension = Math.max(modelWidth, modelHeight, modelDepth);
                const distance = maxVisibleDimension * 2.2;

                // Reposition camera based on angle
                if (angle === 'right') {
                    const sidePos = new Vec3(center.x + distance, center.y, center.z);
                    this.camera.setPosition(sidePos);
                    this.cameraControls.focus(center, sidePos);
                } else {
                    const sidePos = new Vec3(center.x - distance, center.y, center.z);
                    this.camera.setPosition(sidePos);
                    this.cameraControls.focus(center, sidePos);
                }

                // Force camera controls to update
                // @ts-ignore
                if (this.cameraControls._zoomDist !== undefined) {
                    // @ts-ignore
                    this.cameraControls._zoomDist = distance;
                }

                // Force immediate render to apply camera transform
                this.app.fire('prerender');
                this.app.renderNextFrame = true;
                this.app.render();
                this.app.fire('postrender');
            }

            // Force render cycles
            for (let i = 0; i < 3; i++) {
                this.app.fire('prerender');
                this.app.renderNextFrame = true;
                this.app.render();
                this.app.fire('postrender');
            }

            // Wait for render to complete
            await new Promise((resolve) => requestAnimationFrame(resolve));

            const rt = this.camera.camera.renderTarget;
            if (!rt) {
                throw new Error('No render target available');
            }

            if (rt.samples > 1) {
                rt.resolve();
            }

            // Final render
            this.app.fire('prerender');
            this.app.renderNextFrame = true;
            this.app.render();
            this.app.fire('postrender');

            if (rt.samples > 1) {
                rt.resolve();
            }

            await new Promise((resolve) => requestAnimationFrame(resolve));

            const texture = rt.colorBuffer;
            if (!texture) {
                throw new Error('No color buffer available');
            }

            // Read texture data - for MSAA render targets, read without renderTarget option after resolving
            const readOptions =
                rt.samples > 1 ? { immediate: true } : { renderTarget: rt, immediate: true };

            const typedArray = await texture.read(0, 0, texture.width, texture.height, readOptions);
            const imageData = new Uint32Array(typedArray.buffer.slice(0));

            // Convert to blob
            const canvas = document.createElement('canvas');
            canvas.width = texture.width;
            canvas.height = texture.height;
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                throw new Error('Failed to get 2D context');
            }

            const imgData = ctx.createImageData(texture.width, texture.height);
            const data = imgData.data;

            // Flip vertically and convert ABGR to RGBA
            const needsFlip = true;
            if (needsFlip) {
                for (let y = 0; y < texture.height; y++) {
                    const srcRow = (texture.height - 1 - y) * texture.width;
                    const dstRow = y * texture.width;
                    for (let x = 0; x < texture.width; x++) {
                        const srcIdx = srcRow + x;
                        const dstIdx = (dstRow + x) * 4;
                        const pixel = imageData[srcIdx];
                        data[dstIdx + 0] = (pixel >> 0) & 0xff; // R
                        data[dstIdx + 1] = (pixel >> 8) & 0xff; // G
                        data[dstIdx + 2] = (pixel >> 16) & 0xff; // B
                        data[dstIdx + 3] = (pixel >> 24) & 0xff; // A
                    }
                }
            }

            ctx.putImageData(imgData, 0, 0);

            // Convert to blob
            const blob = await new Promise<Blob>((resolve, reject) => {
                const mimeType = outputFormat === 'webp' ? 'image/webp' : 'image/png';
                canvas.toBlob(
                    (blob) => {
                        if (blob) {
                            resolve(blob);
                        } else {
                            reject(new Error(`Failed to create ${outputFormat} blob`));
                        }
                    },
                    mimeType,
                    outputFormat === 'webp' ? 0.95 : undefined
                );
            });

            // Return blob URL
            return URL.createObjectURL(blob);
        } finally {
            // Restore everything
            this.canvas.width = originalWidth;
            this.canvas.height = originalHeight;
            this.canvas.style.width = originalStyleWidth;
            this.canvas.style.height = originalStyleHeight;
            this.app.graphicsDevice.setResolution(originalDeviceWidth, originalDeviceHeight);
            this.destroyRenderTargets();
            this.forceRebuildRenderTargets(originalDeviceWidth, originalDeviceHeight);
            this.camera.camera.aspectRatioMode = originalAspectRatioMode;
            this.camera.camera.aspectRatio = originalAspectRatio;
            this.restoreRenderingState(originalState);

            // Restore original orbit mode if it was changed
            if (originalOrbitMode !== 'target') {
                console.log(
                    `[captureSideViewTransparentAsBlob] Restoring orbit mode to "${originalOrbitMode}"`
                );
                this.setOrbitMode(originalOrbitMode);
            }

            // Re-enable UI camera controls
            this.enableUICameraControls();
        }
    }

    // Capture screenshot from aircraft camera and return as blob URL
    async captureAircraftCameraAsBlob(
        camera: any,
        resolution: 'thumbnail' | 'large' | '4k' | '8k' = 'thumbnail'
    ): Promise<string> {
        // Disable UI camera controls for programmatic camera control
        this.disableUICameraControls();

        // Always use WebP format
        const outputFormat = 'webp';

        // Save current orbit mode and force to "camera-center" for aircraft cameras
        // Aircraft cameras (Captain, Left Wing, etc.) are positioned inside/on the aircraft
        // and need "camera-center" mode to work correctly
        const originalOrbitMode = this.observer.get('camera.orbitMode');
        if (originalOrbitMode !== 'camera-center') {
            console.log(
                `[captureAircraftCameraAsBlob] Forcing orbit mode from "${originalOrbitMode}" to "camera-center" for aircraft camera`
            );
            this.setOrbitMode('camera-center');
            // Wait a bit for orbit mode to take effect
            await new Promise((resolve) => setTimeout(resolve, 100));
        }

        // Calculate resolution dimensions (use rectangular aspect ratio matching side views: ~2.78:1)
        // Thumbnail: 512x184, Large: 2048x736, 4K: 3840x1380, 8K: 7680x2760
        const baseWidth = 512;
        const baseHeight = 184;
        const scale =
            resolution === 'large' ? 4 : resolution === '4k' ? 7.5 : resolution === '8k' ? 15 : 1;
        const targetWidth = baseWidth * scale;
        const targetHeight = baseHeight * scale;

        // Save current state
        const originalWidth = this.canvas.width;
        const originalHeight = this.canvas.height;
        const originalStyleWidth = this.canvas.style.width;
        const originalStyleHeight = this.canvas.style.height;
        const originalDeviceWidth = this.app.graphicsDevice.width;
        const originalDeviceHeight = this.app.graphicsDevice.height;
        const originalAspectRatioMode = this.camera.camera.aspectRatioMode;
        const originalAspectRatio = this.camera.camera.aspectRatio;
        const originalFOV = this.camera.camera.fov;
        const originalState = this.saveRenderingState();

        try {
            // Change resolution only if needed (in batch mode, resolution may already be correct)
            const currentDeviceWidth = this.app.graphicsDevice.width;
            const currentDeviceHeight = this.app.graphicsDevice.height;
            if (currentDeviceWidth !== targetWidth || currentDeviceHeight !== targetHeight) {
                this.app.graphicsDevice.setResolution(targetWidth, targetHeight);
            }
            // forceRebuildRenderTargets will check and reuse existing render target if size matches
            this.forceRebuildRenderTargets(targetWidth, targetHeight);

            // Set camera aspect ratio
            const targetAspectRatio = targetWidth / targetHeight;
            this.camera.camera.aspectRatioMode = 1;
            this.camera.camera.aspectRatio = targetAspectRatio;

            // Setup camera and rendering (this will set position and FOV from config)
            // NOTE: setupAircraftCameraView calls applyScreenshotDefaults which loads skybox
            // We need to do this BEFORE setting up render targets to avoid conflicts
            await this.setupAircraftCameraView(camera, resolution);

            // Get the FOV that was set by moveCameraToAircraftCamera (from config)
            const configFOV = this.camera.camera.fov;
            console.log(
                `[captureAircraftCameraAsBlob] Config FOV: ${configFOV}, Aspect ratio: ${targetAspectRatio}`
            );

            // NOW set up transparent background (after camera setup to avoid conflicts)
            // For batch mode, only set up once and reuse
            if (!this.isBatchScreenshotMode) {
                this.setupTransparentBackground();
            } else {
                console.log(
                    `[captureAircraftCameraAsBlob] Skipping setupTransparentBackground (already set up for batch mode)`
                );
                // But we still need to disable shadows for each capture in batch mode
                this.disableShadowsForScreenshot();
            }

            // Ensure camera aspect ratio is still set correctly after setupAircraftCameraView
            this.camera.camera.aspectRatioMode = 1;
            this.camera.camera.aspectRatio = targetAspectRatio;

            // CRITICAL: Rebuild render targets AFTER all setup is complete to ensure they're correct
            // forceRebuildRenderTargets will check and reuse existing render target if size matches
            this.forceRebuildRenderTargets(targetWidth, targetHeight);

            // Ensure camera is rendering to the render target
            const rt = this.camera.camera.renderTarget;
            if (!rt) {
                throw new Error('No render target available');
            }

            // Re-verify camera position and rotation after render target rebuild (they might have been reset)
            const verifyPos = this.camera.getPosition();
            const verifyRot = this.camera.getEulerAngles();

            // Always re-apply camera positioning after render target rebuild to ensure rotation is correct
            // The camera position might be correct but rotation often gets reset
            const expectedPos = new Vec3(
                camera.position?.x || camera.position?.X || 0,
                camera.position?.y || camera.position?.Y || 0,
                camera.position?.z || camera.position?.Z || 0
            );

            // Get camera angles to determine if rotation should be non-zero
            const angleX = camera.angleX !== undefined ? camera.angleX : camera.AngleX || 0;
            const angleY = camera.angleY !== undefined ? camera.angleY : camera.AngleY || 0;
            const shouldHaveRotation = Math.abs(angleX) > 0.01 || Math.abs(angleY) > 0.01;

            // Check if camera position was reset
            const posChanged =
                Math.abs(verifyPos.x - expectedPos.x) > 0.1 ||
                Math.abs(verifyPos.y - expectedPos.y) > 0.1 ||
                Math.abs(verifyPos.z - expectedPos.z) > 0.1;

            // Check if rotation was reset - but be careful: forward-facing cameras (angleX=0, angleY=0)
            // will have near-zero rotation, which is CORRECT. We should only reposition if the camera
            // was supposed to have non-zero rotation but doesn't.
            const rotIsZero =
                Math.abs(verifyRot.x) < 0.01 &&
                Math.abs(verifyRot.y) < 0.01 &&
                Math.abs(verifyRot.z) < 0.01;
            const rotationMismatch = shouldHaveRotation && rotIsZero;

            if (posChanged || rotationMismatch) {
                console.warn(
                    `[captureAircraftCameraAsBlob] Camera was reset after render target rebuild! Repositioning... (posChanged: ${posChanged}, rotationMismatch: ${rotationMismatch}, shouldHaveRotation: ${shouldHaveRotation}, rotIsZero: ${rotIsZero})`
                );

                // CRITICAL: Disable camera controls updates before repositioning
                // @ts-ignore
                const originalUpdate = this.cameraControls.update;
                // @ts-ignore
                this.cameraControls.update = () => {}; // No-op to prevent camera controls from moving camera

                // Re-setup the camera view completely
                this.moveCameraToAircraftCamera(camera);

                // Force camera controls to match
                this.camera.camera.aspectRatioMode = 1;
                this.camera.camera.aspectRatio = targetAspectRatio;

                // Force immediate render to apply camera transform
                this.app.fire('prerender');
                this.app.renderNextFrame = true;
                this.app.render();
                this.app.fire('postrender');

                // Wait a bit for camera to settle
                await new Promise((resolve) => setTimeout(resolve, 100));

                // Restore update function after a moment
                // @ts-ignore
                setTimeout(() => {
                    // @ts-ignore
                    if (originalUpdate) {
                        // @ts-ignore
                        this.cameraControls.update = originalUpdate;
                    }
                }, 200);
            }

            // Force render cycles to ensure everything is updated
            for (let i = 0; i < 5; i++) {
                this.app.fire('prerender');
                this.app.renderNextFrame = true;
                this.app.render();
                this.app.fire('postrender');
                await new Promise((resolve) => requestAnimationFrame(resolve));
            }

            // Additional delay to ensure scene is fully rendered
            await new Promise((resolve) => setTimeout(resolve, 100));

            // CRITICAL: Re-verify camera rotation after render cycles (camera controls might reset it)
            // But only if the camera should have non-zero rotation
            const finalVerifyRot = this.camera.getEulerAngles();
            const rotStillZero =
                Math.abs(finalVerifyRot.x) < 0.01 &&
                Math.abs(finalVerifyRot.y) < 0.01 &&
                Math.abs(finalVerifyRot.z) < 0.01;
            const rotationStillMismatch = shouldHaveRotation && rotStillZero;
            if (rotationStillMismatch) {
                console.warn(
                    `[captureAircraftCameraAsBlob] Camera rotation was reset again after render cycles! Repositioning one more time... (shouldHaveRotation: ${shouldHaveRotation}, rotStillZero: ${rotStillZero})`
                );

                // CRITICAL: Disable camera controls updates before repositioning
                // @ts-ignore
                const originalUpdate2 = this.cameraControls.update;
                // @ts-ignore
                this.cameraControls.update = () => {}; // No-op to prevent camera controls from moving camera

                // Re-apply camera positioning one final time
                this.moveCameraToAircraftCamera(camera);
                this.camera.camera.aspectRatioMode = 1;
                this.camera.camera.aspectRatio = targetAspectRatio;

                // Force immediate render to apply camera transform
                this.app.fire('prerender');
                this.app.renderNextFrame = true;
                this.app.render();
                this.app.fire('postrender');

                // Force a few more render cycles after final reposition
                for (let i = 0; i < 3; i++) {
                    this.app.fire('prerender');
                    this.app.renderNextFrame = true;
                    this.app.render();
                    this.app.fire('postrender');
                    await new Promise((resolve) => requestAnimationFrame(resolve));
                }
                await new Promise((resolve) => setTimeout(resolve, 100));

                // Restore update function after a moment
                // @ts-ignore
                setTimeout(() => {
                    // @ts-ignore
                    if (originalUpdate2) {
                        // @ts-ignore
                        this.cameraControls.update = originalUpdate2;
                    }
                }, 200);
            }

            // Wait for render to complete
            await new Promise((resolve) => requestAnimationFrame(resolve));

            if (rt.samples > 1) {
                rt.resolve();
            }

            // Final render with explicit render target
            this.app.fire('prerender');
            this.app.renderNextFrame = true;
            this.app.render();
            this.app.fire('postrender');

            if (rt.samples > 1) {
                rt.resolve();
            }

            // Wait for render to complete and ensure GPU has finished rendering
            await new Promise((resolve) => requestAnimationFrame(resolve));
            await new Promise((resolve) => requestAnimationFrame(resolve));
            await new Promise((resolve) => setTimeout(resolve, 200)); // Increased delay for GPU

            // Re-verify render target is still valid (it might have been destroyed)
            const currentRt = this.camera.camera.renderTarget;
            if (!currentRt || currentRt !== rt) {
                throw new Error(
                    `Render target was destroyed or changed. Expected: ${
                        rt ? 'exists' : 'null'
                    }, Got: ${currentRt ? 'exists' : 'null'}`
                );
            }

            const texture = rt.colorBuffer;
            if (!texture) {
                throw new Error('No color buffer available');
            }

            // Verify texture is valid and has valid dimensions
            if (!texture.width || !texture.height) {
                throw new Error(`Invalid texture dimensions: ${texture.width}x${texture.height}`);
            }

            // Verify texture has a valid graphics device
            if (!texture.device) {
                throw new Error('Texture graphics device is invalid or destroyed');
            }

            console.log(
                `[captureAircraftCameraAsBlob] Reading texture: ${texture.width}x${texture.height}`
            );

            // Read texture data - for MSAA render targets, read without renderTarget option after resolving
            const readOptions =
                rt.samples > 1 ? { immediate: true } : { renderTarget: rt, immediate: true };

            let typedArray;
            try {
                typedArray = await texture.read(0, 0, texture.width, texture.height, readOptions);
            } catch (readError) {
                console.error(`[captureAircraftCameraAsBlob] Error reading texture:`, readError);
                console.error(`[captureAircraftCameraAsBlob] Render target state:`, {
                    exists: !!rt,
                    width: rt?.width,
                    height: rt?.height,
                    samples: rt?.samples,
                    colorBufferExists: !!rt?.colorBuffer,
                    colorBufferWidth: rt?.colorBuffer?.width,
                    colorBufferHeight: rt?.colorBuffer?.height,
                    textureDeviceExists: !!texture?.device,
                });
                throw new Error(
                    `Failed to read texture: ${
                        readError instanceof Error ? readError.message : String(readError)
                    }`
                );
            }
            const imageData = new Uint32Array(typedArray.buffer.slice(0));

            // Check if image data is empty (all zeros or all transparent)
            const nonZeroPixels = imageData.filter((pixel) => pixel !== 0).length;
            const totalPixels = imageData.length;
            console.log(
                `[captureAircraftCameraAsBlob] Image data: ${totalPixels} total pixels, ${nonZeroPixels} non-zero pixels (${(
                    (nonZeroPixels / totalPixels) *
                    100
                ).toFixed(2)}%)`
            );

            if (nonZeroPixels === 0) {
                console.warn(
                    `[captureAircraftCameraAsBlob] WARNING: Render target appears to be empty! Camera position:`,
                    this.camera.getPosition(),
                    `Camera rotation:`,
                    this.camera.getEulerAngles()
                );
            }

            // Convert to blob
            const canvas = document.createElement('canvas');
            canvas.width = texture.width;
            canvas.height = texture.height;
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                throw new Error('Failed to get 2D context');
            }

            const imgData = ctx.createImageData(texture.width, texture.height);
            const data = imgData.data;

            // Flip vertically and convert ABGR to RGBA
            const needsFlip = true;
            if (needsFlip) {
                for (let y = 0; y < texture.height; y++) {
                    const srcRow = (texture.height - 1 - y) * texture.width;
                    const dstRow = y * texture.width;
                    for (let x = 0; x < texture.width; x++) {
                        const srcIdx = srcRow + x;
                        const dstIdx = (dstRow + x) * 4;
                        const pixel = imageData[srcIdx];
                        data[dstIdx + 0] = (pixel >> 0) & 0xff; // R
                        data[dstIdx + 1] = (pixel >> 8) & 0xff; // G
                        data[dstIdx + 2] = (pixel >> 16) & 0xff; // B
                        data[dstIdx + 3] = (pixel >> 24) & 0xff; // A
                    }
                }
            }

            ctx.putImageData(imgData, 0, 0);

            // Convert to blob
            const blob = await new Promise<Blob>((resolve, reject) => {
                const mimeType = outputFormat === 'webp' ? 'image/webp' : 'image/png';
                canvas.toBlob(
                    (blob) => {
                        if (blob) {
                            resolve(blob);
                        } else {
                            reject(new Error(`Failed to create ${outputFormat} blob`));
                        }
                    },
                    mimeType,
                    outputFormat === 'webp' ? 0.95 : undefined
                );
            });

            return URL.createObjectURL(blob);
        } finally {
            // For batch mode, don't restore resolution/render targets between captures - keep them for reuse
            if (!this.isBatchScreenshotMode) {
                // Restore everything
                this.canvas.width = originalWidth;
                this.canvas.height = originalHeight;
                this.canvas.style.width = originalStyleWidth;
                this.canvas.style.height = originalStyleHeight;
                this.app.graphicsDevice.setResolution(originalDeviceWidth, originalDeviceHeight);
                this.destroyRenderTargets();
                this.forceRebuildRenderTargets(originalDeviceWidth, originalDeviceHeight);
                this.camera.camera.aspectRatioMode = originalAspectRatioMode;
                this.camera.camera.aspectRatio = originalAspectRatio;
                this.restoreRenderingState(originalState);
            } else {
                // In batch mode, keep render targets and resolution for reuse
                // Only restore canvas dimensions (for display purposes)
                this.canvas.width = originalWidth;
                this.canvas.height = originalHeight;
                this.canvas.style.width = originalStyleWidth;
                this.canvas.style.height = originalStyleHeight;
                console.log(
                    `[captureAircraftCameraAsBlob] Skipping resolution/render target restore (batch mode - keeping for reuse)`
                );
            }

            // Restore original orbit mode if it was changed
            if (originalOrbitMode !== 'camera-center') {
                console.log(
                    `[captureAircraftCameraAsBlob] Restoring orbit mode to "${originalOrbitMode}"`
                );
                this.setOrbitMode(originalOrbitMode);
            }

            // Re-enable UI camera controls
            this.enableUICameraControls();
        }
    }

    // Export image as WebP format
    private async exportAsWebP(
        filename: string,
        imageData: Uint32Array,
        width: number,
        height: number,
        directoryHandle?: any
    ): Promise<void> {
        // Create an offscreen canvas to convert the image data
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
            throw new Error('Failed to get 2D context');
        }

        // Chrome reads texture data top-down (correct orientation, no flip needed)
        // Edge reads texture data bottom-up (needs vertical flip)
        // With flipY: true on screenshot render targets, we need to flip to correct orientation
        const ua = navigator.userAgent;
        const isChrome = /Chrome/.test(ua) && !/Edg/.test(ua);
        // Always flip to ensure correct orientation with flipY: true render targets
        const needsFlip = true;

        // Convert Uint32Array to ImageData
        const imgData = ctx.createImageData(width, height);
        const data = imgData.data;

        // Copy pixels (flip vertically if needed)
        if (needsFlip) {
            // Flip vertically (for Edge and other browsers - texture data is bottom-up)
            for (let y = 0; y < height; y++) {
                const srcRow = (height - 1 - y) * width;
                const dstRow = y * width;
                for (let x = 0; x < width; x++) {
                    const srcIdx = srcRow + x;
                    const dstIdx = (dstRow + x) * 4;
                    const pixel = imageData[srcIdx];

                    // RGBA from ABGR
                    data[dstIdx + 0] = (pixel >> 0) & 0xff; // R
                    data[dstIdx + 1] = (pixel >> 8) & 0xff; // G
                    data[dstIdx + 2] = (pixel >> 16) & 0xff; // B
                    data[dstIdx + 3] = (pixel >> 24) & 0xff; // A
                }
            }
        } else {
            // No flip (for Chrome - data is already in correct orientation)
            for (let y = 0; y < height; y++) {
                const srcRow = y * width;
                const dstRow = y * width;
                for (let x = 0; x < width; x++) {
                    const srcIdx = srcRow + x;
                    const dstIdx = (dstRow + x) * 4;
                    const pixel = imageData[srcIdx];

                    // RGBA from ABGR
                    data[dstIdx + 0] = (pixel >> 0) & 0xff; // R
                    data[dstIdx + 1] = (pixel >> 8) & 0xff; // G
                    data[dstIdx + 2] = (pixel >> 16) & 0xff; // B
                    data[dstIdx + 3] = (pixel >> 24) & 0xff; // A
                }
            }
        }

        ctx.putImageData(imgData, 0, 0);

        // Convert to WebP blob
        const blob = await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob(
                (blob) => {
                    if (blob) {
                        resolve(blob);
                    } else {
                        reject(new Error('Failed to create WebP blob'));
                    }
                },
                'image/webp',
                0.95
            ); // 95% quality
        });

        // Save the blob
        console.log(
            `[exportAsWebP] Directory handle check:`,
            directoryHandle ? `✓ ${directoryHandle.name}` : '✗ null/undefined'
        );
        if (directoryHandle) {
            console.log(
                `[exportAsWebP] Saving WebP to directory: ${directoryHandle.name}, filename: ${filename}`
            );
            // Save to specific directory
            const fileHandle = await directoryHandle.getFileHandle(filename, { create: true });
            const writable = await fileHandle.createWritable();
            await writable.write(blob);
            await writable.close();
            console.log(`[exportAsWebP] ✓ WebP saved successfully to directory`);
        } else {
            console.log(`[exportAsWebP] No directory handle - downloading WebP: ${filename}`);
            // Regular download
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
        }
    }

    // Take screenshot with custom filename (optionally save to directory handle)
    async downloadSideViewTransparentWithFilename(
        filename: string,
        directoryHandle?: any
    ): Promise<void> {
        console.log(
            `[downloadSideViewTransparentWithFilename] Received directoryHandle:`,
            directoryHandle ? `✓ ${directoryHandle.name}` : '✗ null/undefined'
        );
        const outputFormat = this.observer.get('screenshot.outputFormat') || 'png';
        const resolution = this.observer.get('screenshot.resolution') || 'thumbnail';

        // If "all" is selected, export both sizes
        if (resolution === 'all') {
            console.log('Exporting all resolutions...');

            // Export thumbnail first
            await this.downloadSideViewTransparentAtResolution(
                filename,
                'thumbnail',
                directoryHandle
            );

            // Small delay between exports
            await this.delay(100);

            // Export large
            await this.downloadSideViewTransparentAtResolution(filename, 'large', directoryHandle);

            console.log('All resolutions exported successfully!');
            return;
        }

        // Single resolution export
        await this.downloadSideViewTransparentAtResolution(
            filename,
            resolution as 'thumbnail' | 'large',
            directoryHandle
        );
    }

    // Internal helper: Take screenshot at a specific resolution
    private async downloadSideViewTransparentAtResolution(
        baseFilename: string,
        resolution: 'thumbnail' | 'large',
        directoryHandle?: any
    ): Promise<void> {
        console.log(
            `[downloadSideViewTransparentAtResolution] Received directoryHandle:`,
            directoryHandle ? `✓ ${directoryHandle.name}` : '✗ null/undefined'
        );
        const outputFormat = this.observer.get('screenshot.outputFormat') || 'png';

        // Calculate resolution dimensions based on setting
        const baseWidth = 512;
        const baseHeight = 184;
        const scale = resolution === 'large' ? 4 : 1;
        const targetWidth = baseWidth * scale;
        const targetHeight = baseHeight * scale;

        // Add appropriate suffix to filename
        const suffix = resolution === 'large' ? '_profile@2x' : '_profile';
        const filename = `${baseFilename}${suffix}`;

        console.log(
            `Starting screenshot: ${filename}.${outputFormat} at ${targetWidth}x${targetHeight}`
        );

        // Save current canvas and device dimensions
        const originalWidth = this.canvas.width;
        const originalHeight = this.canvas.height;
        const originalStyleWidth = this.canvas.style.width;
        const originalStyleHeight = this.canvas.style.height;
        const originalDeviceWidth = this.app.graphicsDevice.width;
        const originalDeviceHeight = this.app.graphicsDevice.height;

        // Save current camera aspect ratio settings
        const originalAspectRatioMode = this.camera.camera.aspectRatioMode;
        const originalAspectRatio = this.camera.camera.aspectRatio;

        // Save current state
        const originalState = this.saveRenderingState();

        // FIRST: Change resolution
        this.app.graphicsDevice.setResolution(targetWidth, targetHeight);

        // FORCE destroy and rebuild render targets
        this.destroyRenderTargets();
        this.forceRebuildRenderTargets(targetWidth, targetHeight);

        // CRITICAL: Set camera to manual aspect ratio mode and set the correct aspect ratio
        const targetAspectRatio = targetWidth / targetHeight;
        this.camera.camera.aspectRatioMode = 1; // ASPECT_MANUAL = 1, ASPECT_AUTO = 0
        this.camera.camera.aspectRatio = targetAspectRatio;

        // THEN: Do all camera setup with the correct resolution
        await this.setupSideViewCameraFor512x184();

        // Set up for transparent rendering
        this.setupTransparentBackground();

        // Force multiple render cycles to ensure camera position is applied
        for (let i = 0; i < 3; i++) {
            this.app.fire('prerender');
            this.app.renderNextFrame = true;
            this.app.render();
            this.app.fire('postrender');
        }

        // Small delay for one render frame (camera moves instantly with inertiaFactor=1.0)
        return new Promise((resolve, reject) => {
            // Use requestAnimationFrame to ensure Chrome's render is complete
            requestAnimationFrame(async () => {
                const rt = this.camera.camera.renderTarget;
                if (!rt) {
                    reject(new Error('No render target available'));
                    return;
                }

                // Ensure render target is resolved (important for multisampled targets, especially in Chrome)
                if (rt.samples > 1) {
                    rt.resolve();
                }

                // Force one more render to ensure everything is ready (Chrome may need this)
                this.app.fire('prerender');
                this.app.renderNextFrame = true;
                this.app.render();
                this.app.fire('postrender');

                // Resolve again after render (Chrome may need this)
                if (rt.samples > 1) {
                    rt.resolve();
                }

                // Wait for next frame to ensure render completes in Chrome
                await new Promise((resolve) => requestAnimationFrame(resolve));

                const texture = rt.colorBuffer;
                if (!texture) {
                    reject(new Error('No color buffer available'));
                    return;
                }

                // Chrome may need immediate read with explicit renderTarget
                // Also ensure we're reading from the correct render target
                const readOptions = {
                    renderTarget: rt,
                    immediate: true,
                };

                // Small delay for one render frame (camera moves instantly with inertiaFactor=1.0)
                setTimeout(() => {
                    console.log(
                        'Reading texture:',
                        texture.width,
                        'x',
                        texture.height,
                        'from render target:',
                        rt.width,
                        'x',
                        rt.height
                    );

                    texture
                        .read(0, 0, texture.width, texture.height, readOptions)
                    .then(async (typedArray: Uint32Array) => {
                        const outputFormat = this.observer.get('screenshot.outputFormat') || 'png';
                        const outputFilename = `${filename}.${outputFormat}`;

                        console.log(`Exporting ${outputFormat.toUpperCase()}:`, outputFilename);

                        // Validate data - check if we actually have non-transparent pixels
                        const imageData = new Uint32Array(typedArray.buffer.slice(0));

                        // Debug: Check first few pixels
                        console.log(
                            'First 4 pixels (RGBA):',
                            Array.from(imageData.slice(0, 4)).map((p) => {
                                const r = (p >> 0) & 0xff;
                                const g = (p >> 8) & 0xff;
                                const b = (p >> 16) & 0xff;
                                const a = (p >> 24) & 0xff;
                                return `R:${r} G:${g} B:${b} A:${a}`;
                            })
                        );

                        const hasData = imageData.some((pixel) => pixel >>> 24 !== 0); // Check alpha channel

                        if (!hasData) {
                            console.error(
                                'ERROR: Image data appears to be empty/transparent. Texture size:',
                                texture.width,
                                'x',
                                texture.height
                            );
                            console.error(
                                'Data length:',
                                imageData.length,
                                'Expected:',
                                texture.width * texture.height
                            );
                            reject(new Error('Failed to read texture data - image is empty'));
                            return;
                        }

                        // For now, only PNG is supported through pngExporter
                        // WebP would require a different exporter
                        if (outputFormat === 'png') {
                            if (directoryHandle) {
                                console.log(
                                    `Saving PNG to directory: ${directoryHandle.name}, filename: ${outputFilename}`
                                );
                                // Save to specific directory (will overwrite existing files)
                                await this.pngExporter.exportToDirectory(
                                    directoryHandle,
                                    outputFilename,
                                    imageData,
                                    texture.width,
                                    texture.height
                                );
                                console.log(`✓ PNG saved to directory: ${directoryHandle.name}`);
                            } else {
                                console.log(
                                    `No directory handle, downloading PNG: ${outputFilename}`
                                );
                                // Regular download (may add (1), (2) in Chrome)
                                await this.pngExporter.export(
                                    outputFilename,
                                    imageData,
                                    texture.width,
                                    texture.height
                                );
                            }
                        } else if (outputFormat === 'webp') {
                            if (directoryHandle) {
                                console.log(
                                    `Saving WebP to directory: ${directoryHandle.name}, filename: ${outputFilename}`
                                );
                            } else {
                                console.log(
                                    `No directory handle, downloading WebP: ${outputFilename}`
                                );
                            }
                            // WebP export using Canvas API
                            await this.exportAsWebP(
                                outputFilename,
                                imageData,
                                texture.width,
                                texture.height,
                                directoryHandle
                            );
                        }

                        console.log(
                            `Transparent screenshot exported successfully as ${outputFormat.toUpperCase()}!`
                        );
                        resolve();
                    })
                    .catch((error) => {
                        console.error('Error reading texture data:', error);
                        reject(error);
                    })
                    .finally(() => {
                        // Restore everything
                        this.canvas.width = originalWidth;
                        this.canvas.height = originalHeight;
                        this.canvas.style.width = originalStyleWidth;
                        this.canvas.style.height = originalStyleHeight;

                        this.app.graphicsDevice.setResolution(
                            originalDeviceWidth,
                            originalDeviceHeight
                        );
                        this.destroyRenderTargets();
                        this.forceRebuildRenderTargets(originalDeviceWidth, originalDeviceHeight);

                        // Restore original camera aspect ratio settings
                        this.camera.camera.aspectRatioMode = originalAspectRatioMode;
                        this.camera.camera.aspectRatio = originalAspectRatio;

                        this.restoreRenderingState(originalState);

                        // Restore visibility of high-speed spinning nodes
                        this.showHighSpeedSpinningNodes();

                        // Restore altitude and pitch offsets
                        this.restoreOffsetsAfterScreenshot();

                        // Restore original inertia factor
                        const originalInertia = (this as any)._originalInertia;
                        if (originalInertia !== undefined) {
                            // @ts-ignore
                            if (this.cameraControls.inertiaFactor !== undefined) {
                                // @ts-ignore
                                this.cameraControls.inertiaFactor = originalInertia;
                            }
                            delete (this as any)._originalInertia;
                        }

                        this.renderNextFrame();

                        console.log('Canvas and rendering state restored');
                    });
            }, 100);
            });
        });
    }

    // Force rebuild render targets with specific dimensions
    private forceRebuildRenderTargets(
        widthPixels: number,
        heightPixels: number,
        disableMSAA: boolean = false
    ) {
        const device = this.app.graphicsDevice;

        // CRITICAL: Always disable MSAA during screenshot capture to prevent ghosting
        // Check both isCapturingScreenshot and isBatchScreenshotMode for comprehensive coverage
        // @ts-ignore
        const isCapturing = this.isCapturingScreenshot === true;
        const isBatch = this.isBatchScreenshotMode === true;
        const effectiveDisableMSAA = disableMSAA || isCapturing || isBatch;
        
        if (effectiveDisableMSAA && !disableMSAA) {
            console.log(`[forceRebuildRenderTargets] 🔒 Forcing MSAA disabled (isCapturing: ${isCapturing}, isBatch: ${isBatch})`);
        }

        // Check if render target already exists at the right size - reuse it!
        const existingRT = this.camera.camera.renderTarget;
        if (existingRT && existingRT.width === widthPixels && existingRT.height === heightPixels) {
            // Render target already exists at correct size - check if we need to rebuild for MSAA
            if (effectiveDisableMSAA && existingRT.samples > 1) {
                console.log(
                    `[forceRebuildRenderTargets] Rebuilding render target to disable MSAA (current samples: ${existingRT.samples})`
                );
                this.destroyRenderTargets();
            } else if (!effectiveDisableMSAA && existingRT.samples === 1) {
                // If we DON'T need MSAA disabled but have samples: 1, still reuse it during capture
                // This prevents the samples: 4 rebuild issue
                if (isCapturing || isBatch) {
                    console.log(
                        `[forceRebuildRenderTargets] 🛡️ Keeping samples: 1 render target during capture (preventing MSAA rebuild)`
                    );
                    return;
                }
                // Otherwise, let it rebuild with MSAA if that's what's wanted
            } else {
                // Reusing existing render target - the camera's clear color will wipe
                // any old data when we render the next frame
                console.log(
                    `[forceRebuildRenderTargets] Reusing existing render target ${widthPixels}x${heightPixels} (samples: ${existingRT.samples})`
                );
                return;
            }
        }

        console.log(
            `Force rebuilding render targets to ${widthPixels}x${heightPixels}${
                effectiveDisableMSAA ? ' (MSAA disabled for screenshots)' : ''
            }`
        );

        // Destroy old ones only if they don't match
        this.destroyRenderTargets();

        const createTexture = (width: number, height: number, format: number) => {
            return new Texture(device, {
                name: 'viewer-rt-texture',
                width: width,
                height: height,
                format: format,
                mipmaps: false,
                minFilter: FILTER_NEAREST,
                magFilter: FILTER_NEAREST,
                addressU: ADDRESS_CLAMP_TO_EDGE,
                addressV: ADDRESS_CLAMP_TO_EDGE,
            });
        };

        // @ts-ignore
        const maxSamples = device.maxSamples;

        // Create new render targets with exact dimensions
        // For screenshots, disable MSAA to prevent darker edge pixels and aliasing artifacts
        const colorBuffer = createTexture(widthPixels, heightPixels, PIXELFORMAT_RGBA8);
        const depthBuffer = createTexture(widthPixels, heightPixels, PIXELFORMAT_DEPTH);
        const samples = effectiveDisableMSAA ? 1 : this.observer.get('camera.multisample') ? maxSamples : 1;
        const renderTarget = new RenderTarget({
            name: 'viewer-rt',
            colorBuffer: colorBuffer,
            depthBuffer: depthBuffer,
            flipY: false,
            samples: samples,
            autoResolve: false,
        });

        this.camera.camera.renderTarget = renderTarget;

        console.log(
            `Render target created: ${renderTarget.width}x${renderTarget.height}, samples: ${renderTarget.samples}`
        );
        console.log(`Color buffer: ${colorBuffer.width}x${colorBuffer.height}`);
        console.log(`Expected aspect ratio: ${renderTarget.width / renderTarget.height}`);
    }

    // Debug method to check camera state
    debugCameraAspectRatio() {
        console.log('=== Camera Debug Info ===');
        console.log(
            'Graphics Device Resolution:',
            this.app.graphicsDevice.width,
            'x',
            this.app.graphicsDevice.height
        );
        console.log('Canvas Size:', this.canvas.width, 'x', this.canvas.height);
        console.log('Canvas Style Size:', this.canvas.style.width, 'x', this.canvas.style.height);
        console.log(
            'Camera Aspect Ratio Mode:',
            this.camera.camera.aspectRatioMode,
            '(0=AUTO, 1=MANUAL)'
        );
        console.log('Camera Aspect Ratio:', this.camera.camera.aspectRatio);
        console.log('Camera FOV:', this.camera.camera.fov);

        const rt = this.camera.camera.renderTarget;
        if (rt) {
            console.log('Render Target Size:', rt.width, 'x', rt.height);
            console.log('Calculated RT Aspect Ratio:', rt.width / rt.height);
            console.log('Color Buffer Size:', rt.colorBuffer?.width, 'x', rt.colorBuffer?.height);
        } else {
            console.log('No render target found!');
        }
        console.log('========================');
    }

    // Apply default settings for screenshots
    private applyScreenshotDefaults() {
        // Deselect any selected node
        if (this.selectedNode) {
            console.log('  Deselecting node:', this.selectedNode.name);
            this.selectedNode = null;
            this.observer.set('scene.selectedNode', {
                name: '',
                path: '',
                worldPosition: [0, 0, 0],
                worldRotation: [0, 0, 0],
                worldScale: [1, 1, 1],
                position: [0, 0, 0],
                rotation: [0, 0, 0],
                scale: [1, 1, 1],
            });
            this.dirtyWireframe = true;
            this.dirtyBounds = true;
        }

        // Skybox settings - use the currently selected skybox (don't force "Basic Skybox")
        // This respects the skybox setting from batch screenshot page
        const currentSkybox = this.observer.get('skybox.value');

        // Only set to Basic Skybox if no skybox is currently selected
        if (!currentSkybox || currentSkybox === 'None' || currentSkybox === '') {
            const skyboxName = 'Basic Skybox';
            console.log(`  No skybox selected, defaulting to: ${skyboxName} for screenshots`);
            this.observer.set('skybox.value', skyboxName);

            // If Basic Skybox is not already loaded, generate it
            if (!this.skyboxLoaded) {
                this.generateBasicSkybox();
            }
        } else {
            console.log(`  Using current skybox for screenshots: ${currentSkybox}`);
        }

        // Ensure skybox is actually loaded in the scene
        if (!this.app.scene.skybox && !this.skyboxLoaded) {
            console.log(`  Skybox not loaded, generating Basic Skybox as fallback...`);
            this.generateBasicSkybox();
        }

        this.observer.set('skybox.exposure', 0.2); // Reduced to prevent environmental over-lighting and highlight blow-out
        this.observer.set('skybox.rotation', -180);
        this.observer.set('skybox.background', 'Infinite Sphere');
        this.observer.set('skybox.blur', 0);

        // Camera settings - use ACES tone mapping to prevent blown-out highlights
        this.observer.set('camera.tonemapping', 'ACES');
        console.log('  Set tone mapping to ACES for better highlight compression');

        // Light settings
        this.observer.set('light.enabled', false);
        this.observer.set('light.follow', false);
        this.observer.set('light.intensity', 1);
        this.observer.set('light.shadow', false);

        // Shadow Catcher
        this.observer.set('shadowCatcher.enabled', true);
        this.observer.set('shadowCatcher.intensity', 0.3);

        // Debug settings
        this.observer.set('debug.renderMode', 'default');
        this.observer.set('debug.wireframe', false);
        this.observer.set('debug.grid', true);
        this.observer.set('debug.axes', false);
        this.observer.set('debug.skeleton', false);
        this.observer.set('debug.bounds', false);
        this.observer.set('debug.normals', 0);
        this.observer.set('debug.normalMaps', true);
        this.observer.set('debug.stats', false);
    }

    // Test function to prepare view for screenshots (without taking a screenshot)
    async testPrepareViewForScreenshot() {
        console.log('=== Testing Screenshot Preparation ===');

        // Apply default settings (including skybox loading)
        this.applyScreenshotDefaults();

        // Wait for skybox to load
        console.log('Waiting for skybox to load...');
        await this.delay(1000);

        // Remove offsets to return to normal pose
        this.removeOffsetsForScreenshot();

        // Hide high-speed spinning nodes (propeller blurs, etc.)
        this.hideHighSpeedSpinningNodes();

        // Apply screenshot animation settings
        this.applyScreenshotAnimationSettings();

        // Force render update
        this.renderNextFrame();

        console.log(
            'Screenshot preparation complete. Model is now in normal pose with animation states set.'
        );
        console.log('To restore normal view, click "RESTORE NORMAL VIEW" button.');
    }

    // Restore normal view after testing
    restoreNormalViewAfterTest() {
        console.log('=== Restoring Normal View ===');

        // Restore visibility of high-speed spinning nodes
        this.showHighSpeedSpinningNodes();

        // Restore offsets
        this.restoreOffsetsAfterScreenshot();

        // Force render update
        this.renderNextFrame();

        console.log('Normal view restored with offsets applied.');
    }

    // Setup side view camera for 512x184 screenshot
    async setupSideViewCameraFor512x184() {
        console.log('Setting up side view camera specifically for 512x184...');

        // Apply default screenshot settings
        this.applyScreenshotDefaults();

        // Wait for skybox to load
        await this.delay(800);

        // Remove offsets to return model to normal pose for screenshot
        this.removeOffsetsForScreenshot();

        // Hide high-speed spinning nodes (propeller blurs, etc.)
        this.hideHighSpeedSpinningNodes();

        // Apply screenshot animation settings from aircraft configuration before positioning camera
        this.applyScreenshotAnimationSettings();

        // Force multiple render cycles to ensure all transforms are updated after offset removal
        console.log('Forcing render cycles to update transforms...');
        for (let i = 0; i < 3; i++) {
            this.app.fire('prerender');
            this.app.renderNextFrame = true;
            this.app.render();
            this.app.fire('postrender');
        }

        // Calculate scene bounds
        this.calcSceneBounds(this.dynamicSceneBounds);
        const center = this.dynamicSceneBounds.center.clone();
        const bounds = this.dynamicSceneBounds;
        const min = bounds.getMin();
        const max = bounds.getMax();

        console.log('Scene bounds after offset removal:');
        console.log('  Min:', min);
        console.log('  Max:', max);
        console.log('  Center:', center);

        // For side view (camera on -X axis looking at +X), we care about:
        // - Width: Z-axis extent (depth when viewed from side)
        // - Height: Y-axis extent (height when viewed from side)
        // - Depth: X-axis extent (important for engines that extend outward)
        const modelWidth = max.z - min.z; // Z extent = apparent width from side
        const modelHeight = max.y - min.y; // Y extent = apparent height from side
        const modelDepth = max.x - min.x; // X extent = depth (engines extend outward)

        // Keep center at geometric center for balanced framing
        // We'll rely on increased padding instead of biasing the center

        console.log('Model dimensions from side view:');
        console.log('  Width (Z-extent):', modelWidth);
        console.log('  Height (Y-extent):', modelHeight);
        console.log('  Depth (X-extent):', modelDepth);

        // Calculate distance based on the larger visible dimension for consistent framing
        // Also account for X-extent to ensure engines aren't cut off
        // Use the maximum of width, height, and depth to ensure everything fits
        const maxVisibleDimension = Math.max(modelWidth, modelHeight, modelDepth);
        const distance = maxVisibleDimension * 2.2;
        console.log('  Distance to model:', distance);

        // Set camera position (camera on -X axis for side view)
        const sidePos = new Vec3(center.x - distance, center.y, center.z);
        this.camera.setPosition(sidePos);

        // Store original inertia for instant camera movement
        // @ts-ignore - accessing inertiaFactor which may be private
        const originalInertia = this.cameraControls.inertiaFactor;

        // Set inertia to 1.0 for instant movement (no smoothing)
        // @ts-ignore
        if (this.cameraControls.inertiaFactor !== undefined) {
            // @ts-ignore
            this.cameraControls.inertiaFactor = 1.0;
            console.log('Set inertia factor to 1.0 for instant camera movement');
        }

        // Use the built-in focus system to look at the scene
        this.cameraControls.focus(center, sidePos);

        // Debug: Check camera position and rotation after focus
        console.log('Camera after focus:');
        console.log('  Position:', this.camera.getPosition());
        console.log('  Rotation (euler):', this.camera.getEulerAngles());
        console.log('  Looking at center:', center);

        // CRITICAL: Force camera zoom distance to our calculated distance
        // This ensures FOV calculations use the correct distance, not the old camera position
        // @ts-ignore
        if (this.cameraControls._zoomDist !== undefined) {
            // @ts-ignore
            this.cameraControls._zoomDist = distance;
            console.log('Set camera _zoomDist to:', distance);
        }

        // Fallback: Force internal camera state to target immediately
        // @ts-ignore - accessing private properties
        if (this.cameraControls._targetDistance !== undefined) {
            // @ts-ignore
            this.cameraControls._distance = this.cameraControls._targetDistance;
            // @ts-ignore
            if (this.cameraControls._targetYaw !== undefined) {
                // @ts-ignore
                this.cameraControls._yaw = this.cameraControls._targetYaw;
            }
            // @ts-ignore
            if (this.cameraControls._targetPitch !== undefined) {
                // @ts-ignore
                this.cameraControls._pitch = this.cameraControls._targetPitch;
            }
            console.log('Forced camera internal state to target values');
        }

        // Also set origin to center for camera controls
        // @ts-ignore
        if (this.cameraControls._origin !== undefined) {
            // @ts-ignore
            this.cameraControls._origin.copy(center);
            console.log('Set camera _origin to center:', center);
        }

        // Store original inertia to restore later
        (this as any)._originalInertia = originalInertia;

        // Get actual camera aspect ratio
        const actualAspectRatio = this.camera.camera.aspectRatio;
        console.log('Camera aspect ratio:', actualAspectRatio, '(expected: ~2.78 for 512x184)');

        // Apply margin to ensure model doesn't touch edges (including engines that extend outward)
        // Increased padding to 10% to ensure engines and other protruding parts aren't cut off
        const marginFactor = 0.9; // Show 90% of viewport = 10% padding on each side
        const targetWidth = modelWidth / marginFactor;
        const targetHeight = modelHeight / marginFactor;

        console.log('Target dimensions with 10% padding:');
        console.log('  Width:', targetWidth);
        console.log('  Height:', targetHeight);

        // Calculate horizontal FOV needed to fit width
        const hFovForWidthRad = 2 * Math.atan(targetWidth / (2 * distance));
        const hFovForWidth = hFovForWidthRad * (180 / Math.PI);

        // Convert horizontal FOV to vertical FOV (CORRECT formula using tangent)
        const vFovForWidthRad = 2 * Math.atan(Math.tan(hFovForWidthRad / 2) / actualAspectRatio);
        const vFovForWidth = vFovForWidthRad * (180 / Math.PI);

        // Calculate vertical FOV needed to fit height (already vertical)
        const vFovForHeight = 2 * Math.atan(targetHeight / (2 * distance)) * (180 / Math.PI);

        console.log('Calculated FOVs:');
        console.log('  Horizontal FOV for width:', hFovForWidth.toFixed(2), 'degrees');
        console.log('  Vertical FOV for width:', vFovForWidth.toFixed(2), 'degrees');
        console.log('  Vertical FOV for height:', vFovForHeight.toFixed(2), 'degrees');

        // Use the larger vertical FOV to ensure both dimensions fit
        const finalFOV = Math.max(vFovForWidth, vFovForHeight);

        // Clamp FOV to reasonable limits
        const clampedFOV = Math.max(5, Math.min(120, finalFOV));

        console.log('Final FOV:', clampedFOV.toFixed(2), 'degrees');
        console.log('Previous FOV:', this.camera.camera.fov);

        // Set the new FOV
        this.camera.camera.fov = clampedFOV;
        this.observer.set('camera.fov', clampedFOV);

        console.log('Side view camera setup complete');
    }

    // Setup right side view camera (mirror of left side)
    async setupRightSideViewCameraFor512x184() {
        console.log('Setting up right side view camera specifically for 512x184...');

        // Apply default screenshot settings
        this.applyScreenshotDefaults();

        // Wait for skybox to load
        await this.delay(800);

        // Remove offsets to return model to normal pose for screenshot
        this.removeOffsetsForScreenshot();

        // Hide high-speed spinning nodes (propeller blurs, etc.)
        this.hideHighSpeedSpinningNodes();

        // Apply screenshot animation settings from aircraft configuration before positioning camera
        this.applyScreenshotAnimationSettings();

        // Force multiple render cycles to ensure all transforms are updated after offset removal
        console.log('Forcing render cycles to update transforms...');
        for (let i = 0; i < 3; i++) {
            this.app.fire('prerender');
            this.app.renderNextFrame = true;
            this.app.render();
            this.app.fire('postrender');
        }

        // Calculate scene bounds
        this.calcSceneBounds(this.dynamicSceneBounds);
        const center = this.dynamicSceneBounds.center.clone();
        const bounds = this.dynamicSceneBounds;
        const min = bounds.getMin();
        const max = bounds.getMax();

        console.log('Scene bounds after offset removal:');
        console.log('  Min:', min);
        console.log('  Max:', max);
        console.log('  Center:', center);

        // For right side view (camera on +X axis looking at -X), we care about:
        // - Width: Z-axis extent (depth when viewed from side)
        // - Height: Y-axis extent (height when viewed from side)
        // - Depth: X-axis extent (important for engines that extend outward)
        const modelWidth = max.z - min.z; // Z extent = apparent width from side
        const modelHeight = max.y - min.y; // Y extent = apparent height from side
        const modelDepth = max.x - min.x; // X extent = depth (engines extend outward)

        console.log('Model dimensions from right side view:');
        console.log('  Width (Z-extent):', modelWidth);
        console.log('  Height (Y-extent):', modelHeight);
        console.log('  Depth (X-extent):', modelDepth);

        // Calculate distance based on the larger visible dimension for consistent framing
        const maxVisibleDimension = Math.max(modelWidth, modelHeight, modelDepth);
        const distance = maxVisibleDimension * 2.2;
        console.log('  Distance to model:', distance);

        // Set camera position (camera on +X axis for right side view)
        const sidePos = new Vec3(center.x + distance, center.y, center.z);
        this.camera.setPosition(sidePos);

        // Store original inertia for instant camera movement
        // @ts-ignore - accessing inertiaFactor which may be private
        const originalInertia = this.cameraControls.inertiaFactor;

        // Set inertia to 1.0 for instant movement (no smoothing)
        // @ts-ignore
        if (this.cameraControls.inertiaFactor !== undefined) {
            // @ts-ignore
            this.cameraControls.inertiaFactor = 1.0;
            console.log('Set inertia factor to 1.0 for instant camera movement');
        }

        // Use the built-in focus system to look at the scene
        this.cameraControls.focus(center, sidePos);

        // Debug: Check camera position and rotation after focus
        console.log('Camera after focus:');
        console.log('  Position:', this.camera.getPosition());
        console.log('  Rotation (euler):', this.camera.getEulerAngles());
        console.log('  Looking at center:', center);

        // CRITICAL: Force camera zoom distance to our calculated distance
        // @ts-ignore
        if (this.cameraControls._zoomDist !== undefined) {
            // @ts-ignore
            this.cameraControls._zoomDist = distance;
            console.log('Set camera _zoomDist to:', distance);
        }

        // Fallback: Force internal camera state to target immediately
        // @ts-ignore - accessing private properties
        if (this.cameraControls._targetDistance !== undefined) {
            // @ts-ignore
            this.cameraControls._distance = this.cameraControls._targetDistance;
            // @ts-ignore
            if (this.cameraControls._targetYaw !== undefined) {
                // @ts-ignore
                this.cameraControls._yaw = this.cameraControls._targetYaw;
            }
            // @ts-ignore
            if (this.cameraControls._targetPitch !== undefined) {
                // @ts-ignore
                this.cameraControls._pitch = this.cameraControls._targetPitch;
            }
            console.log('Forced camera internal state to target values');
        }

        // Also set origin to center for camera controls
        // @ts-ignore
        if (this.cameraControls._origin !== undefined) {
            // @ts-ignore
            this.cameraControls._origin.copy(center);
            console.log('Set camera _origin to center:', center);
        }

        // Store original inertia to restore later
        (this as any)._originalInertia = originalInertia;

        // Get actual camera aspect ratio
        const actualAspectRatio = this.camera.camera.aspectRatio;
        console.log('Camera aspect ratio:', actualAspectRatio, '(expected: ~2.78 for 512x184)');

        // Apply margin to ensure model doesn't touch edges (including engines that extend outward)
        const marginFactor = 0.9; // Show 90% of viewport = 10% padding on each side
        const targetWidth = modelWidth / marginFactor;
        const targetHeight = modelHeight / marginFactor;

        console.log('Target dimensions with 10% padding:');
        console.log('  Width:', targetWidth);
        console.log('  Height:', targetHeight);

        // Calculate horizontal FOV needed to fit width
        const hFovForWidthRad = 2 * Math.atan(targetWidth / (2 * distance));
        const hFovForWidth = hFovForWidthRad * (180 / Math.PI);

        // Convert horizontal FOV to vertical FOV (CORRECT formula using tangent)
        const vFovForWidthRad = 2 * Math.atan(Math.tan(hFovForWidthRad / 2) / actualAspectRatio);
        const vFovForWidth = vFovForWidthRad * (180 / Math.PI);

        // Calculate vertical FOV needed to fit height (already vertical)
        const vFovForHeight = 2 * Math.atan(targetHeight / (2 * distance)) * (180 / Math.PI);

        console.log('Calculated FOVs:');
        console.log('  Horizontal FOV for width:', hFovForWidth.toFixed(2), 'degrees');
        console.log('  Vertical FOV for width:', vFovForWidth.toFixed(2), 'degrees');
        console.log('  Vertical FOV for height:', vFovForHeight.toFixed(2), 'degrees');

        // Use the larger vertical FOV to ensure both dimensions fit
        const finalFOV = Math.max(vFovForWidth, vFovForHeight);

        // Clamp FOV to reasonable limits
        const clampedFOV = Math.max(5, Math.min(120, finalFOV));

        console.log('Final FOV:', clampedFOV.toFixed(2), 'degrees');
        console.log('Previous FOV:', this.camera.camera.fov);

        // Set the new FOV
        this.camera.camera.fov = clampedFOV;
        this.observer.set('camera.fov', clampedFOV);

        console.log('Right side view camera setup complete');
    }

    // Setup camera at a specific angle around the aircraft (in degrees, 0 = front, 90 = right, 180 = back, 270 = left)
    async setupCameraAtAngle(angleDegrees: number) {
        // Apply default screenshot settings
        this.applyScreenshotDefaults();

        // Wait for skybox to load
        await this.delay(800);

        // Remove offsets to return model to normal pose for screenshot
        this.removeOffsetsForScreenshot();

        // Hide high-speed spinning nodes (propeller blurs, etc.)
        this.hideHighSpeedSpinningNodes();

        // Apply screenshot animation settings from aircraft configuration before positioning camera
        this.applyScreenshotAnimationSettings();

        // Check if we're in screenshot mode (render target is set)
        // If so, skip the early render cycles entirely since transforms are already updated
        // and we don't want to render stale frames with the old camera position
        const isScreenshotMode = this.camera.camera.renderTarget !== null;

        // Force multiple render cycles to ensure all transforms are updated after offset removal
        // BUT: Skip entirely in screenshot mode to avoid rendering stale frames with old camera position
        if (!isScreenshotMode) {
            console.log('Forcing render cycles to update transforms...');
            for (let i = 0; i < 3; i++) {
                this.app.fire('prerender');
                this.app.renderNextFrame = true;
                this.app.render();
                this.app.fire('postrender');
            }
        } else {
            console.log(
                'Screenshot mode detected - skipping early render cycles (transforms already updated)'
            );
        }

        // Calculate scene bounds
        this.calcSceneBounds(this.dynamicSceneBounds);
        const center = this.dynamicSceneBounds.center.clone();
        const bounds = this.dynamicSceneBounds;
        const min = bounds.getMin();
        const max = bounds.getMax();

        // Calculate model dimensions
        const modelWidth = max.z - min.z; // Z extent
        const modelHeight = max.y - min.y; // Y extent
        const modelDepth = max.x - min.x; // X extent

        // Check if this is a diagonal angle (45°, 135°, 225°, 315°)
        const isDiagonalAngle =
            angleDegrees === 45 ||
            angleDegrees === 135 ||
            angleDegrees === 225 ||
            angleDegrees === 315;
        // Check if this is a front/back angle (0°, 180°) where wings extend outward
        const isFrontBackAngle = angleDegrees === 0 || angleDegrees === 180;

        // For diagonal angles and front/back angles, we need to account for wings extending outward
        // and ensure wings aren't cut off.
        let maxVisibleDimension: number;
        if (isDiagonalAngle) {
            // For diagonal views, calculate the diagonal across width and depth
            const diagonalDimension = Math.sqrt(modelWidth * modelWidth + modelDepth * modelDepth);
            // Use the maximum of diagonal, height, and individual dimensions
            maxVisibleDimension = Math.max(diagonalDimension, modelHeight, modelWidth, modelDepth);
        } else if (isFrontBackAngle) {
            // For front/back views, wings extend along X-axis (modelDepth), so we need to ensure
            // both width (Z) and depth (X) fit, similar to diagonal but simpler
            maxVisibleDimension = Math.max(modelWidth, modelDepth, modelHeight);
        } else {
            // For left/right views, use the standard calculation
            maxVisibleDimension = Math.max(modelWidth, modelHeight, modelDepth);
        }

        // Use a larger multiplier for diagonal and front/back angles to ensure wings aren't cut off
        // Front/back angles need more distance (2.8x), diagonal angles can be closer (1.7x)
        const distanceMultiplier = isFrontBackAngle ? 2.8 : isDiagonalAngle ? 1.1 : 2.2;
        const distance = maxVisibleDimension * distanceMultiplier;

        // Convert angle from degrees to radians
        // UI labels: 0° = front, 180° = back
        // Flip by 180° so 0° maps to back view and 180° maps to front view (to match coordinate system)
        // Negate the angle to fix back-left/back-right inversion
        const angleRad = ((-angleDegrees + 180) * Math.PI) / 180;

        // Calculate camera position around the aircraft
        // After 180° flip: 0° (UI front) = back (-Z), 180° (UI back) = front (+Z)
        const cameraX = center.x + distance * Math.sin(angleRad);
        const cameraZ = center.z + distance * Math.cos(angleRad);
        const cameraY = center.y; // Keep camera at same height as center

        const cameraPos = new Vec3(cameraX, cameraY, cameraZ);

        // Store original inertia for instant camera movement
        // @ts-ignore
        const originalInertia = this.cameraControls.inertiaFactor;

        // Set inertia to 0 for completely instant movement (no interpolation at all)
        // @ts-ignore
        if (this.cameraControls.inertiaFactor !== undefined) {
            // @ts-ignore
            this.cameraControls.inertiaFactor = 0;
        }

        // Get actual camera aspect ratio (needed for FOV calculation)
        const actualAspectRatio = this.camera.camera.aspectRatio;

        // Apply margin to ensure model doesn't touch edges
        const marginFactor = 0.9; // 10% padding

        // For diagonal angles and front/back angles, we need to fit the appropriate dimension
        let targetWidth: number;
        let targetHeight: number;
        if (isDiagonalAngle) {
            // For diagonal views, use the larger of width/depth (not the diagonal) to get closer framing
            // The diagonal dimension was making it too zoomed out
            targetWidth = Math.max(modelWidth, modelDepth) / marginFactor;
            targetHeight = modelHeight / marginFactor;
        } else if (isFrontBackAngle) {
            // For front/back views, wings extend along X-axis (modelDepth), so we need to fit
            // the larger of width (Z) and depth (X) to ensure wings aren't cut off
            targetWidth = Math.max(modelWidth, modelDepth) / marginFactor;
            targetHeight = modelHeight / marginFactor;
        } else {
            // For left/right views, use standard width/height
            targetWidth = modelWidth / marginFactor;
            targetHeight = modelHeight / marginFactor;
        }

        // Calculate FOV
        const hFovForWidthRad = 2 * Math.atan(targetWidth / (2 * distance));
        const vFovForWidthRad = 2 * Math.atan(Math.tan(hFovForWidthRad / 2) / actualAspectRatio);
        const vFovForWidth = vFovForWidthRad * (180 / Math.PI);
        const vFovForHeight = 2 * Math.atan(targetHeight / (2 * distance)) * (180 / Math.PI);

        const finalFOV = Math.max(vFovForWidth, vFovForHeight);
        const clampedFOV = Math.max(5, Math.min(120, finalFOV));

        // Set the new FOV first
        this.camera.camera.fov = clampedFOV;
        this.observer.set('camera.fov', clampedFOV);

        // NOW position the camera - do this LAST to ensure it doesn't get reset

        // BYPASS camera controls entirely - directly manipulate camera entity transform
        // Set camera position directly on the entity
        this.camera.setPosition(cameraPos);

        // Set camera to look at center directly (bypasses camera controls)
        this.camera.lookAt(center, Vec3.UP);

        // Verify camera position and rotation were set
        const actualPos = this.camera.getPosition();
        const actualRot = this.camera.getEulerAngles();
        const actualForward = this.camera.forward.clone();
        const toCenter = center.sub(actualPos).normalize();
        const dotProduct = actualForward.dot(toCenter);

        // If camera is not looking at the center, force it again
        if (dotProduct < 0.9) {
            console.warn(
                `[setupCameraAtAngle] Camera is not looking at center! Dot product: ${dotProduct.toFixed(
                    3
                )}. Forcing lookAt again...`
            );
            this.camera.lookAt(center, Vec3.UP);
            // Wait a bit for lookAt to apply
            await new Promise((resolve) => setTimeout(resolve, 50));
            // Verify again
            const actualForward2 = this.camera.forward.clone();
            const toCenter2 = center.sub(this.camera.getPosition()).normalize();
            const dotProduct2 = actualForward2.dot(toCenter2);
            console.log(`After second lookAt - Dot product: ${dotProduct2.toFixed(3)}`);
        }

        // Check if camera controls are enabled - only manipulate them if they are
        // @ts-ignore
        const controlsEnabled = this.cameraControls.enabled;

        if (controlsEnabled) {
            console.log(
                '[setupCameraAtAngle] Camera controls are enabled - updating internal state'
            );

            // Force camera controls internal state to match immediately (no interpolation)
            // Calculate yaw from camera position
            const yaw = Math.atan2(cameraX - center.x, cameraZ - center.z);

            // @ts-ignore
            if (this.cameraControls._distance !== undefined) {
                // @ts-ignore
                this.cameraControls._distance = distance;
            }
            // @ts-ignore
            if (this.cameraControls._targetDistance !== undefined) {
                // @ts-ignore
                this.cameraControls._targetDistance = distance;
            }
            // @ts-ignore
            if (this.cameraControls._yaw !== undefined) {
                // @ts-ignore
                this.cameraControls._yaw = yaw;
            }
            // @ts-ignore
            if (this.cameraControls._targetYaw !== undefined) {
                // @ts-ignore
                this.cameraControls._targetYaw = yaw;
            }
            // @ts-ignore
            if (this.cameraControls._pitch !== undefined) {
                // @ts-ignore
                this.cameraControls._pitch = 0;
            }
            // @ts-ignore
            if (this.cameraControls._targetPitch !== undefined) {
                // @ts-ignore
                this.cameraControls._targetPitch = 0;
            }
            // @ts-ignore
            if (this.cameraControls._zoomDist !== undefined) {
                // @ts-ignore
                this.cameraControls._zoomDist = distance;
            }
            // @ts-ignore
            if (this.cameraControls._origin !== undefined) {
                // @ts-ignore
                this.cameraControls._origin.copy(center);
            }

            // CRITICAL: Disable camera controls update to prevent it from overriding our position
            // Temporarily disable the update function
            // @ts-ignore
            const originalUpdate = this.cameraControls.update;
            // @ts-ignore
            this.cameraControls.update = () => {}; // No-op to prevent camera controls from moving camera

            // Store original update to restore later
            (this as any)._originalCameraControlsUpdate = originalUpdate;
        } else {
            console.log(
                '[setupCameraAtAngle] Camera controls are disabled - skipping internal state manipulation'
            );
        }

        // Skip render cycles in screenshot mode to avoid rendering with wrong settings
        // Rendering will happen AFTER transparent background is set up in captureAtAngleAsBlob
        if (!isScreenshotMode) {
            // Force multiple render cycles to ensure camera transform is fully applied
            for (let i = 0; i < 3; i++) {
                this.app.fire('prerender');
                this.app.renderNextFrame = true;
                this.app.render();
                this.app.fire('postrender');
                // Small delay between renders
                await new Promise((resolve) => setTimeout(resolve, 16)); // ~60fps
            }

            // Verify camera rotation one more time after renders
            const finalForward = this.camera.forward.clone();
            const finalToCenter = center.sub(this.camera.getPosition()).normalize();
            const finalDotProduct = finalForward.dot(finalToCenter);
            console.log(
                `After render cycles - Camera forward: x=${finalForward.x.toFixed(
                    3
                )}, y=${finalForward.y.toFixed(3)}, z=${finalForward.z.toFixed(3)}`
            );
            console.log(`After render cycles - Dot product: ${finalDotProduct.toFixed(3)}`);

            if (finalDotProduct < 0.9) {
                console.warn(
                    `[setupCameraAtAngle] Camera rotation was lost during renders! Dot product: ${finalDotProduct.toFixed(
                        3
                    )}. Re-applying lookAt...`
                );
                this.camera.lookAt(center, Vec3.UP);
                // Force another render cycle
                this.app.fire('prerender');
                this.app.renderNextFrame = true;
                this.app.render();
                this.app.fire('postrender');
                await new Promise((resolve) => setTimeout(resolve, 50));
            }
        } else {
            console.log(
                '[setupCameraAtAngle] Screenshot mode - skipping render cycles (will render after transparent background is set up)'
            );
        }

        // Restore update function ONLY if we modified it (when controls were enabled)
        if (controlsEnabled) {
            // @ts-ignore
            const storedUpdate = (this as any)._originalCameraControlsUpdate;
            // @ts-ignore
            setTimeout(() => {
                // @ts-ignore
                if (storedUpdate) {
                    // @ts-ignore
                    this.cameraControls.update = storedUpdate;
                    console.log('[setupCameraAtAngle] Restored camera controls update function');
                }
            }, 100);
        }

        // Store original inertia to restore later
        (this as any)._originalInertia = originalInertia;
    }

    // Capture screenshot at a specific angle and return as blob URL
    // Version: 2.0 (quote removed 2024)
    async captureAtAngleAsBlob(
        angleDegrees: number,
        resolution: 'thumbnail' | 'large' | '4k' | '8k' = 'thumbnail'
    ): Promise<string> {
        // Disable UI camera controls for programmatic camera control
        this.disableUICameraControls();
        
        console.log(
            `[captureAtAngleAsBlob] ==================== STARTING SCREENSHOT AT ${angleDegrees}° (${resolution}) ====================`
        );

        // Calculate resolution dimensions FIRST
        const baseWidth = 512;
        const baseHeight = 184;
        const scale =
            resolution === 'large' ? 4 : resolution === '4k' ? 7.5 : resolution === '8k' ? 15 : 1;
        const targetWidth = baseWidth * scale;
        const targetHeight = baseHeight * scale;

        console.log(`[captureAtAngleAsBlob] ========== RESOLUTION CALCULATION ==========`);
        console.log(`[captureAtAngleAsBlob] Resolution parameter: "${resolution}"`);
        console.log(`[captureAtAngleAsBlob] Base dimensions: ${baseWidth}x${baseHeight}`);
        console.log(`[captureAtAngleAsBlob] Scale factor: ${scale}`);
        console.log(`[captureAtAngleAsBlob] Target dimensions: ${targetWidth}x${targetHeight}`);
        console.log(`[captureAtAngleAsBlob] ===========================================`);

        const initialCamPos = this.camera.getPosition();
        const initialCamRot = this.camera.getEulerAngles();
        console.log(
            `[captureAtAngleAsBlob] INITIAL Camera: pos=(${initialCamPos.x.toFixed(
                2
            )}, ${initialCamPos.y.toFixed(2)}, ${initialCamPos.z.toFixed(
                2
            )}), rot=(${initialCamRot.x.toFixed(2)}, ${initialCamRot.y.toFixed(
                2
            )}, ${initialCamRot.z.toFixed(2)})`
        );

        // Always use WebP format
        const outputFormat = 'webp';

        // CRITICAL: Disable camera controls FIRST to prevent any automatic camera movements
        // @ts-ignore
        const originalCameraControlsEnabled = this.cameraControls.enabled;
        // @ts-ignore
        this.cameraControls.enabled = false;
        console.log(
            `[captureAtAngleAsBlob] 🔒 Disabled camera controls at start (was: ${originalCameraControlsEnabled})`
        );

        // Save current orbit mode and force to "target" for screenshots
        const originalOrbitMode = this.observer.get('camera.orbitMode');
        if (originalOrbitMode !== 'target') {
            console.log(
                `[captureAtAngleAsBlob] Forcing orbit mode from "${originalOrbitMode}" to "target" for screenshot`
            );
            this.setOrbitMode('target');
            // Wait a bit for orbit mode to take effect
            await new Promise((resolve) => setTimeout(resolve, 100));
            const camPosAfterOrbit = this.camera.getPosition();
            console.log(
                `[captureAtAngleAsBlob] Camera AFTER orbit mode change: pos=(${camPosAfterOrbit.x.toFixed(
                    2
                )}, ${camPosAfterOrbit.y.toFixed(2)}, ${camPosAfterOrbit.z.toFixed(2)})`
            );
        }

        // Save current state
        const originalWidth = this.canvas.width;
        const originalHeight = this.canvas.height;
        const originalStyleWidth = this.canvas.style.width;
        const originalStyleHeight = this.canvas.style.height;
        const originalDeviceWidth = this.app.graphicsDevice.width;
        const originalDeviceHeight = this.app.graphicsDevice.height;
        const originalAspectRatioMode = this.camera.camera.aspectRatioMode;
        const originalAspectRatio = this.camera.camera.aspectRatio;
        const originalState = this.saveRenderingState();

        // Set flag to prevent automatic resolution sync during screenshot capture
        // @ts-ignore
        this.isCapturingScreenshot = true;

        try {
            // Change resolution
            console.log(`[captureAtAngleAsBlob] ========== SETTING RESOLUTION ==========`);
            console.log(
                `[captureAtAngleAsBlob] Requested resolution: ${targetWidth}x${targetHeight}`
            );
            console.log(
                `[captureAtAngleAsBlob] Current graphics device: ${this.app.graphicsDevice.width}x${this.app.graphicsDevice.height}`
            );
            console.log(
                `[captureAtAngleAsBlob] Current canvas: ${this.canvas.width}x${this.canvas.height}`
            );
            const camPosBeforeResolution = this.camera.getPosition();
            console.log(
                `[captureAtAngleAsBlob] Camera BEFORE setResolution: pos=(${camPosBeforeResolution.x.toFixed(
                    2
                )}, ${camPosBeforeResolution.y.toFixed(2)}, ${camPosBeforeResolution.z.toFixed(2)})`
            );

            this.app.graphicsDevice.setResolution(targetWidth, targetHeight);
            console.log(
                `[captureAtAngleAsBlob] Graphics device AFTER setResolution: ${this.app.graphicsDevice.width}x${this.app.graphicsDevice.height}`
            );
            const camPosAfterResolution = this.camera.getPosition();
            console.log(
                `[captureAtAngleAsBlob] Camera AFTER setResolution: pos=(${camPosAfterResolution.x.toFixed(
                    2
                )}, ${camPosAfterResolution.y.toFixed(2)}, ${camPosAfterResolution.z.toFixed(2)})`
            );

            // Let forceRebuildRenderTargets handle checking and reusing - it will reuse if size matches
            // For screenshots, disable MSAA to prevent darker edge pixels and aliasing artifacts
            console.log(
                `[captureAtAngleAsBlob] Calling forceRebuildRenderTargets(${targetWidth}, ${targetHeight})...`
            );
            console.log(
                `[captureAtAngleAsBlob] Disabling MSAA for screenshot capture (samples: 1)`
            );
            this.forceRebuildRenderTargets(targetWidth, targetHeight, true); // Pass true to disable MSAA
            console.log(
                `[captureAtAngleAsBlob] Graphics device AFTER forceRebuildRenderTargets: ${this.app.graphicsDevice.width}x${this.app.graphicsDevice.height}`
            );
            const camPosAfterRebuild = this.camera.getPosition();
            console.log(
                `[captureAtAngleAsBlob] Camera AFTER forceRebuildRenderTargets: pos=(${camPosAfterRebuild.x.toFixed(
                    2
                )}, ${camPosAfterRebuild.y.toFixed(2)}, ${camPosAfterRebuild.z.toFixed(2)})`
            );
            console.log(`[captureAtAngleAsBlob] =======================================`);

            // Set camera aspect ratio
            const targetAspectRatio = targetWidth / targetHeight;
            this.camera.camera.aspectRatioMode = 1;
            this.camera.camera.aspectRatio = targetAspectRatio;
            console.log(
                `[captureAtAngleAsBlob] Set camera aspect ratio to ${targetAspectRatio.toFixed(3)}`
            );
            const camPosAfterAspect = this.camera.getPosition();
            console.log(
                `[captureAtAngleAsBlob] Camera AFTER aspect ratio: pos=(${camPosAfterAspect.x.toFixed(
                    2
                )}, ${camPosAfterAspect.y.toFixed(2)}, ${camPosAfterAspect.z.toFixed(2)})`
            );

            // CRITICAL: Clear the render target immediately after rebuilding to prevent stale data
            // Note: setupCameraAtAngle will handle render cycles without render target to avoid stale frames
            // So we don't need to clear here - the render target will be fresh when camera is positioned

            // Setup camera at the specified angle
            // In screenshot mode, this positions the camera but does NOT render yet
            console.log(
                `[captureAtAngleAsBlob] ========== CALLING setupCameraAtAngle(${angleDegrees}) ==========`
            );
            await this.setupCameraAtAngle(angleDegrees);
            console.log(`[captureAtAngleAsBlob] ========== setupCameraAtAngle COMPLETE ==========`);

            // Verify camera position before setting up background
            const verifyPos = this.camera.getPosition();
            const verifyRot = this.camera.getEulerAngles();
            console.log(
                `[captureAtAngleAsBlob] Camera AFTER setupCameraAtAngle: pos=(${verifyPos.x.toFixed(
                    2
                )}, ${verifyPos.y.toFixed(2)}, ${verifyPos.z.toFixed(
                    2
                )}), rot=(${verifyRot.x.toFixed(2)}, ${verifyRot.y.toFixed(
                    2
                )}, ${verifyRot.z.toFixed(2)})`
            );

            // Now set up transparent background - this MUST happen before any rendering
            // For batch mode, only set up once and reuse
            if (!this.isBatchScreenshotMode) {
                console.log(
                    `[captureAtAngleAsBlob] ========== CALLING setupTransparentBackground ==========`
                );
                this.setupTransparentBackground();
                console.log(
                    `[captureAtAngleAsBlob] ========== setupTransparentBackground COMPLETE ==========`
                );
            } else {
                console.log(
                    `[captureAtAngleAsBlob] Skipping setupTransparentBackground (already set up for batch mode)`
                );
                // But we still need to disable shadows for each capture in batch mode
                this.disableShadowsForScreenshot();
            }

            // CRITICAL: Reposition camera IMMEDIATELY after setupTransparentBackground
            // because something during/after that call resets the camera
            console.log(
                '[captureAtAngleAsBlob] Repositioning camera after setupTransparentBackground...'
            );
            this.calcSceneBounds(this.dynamicSceneBounds);
            const centerAfterBg = this.dynamicSceneBounds.center.clone();
            const angleRadAfterBg = ((-angleDegrees + 180) * Math.PI) / 180;
            const boundsAfterBg = this.dynamicSceneBounds;
            const minAfterBg = boundsAfterBg.getMin();
            const maxAfterBg = boundsAfterBg.getMax();
            const modelWidthAfterBg = maxAfterBg.z - minAfterBg.z;
            const modelHeightAfterBg = maxAfterBg.y - minAfterBg.y;
            const modelDepthAfterBg = maxAfterBg.x - minAfterBg.x;
            const isDiagonalAngleAfterBg =
                angleDegrees === 45 ||
                angleDegrees === 135 ||
                angleDegrees === 225 ||
                angleDegrees === 315;
            const isFrontBackAngleAfterBg = angleDegrees === 0 || angleDegrees === 180;
            let maxVisibleDimensionAfterBg: number;
            if (isDiagonalAngleAfterBg) {
                const diagonalDimension = Math.sqrt(
                    modelWidthAfterBg * modelWidthAfterBg + modelDepthAfterBg * modelDepthAfterBg
                );
                maxVisibleDimensionAfterBg = Math.max(
                    diagonalDimension,
                    modelHeightAfterBg,
                    modelWidthAfterBg,
                    modelDepthAfterBg
                );
            } else if (isFrontBackAngleAfterBg) {
                maxVisibleDimensionAfterBg = Math.max(
                    modelWidthAfterBg,
                    modelDepthAfterBg,
                    modelHeightAfterBg
                );
            } else {
                maxVisibleDimensionAfterBg = Math.max(
                    modelWidthAfterBg,
                    modelHeightAfterBg,
                    modelDepthAfterBg
                );
            }
            const distanceMultiplierAfterBg = isFrontBackAngleAfterBg
                ? 2.8
                : isDiagonalAngleAfterBg
                ? 1.7
                : 2.2;
            const distanceAfterBg = maxVisibleDimensionAfterBg * distanceMultiplierAfterBg;
            const cameraXAfterBg = centerAfterBg.x + distanceAfterBg * Math.sin(angleRadAfterBg);
            const cameraZAfterBg = centerAfterBg.z + distanceAfterBg * Math.cos(angleRadAfterBg);
            const cameraYAfterBg = centerAfterBg.y;
            const cameraPosAfterBg = new Vec3(cameraXAfterBg, cameraYAfterBg, cameraZAfterBg);

            // CRITICAL: Recalculate FOV for the current resolution/aspect ratio before repositioning
            // This ensures the FOV matches the resolution (thumbnail vs large)
            const marginFactor = 0.9;
            let targetWidthAfterBg: number;
            let targetHeightAfterBg: number;
            if (isDiagonalAngleAfterBg) {
                targetWidthAfterBg = Math.max(modelWidthAfterBg, modelDepthAfterBg) / marginFactor;
                targetHeightAfterBg = modelHeightAfterBg / marginFactor;
            } else if (isFrontBackAngleAfterBg) {
                targetWidthAfterBg = Math.max(modelWidthAfterBg, modelDepthAfterBg) / marginFactor;
                targetHeightAfterBg = modelHeightAfterBg / marginFactor;
            } else {
                targetWidthAfterBg = modelWidthAfterBg / marginFactor;
                targetHeightAfterBg = modelHeightAfterBg / marginFactor;
            }

            // Recalculate FOV with the current aspect ratio (which matches the resolution)
            const currentAspectRatio = this.camera.camera.aspectRatio;
            const hFovForWidthRadAfterBg =
                2 * Math.atan(targetWidthAfterBg / (2 * distanceAfterBg));
            const vFovForWidthRadAfterBg =
                2 * Math.atan(Math.tan(hFovForWidthRadAfterBg / 2) / currentAspectRatio);
            const vFovForWidthAfterBg = vFovForWidthRadAfterBg * (180 / Math.PI);
            const vFovForHeightAfterBg =
                2 * Math.atan(targetHeightAfterBg / (2 * distanceAfterBg)) * (180 / Math.PI);
            const finalFOVAfterBg = Math.max(vFovForWidthAfterBg, vFovForHeightAfterBg);
            const clampedFOVAfterBg = Math.max(5, Math.min(120, finalFOVAfterBg));

            // Set FOV before positioning camera
            this.camera.camera.fov = clampedFOVAfterBg;
            this.observer.set('camera.fov', clampedFOVAfterBg);
            console.log(
                `[captureAtAngleAsBlob] Recalculated FOV for ${resolution} resolution: ${clampedFOVAfterBg.toFixed(
                    2
                )}° (aspect ratio: ${currentAspectRatio.toFixed(3)})`
            );

            this.camera.setPosition(cameraPosAfterBg);
            this.camera.lookAt(centerAfterBg, Vec3.UP);
            console.log(
                `[captureAtAngleAsBlob] Camera repositioned to: x=${cameraXAfterBg.toFixed(
                    2
                )}, y=${cameraYAfterBg.toFixed(2)}, z=${cameraZAfterBg.toFixed(2)}`
            );

            // ========== CLEAN RENDER PIPELINE ==========
            // This section implements a reliable render-to-texture flow:
            // 1. Set camera position (synchronously, no async)
            // 2. Clear the render target explicitly
            // 3. Render ONCE to the render target
            // 4. Read texture immediately

            console.log('[captureAtAngleAsBlob] ========== CLEAN RENDER PIPELINE ==========');

            // Get the render target that we'll be rendering to
            const renderTarget = this.camera.camera.renderTarget;
            if (!renderTarget) {
                throw new Error('No render target available for screenshot');
            }
            console.log(
                `[captureAtAngleAsBlob] Render target: ${renderTarget.width}x${renderTarget.height}, samples: ${renderTarget.samples}`
            );

            // STEP 1: Set camera position (NO async operations!)
            console.log('[captureAtAngleAsBlob] STEP 1: Setting camera position...');
            this.camera.setPosition(cameraPosAfterBg);
            this.camera.lookAt(centerAfterBg, Vec3.UP);

            // Force camera transform update
            this.camera.syncHierarchy();

            const camPos = this.camera.getPosition();
            const camRot = this.camera.getEulerAngles();
            console.log(
                `[captureAtAngleAsBlob]   Final camera position: x=${camPos.x.toFixed(
                    2
                )}, y=${camPos.y.toFixed(2)}, z=${camPos.z.toFixed(2)}`
            );
            console.log(
                `[captureAtAngleAsBlob]   Final camera rotation: x=${camRot.x.toFixed(
                    2
                )}, y=${camRot.y.toFixed(2)}, z=${camRot.z.toFixed(2)}`
            );
            console.log(
                `[captureAtAngleAsBlob]   Looking at center: x=${centerAfterBg.x.toFixed(
                    2
                )}, y=${centerAfterBg.y.toFixed(2)}, z=${centerAfterBg.z.toFixed(2)}`
            );

            // Verify the camera position is correct for the requested angle
            // For 270° (left), camera should be at negative X relative to center
            const expectedX = centerAfterBg.x + distanceAfterBg * Math.sin(angleRadAfterBg);
            const expectedZ = centerAfterBg.z + distanceAfterBg * Math.cos(angleRadAfterBg);
            console.log(
                `[captureAtAngleAsBlob]   Expected X: ${expectedX.toFixed(
                    2
                )}, Actual X: ${camPos.x.toFixed(2)}`
            );
            console.log(
                `[captureAtAngleAsBlob]   Expected Z: ${expectedZ.toFixed(
                    2
                )}, Actual Z: ${camPos.z.toFixed(2)}`
            );

            // STEP 2: Clear the render target to prevent stale data
            console.log('[captureAtAngleAsBlob] STEP 2: Clearing render target...');
            const device = this.app.graphicsDevice;

            // Bind the render target and clear it
            // @ts-ignore - accessing internal API
            device.setRenderTarget(renderTarget);
            // @ts-ignore
            device.updateBegin();
            // Clear with transparent black (matches our screenshot background)
            // @ts-ignore
            device.clear({
                color: [0, 0, 0, 0],
                depth: 1.0,
                flags: 3, // COLOR | DEPTH
            });
            // @ts-ignore
            device.updateEnd();
            console.log('[captureAtAngleAsBlob]   ✓ Render target cleared');

            // STEP 3: Render the scene to the render target
            console.log('[captureAtAngleAsBlob] STEP 3: Rendering scene...');

            // Verify camera position one more time before render
            const preRenderCamPos = this.camera.getPosition();
            console.log(
                `[captureAtAngleAsBlob]   Pre-render camera: x=${preRenderCamPos.x.toFixed(
                    2
                )}, y=${preRenderCamPos.y.toFixed(2)}, z=${preRenderCamPos.z.toFixed(2)}`
            );

            // Do a single, clean render
            this.app.renderNextFrame = true;
            this.app.fire('prerender');
            this.app.render();
            this.app.fire('postrender');

            // Verify camera position after render (it should NOT have moved)
            const postRenderCamPos = this.camera.getPosition();
            console.log(
                `[captureAtAngleAsBlob]   Post-render camera: x=${postRenderCamPos.x.toFixed(
                    2
                )}, y=${postRenderCamPos.y.toFixed(2)}, z=${postRenderCamPos.z.toFixed(2)}`
            );

            // Check if camera moved during render
            const renderDelta = Math.sqrt(
                Math.pow(postRenderCamPos.x - preRenderCamPos.x, 2) +
                    Math.pow(postRenderCamPos.y - preRenderCamPos.y, 2) +
                    Math.pow(postRenderCamPos.z - preRenderCamPos.z, 2)
            );
            if (renderDelta > 0.01) {
                console.error(
                    `[captureAtAngleAsBlob] ⚠️ CAMERA MOVED DURING RENDER! Delta: ${renderDelta.toFixed(
                        4
                    )}`
                );
                console.error(
                    `[captureAtAngleAsBlob]   This means something is modifying the camera during app.render()`
                );
            } else {
                console.log(`[captureAtAngleAsBlob]   ✓ Camera position stable during render`);
            }

            // Resolve MSAA if needed
            if (renderTarget.samples > 1) {
                console.log('[captureAtAngleAsBlob]   Resolving MSAA...');
                renderTarget.resolve();
            }

            console.log('[captureAtAngleAsBlob]   ✓ Render complete');

            // STEP 4: Small sync delay (NO requestAnimationFrame - that triggers render loop!)
            console.log('[captureAtAngleAsBlob] STEP 4: GPU sync...');
            await new Promise((resolve) => setTimeout(resolve, 50));
            console.log('[captureAtAngleAsBlob]   ✓ GPU sync complete');

            console.log('[captureAtAngleAsBlob] ========== RENDER PIPELINE COMPLETE ==========');

            // STEP 5: Read the screenshot from render target
            console.log('[captureAtAngleAsBlob] ========== READING SCREENSHOT ==========');
            console.log(
                `[captureAtAngleAsBlob] Expected resolution: ${targetWidth}x${targetHeight}`
            );
            console.log(
                `[captureAtAngleAsBlob] Render target dimensions: ${renderTarget.width}x${renderTarget.height}`
            );

            // Use the render target we already have (don't re-fetch it)
            const texture = renderTarget.colorBuffer;
            if (!texture) {
                throw new Error('No color buffer available');
            }

            console.log(
                `[captureAtAngleAsBlob] Texture dimensions: ${texture.width}x${texture.height}`
            );
            console.log(
                `[captureAtAngleAsBlob] Reading texture data from (0, 0) to (${texture.width}, ${texture.height})...`
            );

            // Read texture data - try without renderTarget option first to avoid format conversion issues
            // This can help prevent darker edge pixels
            let typedArray;
            try {
                console.log(
                    `[captureAtAngleAsBlob] Attempting texture read without renderTarget option...`
                );
                typedArray = await texture.read(0, 0, texture.width, texture.height);
                console.log(
                    `[captureAtAngleAsBlob] ✓ Texture read successful without renderTarget option`
                );
            } catch (e) {
                console.log(
                    `[captureAtAngleAsBlob] Texture read without renderTarget failed, trying with immediate flag...`
                );
                typedArray = await texture.read(0, 0, texture.width, texture.height, {
                    immediate: true,
                });
                console.log(`[captureAtAngleAsBlob] ✓ Texture read successful with immediate flag`);
            }

            console.log(
                `[captureAtAngleAsBlob] Texture read complete. Array length: ${
                    typedArray.length
                }, Expected: ${texture.width * texture.height * 4}`
            );
            const imageData = new Uint32Array(typedArray.buffer.slice(0));

            // Convert to blob
            const canvas = document.createElement('canvas');
            canvas.width = texture.width;
            canvas.height = texture.height;
            console.log(
                `[captureAtAngleAsBlob] Canvas created with dimensions: ${canvas.width}x${canvas.height}`
            );

            // Set proper color space and optimization flags for screenshot capture
            const ctx = canvas.getContext('2d', {
                colorSpace: 'srgb',
                willReadFrequently: true,
            });

            if (!ctx) {
                throw new Error('Failed to get 2D context');
            }

            console.log(`[captureAtAngleAsBlob] Canvas context created with colorSpace: srgb`);

            const imgData = ctx.createImageData(texture.width, texture.height);
            const data = imgData.data;

            // Flip vertically and convert ABGR to RGBA
            const needsFlip = true;
            if (needsFlip) {
                for (let y = 0; y < texture.height; y++) {
                    const srcRow = (texture.height - 1 - y) * texture.width;
                    const dstRow = y * texture.width;
                    for (let x = 0; x < texture.width; x++) {
                        const srcIdx = srcRow + x;
                        const dstIdx = (dstRow + x) * 4;
                        const pixel = imageData[srcIdx];
                        data[dstIdx + 0] = (pixel >> 0) & 0xff; // R
                        data[dstIdx + 1] = (pixel >> 8) & 0xff; // G
                        data[dstIdx + 2] = (pixel >> 16) & 0xff; // B
                        data[dstIdx + 3] = (pixel >> 24) & 0xff; // A
                    }
                }
            }

            // Soften alpha edges to reduce aliasing artifacts (if enabled)
            console.log(
                `[captureAtAngleAsBlob] ========== CHECKING EDGE SOFTENING SETTINGS ==========`
            );
            const softenEdges = this.observer.get('screenshot.softenEdges');
            const softenEdgesRadiusRaw = this.observer.get('screenshot.softenEdgesRadius');
            console.log(
                `[captureAtAngleAsBlob] Observer value for 'screenshot.softenEdges': ${softenEdges} (type: ${typeof softenEdges})`
            );
            console.log(
                `[captureAtAngleAsBlob] Observer value for 'screenshot.softenEdgesRadius': ${softenEdgesRadiusRaw} (type: ${typeof softenEdgesRadiusRaw})`
            );

            if (softenEdges) {
                const radius = softenEdgesRadiusRaw || 1.5;
                console.log(`[captureAtAngleAsBlob] ========== APPLYING EDGE SOFTENING ==========`);
                console.log(`[captureAtAngleAsBlob] Soften edges enabled: ${softenEdges}`);
                console.log(`[captureAtAngleAsBlob] Soften radius: ${radius}`);
                console.log(
                    `[captureAtAngleAsBlob] Image dimensions: ${texture.width}x${texture.height}`
                );
                const softenStart = performance.now();
                this.softenAlphaEdges(data, texture.width, texture.height, radius);
                const softenEnd = performance.now();
                console.log(
                    `[captureAtAngleAsBlob] Edge softening completed in ${(
                        softenEnd - softenStart
                    ).toFixed(2)}ms`
                );
                console.log(`[captureAtAngleAsBlob] ===========================================`);
            } else {
                console.log(
                    `[captureAtAngleAsBlob] Edge softening disabled (softenEdges: ${softenEdges}, type: ${typeof softenEdges})`
                );
                console.log(
                    `[captureAtAngleAsBlob] This may be because the observer value is false, undefined, or null`
                );
            }
            console.log(`[captureAtAngleAsBlob] ===========================================`);

            ctx.putImageData(imgData, 0, 0);

            // Convert to blob
            console.log(`[captureAtAngleAsBlob] Converting canvas to ${outputFormat} blob...`);
            const blob = await new Promise<Blob>((resolve, reject) => {
                const mimeType = outputFormat === 'webp' ? 'image/webp' : 'image/png';
                canvas.toBlob(
                    (blob) => {
                        if (blob) {
                            console.log(`[captureAtAngleAsBlob] Blob created successfully!`);
                            console.log(
                                `[captureAtAngleAsBlob] Blob size: ${blob.size} bytes (${(
                                    blob.size / 1024
                                ).toFixed(2)} KB)`
                            );
                            console.log(`[captureAtAngleAsBlob] Blob type: ${blob.type}`);
                            resolve(blob);
                        } else {
                            reject(new Error(`Failed to create ${outputFormat} blob`));
                        }
                    },
                    mimeType,
                    outputFormat === 'webp' ? 0.95 : undefined
                );
            });

            const blobUrl = URL.createObjectURL(blob);

            // Verify blob dimensions by loading it
            const img = new Image();
            await new Promise<void>((resolve, reject) => {
                img.onload = () => {
                    if (img.width !== targetWidth || img.height !== targetHeight) {
                        console.error(
                            `[captureAtAngleAsBlob] Dimension mismatch! Expected ${targetWidth}x${targetHeight}, got ${img.width}x${img.height}`
                        );
                    }
                    resolve();
                };
                img.onerror = reject;
                img.src = blobUrl;
            });

            // Screenshot captured successfully (v2.0 - quote removed)
            return blobUrl;
        } finally {
            // For batch processing, keep canvas and device at capture size to avoid triggering resize
            // Restoring canvas dimensions would trigger canvasResize, which would reset device resolution
            // and rebuild render targets at the canvas size (breaking render target reuse)
            // Don't restore canvas dimensions - keep them at capture size for subsequent captures
            // this.canvas.width = originalWidth;  // DON'T restore - keep at targetWidth for batch
            // this.canvas.height = originalHeight;  // DON'T restore - keep at targetHeight for batch
            // this.canvas.style.width = originalStyleWidth;  // DON'T restore
            // this.canvas.style.height = originalStyleHeight;  // DON'T restore

            // Keep device resolution, render target, and aspect ratio at capture size (2048x736) for batch processing
            // This allows render target reuse across multiple captures without rebuilding
            // Don't restore to originalDeviceWidth/originalDeviceHeight - keep it at targetWidth/targetHeight
            // Don't restore aspect ratio either - keep it at capture aspect ratio for batch processing

            // Only restore aspect ratio if we're NOT doing batch processing (i.e., if original was different)
            // For batch processing, we keep the capture aspect ratio to preserve render target
            const targetAspectRatio = targetWidth / targetHeight;
            if (Math.abs(originalAspectRatio - targetAspectRatio) > 0.01) {
                // Original aspect ratio was different - restore it (not batch processing)
                this.camera.camera.aspectRatioMode = originalAspectRatioMode;
                this.camera.camera.aspectRatio = originalAspectRatio;
            }
            // Otherwise, keep the capture aspect ratio (batch processing)

            // For batch mode, don't restore rendering state between captures - only restore at end
            if (!this.isBatchScreenshotMode) {
                this.restoreRenderingState(originalState);
            } else {
                console.log(
                    `[captureAtAngleAsBlob] Skipping restoreRenderingState (batch mode - will restore at end)`
                );
            }

            // Restore original orbit mode if it was changed
            if (originalOrbitMode !== 'target') {
                console.log(
                    `[captureAtAngleAsBlob] Restoring orbit mode to "${originalOrbitMode}"`
                );
                this.setOrbitMode(originalOrbitMode);
            }

            // Re-enable camera controls (in case of early exit/error)
            // @ts-ignore
            this.cameraControls.enabled = originalCameraControlsEnabled;
            console.log(
                `[captureAtAngleAsBlob] 🔓 Camera controls restored in finally block (enabled: ${originalCameraControlsEnabled})`
            );

            // Re-enable UI camera controls
            this.enableUICameraControls();

            // Clear the flag to re-enable automatic resolution sync
            // @ts-ignore
            this.isCapturingScreenshot = false;
        }
    }

    // Setup top-down camera (camera directly above model, looking down)
    async setupTopDownCamera() {
        console.log('Setting up top-down camera...');
        
        // @ts-ignore - Log camera controls state on entry
        console.log('[setupTopDownCamera] 🚪 ENTRY - Camera Controls State:', {
            hasYaw: this.cameraControls._yaw !== undefined,
            yaw: this.cameraControls._yaw,
            hasTargetYaw: this.cameraControls._targetYaw !== undefined,
            targetYaw: this.cameraControls._targetYaw,
            hasPitch: this.cameraControls._pitch !== undefined,
            pitch: this.cameraControls._pitch,
            cameraPos: this.camera.getPosition(),
            cameraRot: this.camera.getEulerAngles()
        });

        // Apply default screenshot settings
        this.applyScreenshotDefaults();

        // Wait for skybox to load
        await this.delay(800);

        // Remove offsets to return model to normal pose for screenshot
        this.removeOffsetsForScreenshot();

        // Hide high-speed spinning nodes (propeller blurs, etc.)
        this.hideHighSpeedSpinningNodes();

        // Apply screenshot animation settings from aircraft configuration before positioning camera
        this.applyScreenshotAnimationSettings();
        
        // @ts-ignore - Check yaw after animation settings
        if (this.cameraControls._yaw !== undefined) {
            // @ts-ignore
            console.log(`[setupTopDownCamera] 📐 Yaw AFTER applyScreenshotAnimationSettings: ${(this.cameraControls._yaw * 180 / Math.PI).toFixed(2)}°`);
        }

        // Force multiple render cycles to ensure all transforms are updated after offset removal
        console.log('Forcing render cycles to update transforms...');
        for (let i = 0; i < 3; i++) {
            this.app.fire('prerender');
            this.app.renderNextFrame = true;
            this.app.render();
            this.app.fire('postrender');
        }
        
        // @ts-ignore - Check yaw after render cycles
        if (this.cameraControls._yaw !== undefined) {
            // @ts-ignore
            console.log(`[setupTopDownCamera] 🎬 Yaw AFTER render cycles: ${(this.cameraControls._yaw * 180 / Math.PI).toFixed(2)}°`);
        }

        // Calculate scene bounds
        this.calcSceneBounds(this.dynamicSceneBounds);
        const center = this.dynamicSceneBounds.center.clone();
        const bounds = this.dynamicSceneBounds;
        const min = bounds.getMin();
        const max = bounds.getMax();

        // Calculate model dimensions (from top-down view, we care about X and Z extents)
        const modelWidth = max.x - min.x; // X extent = width from top
        const modelDepth = max.z - min.z; // Z extent = depth from top
        const modelHeight = max.y - min.y; // Y extent = height (for distance calculation)

        // Calculate distance based on the larger visible dimension
        const maxVisibleDimension = Math.max(modelWidth, modelDepth);
        const distance = maxVisibleDimension * 2.2;

        // Position camera directly above the center, looking down
        const cameraX = center.x;
        const cameraY = center.y + distance; // Above the model
        const cameraZ = center.z;

        const cameraPos = new Vec3(cameraX, cameraY, cameraZ);

        // Store original inertia for instant camera movement
        // @ts-ignore
        const originalInertia = this.cameraControls.inertiaFactor;

        // Set inertia to 0 for completely instant movement
        // @ts-ignore
        if (this.cameraControls.inertiaFactor !== undefined) {
            // @ts-ignore
            this.cameraControls.inertiaFactor = 0;
        }

        // Get actual camera aspect ratio (needed for FOV calculation)
        const actualAspectRatio = this.camera.camera.aspectRatio;

        // Apply margin to ensure model doesn't touch edges
        const marginFactor = 0.9; // 10% padding
        const targetWidth = modelWidth / marginFactor;
        const targetDepth = modelDepth / marginFactor;

        // Calculate FOV (from top-down, we need to fit width and depth)
        const hFovForWidthRad = 2 * Math.atan(targetWidth / (2 * distance));
        const vFovForWidthRad = 2 * Math.atan(Math.tan(hFovForWidthRad / 2) / actualAspectRatio);
        const vFovForWidth = vFovForWidthRad * (180 / Math.PI);
        const vFovForDepth = 2 * Math.atan(targetDepth / (2 * distance)) * (180 / Math.PI);

        const finalFOV = Math.max(vFovForWidth, vFovForDepth);
        const clampedFOV = Math.max(5, Math.min(120, finalFOV));

        // Set the new FOV first
        this.camera.camera.fov = clampedFOV;
        this.observer.set('camera.fov', clampedFOV);

        // Position the camera directly above, looking down
        console.log(
            `Positioning camera at: x=${cameraX.toFixed(2)}, y=${cameraY.toFixed(
                2
            )}, z=${cameraZ.toFixed(2)}, looking down at center: x=${center.x.toFixed(
                2
            )}, y=${center.y.toFixed(2)}, z=${center.z.toFixed(2)}`
        );

        // Directly set camera position
        this.camera.setPosition(cameraPos);

        // Set camera to look down at center
        // Use -Z as up vector so the front (nose/prop) of the aircraft appears at the top
        this.camera.lookAt(center, new Vec3(0, 0, -1));

        // @ts-ignore - Log state BEFORE setting camera controls
        console.log('[setupTopDownCamera] 📍 BEFORE camera controls update:', {
            cameraPos: this.camera.getPosition(),
            cameraRot: this.camera.getEulerAngles(),
            yaw: this.cameraControls._yaw,
            targetYaw: this.cameraControls._targetYaw,
            pitch: this.cameraControls._pitch
        });

        // Force camera controls internal state to match immediately
        // @ts-ignore
        if (this.cameraControls._distance !== undefined) {
            // @ts-ignore
            this.cameraControls._distance = distance;
        }
        // @ts-ignore
        if (this.cameraControls._targetDistance !== undefined) {
            // @ts-ignore
            this.cameraControls._targetDistance = distance;
        }
        // @ts-ignore
        if (this.cameraControls._pitch !== undefined) {
            // @ts-ignore
            this.cameraControls._pitch = -Math.PI / 2; // -90 degrees (looking straight down)
        }
        // @ts-ignore
        if (this.cameraControls._targetPitch !== undefined) {
            // @ts-ignore
            this.cameraControls._targetPitch = -Math.PI / 2;
        }
        // @ts-ignore - CRITICAL: Reset yaw to 0 to ensure proper orientation
        if (this.cameraControls._yaw !== undefined) {
            // @ts-ignore
            const oldYaw = this.cameraControls._yaw;
            console.log(`[setupTopDownCamera] 🔄 Yaw BEFORE reset: ${(oldYaw * 180 / Math.PI).toFixed(2)}°`);
            // @ts-ignore
            this.cameraControls._yaw = 0; // Reset horizontal rotation
            // @ts-ignore
            console.log(`[setupTopDownCamera] ✅ Yaw AFTER reset: ${(this.cameraControls._yaw * 180 / Math.PI).toFixed(2)}°`);
        }
        // @ts-ignore
        if (this.cameraControls._targetYaw !== undefined) {
            // @ts-ignore
            const oldTargetYaw = this.cameraControls._targetYaw;
            console.log(`[setupTopDownCamera] 🔄 TargetYaw BEFORE reset: ${(oldTargetYaw * 180 / Math.PI).toFixed(2)}°`);
            // @ts-ignore
            this.cameraControls._targetYaw = 0;
            // @ts-ignore
            console.log(`[setupTopDownCamera] ✅ TargetYaw AFTER reset: ${(this.cameraControls._targetYaw * 180 / Math.PI).toFixed(2)}°`);
        }
        // @ts-ignore
        if (this.cameraControls._zoomDist !== undefined) {
            // @ts-ignore
            this.cameraControls._zoomDist = distance;
        }
        // @ts-ignore
        if (this.cameraControls._origin !== undefined) {
            // @ts-ignore
            this.cameraControls._origin.copy(center);
        }

        // CRITICAL: Disable camera controls update to prevent it from overriding our position
        // @ts-ignore
        const originalUpdate = this.cameraControls.update;
        // @ts-ignore
        this.cameraControls.update = () => {}; // No-op to prevent camera controls from moving camera

        // Force immediate render to apply camera transform
        this.app.fire('prerender');
        this.app.renderNextFrame = true;
        this.app.render();
        this.app.fire('postrender');

        // Restore update function after a moment - but NOT during batch mode!
        // In batch mode, the capture takes 2000+ ms, so 100ms is way too short
        // We'll restore it at the end of the batch screenshot process instead
        if (!this.isBatchScreenshotMode) {
            // @ts-ignore
            setTimeout(() => {
                // @ts-ignore
                if (originalUpdate) {
                    // @ts-ignore
                    this.cameraControls.update = originalUpdate;
                }
            }, 100);
        } else {
            // Store the original update function so it can be restored later
            (this as any)._originalCameraControlsUpdate = originalUpdate;
            console.log('[setupTopDownCamera] 🔒 Camera controls update DISABLED for batch mode (will restore at end)');
        }

        // Store original inertia to restore later
        (this as any)._originalInertia = originalInertia;

        // @ts-ignore - Final verification of camera state
        console.log('[setupTopDownCamera] 🎯 FINAL State:', {
            cameraPos: this.camera.getPosition(),
            cameraRot: this.camera.getEulerAngles(),
            yaw: this.cameraControls._yaw,
            targetYaw: this.cameraControls._targetYaw,
            pitch: this.cameraControls._pitch
        });

        console.log('Top-down camera setup complete');
    }

    // Setup bottom-up camera (camera directly below model, looking up)
    async setupBottomUpCamera() {
        console.log('Setting up bottom-up camera...');

        // Apply default screenshot settings
        this.applyScreenshotDefaults();

        // Wait for skybox to load
        await this.delay(800);

        // Remove offsets to return model to normal pose for screenshot
        this.removeOffsetsForScreenshot();

        // Hide high-speed spinning nodes (propeller blurs, etc.)
        this.hideHighSpeedSpinningNodes();

        // Apply screenshot animation settings from aircraft configuration before positioning camera
        this.applyScreenshotAnimationSettings();

        // Force multiple render cycles to ensure all transforms are updated after offset removal
        console.log('Forcing render cycles to update transforms...');
        for (let i = 0; i < 3; i++) {
            this.app.fire('prerender');
            this.app.renderNextFrame = true;
            this.app.render();
            this.app.fire('postrender');
        }

        // Calculate scene bounds
        this.calcSceneBounds(this.dynamicSceneBounds);
        const center = this.dynamicSceneBounds.center.clone();
        const bounds = this.dynamicSceneBounds;
        const min = bounds.getMin();
        const max = bounds.getMax();

        // Calculate model dimensions (from bottom-up view, we care about X and Z extents)
        const modelWidth = max.x - min.x; // X extent = width from bottom
        const modelDepth = max.z - min.z; // Z extent = depth from bottom
        const modelHeight = max.y - min.y; // Y extent = height (for distance calculation)

        // Calculate distance based on the larger visible dimension
        const maxVisibleDimension = Math.max(modelWidth, modelDepth);
        const distance = maxVisibleDimension * 2.2;

        // Position camera directly below the center, looking up
        const cameraX = center.x;
        const cameraY = center.y - distance; // Below the model
        const cameraZ = center.z;

        const cameraPos = new Vec3(cameraX, cameraY, cameraZ);

        // Store original inertia for instant camera movement
        // @ts-ignore
        const originalInertia = this.cameraControls.inertiaFactor;

        // Set inertia to 0 for completely instant movement
        // @ts-ignore
        if (this.cameraControls.inertiaFactor !== undefined) {
            // @ts-ignore
            this.cameraControls.inertiaFactor = 0;
        }

        // Get actual camera aspect ratio (needed for FOV calculation)
        const actualAspectRatio = this.camera.camera.aspectRatio;

        // Apply margin to ensure model doesn't touch edges
        const marginFactor = 0.9; // 10% padding
        const targetWidth = modelWidth / marginFactor;
        const targetDepth = modelDepth / marginFactor;

        // Calculate FOV (from bottom-up, we need to fit width and depth)
        const hFovForWidthRad = 2 * Math.atan(targetWidth / (2 * distance));
        const vFovForWidthRad = 2 * Math.atan(Math.tan(hFovForWidthRad / 2) / actualAspectRatio);
        const vFovForWidth = vFovForWidthRad * (180 / Math.PI);
        const vFovForDepth = 2 * Math.atan(targetDepth / (2 * distance)) * (180 / Math.PI);

        const finalFOV = Math.max(vFovForWidth, vFovForDepth);
        const clampedFOV = Math.max(5, Math.min(120, finalFOV));

        // Set the new FOV first
        this.camera.camera.fov = clampedFOV;
        this.observer.set('camera.fov', clampedFOV);

        // Position the camera directly below, looking up
        console.log(
            `Positioning camera at: x=${cameraX.toFixed(2)}, y=${cameraY.toFixed(
                2
            )}, z=${cameraZ.toFixed(2)}, looking up at center: x=${center.x.toFixed(
                2
            )}, y=${center.y.toFixed(2)}, z=${center.z.toFixed(2)}`
        );

        // Directly set camera position
        this.camera.setPosition(cameraPos);

        // Set camera to look up at center
        // Use -Z as up vector so the front (nose/prop) of the aircraft appears at the top
        this.camera.lookAt(center, new Vec3(0, 0, -1));

        // Force camera controls internal state to match immediately
        // @ts-ignore
        if (this.cameraControls._distance !== undefined) {
            // @ts-ignore
            this.cameraControls._distance = distance;
        }
        // @ts-ignore
        if (this.cameraControls._targetDistance !== undefined) {
            // @ts-ignore
            this.cameraControls._targetDistance = distance;
        }
        // @ts-ignore
        if (this.cameraControls._pitch !== undefined) {
            // @ts-ignore
            this.cameraControls._pitch = Math.PI / 2; // 90 degrees (looking straight up)
        }
        // @ts-ignore
        if (this.cameraControls._targetPitch !== undefined) {
            // @ts-ignore
            this.cameraControls._targetPitch = Math.PI / 2;
        }
        // @ts-ignore - CRITICAL: Reset yaw to 0 to ensure proper orientation
        if (this.cameraControls._yaw !== undefined) {
            // @ts-ignore
            this.cameraControls._yaw = 0; // Reset horizontal rotation
        }
        // @ts-ignore
        if (this.cameraControls._targetYaw !== undefined) {
            // @ts-ignore
            this.cameraControls._targetYaw = 0;
        }
        // @ts-ignore
        if (this.cameraControls._zoomDist !== undefined) {
            // @ts-ignore
            this.cameraControls._zoomDist = distance;
        }
        // @ts-ignore
        if (this.cameraControls._origin !== undefined) {
            // @ts-ignore
            this.cameraControls._origin.copy(center);
        }

        // CRITICAL: Disable camera controls update to prevent it from overriding our position
        // @ts-ignore
        const originalUpdate = this.cameraControls.update;
        // @ts-ignore
        this.cameraControls.update = () => {}; // No-op to prevent camera controls from moving camera

        // Force immediate render to apply camera transform
        this.app.fire('prerender');
        this.app.renderNextFrame = true;
        this.app.render();
        this.app.fire('postrender');

        // Restore update function after a moment - but NOT during batch mode!
        // In batch mode, the capture takes 2000+ ms, so 100ms is way too short
        // We'll restore it at the end of the batch screenshot process instead
        if (!this.isBatchScreenshotMode) {
            // @ts-ignore
            setTimeout(() => {
                // @ts-ignore
                if (originalUpdate) {
                    // @ts-ignore
                    this.cameraControls.update = originalUpdate;
                }
            }, 100);
        } else {
            // Store the original update function so it can be restored later
            (this as any)._originalCameraControlsUpdate = originalUpdate;
            console.log('[setupBottomUpCamera] 🔒 Camera controls update DISABLED for batch mode (will restore at end)');
        }

        // Store original inertia to restore later
        (this as any)._originalInertia = originalInertia;

        console.log('Bottom-up camera setup complete');
    }

    // Capture top-down screenshot and return as blob URL
    async captureTopDownAsBlob(
        resolution: 'thumbnail' | 'large' | '4k' | '8k' = 'thumbnail'
    ): Promise<string> {
        // Disable UI camera controls for programmatic camera control
        this.disableUICameraControls();

        // Always use WebP format
        const outputFormat = 'webp';

        // Save current orbit mode and force to "target" for screenshots
        const originalOrbitMode = this.observer.get('camera.orbitMode');
        if (originalOrbitMode !== 'target') {
            console.log(
                `[captureTopDownAsBlob] Forcing orbit mode from "${originalOrbitMode}" to "target" for screenshot`
            );
            this.setOrbitMode('target');
            // Wait a bit for orbit mode to take effect
            await new Promise((resolve) => setTimeout(resolve, 100));
        }

        // Calculate resolution dimensions
        const baseWidth = 512;
        const baseHeight = 184;
        const scale =
            resolution === 'large' ? 4 : resolution === '4k' ? 7.5 : resolution === '8k' ? 15 : 1;
        const targetWidth = baseWidth * scale;
        const targetHeight = baseHeight * scale;

        // Save current state
        const originalWidth = this.canvas.width;
        const originalHeight = this.canvas.height;
        const originalStyleWidth = this.canvas.style.width;
        const originalStyleHeight = this.canvas.style.height;
        const originalDeviceWidth = this.app.graphicsDevice.width;
        const originalDeviceHeight = this.app.graphicsDevice.height;
        const originalAspectRatioMode = this.camera.camera.aspectRatioMode;
        const originalAspectRatio = this.camera.camera.aspectRatio;
        const originalState = this.saveRenderingState();

        // Set flag to prevent automatic resolution sync during screenshot capture
        // @ts-ignore
        this.isCapturingScreenshot = true;

        try {
            // Change resolution
            this.app.graphicsDevice.setResolution(targetWidth, targetHeight);
            this.destroyRenderTargets();
            
            // CRITICAL: In batch mode, SKIP building render target until AFTER camera is positioned
            // This prevents capturing stale state into the render target
            if (!this.isBatchScreenshotMode) {
                // Non-batch mode: Build render target now
                this.forceRebuildRenderTargets(targetWidth, targetHeight, true);
            } else {
                console.log(`[captureTopDownAsBlob] ⏭️  Batch mode: Delaying render target build until after camera setup...`);
            }

            // Set camera aspect ratio
            const targetAspectRatio = targetWidth / targetHeight;
            this.camera.camera.aspectRatioMode = 1;
            this.camera.camera.aspectRatio = targetAspectRatio;

            // Setup camera for top-down view
            await this.setupTopDownCamera();

            // Verify camera position before setting up background
            const verifyPos = this.camera.getPosition();
            console.log(
                `[captureTopDownAsBlob] Camera position before setupTransparentBackground: x=${verifyPos.x.toFixed(
                    2
                )}, y=${verifyPos.y.toFixed(2)}, z=${verifyPos.z.toFixed(2)}`
            );

            // For batch mode, only set up once and reuse
            if (!this.isBatchScreenshotMode) {
                this.setupTransparentBackground(false);
            } else {
                console.log(
                    `[captureTopDownAsBlob] Skipping setupTransparentBackground (already set up for batch mode)`
                );
                // But we still need to disable shadows for each capture in batch mode
                this.disableShadowsForScreenshot();
            }
            // Top-down should use skybox/env lighting only (no extra directional "sun" highlights).
            this.light.enabled = false;

            // Verify camera position after setupTransparentBackground
            const verifyPos2 = this.camera.getPosition();
            console.log(
                `[captureTopDownAsBlob] Camera position after setupTransparentBackground: x=${verifyPos2.x.toFixed(
                    2
                )}, y=${verifyPos2.y.toFixed(2)}, z=${verifyPos2.z.toFixed(2)}`
            );

            // If camera was reset, reposition it
            if (Math.abs(verifyPos2.y - verifyPos.y) > 0.1) {
                console.warn(`[captureTopDownAsBlob] Camera was reset! Repositioning...`);
                // Recalculate position
                this.calcSceneBounds(this.dynamicSceneBounds);
                const center = this.dynamicSceneBounds.center.clone();
                const bounds = this.dynamicSceneBounds;
                const min = bounds.getMin();
                const max = bounds.getMax();
                const maxVisibleDimension = Math.max(max.x - min.x, max.z - min.z);
                const distance = maxVisibleDimension * 2.2;
                const cameraPos = new Vec3(center.x, center.y + distance, center.z);
                this.camera.setPosition(cameraPos);
                this.camera.lookAt(center, new Vec3(0, 0, -1));
            }

            // CRITICAL: In batch mode, forcefully reapply top-down camera position and lookAt
            // right before rendering to ensure camera isn't using stale state from previous angle
            if (this.isBatchScreenshotMode) {
                console.log(`[captureTopDownAsBlob] 🔄 Batch mode: Forcefully reapplying top-down camera...`);
                
                // CRITICAL: Disable multiframe accumulation to prevent ghosting from previous angle
                // The multiframe system blends multiple frames together, causing old frames to persist
                if (this.multiframe) {
                    console.log(`[captureTopDownAsBlob] 🧹 Resetting multiframe accumulation buffer...`);
                    this.multiframe.moved(); // Reset the accumulation
                    // Temporarily disable multiframe blending for this capture
                    const originalBlend = this.multiframe.blend;
                    const originalEnabled = this.multiframe.enabled;
                    this.multiframe.enabled = false;
                    this.multiframe.blend = 1.0; // No blending
                    console.log(`[captureTopDownAsBlob] ✓ Multiframe disabled (was enabled: ${originalEnabled}, blend: ${originalBlend})`);
                }
                
                this.calcSceneBounds(this.dynamicSceneBounds);
                const center = this.dynamicSceneBounds.center.clone();
                const bounds = this.dynamicSceneBounds;
                const min = bounds.getMin();
                const max = bounds.getMax();
                const maxVisibleDimension = Math.max(max.x - min.x, max.z - min.z);
                const distance = maxVisibleDimension * 2.2;
                const cameraPos = new Vec3(center.x, center.y + distance, center.z);
                
                // Set position and lookAt
                this.camera.setPosition(cameraPos);
                this.camera.lookAt(center, new Vec3(0, 0, -1));
                
                // CRITICAL: Force camera transform update - this is what captureAtAngleAsBlob does
                // and is missing here, causing the camera position to not "stick"
                this.camera.syncHierarchy();
                
                // Also verify the camera's rotation quaternion directly (sometimes lookAt doesn't stick)
                const finalPos = this.camera.getPosition();
                const finalRot = this.camera.getEulerAngles();
                console.log(`[captureTopDownAsBlob] ✓ Reapplied camera: pos=(${finalPos.x.toFixed(2)}, ${finalPos.y.toFixed(2)}, ${finalPos.z.toFixed(2)}), rot=(${finalRot.x.toFixed(2)}, ${finalRot.y.toFixed(2)}, ${finalRot.z.toFixed(2)})`);
                
                // CRITICAL: Destroy and rebuild render target to ensure no ghosting from previous captures
                // This completely clears any stale content that might be lingering
                console.log(`[captureTopDownAsBlob] 🗑️  Destroying and rebuilding render target to eliminate ghosting...`);
                this.destroyRenderTargets();
                this.forceRebuildRenderTargets(targetWidth, targetHeight, true);
                console.log(`[captureTopDownAsBlob] ✓ Fresh render target created`);
                
                // Do multiple immediate renders to ensure the new render target has fresh content
                console.log(`[captureTopDownAsBlob] 🧹 Rendering multiple frames with fresh render target...`);
                for (let i = 0; i < 3; i++) {
                    this.app.fire('prerender');
                    this.app.renderNextFrame = true;
                    this.app.render();
                    this.app.fire('postrender');
                }
                // Small delay to ensure GPU flushes the renders
                await new Promise((resolve) => setTimeout(resolve, 50));
                console.log(`[captureTopDownAsBlob] ✓ Render target refreshed`);
            }

            // Force multiple render cycles
            for (let i = 0; i < 5; i++) {
                this.app.fire('prerender');
                this.app.renderNextFrame = true;
                this.app.render();
                this.app.fire('postrender');
            }

            // Small fixed delay to ensure GPU has finished rendering
            await new Promise((resolve) => setTimeout(resolve, 50));

            const rt = this.camera.camera.renderTarget;
            if (!rt) {
                throw new Error('No render target available');
            }

            if (rt.samples > 1) {
                rt.resolve();
            }

            // Final render
            this.app.fire('prerender');
            this.app.renderNextFrame = true;
            this.app.render();
            this.app.fire('postrender');

            if (rt.samples > 1) {
                rt.resolve();
            }

            await new Promise((resolve) => requestAnimationFrame(resolve));

            const texture = rt.colorBuffer;
            if (!texture) {
                throw new Error('No color buffer available');
            }

            // Read texture data
            const readOptions =
                rt.samples > 1 ? { immediate: true } : { renderTarget: rt, immediate: true };

            const typedArray = await texture.read(0, 0, texture.width, texture.height, readOptions);
            const imageData = new Uint32Array(typedArray.buffer.slice(0));

            // Convert to blob
            const canvas = document.createElement('canvas');
            canvas.width = texture.width;
            canvas.height = texture.height;
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                throw new Error('Failed to get 2D context');
            }

            const imgData = ctx.createImageData(texture.width, texture.height);
            const data = imgData.data;

            // Flip vertically and convert ABGR to RGBA
            const needsFlip = true;
            if (needsFlip) {
                for (let y = 0; y < texture.height; y++) {
                    const srcRow = (texture.height - 1 - y) * texture.width;
                    const dstRow = y * texture.width;
                    for (let x = 0; x < texture.width; x++) {
                        const srcIdx = srcRow + x;
                        const dstIdx = (dstRow + x) * 4;
                        const pixel = imageData[srcIdx];
                        data[dstIdx + 0] = (pixel >> 0) & 0xff; // R
                        data[dstIdx + 1] = (pixel >> 8) & 0xff; // G
                        data[dstIdx + 2] = (pixel >> 16) & 0xff; // B
                        data[dstIdx + 3] = (pixel >> 24) & 0xff; // A
                    }
                }
            }

            ctx.putImageData(imgData, 0, 0);

            // Convert to blob
            const blob = await new Promise<Blob>((resolve, reject) => {
                const mimeType = outputFormat === 'webp' ? 'image/webp' : 'image/png';
                canvas.toBlob(
                    (blob) => {
                        if (blob) {
                            resolve(blob);
                        } else {
                            reject(new Error(`Failed to create ${outputFormat} blob`));
                        }
                    },
                    mimeType,
                    outputFormat === 'webp' ? 0.95 : undefined
                );
            });

            return URL.createObjectURL(blob);
        } finally {
            // Clear screenshot capture flag
            // @ts-ignore
            this.isCapturingScreenshot = false;

            // Restore everything
            this.canvas.width = originalWidth;
            this.canvas.height = originalHeight;
            this.canvas.style.width = originalStyleWidth;
            this.canvas.style.height = originalStyleHeight;
            this.app.graphicsDevice.setResolution(originalDeviceWidth, originalDeviceHeight);
            this.destroyRenderTargets();
            this.forceRebuildRenderTargets(originalDeviceWidth, originalDeviceHeight);
            this.camera.camera.aspectRatioMode = originalAspectRatioMode;
            this.camera.camera.aspectRatio = originalAspectRatio;

            // For batch mode, don't restore rendering state between captures - only restore at end
            if (!this.isBatchScreenshotMode) {
                this.restoreRenderingState(originalState);
            } else {
                console.log(
                    `[captureTopDownAsBlob] Skipping restoreRenderingState (batch mode - will restore at end)`
                );
            }

            // Restore original orbit mode if it was changed
            if (originalOrbitMode !== 'target') {
                console.log(
                    `[captureTopDownAsBlob] Restoring orbit mode to "${originalOrbitMode}"`
                );
                this.setOrbitMode(originalOrbitMode);
            }

            // Re-enable UI camera controls
            this.enableUICameraControls();
        }
    }

    // Capture bottom-up screenshot and return as blob URL
    async captureBottomUpAsBlob(
        resolution: 'thumbnail' | 'large' | '4k' | '8k' = 'thumbnail'
    ): Promise<string> {
        // Disable UI camera controls for programmatic camera control
        this.disableUICameraControls();

        // Always use WebP format
        const outputFormat = 'webp';

        // Save current orbit mode and force to "target" for screenshots
        const originalOrbitMode = this.observer.get('camera.orbitMode');
        if (originalOrbitMode !== 'target') {
            console.log(
                `[captureBottomUpAsBlob] Forcing orbit mode from "${originalOrbitMode}" to "target" for screenshot`
            );
            this.setOrbitMode('target');
            // Wait a bit for orbit mode to take effect
            await new Promise((resolve) => setTimeout(resolve, 100));
        }

        // Calculate resolution dimensions
        const baseWidth = 512;
        const baseHeight = 184;
        const scale =
            resolution === 'large' ? 4 : resolution === '4k' ? 7.5 : resolution === '8k' ? 15 : 1;
        const targetWidth = baseWidth * scale;
        const targetHeight = baseHeight * scale;

        // Save current state
        const originalWidth = this.canvas.width;
        const originalHeight = this.canvas.height;
        const originalStyleWidth = this.canvas.style.width;
        const originalStyleHeight = this.canvas.style.height;
        const originalDeviceWidth = this.app.graphicsDevice.width;
        const originalDeviceHeight = this.app.graphicsDevice.height;
        const originalAspectRatioMode = this.camera.camera.aspectRatioMode;
        const originalAspectRatio = this.camera.camera.aspectRatio;
        const originalState = this.saveRenderingState();

        // Set flag to prevent automatic resolution sync during screenshot capture
        // @ts-ignore
        this.isCapturingScreenshot = true;

        try {
            // Change resolution
            this.app.graphicsDevice.setResolution(targetWidth, targetHeight);
            this.destroyRenderTargets();
            
            // CRITICAL: In batch mode, SKIP building render target until AFTER camera is positioned
            // This prevents capturing stale state into the render target
            if (!this.isBatchScreenshotMode) {
                // Non-batch mode: Build render target now
                this.forceRebuildRenderTargets(targetWidth, targetHeight, true);
            } else {
                console.log(`[captureBottomUpAsBlob] ⏭️  Batch mode: Delaying render target build until after camera setup...`);
            }

            // Set camera aspect ratio
            const targetAspectRatio = targetWidth / targetHeight;
            this.camera.camera.aspectRatioMode = 1;
            this.camera.camera.aspectRatio = targetAspectRatio;

            // Setup camera for bottom-up view
            await this.setupBottomUpCamera();

            // Verify camera position before setting up background
            const verifyPos = this.camera.getPosition();
            console.log(
                `[captureBottomUpAsBlob] Camera position before setupTransparentBackground: x=${verifyPos.x.toFixed(
                    2
                )}, y=${verifyPos.y.toFixed(2)}, z=${verifyPos.z.toFixed(2)}`
            );

            // For batch mode, only set up once and reuse
            if (!this.isBatchScreenshotMode) {
                this.setupTransparentBackground();
            } else {
                console.log(
                    `[captureBottomUpAsBlob] Skipping setupTransparentBackground (already set up for batch mode)`
                );
                // But we still need to disable shadows for each capture in batch mode
                this.disableShadowsForScreenshot();
            }

            // Verify camera position after setupTransparentBackground
            const verifyPos2 = this.camera.getPosition();
            console.log(
                `[captureBottomUpAsBlob] Camera position after setupTransparentBackground: x=${verifyPos2.x.toFixed(
                    2
                )}, y=${verifyPos2.y.toFixed(2)}, z=${verifyPos2.z.toFixed(2)}`
            );

            // If camera was reset, reposition it
            if (Math.abs(verifyPos2.y - verifyPos.y) > 0.1) {
                console.warn(`[captureBottomUpAsBlob] Camera was reset! Repositioning...`);
                // Recalculate position
                this.calcSceneBounds(this.dynamicSceneBounds);
                const center = this.dynamicSceneBounds.center.clone();
                const bounds = this.dynamicSceneBounds;
                const min = bounds.getMin();
                const max = bounds.getMax();
                const maxVisibleDimension = Math.max(max.x - min.x, max.z - min.z);
                const distance = maxVisibleDimension * 2.2;
                const cameraPos = new Vec3(center.x, center.y - distance, center.z);
                this.camera.setPosition(cameraPos);
                this.camera.lookAt(center, new Vec3(0, 0, -1));
            }

            // CRITICAL: In batch mode, forcefully reapply bottom-up camera position and lookAt
            // right before rendering to ensure camera isn't using stale state from previous angle
            if (this.isBatchScreenshotMode) {
                console.log(`[captureBottomUpAsBlob] 🔄 Batch mode: Forcefully reapplying bottom-up camera...`);
                
                // CRITICAL: Disable multiframe accumulation to prevent ghosting from previous angle
                // The multiframe system blends multiple frames together, causing old frames to persist
                if (this.multiframe) {
                    console.log(`[captureBottomUpAsBlob] 🧹 Resetting multiframe accumulation buffer...`);
                    this.multiframe.moved(); // Reset the accumulation
                    // Temporarily disable multiframe blending for this capture
                    const originalBlend = this.multiframe.blend;
                    const originalEnabled = this.multiframe.enabled;
                    this.multiframe.enabled = false;
                    this.multiframe.blend = 1.0; // No blending
                    console.log(`[captureBottomUpAsBlob] ✓ Multiframe disabled (was enabled: ${originalEnabled}, blend: ${originalBlend})`);
                }
                
                this.calcSceneBounds(this.dynamicSceneBounds);
                const center = this.dynamicSceneBounds.center.clone();
                const bounds = this.dynamicSceneBounds;
                const min = bounds.getMin();
                const max = bounds.getMax();
                const maxVisibleDimension = Math.max(max.x - min.x, max.z - min.z);
                const distance = maxVisibleDimension * 2.2;
                const cameraPos = new Vec3(center.x, center.y - distance, center.z);
                
                // Set position and lookAt
                this.camera.setPosition(cameraPos);
                this.camera.lookAt(center, new Vec3(0, 0, -1));
                
                // CRITICAL: Force camera transform update - this is what captureAtAngleAsBlob does
                // and is missing here, causing the camera position to not "stick"
                this.camera.syncHierarchy();
                
                // Also verify the camera's rotation quaternion directly (sometimes lookAt doesn't stick)
                const finalPos = this.camera.getPosition();
                const finalRot = this.camera.getEulerAngles();
                console.log(`[captureBottomUpAsBlob] ✓ Reapplied camera: pos=(${finalPos.x.toFixed(2)}, ${finalPos.y.toFixed(2)}, ${finalPos.z.toFixed(2)}), rot=(${finalRot.x.toFixed(2)}, ${finalRot.y.toFixed(2)}, ${finalRot.z.toFixed(2)})`);
                
                // CRITICAL: Destroy and rebuild render target to ensure no ghosting from previous captures
                // This completely clears any stale content that might be lingering
                console.log(`[captureBottomUpAsBlob] 🗑️  Destroying and rebuilding render target to eliminate ghosting...`);
                this.destroyRenderTargets();
                this.forceRebuildRenderTargets(targetWidth, targetHeight, true);
                console.log(`[captureBottomUpAsBlob] ✓ Fresh render target created`);
                
                // Do multiple immediate renders to ensure the new render target has fresh content
                console.log(`[captureBottomUpAsBlob] 🧹 Rendering multiple frames with fresh render target...`);
                for (let i = 0; i < 3; i++) {
                    this.app.fire('prerender');
                    this.app.renderNextFrame = true;
                    this.app.render();
                    this.app.fire('postrender');
                }
                // Small delay to ensure GPU flushes the renders
                await new Promise((resolve) => setTimeout(resolve, 50));
                console.log(`[captureBottomUpAsBlob] ✓ Render target refreshed`);
            }

            // Force multiple render cycles
            for (let i = 0; i < 5; i++) {
                this.app.fire('prerender');
                this.app.renderNextFrame = true;
                this.app.render();
                this.app.fire('postrender');
            }

            // Small fixed delay to ensure GPU has finished rendering
            await new Promise((resolve) => setTimeout(resolve, 50));

            const rt = this.camera.camera.renderTarget;
            if (!rt) {
                throw new Error('No render target available');
            }

            if (rt.samples > 1) {
                rt.resolve();
            }

            // Final render
            this.app.fire('prerender');
            this.app.renderNextFrame = true;
            this.app.render();
            this.app.fire('postrender');

            if (rt.samples > 1) {
                rt.resolve();
            }

            await new Promise((resolve) => requestAnimationFrame(resolve));

            const texture = rt.colorBuffer;
            if (!texture) {
                throw new Error('No color buffer available');
            }

            // Read texture data
            const readOptions =
                rt.samples > 1 ? { immediate: true } : { renderTarget: rt, immediate: true };

            const typedArray = await texture.read(0, 0, texture.width, texture.height, readOptions);
            const imageData = new Uint32Array(typedArray.buffer.slice(0));

            // Convert to blob
            const canvas = document.createElement('canvas');
            canvas.width = texture.width;
            canvas.height = texture.height;
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                throw new Error('Failed to get 2D context');
            }

            const imgData = ctx.createImageData(texture.width, texture.height);
            const data = imgData.data;

            // Flip vertically and convert ABGR to RGBA
            const needsFlip = true;
            if (needsFlip) {
                for (let y = 0; y < texture.height; y++) {
                    const srcRow = (texture.height - 1 - y) * texture.width;
                    const dstRow = y * texture.width;
                    for (let x = 0; x < texture.width; x++) {
                        const srcIdx = srcRow + x;
                        const dstIdx = (dstRow + x) * 4;
                        const pixel = imageData[srcIdx];
                        data[dstIdx + 0] = (pixel >> 0) & 0xff; // R
                        data[dstIdx + 1] = (pixel >> 8) & 0xff; // G
                        data[dstIdx + 2] = (pixel >> 16) & 0xff; // B
                        data[dstIdx + 3] = (pixel >> 24) & 0xff; // A
                    }
                }
            }

            ctx.putImageData(imgData, 0, 0);

            // Convert to blob
            const blob = await new Promise<Blob>((resolve, reject) => {
                const mimeType = outputFormat === 'webp' ? 'image/webp' : 'image/png';
                canvas.toBlob(
                    (blob) => {
                        if (blob) {
                            resolve(blob);
                        } else {
                            reject(new Error(`Failed to create ${outputFormat} blob`));
                        }
                    },
                    mimeType,
                    outputFormat === 'webp' ? 0.95 : undefined
                );
            });

            return URL.createObjectURL(blob);
        } finally {
            // Clear screenshot capture flag
            // @ts-ignore
            this.isCapturingScreenshot = false;

            // Restore everything
            this.canvas.width = originalWidth;
            this.canvas.height = originalHeight;
            this.canvas.style.width = originalStyleWidth;
            this.canvas.style.height = originalStyleHeight;
            this.app.graphicsDevice.setResolution(originalDeviceWidth, originalDeviceHeight);
            this.destroyRenderTargets();
            this.forceRebuildRenderTargets(originalDeviceWidth, originalDeviceHeight);
            this.camera.camera.aspectRatioMode = originalAspectRatioMode;
            this.camera.camera.aspectRatio = originalAspectRatio;

            // For batch mode, don't restore rendering state between captures - only restore at end
            if (!this.isBatchScreenshotMode) {
                this.restoreRenderingState(originalState);
            } else {
                console.log(
                    `[captureBottomUpAsBlob] Skipping restoreRenderingState (batch mode - will restore at end)`
                );
            }

            // Restore original orbit mode if it was changed
            if (originalOrbitMode !== 'target') {
                console.log(
                    `[captureBottomUpAsBlob] Restoring orbit mode to "${originalOrbitMode}"`
                );
                this.setOrbitMode(originalOrbitMode);
            }

            // Re-enable UI camera controls
            this.enableUICameraControls();
        }
    }

    // Setup camera for aircraft camera position
    async setupAircraftCameraView(
        camera: any,
        resolution: 'thumbnail' | 'large' | '4k' | '8k' = 'thumbnail'
    ) {
        // Apply default screenshot settings
        this.applyScreenshotDefaults();

        // Wait for skybox to load
        await this.delay(800);

        // Remove offsets to return model to normal pose for screenshot
        this.removeOffsetsForScreenshot();

        // Hide high-speed spinning nodes (propeller blurs, etc.)
        this.hideHighSpeedSpinningNodes();

        // Apply screenshot animation settings from aircraft configuration before positioning camera
        this.applyScreenshotAnimationSettings();

        // Force multiple render cycles to ensure all transforms are updated after offset removal
        console.log('Forcing render cycles to update transforms...');
        for (let i = 0; i < 3; i++) {
            this.app.fire('prerender');
            this.app.renderNextFrame = true;
            this.app.render();
            this.app.fire('postrender');
        }

        // Save current aspect ratio settings before moving camera
        const currentAspectRatioMode = this.camera.camera.aspectRatioMode;
        const currentAspectRatio = this.camera.camera.aspectRatio;

        // Move camera to aircraft camera position (this will set FOV from config)
        this.moveCameraToAircraftCamera(camera);

        // Restore aspect ratio settings (they should be set by caller, but ensure they're preserved)
        // Don't override the aspect ratio here - the caller (captureAircraftCameraAsBlob) sets it
        // But ensure the FOV from config is preserved
        const configFOV = this.camera.camera.fov;
        console.log(`[setupAircraftCameraView] Config FOV preserved: ${configFOV}`);

        // Wait for camera to settle
        await this.delay(200);

        // Force render cycles
        for (let i = 0; i < 3; i++) {
            this.app.fire('prerender');
            this.app.renderNextFrame = true;
            this.app.render();
            this.app.fire('postrender');
        }

        console.log('Aircraft camera view setup complete');
        console.log(`  Final camera FOV: ${this.camera.camera.fov}`);
        console.log(`  Final camera aspect ratio: ${this.camera.camera.aspectRatio}`);
        console.log(`  Final camera aspect ratio mode: ${this.camera.camera.aspectRatioMode}`);
    }

    // Enhanced state saving (same as before but with better error handling)
    private saveRenderingState() {
        const skyboxLayer = this.app.scene.layers.getLayerById(LAYERID_SKYBOX);
        const canvasWrapper = document.getElementById('canvas-wrapper');

        return {
            // Camera settings
            clearColor: this.camera.camera.clearColor.clone(),

            // Skybox settings
            skyboxEnabled: skyboxLayer ? skyboxLayer.enabled : false,
            skybox: this.app.scene.skybox,
            envAtlas: this.app.scene.envAtlas,

            // Lighting settings
            ambientLight: this.app.scene.ambientLight
                ? this.app.scene.ambientLight.clone()
                : new Color(0.2, 0.2, 0.2),
            lightEnabled: this.light.enabled,
            lightIntensity: this.light.light.intensity,
            lightColor: this.light.light.color.clone(),
            lightCastShadows: this.light.light.castShadows,
            lightPosition: this.light.getLocalPosition().clone(),
            lightRotation: this.light.getLocalEulerAngles().clone(),

            // Debug elements
            showGrid: this.showGrid,
            showBounds: this.showBounds,
            showSkeleton: this.showSkeleton,
            showAxes: this.showAxes,

            // Shadow catcher
            shadowCatcherEnabled: this.shadowCatcher ? this.shadowCatcher.enabled : false,

            // Mesh instance shadow settings
            meshInstanceShadowSettings: this.entities
                .map((entity) => {
                    const renderComponent = entity.render;
                    if (!renderComponent) return null;
                    return renderComponent.meshInstances.map((meshInstance: any) => ({
                        castShadows: meshInstance.castShadows,
                        receiveShadows: meshInstance.receiveShadows,
                    }));
                })
                .filter(Boolean),

            // Background color (from CSS)
            canvasWrapperBgColor: canvasWrapper ? canvasWrapper.style.backgroundColor : '',
        };
    }

    // Enhanced transparent background setup
    private setupTransparentBackground(usePhotographyLighting: boolean = true) {
        console.log('[setupTransparentBackground] ========== START ==========');
        const camPosBefore = this.camera.getPosition();
        const camRotBefore = this.camera.getEulerAngles();
        console.log(
            `[setupTransparentBackground] Camera BEFORE: pos=(${camPosBefore.x.toFixed(
                2
            )}, ${camPosBefore.y.toFixed(2)}, ${camPosBefore.z.toFixed(
                2
            )}), rot=(${camRotBefore.x.toFixed(2)}, ${camRotBefore.y.toFixed(
                2
            )}, ${camRotBefore.z.toFixed(2)})`
        );

        // Set camera clear color to transparent
        this.camera.camera.clearColor = new Color(0, 0, 0, 0); // Transparent black
        console.log('[setupTransparentBackground] Set clear color to transparent');

        const camPosAfterClear = this.camera.getPosition();
        console.log(
            `[setupTransparentBackground] Camera AFTER clear color: pos=(${camPosAfterClear.x.toFixed(
                2
            )}, ${camPosAfterClear.y.toFixed(2)}, ${camPosAfterClear.z.toFixed(2)})`
        );

        // Disable skybox layer
        const skyboxLayer = this.app.scene.layers.getLayerById(LAYERID_SKYBOX);
        if (skyboxLayer) {
            skyboxLayer.enabled = false;
            console.log('[setupTransparentBackground] Disabled skybox layer');
        }

        const camPosAfterSkybox = this.camera.getPosition();
        console.log(
            `[setupTransparentBackground] Camera AFTER skybox disable: pos=(${camPosAfterSkybox.x.toFixed(
                2
            )}, ${camPosAfterSkybox.y.toFixed(2)}, ${camPosAfterSkybox.z.toFixed(2)})`
        );

        // Temporarily remove skybox but preserve lighting
        this.app.scene.skybox = null;
        console.log('[setupTransparentBackground] Removed skybox from scene');
        // Keep envAtlas for environmental lighting
        // this.app.scene.envAtlas = null; // Comment this out to preserve lighting

        const camPosAfterRemove = this.camera.getPosition();
        console.log(
            `[setupTransparentBackground] Camera AFTER skybox remove: pos=(${camPosAfterRemove.x.toFixed(
                2
            )}, ${camPosAfterRemove.y.toFixed(2)}, ${camPosAfterRemove.z.toFixed(2)})`
        );

        // Optionally apply photography lighting. Top-down disables this to avoid extra highlights.
        if (usePhotographyLighting) {
            this.setupPhotographyLighting();
        }

        const camPosAfterLighting = this.camera.getPosition();
        console.log(
            `[setupTransparentBackground] Camera AFTER lighting setup: pos=(${camPosAfterLighting.x.toFixed(
                2
            )}, ${camPosAfterLighting.y.toFixed(2)}, ${camPosAfterLighting.z.toFixed(2)})`
        );

        // Hide debug elements
        if (this.showGrid) this.setDebugGrid(false);
        if (this.showBounds) this.setDebugBounds(false);
        if (this.showSkeleton) this.setDebugSkeleton(false);
        if (this.showAxes) this.setDebugAxes(false);
        console.log('[setupTransparentBackground] Hidden debug elements');

        const camPosAfterDebug = this.camera.getPosition();
        console.log(
            `[setupTransparentBackground] Camera AFTER debug hide: pos=(${camPosAfterDebug.x.toFixed(
                2
            )}, ${camPosAfterDebug.y.toFixed(2)}, ${camPosAfterDebug.z.toFixed(2)})`
        );

        // Disable shadow catcher (ground plane)
        if (this.shadowCatcher) {
            this.setShadowCatcherEnabled(false);
            // Also make the shadow catcher plane invisible
            if (this.shadowCatcher.plane) {
                this.shadowCatcher.plane.enabled = false;
            }
            console.log(
                '[setupTransparentBackground] Disabled shadow catcher and made plane invisible'
            );
        }

        // Disable shadows on mesh instances
        this.disableShadowsForScreenshot();

        // Disable shadows on all mesh instances (no self-shadows or shadows on ground)
        let shadowDisabledCount = 0;
        this.entities.forEach((entity) => {
            const renderComponent = entity.render;
            if (renderComponent) {
                renderComponent.meshInstances.forEach((meshInstance: any) => {
                    if (meshInstance.castShadows) {
                        meshInstance.castShadows = false;
                        shadowDisabledCount++;
                    }
                    if (meshInstance.receiveShadows) {
                        meshInstance.receiveShadows = false;
                    }
                });
            }
        });
        if (shadowDisabledCount > 0) {
            console.log(
                `[setupTransparentBackground] Disabled shadows on ${shadowDisabledCount} mesh instances`
            );
        }

        const camPosAfterShadow = this.camera.getPosition();
        console.log(
            `[setupTransparentBackground] Camera AFTER shadow disable: pos=(${camPosAfterShadow.x.toFixed(
                2
            )}, ${camPosAfterShadow.y.toFixed(2)}, ${camPosAfterShadow.z.toFixed(2)})`
        );

        // Set canvas wrapper background to transparent
        const canvasWrapper = document.getElementById('canvas-wrapper');
        if (canvasWrapper) {
            canvasWrapper.style.backgroundColor = 'transparent';
            console.log(
                '[setupTransparentBackground] Set canvas wrapper background to transparent'
            );
        }

        const camPosAfter = this.camera.getPosition();
        const camRotAfter = this.camera.getEulerAngles();
        console.log(
            `[setupTransparentBackground] Camera AFTER ALL: pos=(${camPosAfter.x.toFixed(
                2
            )}, ${camPosAfter.y.toFixed(2)}, ${camPosAfter.z.toFixed(
                2
            )}), rot=(${camRotAfter.x.toFixed(2)}, ${camRotAfter.y.toFixed(
                2
            )}, ${camRotAfter.z.toFixed(2)})`
        );
        console.log('[setupTransparentBackground] ========== END ==========');
    }

    // Disable shadows specifically for screenshots (called for each capture in batch mode)
    private disableShadowsForScreenshot() {
        console.log('[disableShadowsForScreenshot] Disabling ALL shadow casting for screenshot...');

        // Disable shadow casting on ALL lights but keep them enabled for illumination
        this.app.root.findComponents('light').forEach((lightComponent: any) => {
            lightComponent.castShadows = false;
            console.log('[disableShadowsForScreenshot] Disabled shadow casting on a light');
        });

        // Set moderate ambient lighting to ensure good illumination without shadows
        this.app.scene.ambientLight = new Color(0.35, 0.35, 0.35); // Reduced ambient light to prevent blown-out highlights
        console.log('[disableShadowsForScreenshot] Set moderate ambient lighting');

        // Disable shadows on all mesh instances (no self-shadows or shadows on ground)
        let shadowDisabledCount = 0;
        this.entities.forEach((entity) => {
            const renderComponent = entity.render;
            if (renderComponent) {
                renderComponent.meshInstances.forEach((meshInstance: any) => {
                    if (meshInstance.castShadows) {
                        meshInstance.castShadows = false;
                        shadowDisabledCount++;
                    }
                    if (meshInstance.receiveShadows) {
                        meshInstance.receiveShadows = false;
                    }
                });
            }
        });

        if (shadowDisabledCount > 0) {
            console.log(
                `[disableShadowsForScreenshot] Disabled shadows on ${shadowDisabledCount} mesh instances`
            );
        } else {
            console.log('[disableShadowsForScreenshot] No shadows to disable on mesh instances');
        }
    }

    // Setup optimal lighting for photography
    private setupPhotographyLighting() {
        console.log('[setupPhotographyLighting] ========== START ==========');
        const camPosBefore = this.camera.getPosition();
        console.log(
            `[setupPhotographyLighting] Camera BEFORE: pos=(${camPosBefore.x.toFixed(
                2
            )}, ${camPosBefore.y.toFixed(2)}, ${camPosBefore.z.toFixed(2)})`
        );

        // Ensure the main directional light is enabled
        this.light.enabled = true;
        console.log('[setupPhotographyLighting] Enabled main directional light');

        // Store original light settings to restore later
        if (!this.originalLightSettings) {
            this.originalLightSettings = {
                intensity: this.light.light.intensity,
                color: this.light.light.color.clone(),
                castShadows: this.light.light.castShadows,
                position: this.light.getLocalPosition().clone(),
                rotation: this.light.getLocalEulerAngles().clone(),
            };
            console.log('[setupPhotographyLighting] Stored original light settings');
        }

        const camPosAfterStore = this.camera.getPosition();
        console.log(
            `[setupPhotographyLighting] Camera AFTER storing light settings: pos=(${camPosAfterStore.x.toFixed(
                2
            )}, ${camPosAfterStore.y.toFixed(2)}, ${camPosAfterStore.z.toFixed(2)})`
        );

        // Configure light for optimal model visibility
        this.light.light.intensity = 0.75; // Reduced intensity to prevent blown-out highlights
        this.light.light.color = new Color(1, 1, 1); // Pure white light
        this.light.light.castShadows = false; // Disable shadows for cleaner look
        console.log('[setupPhotographyLighting] Configured light properties');

        const camPosAfterConfig = this.camera.getPosition();
        console.log(
            `[setupPhotographyLighting] Camera AFTER light config: pos=(${camPosAfterConfig.x.toFixed(
                2
            )}, ${camPosAfterConfig.y.toFixed(2)}, ${camPosAfterConfig.z.toFixed(2)})`
        );

        // Position light for optimal side-view illumination
        // Place light coming from front-left and slightly above
        this.light.setLocalPosition(-5, 5, 5);
        console.log('[setupPhotographyLighting] Set light position to (-5, 5, 5)');

        const camPosAfterLightPos = this.camera.getPosition();
        console.log(
            `[setupPhotographyLighting] Camera AFTER light setLocalPosition: pos=(${camPosAfterLightPos.x.toFixed(
                2
            )}, ${camPosAfterLightPos.y.toFixed(2)}, ${camPosAfterLightPos.z.toFixed(2)})`
        );

        this.light.lookAt(new Vec3(0, 0, 0)); // Point towards model center
        console.log('[setupPhotographyLighting] Called light.lookAt(0,0,0)');

        const camPosAfterLightLookAt = this.camera.getPosition();
        console.log(
            `[setupPhotographyLighting] Camera AFTER light.lookAt: pos=(${camPosAfterLightLookAt.x.toFixed(
                2
            )}, ${camPosAfterLightLookAt.y.toFixed(2)}, ${camPosAfterLightLookAt.z.toFixed(2)})`
        );

        // Add ambient lighting by increasing scene ambient
        this.app.scene.ambientLight = new Color(0.4, 0.4, 0.4); // Add ambient light
        console.log('[setupPhotographyLighting] Set scene ambient light');

        const camPosAfter = this.camera.getPosition();
        console.log(
            `[setupPhotographyLighting] Camera AFTER ALL: pos=(${camPosAfter.x.toFixed(
                2
            )}, ${camPosAfter.y.toFixed(2)}, ${camPosAfter.z.toFixed(2)})`
        );
        console.log('[setupPhotographyLighting] ========== END ==========');
    }

    // Add property to store original light settings
    private originalLightSettings: {
        intensity: number;
        color: Color;
        castShadows: boolean;
        position: Vec3;
        rotation: Vec3;
    } | null = null;

    // Track batch screenshot mode state
    private isBatchScreenshotMode: boolean = false;
    private batchScreenshotState: any | null = null;

    // Enhanced state restoration
    private restoreRenderingState(state: any) {
        console.log('Restoring original rendering state...');

        // Restore camera clear color
        this.camera.camera.clearColor = state.clearColor;

        // Restore skybox
        const skyboxLayer = this.app.scene.layers.getLayerById(LAYERID_SKYBOX);
        if (skyboxLayer) {
            skyboxLayer.enabled = state.skyboxEnabled;
        }
        this.app.scene.skybox = state.skybox;
        this.app.scene.envAtlas = state.envAtlas;

        // Restore lighting settings
        this.app.scene.ambientLight = state.ambientLight;
        this.light.enabled = state.lightEnabled;
        this.light.light.intensity = state.lightIntensity;
        this.light.light.color = state.lightColor;
        this.light.light.castShadows = state.lightCastShadows;
        this.light.setLocalPosition(state.lightPosition);
        this.light.setLocalEulerAngles(
            state.lightRotation.x,
            state.lightRotation.y,
            state.lightRotation.z
        );

        // Restore debug elements
        this.setDebugGrid(state.showGrid);
        this.setDebugBounds(state.showBounds);
        this.setDebugSkeleton(state.showSkeleton);
        this.setDebugAxes(state.showAxes);

        // Restore shadow catcher
        if (this.shadowCatcher) {
            this.setShadowCatcherEnabled(state.shadowCatcherEnabled);
        }

        // Restore mesh instance shadow settings
        if (state.meshInstanceShadowSettings) {
            let shadowRestoredCount = 0;
            this.entities.forEach((entity, entityIndex) => {
                const renderComponent = entity.render;
                if (renderComponent && state.meshInstanceShadowSettings[entityIndex]) {
                    renderComponent.meshInstances.forEach(
                        (meshInstance: any, meshIndex: number) => {
                            if (state.meshInstanceShadowSettings[entityIndex][meshIndex]) {
                                meshInstance.castShadows =
                                    state.meshInstanceShadowSettings[entityIndex][
                                        meshIndex
                                    ].castShadows;
                                meshInstance.receiveShadows =
                                    state.meshInstanceShadowSettings[entityIndex][
                                        meshIndex
                                    ].receiveShadows;
                                shadowRestoredCount++;
                            }
                        }
                    );
                }
            });
            if (shadowRestoredCount > 0) {
                console.log(`Restored shadow settings on ${shadowRestoredCount} mesh instances`);
            }
        }

        // Restore canvas wrapper background
        const canvasWrapper = document.getElementById('canvas-wrapper');
        if (canvasWrapper) {
            canvasWrapper.style.backgroundColor = state.canvasWrapperBgColor;
        }

        // Clear the stored original light settings
        this.originalLightSettings = null;

        // Force a render to restore the view
        this.renderNextFrame();

        console.log('Original rendering state restored');
    }

    // Start batch screenshot mode - sets up background/lighting once and caches state
    startBatchScreenshotMode() {
        if (this.isBatchScreenshotMode) {
            console.log('[startBatchScreenshotMode] Already in batch mode, skipping');
            return;
        }

        console.log(
            '[startBatchScreenshotMode] Starting batch screenshot mode - caching rendering state'
        );
        this.isBatchScreenshotMode = true;
        this.batchScreenshotState = this.saveRenderingState();

        // CRITICAL: Disable multiframe accumulation for batch mode to prevent ghosting
        // The multiframe system blends multiple frames together for anti-aliasing,
        // but this causes old camera angles to bleed into new captures
        if (this.multiframe) {
            // Save the original state so we can restore it later
            (this as any)._batchOriginalMultiframeEnabled = this.multiframe.enabled;
            (this as any)._batchOriginalMultiframeBlend = this.multiframe.blend;
            
            this.multiframe.enabled = false;
            this.multiframe.blend = 1.0;
            this.multiframe.moved(); // Reset any accumulated frames
            console.log('[startBatchScreenshotMode] Multiframe accumulation DISABLED for batch mode');
        }

        // Set up transparent background and lighting once.
        // Lighting mode can be controlled externally via observer for batch screenshot use-cases.
        const usePhotographyLighting = this.observer.get('screenshot.usePhotographyLighting');
        this.setupTransparentBackground(usePhotographyLighting !== false);
        console.log('[startBatchScreenshotMode] Background and lighting set up for batch mode');
    }

    // End batch screenshot mode - restores original state
    endBatchScreenshotMode() {
        if (!this.isBatchScreenshotMode) {
            console.log('[endBatchScreenshotMode] Not in batch mode, skipping');
            return;
        }

        console.log(
            '[endBatchScreenshotMode] Ending batch screenshot mode - restoring rendering state'
        );
        if (this.batchScreenshotState) {
            this.restoreRenderingState(this.batchScreenshotState);
            this.batchScreenshotState = null;
        }
        
        // Restore multiframe settings
        if (this.multiframe) {
            const originalEnabled = (this as any)._batchOriginalMultiframeEnabled;
            const originalBlend = (this as any)._batchOriginalMultiframeBlend;
            if (originalEnabled !== undefined) {
                this.multiframe.enabled = originalEnabled;
            }
            if (originalBlend !== undefined) {
                this.multiframe.blend = originalBlend;
            }
            console.log(`[endBatchScreenshotMode] Multiframe restored (enabled: ${this.multiframe.enabled}, blend: ${this.multiframe.blend})`);
        }
        
        // Restore camera controls update function if it was disabled
        const originalCameraControlsUpdate = (this as any)._originalCameraControlsUpdate;
        if (originalCameraControlsUpdate) {
            // @ts-ignore
            this.cameraControls.update = originalCameraControlsUpdate;
            (this as any)._originalCameraControlsUpdate = null;
            console.log('[endBatchScreenshotMode] Camera controls update function restored');
        }
        
        this.isBatchScreenshotMode = false;
        console.log('[endBatchScreenshotMode] Batch mode ended, state restored');
    }

    downloadSpecialPngScreenshot() {
        // Save current camera state
        const originalState = this.saveCameraState();

        // Calculate scene bounds
        const bounds = this.dynamicSceneBounds;
        const center = bounds.center.clone();
        const size = bounds.halfExtents.length() * 2;

        // Position camera to the side of the model (fixed side view along Z-axis)
        const distance = Math.max(size * 1.5, 10); // Ensure good viewing distance

        // Create side position - place camera to the side of the model (negative Z for side profile)
        // Aircraft nose-tail is likely along X-axis, so we view from Z-axis for side profile
        const sidePosition = new Vec3(center.x, center.y, center.z - distance);

        // Keep perspective projection to match the reference image
        // The reference shows "Perspective" projection
        this.camera.camera.projection = 0; // PROJECTION_PERSPECTIVE = 0
        this.camera.camera.fov = 75; // Standard FOV

        // Position and orient camera
        this.camera.setPosition(sidePosition);
        this.camera.lookAt(center);

        // Ensure camera is upright (Y-axis up)
        const up = new Vec3(0, 1, 0);
        this.camera.lookAt(center, up);

        // Adjust clipping planes for the new position
        this.fitCameraClipPlanes();

        // Force a render to update the view
        this.renderNextFrame();

        // Wait a frame for the render to complete, then capture
        setTimeout(() => {
            const texture = this.camera.camera.renderTarget.colorBuffer;
            texture.read(0, 0, texture.width, texture.height).then((typedArray: Uint32Array) => {
                // Generate timestamp for filename
                const now = new Date();
                const timestamp = now
                    .toISOString()
                    .replace(/:/g, '-')
                    .replace(/\..+/, '')
                    .replace('T', '_');
                const filename = `if-model-viewer_side-view_${timestamp}.png`;

                this.pngExporter.export(
                    filename,
                    new Uint32Array(typedArray.buffer.slice(0)),
                    texture.width,
                    texture.height
                );

                // Restore original camera state
                this.restoreCameraState(originalState);

                // Force another render to restore the view
                this.renderNextFrame();
            });
        }, 50); // Small delay to ensure render completes
    }

    // adjust camera clipping planes to fit the scene
    fitCameraClipPlanes() {
        if (this.xrMode?.active) {
            return;
        }

        const mat = this.camera.getWorldTransform();

        const cameraPosition = mat.getTranslation();
        const cameraForward = mat.getZ();

        const bound = this.dynamicSceneBounds;
        const boundCenter = bound.center;
        const boundRadius = bound.halfExtents.length() * 2;

        vec.sub2(boundCenter, cameraPosition);
        const dist = -vec.dot(cameraForward);

        const far = dist + boundRadius;
        const near = Math.max(0.001, dist < boundRadius ? far / 1024 : dist - boundRadius);

        this.camera.camera.nearClip = near;
        this.camera.camera.farClip = far;
        this.light.light.shadowDistance = far;
        this.light.light.normalOffsetBias = far / 1024;
    }

    // load gltf model given its url and list of external urls
    private loadGltf(gltfUrl: File, externalUrls: Array<File>) {
        // Load raw glTF JSON in parallel to build animation-node mapping and check for normal maps
        if (gltfUrl.filename && gltfUrl.filename.toLowerCase().endsWith('.gltf')) {
            this.parseGltfAnimationData(gltfUrl.url);
        }

        return new Promise((resolve, reject) => {
            // provide buffer view callback so we can handle models compressed with MeshOptimizer
            // https://github.com/zeux/meshoptimizer
            const processBufferView = function (
                gltfBuffer: any,
                buffers: Array<any>,
                continuation: (err: string, result: any) => void
            ) {
                if (gltfBuffer.extensions && gltfBuffer.extensions.EXT_meshopt_compression) {
                    const extensionDef = gltfBuffer.extensions.EXT_meshopt_compression;

                    Promise.all([MeshoptDecoder.ready, buffers[extensionDef.buffer]]).then(
                        (promiseResult) => {
                            const buffer = promiseResult[1];

                            const byteOffset = extensionDef.byteOffset || 0;
                            const byteLength = extensionDef.byteLength || 0;

                            const count = extensionDef.count;
                            const stride = extensionDef.byteStride;

                            const result = new Uint8Array(count * stride);
                            const source = new Uint8Array(
                                buffer.buffer,
                                buffer.byteOffset + byteOffset,
                                byteLength
                            );

                            MeshoptDecoder.decodeGltfBuffer(
                                result,
                                count,
                                stride,
                                source,
                                extensionDef.mode,
                                extensionDef.filter
                            );

                            continuation(null, result);
                        }
                    );
                } else {
                    continuation(null, null);
                }
            };

            const bufferViewIndices = new WeakMap<any, number>();
            const capturedBufferViews: any[] = [];
            let nextBufferViewIndex = 0;

            const preProcessBufferView = (gltfBufferView: any) => {
                bufferViewIndices.set(gltfBufferView, nextBufferViewIndex++);
            };

            const postProcessBufferView = (gltfBufferView: any, typedArray: any) => {
                const index = bufferViewIndices.get(gltfBufferView);
                if (index !== undefined) {
                    capturedBufferViews[index] = typedArray;
                }
            };

            const postProcessGlobal = (_gltf: any, result: any) => {
                result.__ifBufferViews = capturedBufferViews;
            };

            const processImage = function (
                gltfImage: any,
                continuation: (err: string, result: any) => void
            ) {
                const imageUri = gltfImage.uri || '';
                const isNormalMap = imageUri.toLowerCase().includes('normal');

                if (!imageUri) {
                    console.warn('[Viewer] No URI in gltfImage, skipping');
                    continuation(null, null);
                    return;
                }

                const normalizedUri = decodeURIComponent(path.normalize(imageUri));
                const uriFilename =
                    imageUri.split('/').pop() || imageUri.split('\\').pop() || imageUri;

                // Try multiple matching strategies
                let u: File | undefined;

                // 1. Exact match (full path)
                u = fileMap.get(normalizedUri.toLowerCase());

                // 2. Filename match
                if (!u) {
                    u = fileMap.get(uriFilename.toLowerCase());
                }

                // 3. Try array search as fallback
                if (!u) {
                    u = externalUrls.find((url) => {
                        const urlFilename =
                            url.filename.split('/').pop() ||
                            url.filename.split('\\').pop() ||
                            url.filename;
                        return urlFilename.toLowerCase() === uriFilename.toLowerCase();
                    });
                }

                if (u) {
                    const external = u;
                    this.prepareTextureUrl(external.url, external.filename).then((textureUrl) => {
                        const textureAsset = new Asset(external.filename, 'texture', {
                            url: textureUrl,
                            filename: external.filename,
                        });
                        textureAsset.on('load', () => {
                            Viewer.releasePreparedTextureUrl(textureUrl, external.url);
                            continuation(null, textureAsset);
                        });
                        textureAsset.on('error', (err: string) => {
                            Viewer.releasePreparedTextureUrl(textureUrl, external.url);
                            console.error(`[Viewer] Error loading texture: ${external.filename}`, err);
                            continuation(err, null);
                        });
                        this.app.assets.add(textureAsset);
                        this.app.assets.load(textureAsset);
                    });
                } else {
                    console.error(`[Viewer] Texture not found for URI: ${imageUri}`);
                    continuation(null, null);
                }
            };

            const postProcessImage = (gltfImage: any, textureAsset: Asset) => {
                // max anisotropy on all textures
                (textureAsset.resource as Texture).anisotropy = this.app.graphicsDevice.maxAnisotropy;

                // Log ALL texture details for debugging
                const texture = textureAsset.resource as Texture;
                const isNormalMap =
                    textureAsset.name?.toLowerCase().includes('normal') ||
                    gltfImage?.uri?.toLowerCase().includes('normal');

                console.log(
                    `[TEXTURE LOAD] ${isNormalMap ? '🔵 NORMAL MAP' : '📦'} Texture loaded:`
                );
                console.log(`[TEXTURE LOAD]   - Name: ${textureAsset.name}`);
                console.log(`[TEXTURE LOAD]   - URI: ${gltfImage?.uri || 'embedded'}`);
                console.log(`[TEXTURE LOAD]   - Size: ${texture.width}x${texture.height}`);
                console.log(`[TEXTURE LOAD]   - Format: ${texture.format}`);
                console.log(`[TEXTURE LOAD]   - Type: ${texture.type}`);
                console.log(`[TEXTURE LOAD]   - Mipmaps: ${texture.mipmaps}`);
                console.log(
                    `[TEXTURE LOAD]   - MinFilter: ${texture.minFilter}, MagFilter: ${texture.magFilter}`
                );

                // Ensure texture asset has proper file information for livery system
                if (!textureAsset.file) {
                    // Try to find the corresponding file from the external URLs
                    if (gltfImage && gltfImage.uri) {
                        const normalizedUri = decodeURIComponent(path.normalize(gltfImage.uri));
                        const matchingFile = externalUrls.find((url) => {
                            return url.filename === normalizedUri;
                        });

                        if (matchingFile) {
                            // Add file information to the texture asset
                            (textureAsset as any).file = {
                                url: matchingFile.url,
                                filename: matchingFile.filename,
                            };
                        } else {
                            // Fallback: use the asset name as filename
                            (textureAsset as any).file = {
                                url: '',
                                filename: textureAsset.name,
                            };
                        }
                    } else {
                        // Fallback: use the asset name as filename
                        (textureAsset as any).file = {
                            url: '',
                            filename: textureAsset.name,
                        };
                    }
                }
            };

            const processBuffer = function (
                gltfBuffer: any,
                continuation: (err: string, result: any) => void
            ) {
                const u = externalUrls.find((url) => {
                    return (
                        url.filename === decodeURIComponent(path.normalize(gltfBuffer.uri || ''))
                    );
                });
                if (u) {
                    const bufferAsset = new Asset(u.filename, 'binary', {
                        url: u.url,
                        filename: u.filename,
                    });
                    bufferAsset.on('load', () => {
                        continuation(null, new Uint8Array(bufferAsset.resource as ArrayBuffer));
                    });
                    this.app.assets.add(bufferAsset);
                    this.app.assets.load(bufferAsset);
                } else {
                    continuation(null, null);
                }
            };

            // Create a map of filenames to blob URLs for quick lookup
            const fileMap = new Map<string, File>();
            console.log(
                '[Viewer.loadGltf] Building file map from',
                externalUrls.length,
                'external URLs'
            );
            externalUrls.forEach((file, idx) => {
                // Store both full path and just filename for matching
                const filename =
                    file.filename.split('/').pop() ||
                    file.filename.split('\\').pop() ||
                    file.filename;
                fileMap.set(file.filename.toLowerCase(), file);
                fileMap.set(filename.toLowerCase(), file);
                // Also store with decoded filename
                try {
                    fileMap.set(decodeURIComponent(file.filename).toLowerCase(), file);
                    fileMap.set(decodeURIComponent(filename).toLowerCase(), file);
                } catch (e) {
                    // Ignore decode errors
                }
                if (idx < 10) {
                    console.log(
                        `[Viewer.loadGltf]   [${idx}] Added to map: ${file.filename} (filename: ${filename})`
                    );
                }
            });
            console.log('[Viewer.loadGltf] Created file map with', fileMap.size, 'entries');
            const imageEntries = Array.from(fileMap.keys()).filter((k) =>
                /\.(png|jpg|jpeg|webp|tga|bmp|gif|ktx2)$/i.test(k)
            );
            console.log('[Viewer.loadGltf] Image entries in file map:', imageEntries.length);
            if (imageEntries.length > 0) {
                console.log('[Viewer.loadGltf] First 10 image entries:', imageEntries.slice(0, 10));
            }

            const containerAsset = new Asset(gltfUrl.filename, 'container', gltfUrl, null, {
                // @ts-ignore TODO no definition in pc
                bufferView: {
                    preprocess: preProcessBufferView,
                    processAsync: processBufferView.bind(this),
                    postprocess: postProcessBufferView,
                },
                image: {
                    processAsync: processImage.bind(this),
                    postprocess: postProcessImage,
                },
                buffer: {
                    processAsync: processBuffer.bind(this),
                },
                global: {
                    postprocess: postProcessGlobal,
                },
            });
            containerAsset.on('load', () => {
                console.log('[MATERIAL CHECK] ========================================');
                console.log('[MATERIAL CHECK] glTF container loaded, inspecting materials...');
                const resource = containerAsset.resource as any;
                if (resource && resource.renders) {
                    console.log(
                        `[MATERIAL CHECK] Found ${resource.renders.length} render resources`
                    );
                    resource.renders.forEach((render: any, idx: number) => {
                        if (render.material) {
                            const mat = render.material;
                            console.log(
                                `[MATERIAL CHECK] Render ${idx}: Material "${
                                    mat.name || 'Unnamed'
                                }"`
                            );
                            console.log(`[MATERIAL CHECK]   - Has normalMap: ${!!mat.normalMap}`);
                            if (mat.normalMap) {
                                console.log(
                                    `[MATERIAL CHECK]   - Normal map name: ${mat.normalMap.name}`
                                );
                                console.log(
                                    `[MATERIAL CHECK]   - Normal map size: ${mat.normalMap.width}x${mat.normalMap.height}`
                                );
                            }
                            console.log(`[MATERIAL CHECK]   - Has diffuseMap: ${!!mat.diffuseMap}`);
                            console.log(
                                `[MATERIAL CHECK]   - Has emissiveMap: ${!!mat.emissiveMap}`
                            );
                            console.log(
                                `[MATERIAL CHECK]   - Has metallicMap: ${!!mat.metallicMap}`
                            );
                        }
                    });
                }
                console.log('[MATERIAL CHECK] ========================================');
                resolve(containerAsset);
            });
            containerAsset.on('error', (err: string) => reject(err));
            this.app.assets.add(containerAsset);
            this.app.assets.load(containerAsset);
        });
    }

    private loadPly(url: File, externalUrls: Array<File>) {
        const urls: any = {};
        externalUrls.forEach((url) => {
            urls[url.filename] = url.url;
        });
        return new Promise((resolve, reject) => {
            const asset = new Asset(url.filename, 'gsplat', url, null, {
                // @ts-ignore TODO no definition in pc
                mapUrl: (mapUrl) => urls[mapUrl],
            });
            asset.on('load', () => resolve(asset));
            asset.on('error', (err: string) => reject(err));
            this.app.assets.add(asset);
            this.app.assets.load(asset);
        });
    }

    // returns true if the filename has one of the recognized model extensions
    isModelFilename(filename: string) {
        const parts = filename.split('?')[0].split('/').pop().split('.');
        const result = parts.length === 1 || modelExtensions.includes(parts.pop().toLowerCase());
        return result;
    }

    // extracts LOD level from filename (e.g., "Aircraft_LOD_0.gltf" -> 0)
    extractLODLevel(filename: string): number | null {
        const basename = filename.split('?')[0].split('/').pop();
        // Match patterns like LOD_0, LOD_1, LOD_2, etc.
        const lodMatch = basename.match(/LOD_(\d+)/i);
        return lodMatch ? parseInt(lodMatch[1], 10) : null;
    }

    // returns true if the filename contains LOD pattern
    isLODFilename(filename: string): boolean {
        return this.extractLODLevel(filename) !== null;
    }

    // groups files by LOD and returns sorted LOD data
    detectLODs(
        files: Array<File>
    ): Array<{ name: string; filename: string; url: string; level: number }> {
        const lodFiles = files.filter(
            (file) => this.isLODFilename(file.filename) && this.isModelFilename(file.filename)
        );
        console.log(
            'LOD files found:',
            lodFiles.map((f) => f.filename)
        );

        const lods = lodFiles
            .map((file) => {
                const level = this.extractLODLevel(file.filename);
                const basename = file.filename.split('/').pop();
                const lodData = {
                    name: basename,
                    filename: basename,
                    url: file.url,
                    level: level,
                };
                console.log('Created LOD data:', lodData);
                return lodData;
            })
            .filter((lod) => lod.level !== null);

        // Sort by LOD level (0 = highest detail first)
        const sortedLods = lods.sort((a, b) => a.level - b.level);
        console.log('Final sorted LODs:', sortedLods);
        return sortedLods;
    }

    // detect and load AircraftConfiguration.json file (case-insensitive)
    detectAndLoadAircraftConfiguration(files: Array<File>) {
        const configFile = files.find(
            (file) => file.filename?.toLowerCase() === 'aircraftconfiguration.json'
        );

        if (configFile) {
            // Set loading state
            this.observer.set('aircraftConfig.loaded', false);
            this.observer.set('aircraftConfig.error', null);

            fetch(configFile.url)
                .then((response) => response.json())
                .then((configuration) => {
                    console.log('AircraftConfiguration.json loaded:', configuration);
                    // Migrate deprecated AnimationName to AnimationNames
                    this.migrateAnimationNamesInConfiguration(configuration);
                    this.observer.set('aircraftConfig.configuration', configuration);
                    this.observer.set('aircraftConfig.loaded', true);

                    // Extract and load aircraft cameras
                    this.loadAircraftCameras(configuration);

                    // Reset animations to neutral when config loads (in case config loads after model)
                    setTimeout(() => {
                        this.resetAnimationsToNeutral();
                    }, 100);
                })
                .catch((error) => {
                    console.error('Failed to load AircraftConfiguration.json:', error);
                    this.observer.set('aircraftConfig.error', error.message);
                    this.observer.set('aircraftConfig.loaded', false);
                });
        } else {
            // No AircraftConfiguration.json file found, clear any existing data
            this.observer.set('aircraftConfig.loaded', false);
            this.observer.set('aircraftConfig.configuration', null);
            this.observer.set('aircraftConfig.error', null);
            this.observer.set('aircraftCameras', []);
        }
    }

    // Extract aircraft cameras from configuration
    private loadAircraftCameras(configuration: any) {
        console.log(
            'loadAircraftCameras called with configuration:',
            configuration ? 'exists' : 'null'
        );

        if (!configuration) {
            console.log('No configuration, setting empty cameras array');
            this.observer.set('aircraftCameras', []);
            return;
        }

        // Handle both camelCase and PascalCase
        const cameras = configuration.aircraftCameras || configuration.AircraftCameras || [];

        console.log('Found cameras in config:', cameras);
        console.log('Cameras count:', cameras.length);
        console.log('Is array:', Array.isArray(cameras));

        if (Array.isArray(cameras) && cameras.length > 0) {
            console.log(`Loaded ${cameras.length} aircraft cameras`);
            cameras.forEach((cam: any, idx: number) => {
                console.log(`  Camera ${idx}: ${cam.cameraName || cam.CameraName}`);
            });
            this.observer.set('aircraftCameras', cameras);
            console.log('Set aircraftCameras in observer to:', cameras);
        } else {
            console.log('No cameras found or empty array, setting empty cameras array');
            this.observer.set('aircraftCameras', []);
        }

        // Verify it was set
        const setCameras = this.observer.get('aircraftCameras');
        console.log('Verified aircraftCameras in observer:', setCameras);
    }

    // Move camera to a specific aircraft camera position
    moveCameraToAircraftCamera(camera: any) {
        if (!camera) return;

        // Get position (handle both camelCase and PascalCase)
        const position = camera.position || camera.Position;
        if (!position) {
            console.warn('Camera has no position data');
            return;
        }

        // Extract coordinates (handle both lowercase and uppercase property names)
        let x = position.x !== undefined ? position.x : position.X;
        let y = position.y !== undefined ? position.y : position.Y;
        let z = position.z !== undefined ? position.z : position.Z;

        if (x === undefined || y === undefined || z === undefined) {
            console.warn('Camera position has missing coordinates');
            return;
        }

        // Account for altitude offset that may have been applied to the scene
        // BUT: If offsets have been removed for screenshot (aircraft is at original position),
        // we should NOT apply the offset to the camera, as the camera positions in the config
        // are relative to the aircraft's original position, not the offset position.
        const aircraftConfig = this.observer.get('aircraftConfig.configuration');
        const altitudeOffset =
            aircraftConfig?.resetAltitudeOffset || aircraftConfig?.ResetAltitudeOffset || 0;
        const isCentered = this.observer.get('centerScene');

        // Check if offsets have been removed (aircraft is at original position)
        // We can detect this by checking if the scene root or entities are at their original positions
        const offsetsRemoved =
            (isCentered &&
                this.originalSceneRootPosition !== null &&
                this.sceneRoot.getLocalPosition().equals(this.originalSceneRootPosition)) ||
            (!isCentered &&
                this.originalEntityTransforms.length > 0 &&
                this.entities.length > 0 &&
                this.entities[0]
                    .getLocalPosition()
                    .equals(this.originalEntityTransforms[0].position));

        console.log(
            `Altitude offset: ${altitudeOffset}, isCentered: ${isCentered}, offsetsRemoved: ${offsetsRemoved}`
        );

        // Only apply altitude offset if it was applied AND offsets haven't been removed
        // (i.e., we're not in screenshot mode where aircraft is at original position)
        if (this.altitudeOffsetApplied && altitudeOffset && !offsetsRemoved) {
            if (isCentered) {
                // Scene root was moved up, so camera positions need to move up too
                y += altitudeOffset;
                console.log(`Applied altitude offset to camera Y position: ${y}`);
            } else {
                // Individual entities were moved up
                y += altitudeOffset;
                console.log(`Applied altitude offset to camera Y position: ${y}`);
            }
        } else if (offsetsRemoved) {
            console.log(
                `Offsets have been removed - using camera position from config without altitude offset adjustment`
            );
        }

        // Get angles (handle both camelCase and PascalCase)
        // Angles in config are typically in degrees, convert to radians for Math functions
        let angleX = camera.angleX !== undefined ? camera.angleX : camera.AngleX || 0;
        let angleY = camera.angleY !== undefined ? camera.angleY : camera.AngleY || 0;

        // Convert from degrees to radians if the values look like degrees (typically -90 to 90 for pitch, -180 to 180 for yaw)
        // If angles are already in radians, they'll be small values (< 2), so this check should work
        if (Math.abs(angleX) > 2 || Math.abs(angleY) > 2) {
            // Values are likely in degrees, convert to radians
            angleX = (angleX * Math.PI) / 180;
            angleY = (angleY * Math.PI) / 180;
            console.log(
                `[moveCameraToAircraftCamera] Converted angles from degrees to radians: angleX=${angleX.toFixed(
                    4
                )} rad, angleY=${angleY.toFixed(4)} rad`
            );
        }

        // Get FOV if specified
        let fov =
            camera.defaultFov !== undefined
                ? camera.defaultFov
                : camera.DefaultFov !== undefined
                ? camera.DefaultFov
                : undefined;

        // If FOV is 0 or invalid, use a wider default (55 degrees) for detail cameras like wing cameras
        // These cameras are typically positioned close to the aircraft and need a wider field of view
        // to capture the area they're looking at. Cockpit cameras usually have explicit FOV values.
        if (!fov || fov <= 0) {
            fov = 55; // Default to 55 degrees for cameras without explicit FOV (wider for detail shots)
            console.log(
                `[moveCameraToAircraftCamera] FOV was 0 or invalid, using wider default: ${fov}° (for detail cameras like wing views)`
            );
        }

        console.log(`Moving camera to: ${camera.cameraName || camera.CameraName}`, {
            x,
            y,
            z,
            angleX,
            angleY,
            fov,
        });

        // Get current orbit mode
        const orbitMode = this.observer.get('camera.orbitMode');
        console.log(`Current orbit mode: ${orbitMode}`);

        // CRITICAL: Temporarily disable camera controls updates to prevent interference
        // @ts-ignore
        const originalUpdate = this.cameraControls.update;
        // @ts-ignore
        this.cameraControls.update = () => {}; // No-op to prevent camera controls from moving camera

        // Set camera position
        const cameraPos = new Vec3(x, y, z);

        console.log(`=== Setting Camera Position ===`);
        console.log(`Before setPosition - Camera pos:`, this.camera.getPosition());
        console.log(`Before setPosition - Camera rotation:`, this.camera.getEulerAngles());

        this.camera.setPosition(cameraPos);

        console.log(`After setPosition - Camera pos:`, this.camera.getPosition());
        console.log(`After setPosition - Camera rotation:`, this.camera.getEulerAngles());

        // Calculate the look-at target point based on camera angles
        // angleY is yaw (rotation around Y), angleX is pitch (rotation around X)
        const focusDistance = 5; // meters in front of camera
        const lookAtPoint = new Vec3();

        console.log(`=== Calculating Look-At Point ===`);
        console.log(`  angleX (pitch): ${angleX} rad = ${((angleX * 180) / Math.PI).toFixed(2)}°`);
        console.log(`  angleY (yaw): ${angleY} rad = ${((angleY * 180) / Math.PI).toFixed(2)}°`);
        console.log(`  focusDistance: ${focusDistance}`);

        // Calculate forward direction from angles using proper 3D rotation
        // Forward is NEGATIVE Z in PlayCanvas camera space, so we need to account for that
        // angleX (pitch): positive = looking up, negative = looking down
        // angleY (yaw): positive = looking right, negative = looking left
        lookAtPoint.x = x + focusDistance * Math.sin(angleY) * Math.cos(angleX);
        lookAtPoint.y = y + focusDistance * Math.sin(angleX); // FIXED: Changed from subtraction to addition (positive pitch = look up = higher Y)
        lookAtPoint.z = z - focusDistance * Math.cos(angleY) * Math.cos(angleX); // NEGATIVE for forward direction

        console.log(`  Calculated components:`);
        console.log(`    X offset: ${focusDistance * Math.sin(angleY) * Math.cos(angleX)}`);
        console.log(`    Y offset: ${focusDistance * Math.sin(angleX)}`); // FIXED: Now addition (was subtraction)
        console.log(`    Z offset: ${focusDistance * Math.cos(angleY) * Math.cos(angleX)}`);
        console.log(`  Look-at point:`, lookAtPoint);

        // Make the camera look at the calculated point
        console.log(`=== Calling lookAt ===`);
        console.log(`  Camera position:`, this.camera.getPosition());
        console.log(`  Target point:`, lookAtPoint);

        this.camera.lookAt(lookAtPoint);

        console.log(`After lookAt - Camera rotation:`, this.camera.getEulerAngles());

        // Verify the forward vector
        const forward = this.camera.forward;
        console.log(`Camera forward vector after lookAt:`, forward);

        // Set FOV from config
        if (fov !== undefined && fov !== null) {
            this.camera.camera.fov = fov;
            this.observer.set('camera.fov', fov);
        }

        if (orbitMode === 'target') {
            // TARGET MODE: Also update the camera controls to use this focus point
            this.cameraControls.focus(lookAtPoint, cameraPos);

            // Update internal camera controls state
            // @ts-ignore - accessing potentially private properties
            if (this.cameraControls._distance !== undefined) {
                const distance = cameraPos.distance(lookAtPoint);
                // @ts-ignore
                this.cameraControls._zoomDist = distance;
                // @ts-ignore
                this.cameraControls._distance = distance;
                // @ts-ignore
                this.cameraControls._targetDistance = distance;
            }
        } else {
            // CAMERA-CENTER MODE: Reinitialize the rotation targets
            // @ts-ignore
            if (typeof this.reinitializeCameraCenterRotation === 'function') {
                // @ts-ignore
                this.reinitializeCameraCenterRotation();
            }
        }

        // Set FOV if specified
        if (fov && fov !== this.observer.get('camera.fov')) {
            this.observer.set('camera.fov', fov);
        }

        // Update camera FOV
        this.camera.camera.fov = this.observer.get('camera.fov');

        this.renderNextFrame();

        // Restore camera controls update function after a short delay
        // @ts-ignore
        setTimeout(() => {
            // @ts-ignore
            if (originalUpdate) {
                // @ts-ignore
                this.cameraControls.update = originalUpdate;
            }
        }, 100);
    }

    // Migrate deprecated AnimationName properties to AnimationNames arrays
    private migrateAnimationNamesInConfiguration(configuration: any) {
        if (!configuration) return;

        // Handle both camelCase and PascalCase engine definitions
        const engineArrays = [
            configuration.engineDefinitions,
            configuration.EngineDefinitions,
        ].filter((arr) => Array.isArray(arr));

        engineArrays.forEach((engines) => {
            engines.forEach((engine: any) => {
                // If AnimationName exists but AnimationNames doesn't, create the array
                if (engine.AnimationName && !engine.AnimationNames) {
                    engine.AnimationNames = [engine.AnimationName];
                    console.log(
                        `Migrated AnimationName "${
                            engine.AnimationName
                        }" to AnimationNames array for engine: ${engine.Name || 'Unnamed'}`
                    );
                }
            });
        });

        // Handle both camelCase and PascalCase cockpit UI items
        const cockpitUIArrays = [
            configuration.cockpitUIItems,
            configuration.CockpitUIItems,
        ].filter((arr) => Array.isArray(arr));

        cockpitUIArrays.forEach((items) => {
            items.forEach((item: any) => {
                // If NodeName exists but NodeNames doesn't, create the array
                if (item.NodeName && !item.NodeNames) {
                    item.NodeNames = [item.NodeName];
                    console.log(
                        `Migrated NodeName "${item.NodeName}" to NodeNames array for UI item: ${
                            item.Name || item.UIName || 'Unnamed'
                        }`
                    );
                }
            });
        });
    }

    isGSplatFilename(filename: string) {
        const parts = filename.split('?')[0].split('/').pop().split('.');
        const extension = parts.pop()?.toLowerCase();

        // PLY files are always GSplat
        if (extension === 'ply') {
            return true;
        }

        // Only specific JSON files are GSplat (not configuration files)
        if (extension === 'json') {
            const basename = parts.join('.').toLowerCase();
            // GSplat JSON files typically have specific naming patterns
            // Skip configuration files like AircraftConfiguration.json
            if (basename.includes('configuration') || basename.includes('config')) {
                return false;
            }
            // For now, be conservative and only accept .ply files as GSplat
            return false;
        }

        return false;
    }

    // returns true if the filename is an image file
    isImageFilename(filename: string) {
        const parts = filename.split('?')[0].split('/').pop().split('.');
        const imageExtensions = ['png', 'jpg', 'jpeg', 'tga', 'bmp', 'gif', 'ktx2', 'webp'];
        const result = parts.length > 1 && imageExtensions.includes(parts.pop().toLowerCase());
        return result;
    }

    /**
     * Returns a URL for a texture image that fits the screenshot texture cap. The batch
     * screenshot page sets `screenshot.maxTextureSize` because its headless Chromium runs
     * WebGL on SwiftShader, where every decoded texture and its mipmaps live in renderer
     * memory; a package with dozens of 4096² textures crashes the page during load.
     * Images within the cap, and any failure, return the original URL.
     */
    private async prepareTextureUrl(url: string, filename: string): Promise<string> {
        const maxTextureSize = Number(this.observer.get('screenshot.maxTextureSize')) || 0;
        if (maxTextureSize <= 0) {
            return url;
        }

        try {
            const blob = await (await fetch(url)).blob();
            const bitmap = await createImageBitmap(blob);
            const { width, height } = bitmap;
            const largest = Math.max(width, height);
            if (largest <= maxTextureSize) {
                bitmap.close();
                return url;
            }

            const scale = maxTextureSize / largest;
            const canvas = document.createElement('canvas');
            canvas.width = Math.max(1, Math.round(width * scale));
            canvas.height = Math.max(1, Math.round(height * scale));
            const context = canvas.getContext('2d');
            if (!context) {
                bitmap.close();
                return url;
            }
            context.imageSmoothingEnabled = true;
            context.imageSmoothingQuality = 'high';
            context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
            bitmap.close();

            const resized = await new Promise<Blob | null>((resolve) =>
                canvas.toBlob(resolve, 'image/png')
            );
            if (!resized) {
                return url;
            }
            console.log(
                `[TEXTURE LOAD] Downscaled ${filename} from ${width}x${height} to ${canvas.width}x${canvas.height}`
            );
            return URL.createObjectURL(resized);
        } catch (error) {
            console.warn(`[TEXTURE LOAD] Could not downscale ${filename}, loading it as is`, error);
            return url;
        }
    }

    /**
     * Frees the blob behind a downscaled texture once the GPU has it. Blob URLs count
     * against Chromium's blob quota, and a package with dozens of downscaled 4096²
     * textures exhausted it before the screenshot blob could be created.
     */
    private static releasePreparedTextureUrl(textureUrl: string, originalUrl: string) {
        if (textureUrl !== originalUrl && textureUrl.startsWith('blob:')) {
            URL.revokeObjectURL(textureUrl);
        }
    }

    // load a standalone texture file
    private loadTexture(file: File) {
        return new Promise((resolve, reject) => {
            this.prepareTextureUrl(file.url, file.filename).then((textureUrl) => {
                const textureAsset = new Asset(file.filename, 'texture', {
                    url: textureUrl,
                    filename: file.filename,
                });
                textureAsset.on('load', () => {
                    Viewer.releasePreparedTextureUrl(textureUrl, file.url);
                    // max anisotropy on all textures
                    (textureAsset.resource as Texture).anisotropy = this.app.graphicsDevice.maxAnisotropy;
                    resolve(textureAsset);
                });
                textureAsset.on('error', (err: string) => {
                    Viewer.releasePreparedTextureUrl(textureUrl, file.url);
                    reject(err);
                });
                this.app.assets.add(textureAsset);
                this.app.assets.load(textureAsset);
            });
        });
    }

    // load the list of urls.
    // urls can reference glTF files, glb files and skybox textures.
    // returns true if a model was loaded.
    loadFiles(files: Array<File>, resetScene = false) {
        // convert single url to list
        if (!Array.isArray(files)) {
            files = [files];
        }

        // Store ALL original files for passing to loadGltf as externalUrls
        // This ensures textures are available for resolution
        const allFiles = [...files];

        // check if any file is a glTF/glb model
        const hasGltfModel = files.some((f) => this.isModelFilename(f.filename));

        // filter out JSON files if we have glTF files (JSON files are likely part of glTF, not standalone Gaussian Splats)
        // but preserve AircraftConfiguration.json files
        const filteredFiles = hasGltfModel
            ? files.filter(
                  (f) =>
                      !f.filename.toLowerCase().endsWith('.json') ||
                      f.filename.toLowerCase() === 'aircraftconfiguration.json'
              )
            : files;

        const imageFiles = files.filter((f) => this.isImageFilename(f.filename));
        if (imageFiles.length === 0) {
            console.warn('[Viewer.loadFiles] ⚠ NO IMAGE FILES FOUND IN FILES ARRAY!');
        }

        const allFilesImages = allFiles.filter((f) => this.isImageFilename(f.filename));
        if (allFilesImages.length === 0) {
            console.warn('[Viewer.loadFiles] ⚠ NO IMAGE FILES IN allFiles!');
        }

        // check if any file is a model or texture
        const hasModelFilename = filteredFiles.reduce(
            (p, f) =>
                p ||
                this.isModelFilename(f.filename) ||
                this.isGSplatFilename(f.filename) ||
                this.isImageFilename(f.filename),
            false
        );

        if (hasModelFilename) {
            if (resetScene) {
                this.resetScene();
            }

            // Detect LODs and update observer
            const detectedLODs = this.detectLODs(files);
            this.observer.set('lods.available', detectedLODs);

            // Ensure LOD loading state is cleared initially
            this.observer.set('lods.loading', false);

            // Filter files for loading - if LODs exist, only load LOD_0
            let filesToLoad = filteredFiles;
            if (detectedLODs.length > 0) {
                // Find LOD_0 file
                const lod0 = detectedLODs.find((lod) => lod.level === 0);
                if (lod0) {
                    // Set LOD_0 as selected
                    this.observer.set('lods.selected', lod0.name);

                    // Only load LOD_0 and non-LOD files (textures, etc.)
                    filesToLoad = filteredFiles.filter((file) => {
                        const isLOD = this.isLODFilename(file.filename);
                        if (isLOD) {
                            const level = this.extractLODLevel(file.filename);
                            return level === 0; // Only load LOD_0
                        }
                        return true; // Load non-LOD files (textures, etc.)
                    });
                } else {
                    // No LOD_0 found, load the lowest available LOD
                    const lowestLOD = detectedLODs[0]; // Already sorted by level
                    this.observer.set('lods.selected', lowestLOD.name);

                    filesToLoad = filteredFiles.filter((file) => {
                        const isLOD = this.isLODFilename(file.filename);
                        if (isLOD) {
                            const level = this.extractLODLevel(file.filename);
                            return level === lowestLOD.level;
                        }
                        return true;
                    });
                }
            }

            // Detect and load aircraft configuration
            this.detectAndLoadAircraftConfiguration(files);

            const loadTimestamp = Date.now();

            this.observer.set('ui.spinner', true);
            this.observer.set('ui.loadingOverlay', true); // Show loading overlay immediately
            this.observer.set('ui.loadingMessage', 'Loading files...');
            this.observer.set('ui.loadingProgress', undefined); // Indeterminate
            this.observer.set('ui.error', null);
            this.clearCta();

            // load asset files (using filtered filesToLoad)
            // BUT: Pass allFiles to loadGltf so textures can be resolved
            console.log(
                '[Viewer.loadFiles] Creating load promises for',
                filesToLoad.length,
                'file(s)'
            );
            console.log(
                '[Viewer.loadFiles] allFiles contains',
                allFiles.length,
                'file(s) for texture resolution'
            );
            const promises = filesToLoad.map((file, index) => {
                const isModel = this.isModelFilename(file.filename);
                const isGSplat = this.isGSplatFilename(file.filename);
                const isImage = this.isImageFilename(file.filename);

                console.log(
                    `[Viewer.loadFiles]   [${index}] ${file.filename} - isModel: ${isModel}, isGSplat: ${isGSplat}, isImage: ${isImage}`
                );

                if (isModel) {
                    console.log(
                        `[Viewer.loadFiles]   [${index}] Loading as glTF: ${file.filename}`
                    );
                    // CRITICAL: Pass ALL original files (not just filesToLoad) to loadGltf
                    // so it can resolve texture references. Use the original 'files' array from loadFiles parameter.
                    console.log(
                        `[Viewer.loadFiles]   [${index}] Passing ${allFiles.length} file(s) as externalUrls to loadGltf (ALL files, not just filesToLoad)`
                    );
                    const imageFilesInAll = allFiles.filter((f) =>
                        this.isImageFilename(f.filename)
                    );
                    console.log(
                        `[Viewer.loadFiles]   [${index}] Image files in allFiles:`,
                        imageFilesInAll.length
                    );
                    console.log(
                        `[Viewer.loadFiles]   [${index}] Sample external URLs:`,
                        allFiles.slice(0, 10).map((f) => f.filename)
                    );
                    return this.loadGltf(file, allFiles);
                } else if (isGSplat) {
                    console.log(
                        `[Viewer.loadFiles]   [${index}] Loading as GSplat: ${file.filename}`
                    );
                    return this.loadPly(file, files);
                } else if (isImage) {
                    console.log(
                        `[Viewer.loadFiles]   [${index}] Loading as texture: ${file.filename}`
                    );
                    return this.loadTexture(file);
                } else {
                    console.log(
                        `[Viewer.loadFiles]   [${index}] Unknown file type, skipping: ${file.filename}`
                    );
                    return null;
                }
            });

            // update files list BEFORE loading assets so detectLiveries() can access it
            const filesList = files.map((file) => ({
                filename: file.filename,
                url: file.url,
                size: (file as any).size, // File objects from drag/drop have size property
            }));
            if (resetScene) {
                this.observer.set('files.list', filesList);
            } else {
                const existingFiles = this.observer.get('files.list');
                const existingFilesArray = Array.isArray(existingFiles) ? existingFiles : [];
                this.observer.set('files.list', existingFilesArray.concat(filesList));
            }

            console.log(
                '[Viewer.loadFiles] Waiting for',
                promises.filter((p) => p !== null).length,
                'promise(s) to resolve'
            );
            
            // Show loading overlay immediately - textures may still be loading after assets resolve
            this.observer.set('ui.spinner', false);
            this.observer.set('ui.loadingOverlay', true);
            this.observer.set('ui.loadingMessage', 'Loading textures...');
            this.observer.set('ui.loadingProgress', undefined); // Indeterminate
            
            Promise.all(promises)
                .then((assets: Asset[]) => {
                    console.log(
                        '[Viewer.loadFiles] ✓ All promises resolved, got',
                        assets.length,
                        'asset(s)'
                    );
                    console.log(
                        '[Viewer.loadFiles] Assets:',
                        assets.map((a, i) => ({
                            index: i,
                            name: a?.name,
                            type: a?.type,
                            hasResource: !!a?.resource,
                        }))
                    );

                    // Ensure loading overlay stays visible during texture loading and asset addition
                    this.observer.set('ui.spinner', false);
                    this.observer.set('ui.loadingOverlay', true);
                    
                    // Update message - textures may still be loading, then we'll add assets
                    this.observer.set('ui.loadingMessage', 'Loading textures...');
                    this.observer.set('ui.loadingProgress', undefined); // Indeterminate

                    this.loadTimestamp = loadTimestamp;

                    // add assets to the scene with progress updates
                    const validAssets = assets.filter((a) => a);
                    const totalAssets = validAssets.length;
                    let addedCount = 0;
                    
                    // Use a recursive function with setTimeout to yield control to browser for UI updates
                    const addAssetWithProgress = (index: number) => {
                        if (index >= validAssets.length) {
                            console.log('[Viewer.loadFiles] Added', addedCount, 'asset(s) to scene');
                            // All assets added, continue with postSceneLoad
                            this.continueAfterAssetsAdded(files, resetScene);
                            return;
                        }
                        
                        // Ensure loading overlay stays visible
                        this.observer.set('ui.loadingOverlay', true);
                        
                        const asset = validAssets[index];
                        if (asset) {
                            // Update progress as we add assets
                            if (totalAssets > 1) {
                                // Show 100% when all assets are added, otherwise show progress
                                const isComplete = (index + 1) >= totalAssets;
                                const percentage = isComplete ? 100 : Math.round(((index + 1) / totalAssets) * 100);
                                this.observer.set('ui.loadingMessage', `Adding assets to scene... (${index + 1}/${totalAssets})`);
                                this.observer.set('ui.loadingProgress', {
                                    currentIndex: index + 1,
                                    total: totalAssets,
                                    percentage: percentage,
                                });
                            } else {
                                // Single asset - still show loading overlay
                                this.observer.set('ui.loadingMessage', 'Adding assets to scene...');
                            }
                            
                            console.log(
                                `[Viewer.loadFiles] Adding asset [${index}] to scene:`,
                                asset.name,
                                asset.type
                            );
                            this.addToScene(asset);
                            addedCount++;
                        }
                        
                        // Yield control to browser, then continue with next asset
                        setTimeout(() => {
                            addAssetWithProgress(index + 1);
                        }, 0);
                    };
                    
                    // Start adding assets
                    addAssetWithProgress(0);
                })
                .catch((err) => {
                    console.error('[Viewer.loadFiles] ✗ Error loading files:', err);
                    console.error('[Viewer.loadFiles] Error stack:', err?.stack);
                    this.observer.set('ui.error', err?.toString() || err);
                    // Hide spinner on error
                    this.observer.set('ui.spinner', false);
                    this.observer.set('ui.loadingOverlay', false);
                });
            // Note: Don't hide spinner in finally - let setSelectedLivery handle it after textures load
        } else {
            // load skybox
            this.loadSkybox(files);
        }

        // return true if a model/scene was loaded and false otherwise
        return hasModelFilename;
    }

    // Continue processing after assets are added to scene
    private continueAfterAssetsAdded(files: Array<File>, resetScene: boolean) {
        // Ensure loading overlay stays visible
        this.observer.set('ui.loadingOverlay', true);
        
        // prepare scene post load
        this.observer.set('ui.loadingMessage', 'Processing scene...');
        this.observer.set('ui.loadingProgress', undefined); // Indeterminate during postSceneLoad
        this.postSceneLoad();

        // Note: Livery selection is now handled in onPostrender() to avoid duplicate calls
        // This prevents duplicate texture loading operations

        // update scene urls
        this.observer.set('ui.loadingMessage', 'Finalizing...');
        const urls = files.map((f) => f.url);
        const filenames = files.map((f) => f.filename.split('/').pop());
        if (resetScene) {
            this.observer.set('scene.urls', urls);
            this.observer.set('scene.filenames', filenames);
        } else {
            this.observer.set(
                'scene.urls',
                this.observer.get('scene.urls').concat(urls)
            );
            this.observer.set(
                'scene.filenames',
                this.observer.get('scene.filenames').concat(filenames)
            );
        }

        console.log('[Viewer.loadFiles] ✓ Load complete!');
        
        // Hide loading overlay after a short delay to ensure UI updates
        // The overlay will be shown again if livery selection is needed
        setTimeout(() => {
            // Only hide if we're not in the middle of livery selection
            const isLiveryLoading = this.observer.get('liveries.loading');
            if (!isLiveryLoading) {
                this.observer.set('ui.loadingOverlay', false);
                this.observer.set('ui.loadingProgress', undefined);
            }
        }, 100);
    }

    // set the currently selected track
    setSelectedTrack(trackName: string) {
        if (trackName !== 'ALL_TRACKS') {
            const a = this.animationMap[trackName];
            this.entities.forEach((e) => {
                e.anim?.baseLayer?.transition(a);
            });
        }
    }

    // play an animation / play all the animations
    play() {
        this.entities.forEach((e) => {
            if (e.anim) {
                e.anim.playing = true;
                e.anim.baseLayer?.play();
            }
        });
    }

    // stop playing animations
    stop() {
        this.entities.forEach((e) => {
            if (e.anim) {
                e.anim.playing = false;
                e.anim.baseLayer?.pause();
            }
        });
    }

    // set the animation speed
    setSpeed(speed: number) {
        this.animSpeed = speed;
        this.entities.forEach((e) => {
            const anim = e.anim;
            if (anim) {
                anim.speed = speed;
            }
        });
    }

    setTransition(transition: number) {
        this.animTransition = transition;

        // it's not possible to change the transition time after creation,
        // so rebuilt the animation graph with the new transition
        if (this.animTracks.length > 0) {
            this.rebuildAnimTracks();
        }
    }

    setLoops(loops: number) {
        this.animLoops = loops;

        // it's not possible to change the transition time after creation,
        // so rebuilt the animation graph with the new transition
        if (this.animTracks.length > 0) {
            this.rebuildAnimTracks();
        }
    }

    setAnimationProgress(progress: number) {
        if (this.suppressAnimationProgressUpdate) return;
        this.entities.forEach((e) => {
            const anim = e.anim;
            const baseLayer = anim?.baseLayer;
            if (baseLayer) {
                this.play();
                baseLayer.activeStateCurrentTime = baseLayer.activeStateDuration * progress;
                anim.update(0);
                anim.playing = false;
            }
        });
        this.refreshSelectedNodeTransform();
        this.renderNextFrame();
    }

    private selectedNodeVectorChanged(current: unknown, next: number[]) {
        if (!Array.isArray(current) || current.length !== next.length) {
            return true;
        }

        return current.some((value, index) => Math.abs(value - next[index]) > 1e-6);
    }

    private getNodeTransformVectors(node: GraphNode) {
        const localPosition = node.getLocalPosition();
        const localRotation = node.getLocalEulerAngles();
        const localScale = node.getLocalScale();
        const worldPosition = node.getPosition();
        const worldRotation = node.getRotation().getEulerAngles();
        const worldScale = node.getScale();

        return {
            worldPosition: [worldPosition.x, worldPosition.y, worldPosition.z],
            worldRotation: [worldRotation.x, worldRotation.y, worldRotation.z],
            worldScale: [worldScale.x, worldScale.y, worldScale.z],
            position: [localPosition.x, localPosition.y, localPosition.z],
            rotation: [localRotation.x, localRotation.y, localRotation.z],
            scale: [localScale.x, localScale.y, localScale.z],
        };
    }

    private refreshSelectedNodeTransform() {
        if (!this.selectedNode || !this.observer.get('scene.selectedNode.path')) {
            return;
        }

        const transforms = this.getNodeTransformVectors(this.selectedNode);

        if (
            !this.selectedNodeVectorChanged(
                this.observer.get('scene.selectedNode.worldPosition'),
                transforms.worldPosition
            ) &&
            !this.selectedNodeVectorChanged(
                this.observer.get('scene.selectedNode.worldRotation'),
                transforms.worldRotation
            ) &&
            !this.selectedNodeVectorChanged(
                this.observer.get('scene.selectedNode.worldScale'),
                transforms.worldScale
            ) &&
            !this.selectedNodeVectorChanged(
                this.observer.get('scene.selectedNode.position'),
                transforms.position
            ) &&
            !this.selectedNodeVectorChanged(
                this.observer.get('scene.selectedNode.rotation'),
                transforms.rotation
            ) &&
            !this.selectedNodeVectorChanged(
                this.observer.get('scene.selectedNode.scale'),
                transforms.scale
            )
        ) {
            return;
        }

        this.observer.set('scene.selectedNode', {
            ...(this.observer.get('scene.selectedNode') || {}),
            ...transforms,
        });
    }

    setSelectedNode(
        path: string,
        meshInfo?: {
            meshName?: string;
            meshBounds?: string;
            materialName?: string;
        }
    ) {
        const graphNode = this.app.root.findByPath(path);
        if (graphNode) {
            // Get animations for this node
            const nodeAnimations = this.getAnimationsForNode(path, graphNode);
            const animations = nodeAnimations.map((result) => ({
                animationName: result.animationName,
                duration:
                    result.animationTrack._duration || result.animationTrack.duration || undefined,
                isActive: this.isAnimationCurrentlyActive(result.animationName),
            }));

            const selectedNodeData = {
                name: graphNode.name,
                path: path,
                meshName: meshInfo?.meshName,
                meshBounds: meshInfo?.meshBounds,
                materialName: meshInfo?.materialName,
                animations: animations.length > 0 ? animations : undefined,
                ...this.getNodeTransformVectors(graphNode),
            };

            this.observer.set('scene.selectedNode', selectedNodeData);

            // Log animations targeting this node
            this.logAnimationsForNode(path, graphNode);
        }

        this.selectedNode = graphNode;
        this.dirtyWireframe = true;
        this.dirtyBounds = true;
        this.dirtySkeleton = true;
        this.renderNextFrame();
    }

    // Toggle a node in/out of multi-selection (Ctrl+Click)
    toggleMultiSelectNode(name: string, path: string, meshName?: string) {
        const currentSelection: Array<{ name: string; path: string; meshName?: string }> =
            this.observer.get('scene.multiSelectedNodes') || [];

        // Check if already selected
        const existingIndex = currentSelection.findIndex((n) => n.path === path);

        if (existingIndex >= 0) {
            // Remove from selection
            const newSelection = currentSelection.filter((_, i) => i !== existingIndex);
            this.observer.set('scene.multiSelectedNodes', newSelection);
            console.log('Removed from multi-selection:', name);
        } else {
            // Add to selection
            const newSelection = [...currentSelection, { name, path, meshName }];
            this.observer.set('scene.multiSelectedNodes', newSelection);
            console.log('Added to multi-selection:', name);
        }

        // Clear single selection when in multi-select mode
        this.observer.set('scene.selectedNode.path', '');
        this.selectedNode = null;
        this.dirtyWireframe = true;
        this.dirtyBounds = true;
        this.renderNextFrame();
    }

    // Clear all multi-selected nodes
    clearMultiSelection() {
        this.observer.set('scene.multiSelectedNodes', []);
        this.dirtyWireframe = true;
        this.dirtyBounds = true;
    }

    clearSelection() {
        this.selectedNode = null;
        this.observer.set('scene.selectedNode', {
            name: '',
            path: '',
            worldPosition: [0, 0, 0],
            worldRotation: [0, 0, 0],
            worldScale: [1, 1, 1],
            position: [0, 0, 0],
            rotation: [0, 0, 0],
            scale: [1, 1, 1],
        });
        this.observer.set('scene.selectedNode.path', '');
        this.observer.set('scene.multiSelectedNodes', []);
        this.dirtyWireframe = true;
        this.dirtyBounds = true;
        this.dirtySkeleton = true;
        this.renderNextFrame();
    }

    // Find animations that target a specific node using the glTF mapping
    private getAnimationsForNode(
        nodePath: string,
        targetNode: any
    ): Array<{ animationName: string; animationTrack: any; channels: Array<any> }> {
        const results: Array<{
            animationName: string;
            animationTrack: any;
            channels: Array<any>;
        }> = [];

        if (!targetNode) {
            return results;
        }

        // First, find which glTF node index corresponds to our target node
        const targetNodeIndex = this.findGltfNodeIndex(targetNode);
        if (targetNodeIndex === -1) {
            return results;
        }

        console.log(
            `   🔍 Target node "${targetNode.name}" corresponds to glTF node index: ${targetNodeIndex}`
        );

        // Use our pre-built glTF animation mapping
        this.gltfAnimationNodeMap.forEach((targetNodes, animationName) => {
            const matchingChannels: Array<any> = [];

            targetNodes.forEach((target) => {
                if (target.nodeIndex === targetNodeIndex) {
                    matchingChannels.push({
                        nodeIndex: target.nodeIndex,
                        property: target.property,
                    });
                }
            });

            if (matchingChannels.length > 0) {
                // Find the corresponding animation track
                const track = this.animTracks.find((t) => (t._name || t.name) === animationName);

                if (track) {
                    results.push({
                        animationName: animationName,
                        animationTrack: track,
                        channels: matchingChannels,
                    });
                }
            }
        });

        return results;
    }

    // Find the glTF node index for a given PlayCanvas entity by matching against stored glTF node names
    private findGltfNodeIndex(targetNode: any): number {
        // We need to match the PlayCanvas node name to the glTF nodes array by name, not by position

        if (!this.gltfNodeNames || this.gltfNodeNames.length === 0) {
            return -1;
        }

        // Find exact name match in glTF nodes array
        const exactMatch = this.gltfNodeNames.findIndex((nodeName) => nodeName === targetNode.name);
        if (exactMatch !== -1) {
            console.log(
                `🔍 Found exact name match: "${targetNode.name}" = glTF node ${exactMatch}`
            );
            return exactMatch;
        }

        // Try partial matching for LOD variations
        const partialMatch = this.gltfNodeNames.findIndex((nodeName) => {
            if (!nodeName || !targetNode.name) return false;
            const nodeBase = nodeName.replace(/_LOD\d+$/, '');
            const targetBase = targetNode.name.replace(/_LOD\d+$/, '');
            return nodeBase === targetBase;
        });

        if (partialMatch !== -1) {
            console.log(
                `🔍 Found partial name match: "${targetNode.name}" matches glTF node ${partialMatch} ("${this.gltfNodeNames[partialMatch]}")`
            );
            return partialMatch;
        }

        return -1;
    }

    // Helper method to get all nodes in a flat array (similar to glTF node array)
    private getAllNodesFlat(): Array<any> {
        const nodes: Array<any> = [];

        const traverse = (entity: any) => {
            nodes.push(entity);
            if (entity.children) {
                entity.children.forEach((child: any) => traverse(child));
            }
        };

        this.entities.forEach((entity) => traverse(entity));
        return nodes;
    }

    // Log animations targeting the selected node
    private logAnimationsForNode(nodePath: string, targetNode: any) {
        console.log(`\n🎬 Animations targeting node: "${targetNode.name}" (${nodePath})`);

        if (this.gltfAnimationNodeMap.size === 0) {
            console.log('⚠️  No glTF animation mappings available');
        }

        const nodeAnimations = this.getAnimationsForNode(nodePath, targetNode);

        if (nodeAnimations.length === 0) {
            console.log('   ❌ No animations target this node');
            this.debugNodeMatching(nodePath, targetNode);
            return;
        }

        nodeAnimations.forEach((result, index) => {
            console.log(`   ✅ Animation ${index + 1}: "${result.animationName}"`);
            console.log(
                `      Duration: ${
                    result.animationTrack._duration?.toFixed(2) ||
                    result.animationTrack.duration?.toFixed(2) ||
                    'unknown'
                } seconds`
            );

            result.channels.forEach((channelInfo, channelIndex) => {
                console.log(
                    `      Channel ${channelIndex + 1}: animates "${channelInfo.property}"`
                );
                console.log(`         glTF node index: ${channelInfo.nodeIndex}`);
            });

            // Check if this animation is currently active
            const isCurrentlyActive = this.isAnimationCurrentlyActive(result.animationName);
            if (isCurrentlyActive) {
                console.log(`      🔄 Currently active!`);
            }
        });
    }

    // Helper method to check if an animation is currently active
    private isAnimationCurrentlyActive(animationName: string): boolean {
        const currentTrack = this.observer.get('animation.selectedTrack');
        const isPlaying = this.observer.get('animation.playing');

        // Check if this specific animation is the selected track and playing
        if (currentTrack === animationName && isPlaying) {
            return true;
        }

        // Check if ALL_TRACKS is selected and playing (meaning all animations might be active)
        if (currentTrack === 'ALL_TRACKS' && isPlaying) {
            return true;
        }

        // Check individual animation states
        const individualAnims = this.observer.get('animations');
        if (
            individualAnims &&
            individualAnims[animationName] &&
            individualAnims[animationName].playing
        ) {
            return true;
        }

        return false;
    }

    // Parse raw glTF JSON to build animation-to-node mapping
    private parseGltfAnimationData(gltfUrl: string) {
        fetch(gltfUrl)
            .then((response) => response.json())
            .then((gltfData) => {
                this.buildAnimationNodeMapping(gltfData);
            })
            .catch((error) => {
                console.warn('Failed to load raw glTF JSON:', error);
            });
    }

    // Build mapping from animation names to their target nodes
    private buildAnimationNodeMapping(gltfData: any) {
        if (!gltfData.animations || !Array.isArray(gltfData.animations)) {
            return;
        }

        // Store the glTF nodes array
        if (gltfData.nodes && Array.isArray(gltfData.nodes)) {
            this.gltfNodeNames = gltfData.nodes.map((node: any) => node.name || '');
        }

        gltfData.animations.forEach((animation: any, animIndex: number) => {
            const animationName = animation.name || `Animation_${animIndex}`;
            const targetNodes: Array<{ nodeIndex: number; property: string }> = [];

            if (animation.channels && Array.isArray(animation.channels)) {
                animation.channels.forEach((channel: any) => {
                    if (channel.target && typeof channel.target.node === 'number') {
                        targetNodes.push({
                            nodeIndex: channel.target.node,
                            property: channel.target.path, // 'translation', 'rotation', 'scale', etc.
                        });
                    }
                });
            }

            if (targetNodes.length > 0) {
                this.gltfAnimationNodeMap.set(animationName, targetNodes);
            }
        });
    }

    // Debug method to understand node matching issues
    private debugNodeMatching(nodePath: string, targetNode: any) {
        // Debug info available if needed for troubleshooting
    }

    setSelectedVariant(variant: string) {
        if (variant) {
            this.entityAssets.forEach((entityAsset) => {
                const resource = entityAsset.asset?.resource as ContainerResource;
                if (resource && resource.getMaterialVariants().indexOf(variant) !== -1) {
                    resource.applyMaterialVariant(entityAsset.entity, variant);
                }
            });
            this.renderNextFrame();
        }
    }

    setCenterScene(value: boolean) {
        this.sceneRoot.setLocalPosition(0, 0, 0);

        // calculate scene bounds after first render in order to get accurate morph target and skinned bounds
        this.calcSceneBounds(this.sceneBounds);

        // offset scene geometry to place it at the origin
        if (value) {
            console.log(
                `Scene bounds - min Y: ${this.sceneBounds.getMin().y}, center Y: ${
                    this.sceneBounds.center.y
                }`
            );
            this.sceneRoot.setLocalPosition(
                -this.sceneBounds.center.x,
                -this.sceneBounds.getMin().y,
                -this.sceneBounds.center.z
            );
            console.log(
                `After centering - scene root Y position: ${this.sceneRoot.getLocalPosition().y}`
            );
        }

        // Apply altitude and pitch offsets after centering to ensure proper positioning
        this.applyAltitudeOffset();
        this.applyPitchOffset();

        this.dirtyBounds = true;

        this.renderNextFrame();
    }

    // Livery system methods
    private detectLiveries() {
        // Clear the livery ID map
        this.liveryIdMap.clear();

        // Get aircraft configuration to find the model ID
        const aircraftConfig = this.observer.get('aircraftConfig');
        if (!aircraftConfig?.loaded || !aircraftConfig?.configuration) {
            this.observer.set('liveries.available', ['Generic']);
            this.observer.set('liveries.selected', 'Generic');
            return;
        }

        const config = aircraftConfig.configuration;
        const aircraftModelId = config.id || config.ID;

        if (!aircraftModelId) {
            this.observer.set('liveries.available', ['Generic']);
            this.observer.set('liveries.selected', 'Generic');
            return;
        }

        // Get the manifest
        const manifestData = this.observer.get('manifestData');
        if (!manifestData?.loaded || !manifestData?.manifest?.liveryPackages) {
            // Don't fall back to file scanning if manifest isn't loaded - it will be called again when manifest loads
            // Set Generic as available for now
            this.observer.set('liveries.available', ['Generic']);
            if (!this.observer.get('liveries.selected')) {
                this.observer.set('liveries.selected', 'Generic');
            }
            return;
        }

        // Find all livery packages for this aircraft model
        const manifest = manifestData.manifest;
        const matchingLiveries: any[] = [];

        for (const liveryPkg of manifest.liveryPackages) {
            if (
                liveryPkg.modelID &&
                liveryPkg.modelID.toLowerCase() === aircraftModelId.toLowerCase()
            ) {
                matchingLiveries.push(liveryPkg);
            }
        }

        // Build the available liveries list from manifest data
        const liveries = ['Generic']; // Always include Generic

        for (const liveryPkg of matchingLiveries) {
            // Extract directory name from path
            const path = liveryPkg.path;
            if (path) {
                const pathParts: string[] = path.split(/[/\\]/);
                const liveriesIndex = pathParts.findIndex(
                    (part: string) => part.toLowerCase() === 'liveries'
                );
                if (liveriesIndex !== -1 && liveriesIndex < pathParts.length - 1) {
                    const liveryDirName = pathParts[liveriesIndex + 1];
                    // Skip shared livery entries from the selectable list
                    if (this.isSharedLiveryName(liveryDirName)) {
                        continue;
                    }
                    liveries.push(liveryDirName);
                    // Map directory name to ID
                    this.liveryIdMap.set(liveryDirName, liveryPkg.id);
                }
            }
        }

        // Normalize list (drop shared, sort A-Z alphabetically)
        const sortedLiveries = this.normalizeLiveryList(liveries);

        this.observer.set('liveries.available', sortedLiveries);

        // Preserve current selection when valid; otherwise prefer Generic (or first available)
        const previousSelection = this.observer.get('liveries.selected');
        const currentSelection = this.resolveLiveryAlias(previousSelection || '');
        const liveryToSelect = sortedLiveries.includes(currentSelection)
            ? currentSelection
            : (sortedLiveries.includes('Generic') ? 'Generic' : (sortedLiveries[0] || 'Generic'));
        this.observer.set('liveries.selected', liveryToSelect);

        // A livery's variants (engine type, sharklets, ...) come from its manifest package,
        // looked up through liveryIdMap. When the livery was selected before the manifest
        // arrived, its variants were applied without an ID, so every variant is hidden. An
        // unchanged selection does not fire the observer, so apply them again here: the
        // batch thumbnails select Generic that way and rendered it with no engines.
        if (
            previousSelection === liveryToSelect &&
            this.liveryIdMap.has(liveryToSelect) &&
            (this.materialTextureMap.size > 0 || this.meshInstances.length > 0)
        ) {
            this.applyLiveryVariantAnimations(liveryToSelect);
        }
    }

    // Fallback: Detect liveries from loaded files if manifest is not available
    // NOTE: This should rarely be called now - detectLiveries waits for manifest instead
    private detectLiveriesFromFiles() {
        const filesRaw = this.observer.get('files.list');
        // Ensure files is always an array (handle cases where it might be undefined, null, or non-array)
        const files: Array<{ filename: string; url: string; size?: number }> = Array.isArray(
            filesRaw
        )
            ? filesRaw
            : [];
        const liveries = new Set<string>(['Generic']); // Always include Generic (base textures)

        if (!Array.isArray(filesRaw)) {
            console.warn('[Viewer] detectLiveriesFromFiles - files.list is not an array');
            // If files.list is not available, just set Generic and return
            this.observer.set('liveries.available', ['Generic']);
            if (!this.observer.get('liveries.selected')) {
                this.observer.set('liveries.selected', 'Generic');
            }
            return;
        }

        files.forEach((file: { filename: string; url: string; size?: number }) => {
            const pathParts = file.filename.split('/');

            // Check for both "Liveries/" and "liveries/" (case-insensitive)
            // Format: Liveries/LiveryName/texture.png or liveries/LiveryName/texture.png
            if (pathParts.length >= 3 && pathParts[0].toLowerCase() === 'liveries') {
                const liveryDirName = pathParts[1];
                // Skip shared livery entries from the selectable list
                if (!this.isSharedLiveryName(liveryDirName)) {
                    liveries.add(liveryDirName);
                }

                // Extract livery ID from .json filename if present
                const fileName = pathParts[pathParts.length - 1];
                if (fileName.endsWith('.json')) {
                    const liveryId = fileName.replace('.json', '');
                    if (
                        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
                            liveryId
                        )
                    ) {
                        this.liveryIdMap.set(liveryDirName, liveryId);
                    }
                }
            }
        });

        // Normalize list (drop shared, sort A-Z alphabetically)
        const availableLiveries = this.normalizeLiveryList(Array.from(liveries));
        this.observer.set('liveries.available', availableLiveries);

        // Preserve current selection when valid; otherwise prefer Generic (or first available)
        const currentSelection = this.resolveLiveryAlias(this.observer.get('liveries.selected') || '');
        const liveryToSelect = availableLiveries.includes(currentSelection)
            ? currentSelection
            : (availableLiveries.includes('Generic') ? 'Generic' : (availableLiveries[0] || 'Generic'));
        this.observer.set('liveries.selected', liveryToSelect);
    }

    private categorizeTextures() {
        // Clear existing texture maps
        this.baseTextures.clear();
        this.liveryTextures.clear();

        // Process this.assets directly instead of filename matching
        this.assets.forEach((asset, index) => {
            if (asset.type !== 'texture') return;

            // Ensure texture asset has proper file information
            const file = asset.file as any;
            const filename = file?.filename || asset.name;
            const url = file?.url || '';

            if (!filename) {
                return;
            }

            // Check if it's an image file
            const isImage = /\.(png|jpg|jpeg|tga|bmp|gif|ktx2|webp)$/i.test(filename);
            if (!isImage) {
                return;
            }

            const pathParts = filename.split('/');
            const fileName = pathParts[pathParts.length - 1];

            const isNormalMap =
                fileName.toLowerCase().includes('_n.webp') ||
                fileName.toLowerCase().includes('normal');

            // Check for both "Liveries/" and "liveries/" directories (case-insensitive)
            if (pathParts.length >= 3 && pathParts[0].toLowerCase() === 'liveries') {
                // This is a livery override texture from Liveries/ or liveries/
                const liveryName = pathParts[1];

                // Generic livery should not have its own textures - it only uses shared textures
                // Treat Generic package textures as base textures instead
                if (liveryName === 'Generic') {
                    this.baseTextures.set(fileName, asset);
                } else {
                    if (!this.liveryTextures.has(liveryName)) {
                        this.liveryTextures.set(liveryName, new Map());
                    }

                    this.liveryTextures.get(liveryName)!.set(fileName, asset);
                }

                if (isNormalMap) {
                    console.log(
                        `[CATEGORIZE] 🔵 Stored normal map in livery "${liveryName}": ${fileName}`
                    );
                }
            } else {
                // This is a base texture (root level OR from glTF with path)
                // Always store by just the filename regardless of path
                this.baseTextures.set(fileName, asset);

                if (isNormalMap) {
                    console.log(
                        `[CATEGORIZE] 🔵 Stored normal map as base texture: ${fileName} (from path: ${filename})`
                    );
                }
            }
        });

        console.log('[CATEGORIZE] ========================================');
        console.log(
            '[CATEGORIZE] categorizeTextures - Found',
            this.baseTextures.size,
            'base textures,',
            this.liveryTextures.size,
            'liveries'
        );
        // Log all livery names and their texture counts
        this.liveryTextures.forEach((textureMap, liveryName) => {
            const isShared = this.isSharedLiveryName(liveryName);
            console.log(`[CATEGORIZE]   Livery "${liveryName}" (${isShared ? 'SHARED' : 'regular'}): ${textureMap.size} textures`);
            if (isShared) {
                // Log first few texture names for shared liveries
                const textureNames = Array.from(textureMap.keys()).slice(0, 5);
                console.log(`[CATEGORIZE]     Sample textures: ${textureNames.join(', ')}${textureMap.size > 5 ? '...' : ''}`);
            }
        });
        console.log('[CATEGORIZE] ========================================');
    }

    // Apply XXXY decoding shader chunks to all materials with normal maps
    private applyXXXYNormalMapDecoding() {
        let patchedCount = 0;
        let totalMaterials = 0;

        console.log('[NORMAL MAP] ========================================');
        console.log('[NORMAL MAP] Applying XXXY decoding to all materials with normal maps...');
        console.log('[NORMAL MAP] Entity count:', this.entities.length);

        // Collect all materials that need updating
        const materialsToUpdate: StandardMaterial[] = [];

        this.entities.forEach((entity) => {
            const renderComponents = entity.findComponents('render') as RenderComponent[];

            renderComponents.forEach((render) => {
                render.meshInstances.forEach((meshInstance) => {
                    const material = meshInstance.material as StandardMaterial;
                    totalMaterials++;

                    if (!material) {
                        return;
                    }

                    const normalMap = (material as any).normalMap;
                    if (normalMap) {
                        // Only apply XXXY decoding to .webp normal maps
                        // .png normal maps should use standard decoding
                        const normalMapName = normalMap.name || '';
                        const isWebpNormalMap = normalMapName.toLowerCase().endsWith('.webp') || 
                                               normalMapName.toLowerCase().includes('_n.webp');
                        
                        if (isWebpNormalMap) {
                            if (!(material as any)._originalNormalMap) {
                                (material as any)._originalNormalMap = normalMap;
                            }
                            if (!material.chunks) {
                                (material as any).chunks = {};
                            }
                            (material.chunks as any).normalMapPS = normalMapXXXYGlsl;
                            materialsToUpdate.push(material);
                            patchedCount++;
                        }
                    }
                });
            });
        });

        // Batch material updates in groups of 50 to prevent main thread blocking
        const BATCH_SIZE = 50;
        let batchIndex = 0;

        const processBatch = () => {
            const start = batchIndex * BATCH_SIZE;
            const end = Math.min(start + BATCH_SIZE, materialsToUpdate.length);

            // Update materials in this batch
            for (let i = start; i < end; i++) {
                materialsToUpdate[i].update();
            }

            batchIndex++;

            // If there are more materials to process, schedule next batch
            if (end < materialsToUpdate.length) {
                setTimeout(processBatch, 0); // Yield control to browser between batches
            } else {
                // All batches complete
                console.log('[NORMAL MAP] ========================================');
                console.log(
                    `[NORMAL MAP] Summary: Applied XXXY decoding to ${patchedCount}/${totalMaterials} materials`
                );
                console.log('[NORMAL MAP] ========================================');
            }
        };

        // Start processing batches
        if (materialsToUpdate.length > 0) {
            processBatch();
        } else {
            console.log('[NORMAL MAP] ========================================');
            console.log(
                `[NORMAL MAP] Summary: Applied XXXY decoding to ${patchedCount}/${totalMaterials} materials`
            );
            console.log('[NORMAL MAP] ========================================');
        }
    }

    // Toggle normal maps on/off for all materials (with XXXY decode when enabled)
    private setNormalMapsEnabled(enabled: boolean) {
        let affectedCount = 0;

        console.log(
            `[NORMAL MAP] ${enabled ? 'Enabling with XXXY decode' : 'Disabling'} normal maps...`
        );

        this.entities.forEach((entity) => {
            const renderComponents = entity.findComponents('render') as RenderComponent[];

            renderComponents.forEach((render) => {
                render.meshInstances.forEach((meshInstance) => {
                    const material = meshInstance.material as StandardMaterial;

                    if (!material) {
                        return;
                    }

                    if (!enabled) {
                        // Disabling: store original and null out the map, remove chunk
                        if ((material as any).normalMap) {
                            if (!(material as any)._originalNormalMap) {
                                (material as any)._originalNormalMap = (material as any).normalMap;
                                console.log(
                                    `[NORMAL MAP] Storing original normal map for material: ${
                                        material.name || 'Unnamed'
                                    }`
                                );
                            }
                            (material as any).normalMap = null;
                            if (material.chunks) {
                                delete (material.chunks as any).normalMapPS;
                            }
                            material.update();
                            affectedCount++;
                        }
                    } else if ((material as any)._originalNormalMap) {
                        // Enabling: restore and attach decode chunk (only for .webp normal maps)
                        const restoredNormalMap = (material as any)._originalNormalMap;
                        const normalMapName = restoredNormalMap.name || '';
                        const isWebpNormalMap = normalMapName.toLowerCase().endsWith('.webp') || 
                                               normalMapName.toLowerCase().includes('_n.webp');
                        
                        (material as any).normalMap = restoredNormalMap;
                        if (isWebpNormalMap) {
                            if (!material.chunks) {
                                (material as any).chunks = {};
                            }
                            (material.chunks as any).normalMapPS = normalMapXXXYGlsl;
                        }
                        material.update();
                        console.log(
                            `[NORMAL MAP] Restored normal map${isWebpNormalMap ? ' with XXXY decode' : ''} for material: ${
                                material.name || 'Unnamed'
                            }`
                        );
                        affectedCount++;
                    }
                });
            });
        });

        console.log(
            `[NORMAL MAP] ${
                enabled ? 'Enabled' : 'Disabled'
            } normal maps on ${affectedCount} materials`
        );
        this.renderNextFrame();
    }

    private buildMaterialTextureMap() {
        // Build a map of which textures each material uses
        this.materialTextureMap.clear();

        let materialCount = 0;
        let totalMeshInstances = 0;

        this.entities.forEach((entity, entityIndex) => {
            const renderComponents = entity.findComponents('render') as RenderComponent[];

            renderComponents.forEach((render, renderIndex) => {
                render.meshInstances.forEach((meshInstance, meshIndex) => {
                    totalMeshInstances++;
                    const material = meshInstance.material as StandardMaterial;

                    if (!material) {
                        return;
                    }

                    const textureMap = new Map<string, string>();

                    // Check common texture properties
                    const textureProperties = [
                        'diffuseMap',
                        'normalMap',
                        'specularMap',
                        'glossMap',
                        'metalnessMap',
                        'emissiveMap',
                        'aoMap',
                        'heightMap',
                        'opacityMap',
                        'lightMap',
                    ];

                    textureProperties.forEach((prop) => {
                        const texture = (material as any)[prop];

                        if (texture && texture.name) {
                            // Extract just the filename from the texture name (remove any livery path)
                            const fullTextureName = texture.name;
                            const textureName = fullTextureName.includes('/')
                                ? fullTextureName.split('/').pop()
                                : fullTextureName;
                            textureMap.set(prop, textureName);
                        }
                    });

                    if (textureMap.size > 0) {
                        this.materialTextureMap.set(material, textureMap);
                        materialCount++;
                    }
                });
            });
        });
    }

    // Add missing refreshAllMaterials() method to force update all materials
    private refreshAllMaterials() {
        let refreshedCount = 0;

        // Force update all materials in the material texture map
        this.materialTextureMap.forEach((textureMap, material) => {

            // Force material update
            material.update();

            refreshedCount++;
        });

        // Also refresh materials from mesh instances to catch any not in the map
        this.entities.forEach((entity) => {
            const renderComponents = entity.findComponents('render') as RenderComponent[];

            renderComponents.forEach((render) => {
                render.meshInstances.forEach((meshInstance) => {
                    const material = meshInstance.material as StandardMaterial;

                    if (material && !this.materialTextureMap.has(material)) {

                        // Force material update
                        material.update();

                        refreshedCount++;
                    }
                });
            });
        });

        // Force scene rendering updates
        this.dirtyWireframe = true;
        this.dirtyBounds = true;
        this.dirtySkeleton = true;
        this.dirtyNormals = true;

        // Fire render events
        this.app.fire('prerender');
        this.renderNextFrame();
    }

    setSelectedLivery(liveryName: string) {
        // Declared outside the try so the recovery below can read it.
        let resolvedLiveryName = liveryName;
        try {
            resolvedLiveryName = this.resolveLiveryAlias(liveryName);

            // Normalize available list to drop shared and sort
            const currentAvailable = this.observer.get('liveries.available') || [];
            const normalized = this.normalizeLiveryList(currentAvailable);
            if (
                normalized.length !== currentAvailable.length ||
                normalized.some((v, i) => v !== currentAvailable[i])
            ) {
                this.observer.set('liveries.available', normalized);
            }

            // Immediately update the selected livery in the observer
            this.observer.set('liveries.selected', liveryName);
            this.observer.set('liveries.loading', false);

            // Rebuild material texture map if needed
            if (this.materialTextureMap.size === 0) {
                this.detectLiveries();
                this.categorizeTextures();
            }
            this.buildMaterialTextureMap();

            const materialMapCount = this.materialTextureMap.size;

            // Validate that we have the necessary data
            // If material maps aren't ready yet, defer livery selection until they are
            if (materialMapCount === 0) {
                // Defer until after first render when material maps will be available
                setTimeout(() => {
                    // Check if material maps are ready now
                    this.buildMaterialTextureMap();
                    if (this.materialTextureMap.size > 0) {
                        this.setSelectedLivery(liveryName);
                    }
                }, 100);
                return;
            }

            // Validate that the requested livery exists (except for Generic)
            if (resolvedLiveryName !== 'Generic' && !this.liveryTextures.has(resolvedLiveryName)) {
                console.error('[Viewer] Livery not found:', liveryName, '(resolved as', resolvedLiveryName + ')');
                return;
            }

            // Immediately apply textures (they should be preloaded)
            this.applyLiveryTexturesImmediate(resolvedLiveryName);

            // Apply livery variant animations based on visible groups
            this.applyLiveryVariantAnimations(resolvedLiveryName);

            // Force scene refresh with dirty flags after livery change
            this.dirtyWireframe = true;
            this.dirtyBounds = true;
            this.dirtySkeleton = true;
            this.dirtyNormals = true;

            // Force all materials to be dirty for rendering
            this.materialTextureMap.forEach((textureMap, material) => {
                material.update();
            });

            // Force comprehensive render update
            this.app.fire('prerender');
            this.renderNextFrame();

            // Update scene stats to reflect changes
            this.updateSceneStats();
        } catch (error) {
            console.error('[Viewer] setSelectedLivery error:', error);
            // Attempt to recover by reverting to Generic livery
            if (resolvedLiveryName !== 'Generic') {
                this.observer.set('liveries.selected', 'Generic');
            }
        }
    }

    // Evaluate an animation binding to get the animation value for a given target value
    // This mimics the C# AnimationBinding.Evaluate() method
    private evaluateAnimationBinding(binding: any, targetValue: number): number {
        const input = binding.Input || binding.input;
        const animation = binding.Animation || binding.animation;

        if (!input || !animation || input.length === 0 || animation.length === 0) {
            return targetValue;
        }

        // If input has only one value, return the corresponding animation value
        if (input.length === 1) {
            return animation[0];
        }

        // Find the two input values that bracket the target value
        let lowerIndex = 0;
        let upperIndex = input.length - 1;

        // Clamp to bounds
        if (targetValue <= input[0]) {
            return animation[0];
        }
        if (targetValue >= input[input.length - 1]) {
            return animation[animation.length - 1];
        }

        // Find bracketing indices
        for (let i = 0; i < input.length - 1; i++) {
            if (targetValue >= input[i] && targetValue <= input[i + 1]) {
                lowerIndex = i;
                upperIndex = i + 1;
                break;
            }
        }

        // Linear interpolation between the two animation values
        const inputRange = input[upperIndex] - input[lowerIndex];
        const t = (targetValue - input[lowerIndex]) / inputRange;
        const animValue =
            animation[lowerIndex] + t * (animation[upperIndex] - animation[lowerIndex]);

        return animValue;
    }

    // Apply livery variant animations based on visible groups
    // This mimics the C# logic for showing/hiding variants per livery
    private applyLiveryVariantAnimations(liveryName: string) {
        console.log('[LIVERY VARIANTS] Starting variant animation application for:', liveryName);

        // Get aircraft configuration
        const aircraftConfig = this.observer.get('aircraftConfig');
        if (!aircraftConfig?.loaded || !aircraftConfig?.configuration) {
            console.log('[LIVERY VARIANTS] No aircraft configuration loaded');
            return;
        }

        const config = aircraftConfig.configuration;
        const variantsAnimationBindings =
            config.VariantsAnimationBindings || config.variantsAnimationBindings;

        if (!variantsAnimationBindings || variantsAnimationBindings.length === 0) {
            console.log(
                '[LIVERY VARIANTS] No VariantsAnimationBindings found in aircraft configuration'
            );
            return;
        }

        console.log(
            '[LIVERY VARIANTS] Found',
            variantsAnimationBindings.length,
            'variant bindings'
        );

        // Get the livery ID for this livery directory name
        const liveryId = this.liveryIdMap.get(liveryName);
        console.log(
            '[LIVERY VARIANTS] Livery directory:',
            liveryName,
            '-> ID:',
            liveryId || 'not found'
        );

        // Get the livery package from manifest using the ID
        const manifestData = this.observer.get('manifestData');
        let visibleGroups: string[] = [];

        if (manifestData?.loaded && manifestData?.manifest?.liveryPackages && liveryId) {
            const liveryPackages = manifestData.manifest.liveryPackages;
            // Match by ID instead of name to handle multiple liveries with same display name
            const liveryPackage = liveryPackages.find((pkg: any) => pkg.id === liveryId);

            if (liveryPackage) {
                console.log(
                    '[LIVERY VARIANTS] Found livery package:',
                    liveryPackage.name,
                    '(ID:',
                    liveryPackage.id,
                    ')'
                );
                if (liveryPackage.visibleGroups) {
                    visibleGroups = liveryPackage.visibleGroups;
                    console.log(
                        '[LIVERY VARIANTS] Livery contains',
                        visibleGroups.length,
                        'visible groups:',
                        visibleGroups
                    );
                } else {
                    console.log('[LIVERY VARIANTS] Livery has no visibleGroups defined');
                }
            } else {
                console.log(
                    '[LIVERY VARIANTS] Livery package with ID',
                    liveryId,
                    'not found in manifest'
                );
            }
        } else {
            if (!liveryId) {
                console.log('[LIVERY VARIANTS] No livery ID found for:', liveryName);
            } else {
                console.log('[LIVERY VARIANTS] No manifest data available');
            }
        }

        // Process each variant binding
        for (const variantBinding of variantsAnimationBindings) {
            const variantAnim = variantBinding.AnimationName || variantBinding.animationName;
            const variantName = variantBinding.Name || variantBinding.name;

            if (!variantAnim) {
                console.log('[LIVERY VARIANTS] Skipping binding with no animation name');
                continue;
            }

            // Check if this animation exists in the model
            const trackPath = this.animationMap[variantAnim];
            if (!trackPath) {
                console.log(`[LIVERY VARIANTS] Animation "${variantAnim}" not found in model`);
                continue;
            }

            // Determine target value: 1 if variant is in visible groups, 0 otherwise
            let targetValue = 0;
            if (visibleGroups.includes(variantName)) {
                targetValue = 1;
            }

            // Evaluate the animation binding to get the final animation value
            const animValue = this.evaluateAnimationBinding(variantBinding, targetValue);

            console.log(
                `[LIVERY VARIANTS] Applying livery variant ${variantAnim} - ${variantName} -> Target: ${targetValue}, AnimValue: ${animValue}`
            );

            // Apply the animation
            this.setIndividualAnimationProgress(variantAnim, animValue);
        }

        console.log('[LIVERY VARIANTS] Variant animation application complete');
    }

    setSelectedLOD(lodName: string) {
        // Clear any existing loading state first
        this.observer.set('lods.loading', false);

        const availableLODsRaw = this.observer.get('lods.available');
        // Ensure availableLODs is always an array
        const availableLODs: Array<{ name: string; level: number }> = Array.isArray(
            availableLODsRaw
        )
            ? availableLODsRaw
            : [];
        console.log('Available LODs:', availableLODs);
        console.log('Looking for LOD name:', lodName);

        if (!Array.isArray(availableLODsRaw)) {
            console.warn(
                '[LOD] lods.available is not an array:',
                typeof availableLODsRaw,
                availableLODsRaw
            );
        }

        const selectedLOD = availableLODs.find((lod: any) => lod.name === lodName);
        console.log('Found LOD:', selectedLOD);

        if (!selectedLOD) {
            const lodNames = availableLODs.map((lod: any) => lod.name);
            console.error(`LOD ${lodName} not found in available LODs:`, lodNames);
            return;
        }

        // Get current selection for logging
        const currentSelected = this.observer.get('lods.selected');
        console.log(`Switching from ${currentSelected} to ${lodName} (LOD ${selectedLOD.level})`);

        // Set loading state (the UI already updated lods.selected)
        this.observer.set('lods.loading', true);

        // Get the current files list to maintain textures and other supporting files
        const currentFilesRaw = this.observer.get('files.list');
        // Ensure currentFiles is always an array
        const currentFiles: Array<{ filename: string; url: string; size?: number }> = Array.isArray(
            currentFilesRaw
        )
            ? currentFilesRaw
            : [];
        if (!Array.isArray(currentFilesRaw)) {
            console.warn(
                '[LOD] files.list is not an array:',
                typeof currentFilesRaw,
                currentFilesRaw
            );
        }
        console.log(
            'Current files list:',
            currentFiles.map((file: any) => file.filename)
        );

        // Create a new file list with the selected LOD and relevant non-LOD files
        // currentFiles is already guaranteed to be an array above
        const filesToLoad = currentFiles.filter((file: any) => {
            const isLOD = this.isLODFilename(file.filename);
            const lodLevel = this.extractLODLevel(file.filename);

            console.log(
                `File: ${file.filename}, isLOD: ${isLOD}, lodLevel: ${lodLevel}, target: ${selectedLOD.level}`
            );

            if (isLOD) {
                // Only include the selected LOD
                const shouldInclude = lodLevel === selectedLOD.level;
                console.log(
                    `LOD file ${file.filename} (level ${lodLevel}) - ${
                        shouldInclude ? 'INCLUDED' : 'EXCLUDED'
                    }`
                );
                return shouldInclude;
            }

            // For non-LOD files, only include assets that can be loaded (models, textures, GSplats)
            const canLoad =
                this.isModelFilename(file.filename) ||
                this.isImageFilename(file.filename) ||
                this.isGSplatFilename(file.filename);

            console.log(
                `Non-LOD file ${file.filename} - ${
                    canLoad ? 'INCLUDED' : 'EXCLUDED'
                } (loadable: ${canLoad})`
            );
            return canLoad;
        });

        console.log(
            `Files to load for LOD ${selectedLOD.level}:`,
            filesToLoad.map((file: any) => file.filename)
        );

        // Convert to File array format expected by loadFiles
        const filesArray = filesToLoad.map((file: any) => ({
            url: file.url,
            filename: file.filename,
        }));

        console.log('Final files array for loadFiles:', filesArray);

        if (filesArray.length === 0) {
            console.error('No files to load! LOD switching aborted.');
            this.observer.set('lods.loading', false);
            return;
        }

        // Use dedicated LOD switching that doesn't reset the entire scene
        setTimeout(() => {
            this.switchLODModel(filesArray);
        }, 10);
    }

    // Switch LOD without resetting the entire scene - only replace the model
    private switchLODModel(files: Array<File>) {
        console.log(
            'switchLODModel called with files:',
            files.map((f) => f.filename)
        );

        // Remove current entities from scene but preserve LOD system state
        this.entities.forEach((entity) => {
            this.sceneRoot.removeChild(entity);
            this.shadowCatcher.onEntityRemoved(entity);
            entity.destroy();
        });
        this.entities = [];

        // Reset altitude and pitch offset flags when switching LOD
        this.altitudeOffsetApplied = false;
        this.pitchOffsetApplied = false;

        // Clear stored original transforms
        this.originalSceneRootPosition = null;
        this.originalSceneRootRotation = null;
        this.originalEntityTransforms = [];

        // Clear entityAssets array
        this.entityAssets = [];

        // Remove current model assets but not texture assets (for liveries)
        const assetsToRemove = this.assets.filter(
            (asset) => asset.type === 'container' || asset.type === 'gsplat'
        );
        assetsToRemove.forEach((asset) => {
            this.app.assets.remove(asset);
            asset.unload();
        });
        // Keep texture assets for liveries, just remove the model assets
        this.assets = this.assets.filter(
            (asset) => asset.type !== 'container' && asset.type !== 'gsplat'
        );

        this.meshInstances = [];
        this.resetWireframeMeshes();

        // Load the new LOD files
        const promises = files.map((file) => {
            if (this.isModelFilename(file.filename)) {
                return this.loadGltf(file, files);
            } else if (this.isGSplatFilename(file.filename)) {
                return this.loadPly(file, files);
            } else if (this.isImageFilename(file.filename)) {
                return this.loadTexture(file);
            } else {
                return null;
            }
        });

        console.log(`Loading ${files.length} files for LOD switch...`);
        Promise.all(promises)
            .then((assets: Asset[]) => {
                const validAssets = assets.filter((a) => a);
                console.log(`LOD switch: loaded ${validAssets.length} assets`);

                // Add new assets to the scene
                validAssets.forEach((asset) => {
                    this.addToScene(asset);
                });

                // Perform post-load operations (but don't reset LOD system)
                this.postSceneLoadForLODSwitch();

                console.log('LOD switch completed successfully');
            })
            .catch((err) => {
                console.error('Error during LOD switch:', err);
                this.observer.set('ui.error', err?.toString() || err);
            })
            .finally(() => {
                this.observer.set('lods.loading', false);
            });
    }

    // Post-load operations specific to LOD switching (doesn't reset LOD system)
    private postSceneLoadForLODSwitch() {
        // Rebuild mesh instances
        this.meshInstances = this.entities
            .map((entity) => {
                return this.collectMeshInstances(entity);
            })
            .flat();

        // Apply altitude and pitch offsets from aircraft configuration if available
        this.applyAltitudeOffset();
        this.applyPitchOffset();

        // Update scene stats but don't reset LOD data
        this.updateSceneStats();

        // Rebuild animations if any
        if (this.animTracks.length > 0) {
            this.rebuildAnimTracks();
        }

        // Dirty everything for redraw
        this.dirtyWireframe = this.dirtyBounds = this.dirtySkeleton = this.dirtyGrid = this.dirtyNormals = true;

        this.renderNextFrame();
    }

    private async applyLiveryTextures(liveryName: string): Promise<void> {
        const liveryTextures = this.liveryTextures.get(liveryName);

        return new Promise((resolve) => {
            let pendingLoads = 0;
            let completedLoads = 0;
            let appliedCount = 0;

            const updateProgress = () => {
                if (pendingLoads > 0) {
                    const percentage = Math.round((completedLoads / pendingLoads) * 100);
                    this.observer.set('ui.loadingProgress', {
                        currentIndex: completedLoads,
                        total: pendingLoads,
                        percentage: percentage,
                    });
                }
            };

            const checkComplete = () => {
                if (completedLoads >= pendingLoads) {
                    resolve();
                }
            };

            // Apply textures to all materials
            this.materialTextureMap.forEach((textureMap, material) => {

                textureMap.forEach((fileName, textureProperty) => {
                    let textureAsset: Asset | undefined;
                    // Try livery override first (case-insensitive), fallback to base, then shared liveries
                    const liveryTextures = this.liveryTextures.get(liveryName);

                    // Case-insensitive lookup for livery textures
                    let liveryTextureAsset: Asset | undefined;
                    if (liveryTextures) {
                        const lowerFileName = fileName.toLowerCase();

                        for (const [key, value] of liveryTextures.entries()) {
                            if (key.toLowerCase() === lowerFileName) {
                                liveryTextureAsset = value;
                                break;
                            }
                        }
                    }

                    // Special handling: Generic livery should always use shared textures
                    const isGenericLivery = liveryName === 'Generic';
                    const hasLiveryTexture = !!liveryTextureAsset;

                    // For Generic: always use shared textures, ignore any textures it might have
                    // For other liveries: use livery texture -> base texture -> shared -> other liveries
                    if (isGenericLivery) {
                        // Generic: always search shared liveries first, ignore Generic's own textures
                        textureAsset = undefined; // Force search in shared liveries
                    } else {
                        // Normal fallback order: livery texture -> base texture -> shared livery textures -> other liveries
                        textureAsset = liveryTextureAsset || this.baseTextures.get(fileName);
                    }

                    // If not found (or Generic), search in shared liveries first
                    if (!textureAsset || isGenericLivery) {
                        const lowerFileName = fileName.toLowerCase();

                        // For Generic: explicitly check "Boeing 747-8-Shared" first (or similar shared livery name)
                        // This ensures Generic always uses the correct shared textures
                        if (isGenericLivery) {
                            // Find the shared livery explicitly (usually contains "Shared" in name)
                            let sharedLiveryKey: string | undefined;
                            const availableSharedLiveries: string[] = [];
                            for (const [key] of this.liveryTextures.entries()) {
                                if (this.isSharedLiveryName(key)) {
                                    availableSharedLiveries.push(key);
                                    if (!sharedLiveryKey) {
                                        sharedLiveryKey = key;
                                    }
                                }
                            }

                            if (sharedLiveryKey) {
                                const sharedLiveryTextures = this.liveryTextures.get(
                                    sharedLiveryKey
                                );
                                if (sharedLiveryTextures) {
                                    console.log(`[Generic Livery] Searching for "${fileName}" in shared livery "${sharedLiveryKey}" (${sharedLiveryTextures.size} textures available)`);
                                    for (const [
                                        texKey,
                                        texAsset,
                                    ] of sharedLiveryTextures.entries()) {
                                        if (texKey.toLowerCase() === lowerFileName) {
                                            textureAsset = texAsset;
                                            console.log(`[Generic Livery] ✓ Found "${fileName}" in shared livery "${sharedLiveryKey}"`);
                                            break;
                                        }
                                    }
                                    if (!textureAsset) {
                                        console.log(`[Generic Livery] ✗ "${fileName}" NOT found in "${sharedLiveryKey}". Available textures: ${Array.from(sharedLiveryTextures.keys()).slice(0, 10).join(', ')}${sharedLiveryTextures.size > 10 ? '...' : ''}`);
                                    }
                                } else {
                                    console.warn(`[Generic Livery] Shared livery "${sharedLiveryKey}" found but has no textures!`);
                                }
                            } else {
                                console.warn(`[Generic Livery] No shared liveries found! Available liveries: ${Array.from(this.liveryTextures.keys()).join(', ')}`);
                            }
                        }

                        // If still not found (or not Generic), search all shared liveries
                        if (!textureAsset) {
                            for (const [
                                liveryKey,
                                liveryTextureMap,
                            ] of this.liveryTextures.entries()) {
                                // Skip the current livery since we already checked it
                                if (liveryKey === liveryName) continue;

                                // Only check shared liveries
                                const isSharedLivery = this.isSharedLiveryName(liveryKey);
                                if (!isSharedLivery) continue;

                                for (const [texKey, texAsset] of liveryTextureMap.entries()) {
                                    if (texKey.toLowerCase() === lowerFileName) {
                                        textureAsset = texAsset;
                                        break;
                                    }
                                }
                                if (textureAsset) break;
                            }
                        }

                        // For Generic: if we didn't find it in shared liveries, log a warning
                        if (isGenericLivery && !textureAsset) {
                            console.warn(`[Viewer] Generic livery: ${fileName} NOT found in any shared livery!`);
                        }

                        // Second pass: if still not found and NOT Generic, search other liveries (for fallback)
                        // Generic should ONLY use shared textures, never other liveries
                        if (!textureAsset && !isGenericLivery) {
                            for (const [
                                liveryKey,
                                liveryTextureMap,
                            ] of this.liveryTextures.entries()) {
                                // Skip the current livery and shared liveries (already checked)
                                if (liveryKey === liveryName) continue;
                                if (this.isSharedLiveryName(liveryKey)) continue;

                                for (const [texKey, texAsset] of liveryTextureMap.entries()) {
                                    if (texKey.toLowerCase() === lowerFileName) {
                                        textureAsset = texAsset;
                                        break;
                                    }
                                }
                                if (textureAsset) break;
                            }
                        }

                        if (!textureAsset && textureProperty !== 'normalMap') {
                            // Only warn for missing non-normal-map textures
                            console.warn(`[Viewer] Texture not found: ${fileName} for property ${textureProperty}`);
                        }
                    }

                    if (textureAsset) {
                        pendingLoads++;

                        // Wait for texture to load if not already loaded
                        const applyTexture = () => {
                            if (!textureAsset.resource) {
                                return;
                            }

                            // Store the current texture for comparison
                            const currentTexture = (material as any)[textureProperty];
                            const newTexture = textureAsset.resource;

                            // For Generic: always re-apply textures to ensure they're from shared liveries
                            // For other liveries: only update if the texture is actually different
                            const shouldApply = isGenericLivery || currentTexture !== newTexture;

                            if (shouldApply) {
                                // Apply texture to material
                                (material as any)[textureProperty] = newTexture;

                                // If this is a normal map and normal maps are enabled, attach XXXY decode chunk
                                // Only apply XXXY decoding to .webp normal maps
                                if (textureProperty === 'normalMap') {
                                    const textureName = (newTexture as any).name || fileName || '';
                                    const isWebpNormalMap = textureName.toLowerCase().endsWith('.webp') || 
                                                           textureName.toLowerCase().includes('_n.webp');
                                    
                                    if (isWebpNormalMap) {
                                        if (!(material as any)._originalNormalMap) {
                                            (material as any)._originalNormalMap = newTexture;
                                        }
                                        if (this.observer.get('debug.normalMaps')) {
                                            if (!material.chunks) {
                                                (material as any).chunks = {};
                                            }
                                            (material.chunks as any).normalMapPS = normalMapXXXYGlsl;
                                            console.log(
                                                '[NORMAL MAP] Attached XXXY decode chunk via livery for material:',
                                                material.name
                                            );
                                        }
                                    }
                                }

                                // Explicit material update call
                                material.update();

                                appliedCount++;
                            }

                            completedLoads++;
                            updateProgress();
                            checkComplete();
                        };

                        // If texture is already loaded, apply immediately
                        if (textureAsset.resource) {
                            applyTexture();
                        } else {
                            // Wait for texture to load
                            textureAsset.ready(() => {
                                applyTexture();
                            });
                        }
                    }
                });
            });

            // Update progress with total count after counting all pending loads
            if (pendingLoads > 0) {
                updateProgress();
            }

            // If no textures to load, resolve immediately
            if (pendingLoads === 0) {
                resolve();
            } else {
                checkComplete();
            }
        });
    }

    // Apply livery textures immediately (synchronously) - assumes textures are preloaded
    private applyLiveryTexturesImmediate(liveryName: string): void {
        const liveryTextures = this.liveryTextures.get(liveryName);
        const isGenericLivery = liveryName === 'Generic';
        let appliedCount = 0;
        
        if (isGenericLivery) {
            console.log(`[Generic Livery] Applying textures from shared livery. Material count: ${this.materialTextureMap.size}, Available shared liveries: ${Array.from(this.liveryTextures.keys()).filter(k => this.isSharedLiveryName(k)).join(', ')}`);
        }

        // Apply textures to all materials
        this.materialTextureMap.forEach((textureMap, material) => {
            textureMap.forEach((fileName, textureProperty) => {
                let textureAsset: Asset | undefined;
                const liveryTextures = this.liveryTextures.get(liveryName);

                // Case-insensitive lookup for livery textures
                let liveryTextureAsset: Asset | undefined;
                if (liveryTextures) {
                    const lowerFileName = fileName.toLowerCase();
                    for (const [key, value] of liveryTextures.entries()) {
                        if (key.toLowerCase() === lowerFileName) {
                            liveryTextureAsset = value;
                            break;
                        }
                    }
                }

                // For Generic: always search shared liveries first
                if (isGenericLivery) {
                    textureAsset = undefined; // Force search in shared liveries
                } else {
                    textureAsset = liveryTextureAsset || this.baseTextures.get(fileName);
                }

                // If not found (or Generic), search in shared liveries
                if (!textureAsset || isGenericLivery) {
                    const lowerFileName = fileName.toLowerCase();

                    if (isGenericLivery) {
                        // Find the shared livery explicitly
                        let sharedLiveryKey: string | undefined;
                        const availableSharedLiveries: string[] = [];
                        for (const [key] of this.liveryTextures.entries()) {
                            if (this.isSharedLiveryName(key)) {
                                availableSharedLiveries.push(key);
                                if (!sharedLiveryKey) {
                                    sharedLiveryKey = key;
                                }
                            }
                        }

                        if (sharedLiveryKey) {
                            const sharedLiveryTextures = this.liveryTextures.get(sharedLiveryKey);
                            if (sharedLiveryTextures) {
                                for (const [texKey, texAsset] of sharedLiveryTextures.entries()) {
                                    if (texKey.toLowerCase() === lowerFileName) {
                                        textureAsset = texAsset;
                                        break;
                                    }
                                }
                            } else {
                                console.warn(`[Generic Livery] Shared livery "${sharedLiveryKey}" found but has no textures!`);
                            }
                        } else {
                            console.warn(`[Generic Livery] No shared liveries found! Available liveries: ${Array.from(this.liveryTextures.keys()).join(', ')}`);
                        }
                    }

                    // If still not found, search all shared liveries
                    if (!textureAsset) {
                        for (const [liveryKey, liveryTextureMap] of this.liveryTextures.entries()) {
                            if (liveryKey === liveryName) continue;
                            if (!this.isSharedLiveryName(liveryKey)) continue;

                            for (const [texKey, texAsset] of liveryTextureMap.entries()) {
                                if (texKey.toLowerCase() === lowerFileName) {
                                    textureAsset = texAsset;
                                    break;
                                }
                            }
                            if (textureAsset) break;
                        }
                    }

                    // For Generic: if still not found in shared liveries, check base textures as fallback
                    if (!textureAsset && isGenericLivery) {
                        textureAsset = this.baseTextures.get(fileName);
                    }

                    // If still not found and NOT Generic, search other liveries
                    if (!textureAsset && !isGenericLivery) {
                        for (const [liveryKey, liveryTextureMap] of this.liveryTextures.entries()) {
                            if (liveryKey === liveryName) continue;
                            if (this.isSharedLiveryName(liveryKey)) continue;

                            for (const [texKey, texAsset] of liveryTextureMap.entries()) {
                                if (texKey.toLowerCase() === lowerFileName) {
                                    textureAsset = texAsset;
                                    break;
                                }
                            }
                            if (textureAsset) break;
                        }
                    }
                }

                if (textureAsset) {
                    if (!textureAsset.resource) {
                        // Texture found but not loaded yet - this shouldn't happen if preloading worked
                        if (isGenericLivery) {
                            console.warn(`[Generic Livery] ⚠ Texture "${fileName}" found but resource not ready yet for material "${material.name || 'Unnamed'}"`);
                        }
                    } else {
                        // Texture is loaded, apply immediately
                        const currentTexture = (material as any)[textureProperty];
                        const newTexture = textureAsset.resource;
                        const shouldApply = isGenericLivery || currentTexture !== newTexture;

                            if (shouldApply) {
                            // Apply texture to material
                            (material as any)[textureProperty] = newTexture;

                            // If this is a normal map and normal maps are enabled, attach XXXY decode chunk
                            // Only apply XXXY decoding to .webp normal maps
                            if (textureProperty === 'normalMap') {
                                const textureName = (newTexture as any).name || fileName || '';
                                const isWebpNormalMap = textureName.toLowerCase().endsWith('.webp') || 
                                                       textureName.toLowerCase().includes('_n.webp');
                                
                                if (isWebpNormalMap) {
                                    if (!(material as any)._originalNormalMap) {
                                        (material as any)._originalNormalMap = newTexture;
                                    }
                                    if (this.observer.get('debug.normalMaps')) {
                                        if (!material.chunks) {
                                            (material as any).chunks = {};
                                        }
                                        (material.chunks as any).normalMapPS = normalMapXXXYGlsl;
                                    }
                                }
                            }

                            // Explicit material update call
                            material.update();
                            appliedCount++;
                        }
                    }
                } else if (!textureAsset && isGenericLivery) {
                    // For Generic livery, log missing textures
                    console.warn(`[Generic Livery] ✗ Texture "${fileName}" (${textureProperty}) NOT found for material "${material.name || 'Unnamed'}"`);
                }
            });
        });
        
        if (isGenericLivery) {
            console.log(`[Generic Livery] Summary: Applied ${appliedCount} texture(s) to materials`);
        }
    }

    // Preload all textures for all available liveries in the background
    private async preloadAllLiveryTextures(): Promise<void> {
        const availableLiveries = this.observer.get('liveries.available') || [];
        const preloadPromises: Promise<void>[] = [];

        // Collect all unique texture assets that need to be preloaded
        const texturesToPreload = new Set<Asset>();

        // Add base textures
        this.baseTextures.forEach((asset) => {
            if (asset && !texturesToPreload.has(asset)) {
                texturesToPreload.add(asset);
            }
        });

        // Add all livery textures
        this.liveryTextures.forEach((liveryTextureMap) => {
            liveryTextureMap.forEach((asset) => {
                if (asset && !texturesToPreload.has(asset)) {
                    texturesToPreload.add(asset);
                }
            });
        });

        console.log(`[LIVERY PRELOAD] Found ${texturesToPreload.size} unique textures to preload`);

        // Preload all textures
        texturesToPreload.forEach((asset) => {
            if (!asset.resource) {
                // Texture not loaded yet, wait for it
                const preloadPromise = new Promise<void>((resolve) => {
                    asset.ready(() => {
                        this.preloadedTextures.add(asset);
                        resolve();
                    });
                });
                preloadPromises.push(preloadPromise);
            } else {
                // Already loaded
                this.preloadedTextures.add(asset);
            }
        });

        // Wait for all textures to load
        await Promise.all(preloadPromises);
        console.log(`[LIVERY PRELOAD] Completed preloading ${this.preloadedTextures.size} textures`);
    }

    setDebugStats(show: boolean) {
        this.miniStats.enabled = show;
        this.renderNextFrame();
    }

    setDebugWireframe(show: boolean) {
        this.showWireframe = show;
        this.dirtyWireframe = true;
        this.renderNextFrame();
    }

    setWireframeColor(color: { r: number; g: number; b: number }) {
        this.wireframeMaterial.emissive = new Color(color.r, color.g, color.b);
        this.wireframeMaterial.update();
        this.renderNextFrame();
    }

    setDebugBounds(show: boolean) {
        this.showBounds = show;
        this.dirtyBounds = true;
        this.renderNextFrame();
    }

    setDebugSkeleton(show: boolean) {
        this.showSkeleton = show;
        this.dirtySkeleton = true;
        this.renderNextFrame();
    }

    setDebugAxes(show: boolean) {
        this.showAxes = show;
        this.dirtySkeleton = true;
        this.renderNextFrame();
    }

    setDebugGrid(show: boolean) {
        this.showGrid = show;
        this.dirtyGrid = true;
        this.renderNextFrame();
    }

    setNormalLength(length: number) {
        this.normalLength = length;
        this.dirtyNormals = true;
        this.renderNextFrame();
    }

    setFov(fov: number) {
        this.camera.camera.fov = fov;
        this.renderNextFrame();
    }

    private setupGlobalInputFocusTracking() {
        console.log('[setupGlobalInputFocusTracking] Setting up global input tracking');

        document.addEventListener('focusin', (e) => {
            const target = e.target as HTMLElement;
            const tag = target.tagName;
            console.log('[Global Focus Event] Target tag:', tag, 'Element:', target);
            if (tag === 'INPUT' || tag === 'TEXTAREA' || (target as any).isContentEditable) {
                console.log('[Global Input Focus] Setting input focus flag (keyboard only)');
                this.isAnyInputFocused = true;
                // NOTE: We no longer disable cameraControls.enabled here because that
                // blocks mouse orbit/pan/zoom as well as keyboard. The isAnyInputFocused
                // flag is checked by keyboard handlers to prevent WASD/F/R key processing.
                // Mouse controls should continue to work while typing.
            }
        });

        document.addEventListener('focusout', (e) => {
            const target = e.target as HTMLElement;
            const tag = target.tagName;
            console.log('[Global Blur Event] Target tag:', tag);
            if (tag === 'INPUT' || tag === 'TEXTAREA' || (target as any).isContentEditable) {
                console.log('[Global Input Blur] Clearing input focus flag');
                this.isAnyInputFocused = false;
            }
        });
    }

    setOrbitMode(mode: 'target' | 'camera-center') {
        console.log('Setting orbit mode to:', mode);

        if (mode === 'camera-center') {
            // Disable the standard camera controls
            this.cameraControls.enabled = false;
            console.log('[setOrbitMode] Disabled cameraControls for camera-center mode');

            // Implement custom camera-center rotation
            this.setupCameraCenterControls();
        } else {
            // Re-enable standard target-based orbit controls
            this.removeCameraCenterControls();

            // Clear any active keys that might interfere
            if (this.cameraCenterMouseHandlers) {
                // Clear the keysPressed set if it still exists
                const keysPressed = (this.cameraCenterMouseHandlers as any).keysPressed;
                if (keysPressed && keysPressed.clear) {
                    keysPressed.clear();
                }
            }

            this.cameraControls.enabled = true;
            console.log('[setOrbitMode] Enabled cameraControls for target mode');
            
            // Focus camera on scene with proper distance (only if a model is loaded)
            if (this.entities && this.entities.length > 0) {
                this.focusSelection();
                
                const sceneSize = this.cameraControls.sceneSize || 1;
                // @ts-ignore
                let zoomDist = this.cameraControls._zoomDist || 0;
                
                // If zoom distance is too small, set it to a reasonable value
                if (zoomDist < sceneSize * 0.5) {
                    const newZoomDist = sceneSize * 2.5;
                    // @ts-ignore
                    this.cameraControls._zoomDist = newZoomDist;
                    zoomDist = newZoomDist;
                    console.log(`[setOrbitMode] Zoom was too close, set to ${newZoomDist}`);
                }
                
                console.log(`[setOrbitMode] Camera focused - sceneSize: ${sceneSize}, zoomDist: ${zoomDist}`);
            } else {
                console.log('[setOrbitMode] No model loaded yet, skipping focus');
            }
        }

        this.renderNextFrame();
    }

    // Global keyboard state for UI camera modes
    private uiCameraKeysPressed: Set<string> = new Set();
    private uiCameraKeyboardInitialized = false;

    /**
     * Set up global keyboard handler for UI camera modes (called once)
     */
    private setupUICameraKeyboard() {
        if (this.uiCameraKeyboardInitialized) return;
        this.uiCameraKeyboardInitialized = true;

        const onKeyDown = (e: KeyboardEvent) => {
            if (this.isAnyInputFocused) {
                const activeElement = document.activeElement as HTMLElement | null;
                const activeTag = activeElement?.tagName;
                const isTypingTarget = Boolean(
                    activeElement &&
                    (activeTag === 'INPUT' ||
                        activeTag === 'TEXTAREA' ||
                        (activeElement as any).isContentEditable)
                );

                if (!isTypingTarget) {
                    // Prevent a stale focus flag from blocking camera input
                    this.isAnyInputFocused = false;
                } else {
                    this.uiCameraKeysPressed.clear();
                    return;
                }
            }

            // Track WASD, Q, E, F, R keys
            if (['KeyW', 'KeyA', 'KeyS', 'KeyD', 'KeyQ', 'KeyE', 'KeyF', 'KeyR'].includes(e.code)) {
                // Don't process Shift+WASD (used for aircraft rotation)
                if (e.shiftKey && ['KeyW', 'KeyA', 'KeyS', 'KeyD'].includes(e.code)) {
                    return;
                }
                this.uiCameraKeysPressed.add(e.code);
                e.preventDefault();
                // Trigger render to process the key
                this.renderNextFrame();
            }
        };

        const onKeyUp = (e: KeyboardEvent) => {
            if (['KeyW', 'KeyA', 'KeyS', 'KeyD', 'KeyQ', 'KeyE', 'KeyF', 'KeyR'].includes(e.code)) {
                this.uiCameraKeysPressed.delete(e.code);
            }
        };

        window.addEventListener('keydown', onKeyDown, true);
        window.addEventListener('keyup', onKeyUp, true);
        
        console.log('[setupUICameraKeyboard] Global UI camera keyboard handler initialized');
    }

    /**
     * Set the UI camera mode (separate from internal orbitMode used by screenshots)
     * - 'orbit': Camera orbits around aircraft center, WASD changes orbit angles
     * - 'free': Free-fly camera, WASD moves camera position
     */
    setUICameraMode(mode: 'orbit' | 'free') {
        console.log('[setUICameraMode] Setting UI camera mode to:', mode);

        // Initialize global keyboard handler (only once)
        this.setupUICameraKeyboard();

        // CRITICAL: Disable PlayCanvas camera controls FIRST to prevent interference
        if (this.cameraControls) {
            this.cameraControls.enabled = false;
        }

        // Clear any pressed keys when switching modes
        this.uiCameraKeysPressed.clear();

        // Clean up any existing UI camera controls (mouse handlers only now)
        this.removeUIOrbitCameraControls();
        this.removeCameraCenterControls();

        if (mode === 'orbit') {
            // Use custom orbit camera with WASD angle control
            this.setupUIOrbitCamera();
        } else {
            // Use free camera (same as camera-center)
            this.setupCameraCenterControls();
        }

        // Ensure the canvas can receive keyboard focus for WASD on first load
        this.ensureUICameraFocus();

        this.renderNextFrame();
    }

    private ensureUICameraFocus() {
        const activeElement = document.activeElement as HTMLElement | null;
        const activeTag = activeElement?.tagName;
        const isTypingTarget = Boolean(
            activeElement &&
            (activeTag === 'INPUT' ||
                activeTag === 'TEXTAREA' ||
                (activeElement as any).isContentEditable)
        );

        if (!isTypingTarget && this.canvas) {
            if (this.canvas.tabIndex < 0) {
                this.canvas.tabIndex = 0;
            }
            try {
                this.canvas.focus({ preventScroll: true });
            } catch {
                this.canvas.focus();
            }
        }
    }

    // UI Orbit Camera handlers storage (keyboard is handled globally)
    private uiOrbitCameraHandlers: {
        onMouseDown?: (e: MouseEvent) => void;
        onMouseMove?: (e: MouseEvent) => void;
        onMouseUp?: (e: MouseEvent) => void;
        onWheel?: (e: WheelEvent) => void;
        updateOrbit?: (deltaTime: number) => void;
    } = null;

    /**
     * Setup the UI Orbit Camera
     * - Orbits around aircraft center at a fixed distance
     * - WASD changes azimuth (A/D) and elevation (W/S)
     * - Mouse drag also changes angles
     * - Mouse wheel changes distance
     */
    private setupUIOrbitCamera() {
        const canvas = this.canvas;
        let isDragging = false;
        let lastMouseX = 0;
        let lastMouseY = 0;

        // Orbit parameters
        let azimuth = this.observer.get('camera.orbitAzimuth') || 45;  // horizontal angle (degrees)
        let elevation = this.observer.get('camera.orbitElevation') || 20; // vertical angle (degrees)
        let distance = this.observer.get('camera.orbitDistance') || 50;

        // Get scene size for distance calculation
        const sceneSize = this.cameraControls?.sceneSize || 10;
        if (distance < sceneSize * 1.5) {
            distance = sceneSize * 2.5;
        }

        // Target orbit parameters (for smooth interpolation)
        let targetAzimuth = azimuth;
        let targetElevation = elevation;
        let targetDistance = distance;

        // Smoothing
        const smoothSpeed = 10.0;

        // Use global keyboard state
        const keysPressed = this.uiCameraKeysPressed;
        const rotateSpeed = 90; // degrees per second

        // Get the orbit target (center of scene/aircraft)
        const getOrbitTarget = (): Vec3 => {
            if (this.dynamicSceneBounds) {
                return this.dynamicSceneBounds.center.clone();
            }
            return new Vec3(0, 0, 0);
        };

        // Update camera position based on orbit parameters
        const updateCameraPosition = () => {
            const target = getOrbitTarget();
            
            // Convert spherical to Cartesian
            const azimuthRad = azimuth * Math.PI / 180;
            const elevationRad = elevation * Math.PI / 180;

            const x = target.x + distance * Math.cos(elevationRad) * Math.sin(azimuthRad);
            const y = target.y + distance * Math.sin(elevationRad);
            const z = target.z + distance * Math.cos(elevationRad) * Math.cos(azimuthRad);

            this.camera.setPosition(x, y, z);
            this.camera.lookAt(target);

            // Update observer values
            this.observer.set('camera.orbitAzimuth', azimuth);
            this.observer.set('camera.orbitElevation', elevation);
            this.observer.set('camera.orbitDistance', distance);
        };

        // Initialize camera position
        updateCameraPosition();

        const onMouseDown = (e: MouseEvent) => {
            if (e.button === 0) {
                isDragging = true;
                lastMouseX = e.clientX;
                lastMouseY = e.clientY;
                canvas.style.cursor = 'grabbing';
                e.preventDefault();
            }
        };

        const onMouseMove = (e: MouseEvent) => {
            if (!isDragging) return;

            const deltaX = e.clientX - lastMouseX;
            const deltaY = e.clientY - lastMouseY;

            const sensitivity = 0.3;
            targetAzimuth -= deltaX * sensitivity;
            targetElevation += deltaY * sensitivity;

            // Clamp elevation
            targetElevation = Math.max(-89, Math.min(89, targetElevation));

            lastMouseX = e.clientX;
            lastMouseY = e.clientY;
        };

        const onMouseUp = (e: MouseEvent) => {
            if (e.button === 0) {
                isDragging = false;
                canvas.style.cursor = 'default';
            }
        };

        const onWheel = (e: WheelEvent) => {
            // Zoom in/out
            const zoomFactor = e.deltaY > 0 ? 1.1 : 0.9;
            targetDistance *= zoomFactor;
            
            // Clamp distance
            const minDist = sceneSize * 0.5;
            const maxDist = sceneSize * 20;
            targetDistance = Math.max(minDist, Math.min(maxDist, targetDistance));

            e.preventDefault();
        };

        // Keyboard is now handled by global handler - just use keysPressed in update

        const updateOrbit = (deltaTime: number) => {
            // Only process in orbit mode
            const uiCameraMode = this.observer.get('camera.uiCameraMode') || 'orbit';
            if (uiCameraMode !== 'orbit') {
                return;
            }

            // Process WASD input from global keyboard state
            if (keysPressed.size > 0) {
                const angleDelta = rotateSpeed * deltaTime;

                // A/D control azimuth (horizontal orbit)
                if (keysPressed.has('KeyA')) {
                    targetAzimuth -= angleDelta;  // A = orbit left (camera moves left around object)
                }
                if (keysPressed.has('KeyD')) {
                    targetAzimuth += angleDelta;  // D = orbit right (camera moves right around object)
                }
                // W/S control elevation (vertical orbit)
                if (keysPressed.has('KeyW')) {
                    targetElevation += angleDelta;  // W = orbit up (camera moves up)
                }
                if (keysPressed.has('KeyS')) {
                    targetElevation -= angleDelta;  // S = orbit down (camera moves down)
                }
                if (keysPressed.has('KeyQ')) {
                    targetDistance *= (1 - deltaTime);  // Zoom in
                }
                if (keysPressed.has('KeyE')) {
                    targetDistance *= (1 + deltaTime);  // Zoom out
                }
                if (keysPressed.has('KeyF')) {
                    // Focus - reset to default view
                    targetAzimuth = 45;
                    targetElevation = 20;
                    targetDistance = sceneSize * 2.5;
                }
                if (keysPressed.has('KeyR')) {
                    // Reset view
                    targetAzimuth = 0;
                    targetElevation = 0;
                    targetDistance = sceneSize * 2.5;
                }

                // Clamp elevation
                targetElevation = Math.max(-89, Math.min(89, targetElevation));

                // Clamp distance
                const minDist = sceneSize * 0.5;
                const maxDist = sceneSize * 20;
                targetDistance = Math.max(minDist, Math.min(maxDist, targetDistance));
            }

            // Smooth interpolation
            const t = 1 - Math.exp(-smoothSpeed * deltaTime);
            azimuth += (targetAzimuth - azimuth) * t;
            elevation += (targetElevation - elevation) * t;
            distance += (targetDistance - distance) * t;

            // Update camera if there's significant change
            if (Math.abs(targetAzimuth - azimuth) > 0.01 || 
                Math.abs(targetElevation - elevation) > 0.01 ||
                Math.abs(targetDistance - distance) > 0.01 ||
                keysPressed.size > 0) {
                updateCameraPosition();
                this.renderNextFrame();
            }
        };

        // Register mouse event listeners (keyboard is handled globally)
        canvas.addEventListener('mousedown', onMouseDown);
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
        canvas.addEventListener('wheel', onWheel, { passive: false });

        // Store handlers for cleanup
        this.uiOrbitCameraHandlers = {
            onMouseDown,
            onMouseMove,
            onMouseUp,
            onWheel,
            updateOrbit
        };

        console.log('[setupUIOrbitCamera] UI Orbit Camera initialized');
        console.log(`  Azimuth: ${azimuth}°, Elevation: ${elevation}°, Distance: ${distance}`);
    }

    private removeUIOrbitCameraControls() {
        if (!this.uiOrbitCameraHandlers) return;

        const canvas = this.canvas;

        if (this.uiOrbitCameraHandlers.onMouseDown) {
            canvas.removeEventListener('mousedown', this.uiOrbitCameraHandlers.onMouseDown);
        }
        if (this.uiOrbitCameraHandlers.onMouseMove) {
            window.removeEventListener('mousemove', this.uiOrbitCameraHandlers.onMouseMove);
        }
        if (this.uiOrbitCameraHandlers.onMouseUp) {
            window.removeEventListener('mouseup', this.uiOrbitCameraHandlers.onMouseUp);
        }
        if (this.uiOrbitCameraHandlers.onWheel) {
            canvas.removeEventListener('wheel', this.uiOrbitCameraHandlers.onWheel);
        }
        // Note: Keyboard is handled globally, no per-mode cleanup needed

        this.uiOrbitCameraHandlers = null;
        console.log('[removeUIOrbitCameraControls] UI Orbit Camera controls removed');
    }

    // Flag to temporarily disable UI camera controls (for screenshots)
    private uiCameraControlsDisabled = false;

    // Track graphics device loss (Firefox can throw InvalidStateError on lost context)
    private deviceLost = false;

    /**
     * Temporarily disable UI camera controls for programmatic camera control (screenshots)
     */
    disableUICameraControls() {
        this.uiCameraControlsDisabled = true;
        this.removeUIOrbitCameraControls();
        this.removeCameraCenterControls();
        console.log('[disableUICameraControls] UI camera controls disabled for programmatic control');
    }

    /**
     * Re-enable UI camera controls after programmatic camera control
     */
    enableUICameraControls() {
        this.uiCameraControlsDisabled = false;
        const mode = this.observer.get('camera.uiCameraMode') || 'orbit';
        this.setUICameraMode(mode);
        console.log('[enableUICameraControls] UI camera controls re-enabled');
    }

    private setupCameraCenterControls() {
        const canvas = this.canvas;
        let isRotating = false;
        let lastMouseX = 0;
        let lastMouseY = 0;

        // Use global keyboard state
        const keysPressed = this.uiCameraKeysPressed;
        const baseMoveSpeed = 10.0; // base units per second
        let speedMultiplier = 0.02; // 0.02x to 12x speed multiplier (controlled by mouse wheel)

        // Rotation interpolation
        let targetYaw = 0;
        let targetPitch = 0;
        let currentYaw = 0;
        let currentPitch = 0;
        const rotationSpeed = 8.0; // interpolation speed

        // Position interpolation
        let currentVelocity = new Vec3();
        let targetVelocity = new Vec3();
        const velocitySmoothing = 15.0; // how quickly velocity changes (higher = more responsive, increased from 8.0)
        const velocityDamping = 0.85; // how quickly velocity decays when no input (lower = more damping)

        // Initialize target rotations from current camera orientation
        const initializeRotationTargets = () => {
            // Calculate yaw and pitch from camera's forward direction
            // This is more reliable than getLocalEulerAngles() after lookAt()
            const forward = this.camera.forward;
            
            // Pitch: In free cam, negative pitch = looking down, positive = looking up
            // forward.y: negative = looking down, positive = looking up
            // So pitch = asin(forward.y) gives the right sign
            currentPitch = targetPitch = Math.asin(forward.y) * (180 / Math.PI);
            currentPitch = targetPitch = Math.max(-89, Math.min(89, currentPitch));
            
            // Yaw: angle in XZ plane
            // forward.z negative = looking toward -Z, forward.x negative = looking toward -X
            currentYaw = targetYaw = Math.atan2(-forward.x, -forward.z) * (180 / Math.PI);
            
            // Apply the calculated rotation to ensure camera is properly oriented
            this.camera.setLocalEulerAngles(currentPitch, currentYaw, 0);
            
            console.log(
                `[Camera-Center] Initialized from forward vector - Yaw: ${currentYaw.toFixed(1)}°, Pitch: ${currentPitch.toFixed(1)}°, Forward: (${forward.x.toFixed(2)}, ${forward.y.toFixed(2)}, ${forward.z.toFixed(2)})`
            );
        };

        // Store this function so it can be called externally to reinitialize after programmatic camera moves
        // @ts-ignore
        this.reinitializeCameraCenterRotation = initializeRotationTargets;

        // Call this when first setting up
        initializeRotationTargets();

        const onMouseDown = (event: MouseEvent) => {
            if (event.button === 0) {
                // Left mouse button
                isRotating = true;
                lastMouseX = event.clientX;
                lastMouseY = event.clientY;
                canvas.style.cursor = 'grabbing';
                event.preventDefault();
            }
        };

        const onMouseMove = (event: MouseEvent) => {
            if (!isRotating) return;

            const deltaX = event.clientX - lastMouseX;
            const deltaY = event.clientY - lastMouseY;

            const sensitivity = 0.5;

            // Update target rotations instead of directly rotating camera
            targetYaw -= deltaX * sensitivity;
            targetPitch -= deltaY * sensitivity;

            // Clamp pitch to prevent camera flipping
            targetPitch = Math.max(-89, Math.min(89, targetPitch));

            lastMouseX = event.clientX;
            lastMouseY = event.clientY;
        };

        const onMouseUp = (event: MouseEvent) => {
            if (event.button === 0) {
                isRotating = false;
                canvas.style.cursor = 'default';
            }
        };

        const onWheel = (event: WheelEvent) => {
            // Control movement speed instead of zoom
            const speedChange = event.deltaY > 0 ? -0.02 : 0.02;
            speedMultiplier = Math.max(0.02, Math.min(12.0, speedMultiplier + speedChange));

            // Update the handlers object with new speed multiplier
            if (this.cameraCenterMouseHandlers) {
                this.cameraCenterMouseHandlers.speedMultiplier = speedMultiplier;
            }

            // Visual feedback could be added here (e.g., UI indicator)

            event.preventDefault();
        };

        // Keyboard is now handled by global handler - just use keysPressed in update

        // Movement and rotation update function called each frame
        // Debug: log every 60 frames to show keys state
        let frameCount = 0;
        const updateMovement = (deltaTime: number) => {
            frameCount++;
            
            // Only update movement in free camera mode
            const uiCameraMode = this.observer.get('camera.uiCameraMode') || 'orbit';
            if (uiCameraMode !== 'free') {
                // Clear velocity when not in free mode (but don't clear global keysPressed)
                currentVelocity.set(0, 0, 0);
                return;
            }

            const camera = this.camera;

            // Log every second if keys are pressed
            if (keysPressed.size > 0 && frameCount % 60 === 0) {
                console.log(`[Camera-Center updateMovement] Keys: [${Array.from(keysPressed).join(', ')}], speedMultiplier: ${speedMultiplier}`);
            }

            // Interpolate camera rotations smoothly
            const rotationLerpSpeed = rotationSpeed * deltaTime;
            currentYaw += (targetYaw - currentYaw) * rotationLerpSpeed;
            currentPitch += (targetPitch - currentPitch) * rotationLerpSpeed;

            // Apply interpolated rotations
            camera.setLocalEulerAngles(currentPitch, currentYaw, 0);

            // Handle WASD movement with smooth velocity interpolation
            targetVelocity.set(0, 0, 0);

            if (keysPressed.size > 0) {
                const moveSpeed = baseMoveSpeed * speedMultiplier;

                // Forward/Backward (W/S) - along camera's forward axis
                if (keysPressed.has('KeyW')) {
                    const forward = camera.forward.clone();
                    forward.mulScalar(moveSpeed);
                    targetVelocity.add(forward);
                }
                if (keysPressed.has('KeyS')) {
                    const backward = camera.forward.clone();
                    backward.mulScalar(-moveSpeed);
                    targetVelocity.add(backward);
                }

                // Left/Right (A/D) - along camera's right axis
                if (keysPressed.has('KeyA')) {
                    const left = camera.right.clone();
                    left.mulScalar(-moveSpeed);
                    targetVelocity.add(left);
                }
                if (keysPressed.has('KeyD')) {
                    const right = camera.right.clone();
                    right.mulScalar(moveSpeed);
                    targetVelocity.add(right);
                }

                // Up/Down (Q/E) - along world up axis
                if (keysPressed.has('KeyQ')) {
                    targetVelocity.y -= moveSpeed;
                }
                if (keysPressed.has('KeyE')) {
                    targetVelocity.y += moveSpeed;
                }
            }

            // Smoothly interpolate current velocity towards target velocity
            const velocityLerpSpeed = velocitySmoothing * deltaTime;
            currentVelocity.lerp(currentVelocity, targetVelocity, velocityLerpSpeed);

            // Apply velocity damping when no input to create smooth stopping
            if (keysPressed.size === 0) {
                currentVelocity.mulScalar(Math.pow(velocityDamping, deltaTime * 60)); // frame-rate independent damping
            }

            // Apply the smoothed velocity to camera position
            if (currentVelocity.length() > 0.001) {
                // threshold to prevent micro-movements
                const movement = currentVelocity.clone();
                movement.mulScalar(deltaTime);
                
                camera.translate(movement);
                
                // Log movement when it's significant (AFTER translate to show new position)
                if (movement.length() > 0.01 && frameCount % 30 === 0) {
                    const pos = camera.getPosition();
                    console.log(`[Camera-Center] Moved camera: delta=(${movement.x.toFixed(3)}, ${movement.y.toFixed(3)}, ${movement.z.toFixed(3)}), newPos=(${pos.x.toFixed(2)}, ${pos.y.toFixed(2)}, ${pos.z.toFixed(2)})`);
                }
            }

            // Always render when in camera center mode (for smooth rotation interpolation)
            this.renderNextFrame();
        };

        // Store references for cleanup (keyboard is handled globally)
        this.cameraCenterMouseHandlers = {
            onMouseDown,
            onMouseMove,
            onMouseUp,
            onWheel,
            updateMovement,
            speedMultiplier: speedMultiplier,
        } as any;

        canvas.addEventListener('mousedown', onMouseDown);
        canvas.addEventListener('mousemove', onMouseMove);
        canvas.addEventListener('mouseup', onMouseUp);
        canvas.addEventListener('wheel', onWheel, { passive: false });
        // Note: Keyboard is handled globally, no per-mode registration needed
        
        console.log('[Camera-Center] Free camera initialized');
    }

    private removeCameraCenterControls() {
        if (this.cameraCenterMouseHandlers) {
            const canvas = this.canvas;
            canvas.removeEventListener('mousedown', this.cameraCenterMouseHandlers.onMouseDown);
            canvas.removeEventListener('mousemove', this.cameraCenterMouseHandlers.onMouseMove);
            canvas.removeEventListener('mouseup', this.cameraCenterMouseHandlers.onMouseUp);
            canvas.removeEventListener('wheel', this.cameraCenterMouseHandlers.onWheel);
            // Note: Keyboard is handled globally, no per-mode cleanup needed

            canvas.style.cursor = 'default';
            this.cameraCenterMouseHandlers = null;
        }
    }

    setRenderMode(renderMode: string) {
        this.camera.camera.setShaderPass(
            renderMode !== 'default' ? `debug_${renderMode}` : 'forward'
        );
        this.renderNextFrame();
    }

    setLightEnabled(value: boolean) {
        this.light.enabled = value;
        this.renderNextFrame();
    }

    setLightIntensity(factor: number) {
        this.light.light.intensity = factor;
        this.renderNextFrame();
    }

    setAmbientLightIntensity(value: number) {
        const intensity = Math.max(0, Math.min(1, value));
        this.app.scene.ambientLight = new Color(intensity, intensity, intensity);
        this.renderNextFrame();
    }

    setLightColor(color: { r: number; g: number; b: number }) {
        this.light.light.color = new Color(color.r, color.g, color.b);
        this.renderNextFrame();
    }

    setLightFollow(enable: boolean) {
        this.light.reparent(enable ? this.camera : this.app.root);
        if (enable) {
            this.light.setLocalEulerAngles(90, 0, 0);
        } else {
            this.light.setLocalEulerAngles(45, 30, 0);
        }
        this.renderNextFrame();
    }

    setLightShadow(enable: boolean) {
        this.light.light.castShadows = enable;
        this.renderNextFrame();
    }

    setShadowCatcherEnabled(value: boolean) {
        this.shadowCatcher.enabled = value;
        this.renderNextFrame();
    }

    setShadowCatcherIntensity(value: number) {
        this.shadowCatcher.intensity = value;
        this.renderNextFrame();
    }

    setAircraftRotation() {
        if (!this.sceneRoot || this.entities.length === 0) {
            return;
        }

        const pitch = this.observer.get('aircraftRotation.pitch') || 0;
        const roll = this.observer.get('aircraftRotation.roll') || 0;

        // Store original rotation if not already stored
        if (this.originalSceneRootRotation === null) {
            this.originalSceneRootRotation = this.sceneRoot.getLocalEulerAngles().clone();
        }

        // Ensure original entity transforms are stored
        if (this.originalEntityTransforms.length === 0 && this.entities.length > 0) {
            this.entities.forEach((entity) => {
                this.originalEntityTransforms.push({
                    position: entity.getLocalPosition().clone(),
                    rotation: entity.getLocalEulerAngles().clone(),
                });
            });
        }

        const isCentered = this.observer.get('centerScene');

        if (isCentered) {
            // Apply rotation to scene root
            const newRotation = new Vec3(
                this.originalSceneRootRotation.x + pitch,
                this.originalSceneRootRotation.y,
                this.originalSceneRootRotation.z + roll
            );
            this.sceneRoot.setLocalEulerAngles(newRotation.x, newRotation.y, newRotation.z);
        } else {
            // Apply rotation to individual entities
            this.entities.forEach((entity, index) => {
                if (index < this.originalEntityTransforms.length) {
                    const originalRot = this.originalEntityTransforms[index].rotation;
                    const newRotation = new Vec3(
                        originalRot.x + pitch,
                        originalRot.y,
                        originalRot.z + roll
                    );
                    entity.setLocalEulerAngles(newRotation.x, newRotation.y, newRotation.z);
                }
            });
        }

        this.renderNextFrame();
    }

    setSkyboxExposure(factor: number) {
        this.app.scene.skyboxIntensity = Math.pow(2, factor);
        this.renderNextFrame();
    }

    setSkyboxRotation(factor: number) {
        const rot = new Quat();
        rot.setFromEulerAngles(0, factor, 0);
        this.app.scene.skyboxRotation = rot;

        this.renderNextFrame();
    }

    setSkyboxBackground(background: string) {
        const { scene } = this.app;

        this.app.scene.layers.getLayerById(LAYERID_SKYBOX).enabled = background !== 'Solid Color';

        switch (background) {
            case 'Solid Color':
                break;
            case 'Infinite Sphere':
                scene.sky.type = SKYTYPE_INFINITE;
                break;
            case 'Projective Dome':
                scene.sky.type = SKYTYPE_DOME;
                break;
            case 'Projective Box':
                scene.sky.type = SKYTYPE_BOX;
                break;
        }

        this.app.scene.skyboxMip =
            background === 'Infinite Sphere' ? this.observer.get('skybox.blur') : 0;

        this.renderNextFrame();
    }

    setSkyboxBlur(blur: number) {
        this.app.scene.skyboxMip =
            this.observer.get('skybox.background') === 'Infinite Sphere' ? blur : 0;
        this.renderNextFrame();
    }

    setSkyboxDomeRadius(radius: number) {
        const scale = (this.sceneBounds?.halfExtents.length() ?? 1) * radius;
        this.app.scene.sky.node.setLocalScale(scale, scale, scale);
        this.renderNextFrame();
    }

    setSkyboxTripodOffset(offset: number) {
        this.app.scene.sky.center = new Vec3(0, offset, 0);
        this.renderNextFrame();
    }

    setTonemapping(tonemapping: string) {
        const mapping: Record<string, number> = {
            None: TONEMAP_NONE,
            Linear: TONEMAP_LINEAR,
            Neutral: TONEMAP_NEUTRAL,
            Filmic: TONEMAP_FILMIC,
            Hejl: TONEMAP_HEJL,
            ACES: TONEMAP_ACES,
            ACES2: TONEMAP_ACES2,
        };

        this.camera.camera.toneMapping = mapping.hasOwnProperty(tonemapping)
            ? mapping[tonemapping]
            : TONEMAP_ACES;
        this.renderNextFrame();
    }

    setCameraExposure(value: number) {
        const exposure = Math.max(0.1, Math.min(4, value));
        this.camera.camera.exposure = exposure;
        this.renderNextFrame();
    }

    setBackgroundColor(color: { r: number; g: number; b: number }) {
        const cnv = (value: number) => Math.max(0, Math.min(255, Math.floor(value * 255)));
        document.getElementById('canvas-wrapper').style.backgroundColor = `rgb(${cnv(
            color.r
        )}, ${cnv(color.g)}, ${cnv(color.b)})`;
    }

    update(deltaTime: number) {
        if (this.deviceLost) {
            return;
        }

        // CRITICAL: Skip camera updates during screenshot capture
        // The cameraControls.update() can move the camera due to inertia/interpolation
        // even when cameraControls.enabled = false (which only disables user input)
        // @ts-ignore
        if (this.isCapturingScreenshot) {
            // Safety timeout: if isCapturingScreenshot has been true for > 60 seconds, auto-reset
            // This prevents the camera from getting permanently stuck
            // @ts-ignore
            if (!this._screenshotStartTime) {
                // @ts-ignore
                this._screenshotStartTime = Date.now();
            } else {
                // @ts-ignore
                const elapsedMs = Date.now() - this._screenshotStartTime;
                if (elapsedMs > 60000) {
                    console.warn(
                        `[update] ⚠️ isCapturingScreenshot stuck for ${(elapsedMs / 1000).toFixed(1)}s - auto-resetting!`
                    );
                    // @ts-ignore
                    this.isCapturingScreenshot = false;
                    // @ts-ignore
                    this._screenshotStartTime = null;
                    // Don't return - let the update proceed
                } else {
                    return;
                }
            }
        } else {
            // Clear the timestamp when not capturing
            // @ts-ignore
            this._screenshotStartTime = null;
        }

        // update the PlayCanvas orbit camera - ONLY when NOT using custom UI camera modes
        // The UI camera modes (orbit/free) handle their own updates
        const currentOrbitMode = this.observer.get('camera.orbitMode');
        const uiCameraMode = this.observer.get('camera.uiCameraMode') || 'orbit';
        const usePlayCanvasControls = !this.xrMode?.active && 
                                       currentOrbitMode === 'target' && 
                                       this.uiCameraControlsDisabled; // Only use PlayCanvas controls during screenshots
        
        if (usePlayCanvasControls) {
            // @ts-ignore _zoomDist is currently flagged as private
            const speed = this.cameraControls._zoomDist / this.cameraControls.sceneSize;

            // make fly speed based on orbit zoom distance
            this.cameraControls.moveSpeed = speed;
            this.cameraControls.moveFastSpeed = speed * 4;
            this.cameraControls.moveSlowSpeed = speed * 0.005;

            // update observer with current camera speed
            this.observer.set('camera.currentSpeed', speed);

            this.cameraControls.update(deltaTime);
        }

        // Update UI camera controls based on uiCameraMode (only if not disabled for screenshots)
        if (!this.uiCameraControlsDisabled) {
            const uiCameraMode = this.observer.get('camera.uiCameraMode') || 'orbit';
            
            // Update UI Orbit camera if active
            if (uiCameraMode === 'orbit' && this.uiOrbitCameraHandlers && this.uiOrbitCameraHandlers.updateOrbit) {
                this.uiOrbitCameraHandlers.updateOrbit(deltaTime);
            }
            
            // Update camera-center (free cam) movement if active
            if (uiCameraMode === 'free' && this.cameraCenterMouseHandlers && this.cameraCenterMouseHandlers.updateMovement) {
                this.cameraCenterMouseHandlers.updateMovement(deltaTime);

                // Track camera-center mode speed (base speed * multiplier from mouse wheel)
                const speedMultiplier = this.cameraCenterMouseHandlers.speedMultiplier || 1.0;
                const cameraCenterSpeed = 10.0 * speedMultiplier; // base speed is 10.0
                this.observer.set('camera.currentSpeed', cameraCenterSpeed);
            }
        }

        const maxdiff = (a: Mat4, b: Mat4) => {
            let result = 0;
            for (let i = 0; i < 16; ++i) {
                result = Math.max(result, Math.abs(a.data[i] - b.data[i]));
            }
            return result;
        };

        // if the camera has moved since the last render
        const cameraWorldTransform = this.camera.getWorldTransform();
        if (maxdiff(cameraWorldTransform, this.prevCameraMat) > 1e-4) {
            this.prevCameraMat.copy(cameraWorldTransform);
            this.renderNextFrame();
        }

        // always render during xr sessions
        if (this.xrMode?.active) {
            this.renderNextFrame();
        }

        // or an animation is loaded and we're animating
        let isAnimationPlaying = false;
        for (let i = 0; i < this.entities.length; ++i) {
            const anim = this.entities[i].anim;
            if (anim && anim.baseLayer && anim.baseLayer.playing) {
                isAnimationPlaying = true;
                break;
            }
        }

        if (isAnimationPlaying) {
            this.dirtyBounds = true;
            this.dirtySkeleton = true;
            this.dirtyNormals = true;
            this.refreshSelectedNodeTransform();
            this.renderNextFrame();
            this.observer.emit('animationUpdate');
        }

        // or the ministats is enabled
        if (this.miniStats.enabled) {
            this.renderNextFrame();
        }
    }

    renderNextFrame() {
        this.app.renderNextFrame = true;
        if (this.multiframe) {
            this.multiframe.moved();
        }
    }

    clearCta() {
        // Safely access DOM elements - they may not exist in iframe context
        const panelLeft = document.querySelector('#panel-left');
        if (panelLeft) {
            panelLeft.classList.add('no-cta');
        }
        const canvas = document.querySelector('#application-canvas');
        if (canvas) {
            canvas.classList.add('no-cta');
        }
        const loadButtonPanel = document.querySelector('.load-button-panel');
        if (loadButtonPanel) {
            loadButtonPanel.classList.add('hide');
        }
    }

    // add a loaded asset to the scene
    // asset is a container asset with renders and/or animations, or a standalone texture asset
    private addToScene(asset: Asset) {
        // Handle standalone texture assets (for livery system)
        if (asset.type === 'texture') {
            this.assets.push(asset);
            return;
        }

        const resource = asset.resource as any;
        const meshesLoaded = resource.renders && resource.renders.length > 0;
        const animsLoaded = resource.animations && resource.animations.length > 0;
        const prevEntity: Entity =
            this.entities.length === 0 ? null : this.entities[this.entities.length - 1];

        let entity: Entity;

        // create entity
        if (!meshesLoaded && prevEntity && prevEntity.findComponent('render')) {
            entity = prevEntity;
        } else {
            if (asset.type === 'container') {
                // container/glb
                entity = resource.instantiateRenderEntity();
            } else {
                // gaussian splat scene
                entity = new Entity();
                entity.setEulerAngles(0, 0, 180);
                entity.addComponent('gsplat', { asset });

                // render frame if gaussian splat sorter updates)
                entity.gsplat.instance.sorter.on('updated', () => {
                    this.renderNextFrame();
                });
            }

            this.entities.push(entity);
            this.entityAssets.push({ entity: entity, asset: asset });
            this.sceneRoot.addChild(entity);
            this.captureRestLocalTransforms(entity);
            this.configureSkinnedMeshInstances(entity, resource);
            this.shadowCatcher.onEntityAdded(entity);
        }

        // create animation component
        if (animsLoaded) {
            // append anim tracks to global list
            resource.animations.forEach((a: any) => {
                const sourceTrack = a.resource as AnimTrack;
                this.animTracks.push(sourceTrack);
                this.animTracks.push(...this.createIndividualAnimationChannels(sourceTrack));
            });
        }

        // store the loaded asset
        this.assets.push(asset);

        // Extract texture assets from container asset for livery system
        if (asset.type === 'container' && resource.textures) {
            resource.textures.forEach((textureAsset: Asset) => {
                this.assets.push(textureAsset);
            });
        }
    }

    private buildGltfCloneNodeMap(resource: any, root: Entity): Map<number, GraphNode> {
        const result = new Map<number, GraphNode>();
        const data = resource?.data;
        const gltfNodes = data?.gltf?.nodes;
        const sourceNodes = data?.nodes;
        const sourceScenes = data?.scenes;
        if (!gltfNodes || !sourceNodes || !sourceScenes) {
            return result;
        }

        const sourceNodeToIndex = new Map<GraphNode, number>();
        sourceNodes.forEach((node: GraphNode, index: number) => {
            sourceNodeToIndex.set(node, index);
        });

        const findCloneChild = (
            sourceParent: GraphNode,
            cloneParent: GraphNode,
            sourceChild: GraphNode,
            sourceChildIndex: number
        ): GraphNode | null => {
            let sameNameOrdinal = 0;
            for (let i = 0; i < sourceChildIndex; i++) {
                if (sourceParent.children[i].name === sourceChild.name) {
                    sameNameOrdinal++;
                }
            }

            let matchedOrdinal = 0;
            for (const cloneChild of cloneParent.children) {
                if (cloneChild.name !== sourceChild.name) {
                    continue;
                }

                if (matchedOrdinal === sameNameOrdinal) {
                    return cloneChild;
                }

                matchedOrdinal++;
            }

            return cloneParent.children[sourceChildIndex] || null;
        };

        const mapHierarchy = (sourceNode: GraphNode, cloneNode: GraphNode) => {
            const nodeIndex = sourceNodeToIndex.get(sourceNode);
            if (nodeIndex !== undefined) {
                result.set(nodeIndex, cloneNode);
                (cloneNode as any).__ifGltfNodeIndex = nodeIndex;
            }

            for (let i = 0; i < sourceNode.children.length; i++) {
                const sourceChild = sourceNode.children[i];
                const cloneChild = findCloneChild(sourceNode, cloneNode, sourceChild, i);
                if (cloneChild) {
                    mapHierarchy(sourceChild, cloneChild);
                }
            }
        };

        if (sourceScenes.length === 1) {
            mapHierarchy(sourceScenes[0], root);
        } else {
            const sceneCount = Math.min(sourceScenes.length, root.children.length);
            for (let i = 0; i < sceneCount; i++) {
                mapHierarchy(sourceScenes[i], root.children[i]);
            }
        }

        return result;
    }

    private buildMeshToGltfSkinMap(resource: any): WeakMap<Mesh, any> {
        const result = new WeakMap<Mesh, any>();
        const gltf = resource?.data?.gltf;
        const renders = resource?.data?.renders;
        if (!gltf?.nodes || !gltf?.skins || !renders) {
            return result;
        }

        gltf.nodes.forEach((gltfNode: any) => {
            const meshIndex = gltfNode?.mesh;
            const skinIndex = gltfNode?.skin;
            if (typeof meshIndex !== 'number' || typeof skinIndex !== 'number') {
                return;
            }

            const gltfSkin = gltf.skins[skinIndex];
            const renderMeshes = renders[meshIndex]?.meshes;
            if (!gltfSkin || !Array.isArray(renderMeshes)) {
                return;
            }

            renderMeshes.forEach((mesh: Mesh | null) => {
                if (!mesh) {
                    return;
                }

                const existingSkin = result.get(mesh);
                if (result.has(mesh) && existingSkin !== gltfSkin) {
                    result.set(mesh, null);
                } else {
                    result.set(mesh, gltfSkin);
                }
            });
        });

        return result;
    }

    private captureRestLocalTransforms(root: GraphNode) {
        const stack: GraphNode[] = [root];
        while (stack.length > 0) {
            const node = stack.pop();
            if (!node) {
                continue;
            }

            if (!this.restLocalTransforms.has(node)) {
                this.restLocalTransforms.set(node, {
                    position: node.getLocalPosition().clone(),
                    rotation: node.getLocalRotation().clone(),
                    scale: node.getLocalScale().clone(),
                });
            }

            for (let i = 0; i < node.children.length; i++) {
                stack.push(node.children[i]);
            }
        }
    }

    private restoreAnimatedLocalTransforms() {
        const restoredNodes = new Set<GraphNode>();
        this.entities.forEach((entity) => {
            this.animTracks.forEach((track) => {
                track.curves.forEach((curve) => {
                    const paths = curve.paths as unknown as Array<any>;
                    paths.forEach((targetPath) => {
                        const node = this.findAnimationTargetNode(entity, targetPath);
                        const rest = node ? this.restLocalTransforms.get(node) : null;
                        if (node && rest && !restoredNodes.has(node)) {
                            node.setLocalPosition(rest.position);
                            node.setLocalRotation(rest.rotation);
                            node.setLocalScale(rest.scale);
                            restoredNodes.add(node);
                        }
                    });
                });
            });
        });
    }

    private createIndividualAnimationChannels(sourceTrack: AnimTrack): AnimTrack[] {
        const curves = sourceTrack.curves || [];
        const result: AnimTrack[] = [];
        const existingTrackNames = new Set(this.animTracks.map(track => track.name));
        let channelIndex = 0;

        curves.forEach((curve) => {
            const paths = curve.paths as unknown as Array<any>;
            paths.forEach((targetPath) => {
                channelIndex++;
                const entityPath = Array.isArray(targetPath?.entityPath) ?
                    targetPath.entityPath :
                    [];
                const targetName =
                    entityPath[entityPath.length - 1] || `Channel ${channelIndex}`;
                const rawProperty = Array.isArray(targetPath?.propertyPath) ?
                    targetPath.propertyPath.join('.') :
                    targetPath?.propertyPath || 'value';
                const propertyLabels: Record<string, string> = {
                    localPosition: 'translation',
                    localRotation: 'rotation',
                    localScale: 'scale'
                };
                const targetProperty = propertyLabels[rawProperty] || rawProperty;
                const displayName = `${targetName} · ${targetProperty}`;
                const baseName = `${sourceTrack.name} › ${displayName}`.replace(/\./g, '_');
                let trackName = baseName;
                let duplicateIndex = 2;

                while (existingTrackNames.has(trackName)) {
                    trackName = `${baseName} (${duplicateIndex})`;
                    duplicateIndex++;
                }
                existingTrackNames.add(trackName);

                const individualCurve = new AnimCurve(
                    [targetPath],
                    curve.input,
                    curve.output,
                    curve.interpolation
                );
                const individualTrack = new AnimTrack(
                    trackName,
                    sourceTrack.duration,
                    sourceTrack.inputs,
                    sourceTrack.outputs,
                    [individualCurve]
                );

                this.individualAnimationChannels.set(trackName, {
                    displayName,
                    sourceAnimation: sourceTrack.name,
                    targetProperty
                });
                result.push(individualTrack);
            });
        });

        return result;
    }

    private configureSkinBonesFromGltfJoints(
        skinInstance: any,
        gltfSkin: any,
        cloneNodeMap: Map<number, GraphNode>
    ): boolean {
        if (!skinInstance || !gltfSkin?.joints || cloneNodeMap.size === 0) {
            return false;
        }

        const bones = gltfSkin.joints.map((jointNodeIndex: number, jointIndex: number) => {
            return cloneNodeMap.get(jointNodeIndex) || skinInstance.bones[jointIndex];
        });

        skinInstance.bones = bones;
        skinInstance._dirty = true;
        skinInstance._skinUpdateIndex = -1;
        return true;
    }

    private createSkinFromGltfSkin(gltfSkin: any, resource: any): Skin | null {
        const data = resource?.data;
        const gltf = data?.gltf;
        const joints = gltfSkin?.joints;
        if (!gltf || !Array.isArray(joints)) {
            return null;
        }

        const inverseBindPose: Mat4[] = [];
        const inverseBindMatrices = gltfSkin.inverseBindMatrices;
        if (typeof inverseBindMatrices === 'number') {
            const accessor = gltf.accessors?.[inverseBindMatrices];
            const capturedBufferViews = data?.__ifBufferViews || resource?.__ifBufferViews;
            const bufferView = capturedBufferViews?.[accessor?.bufferView];
            if (!accessor || !bufferView) {
                return null;
            }
            if (
                accessor.componentType !== 5126 ||
                accessor.type !== 'MAT4' ||
                accessor.count < joints.length
            ) {
                return null;
            }

            const byteStride = bufferView.byteStride || 16 * 4;
            const byteOffset = accessor.byteOffset || 0;
            const requiredByteLength = byteOffset + (joints.length - 1) * byteStride + 16 * 4;
            if (requiredByteLength > bufferView.byteLength) {
                return null;
            }

            const view = new DataView(
                bufferView.buffer,
                bufferView.byteOffset,
                bufferView.byteLength
            );

            for (let jointIndex = 0; jointIndex < joints.length; jointIndex++) {
                const values: number[] = [];
                const matrixOffset = byteOffset + jointIndex * byteStride;
                for (let valueIndex = 0; valueIndex < 16; valueIndex++) {
                    values[valueIndex] = view.getFloat32(matrixOffset + valueIndex * 4, true);
                }

                const matrix = new Mat4();
                matrix.set(values);
                inverseBindPose.push(matrix);
            }
        } else {
            for (let jointIndex = 0; jointIndex < joints.length; jointIndex++) {
                inverseBindPose.push(new Mat4());
            }
        }

        const boneNames = joints.map((jointNodeIndex: number) => {
            return gltf.nodes?.[jointNodeIndex]?.name || data.nodes?.[jointNodeIndex]?.name || '';
        });

        return new Skin(this.app.graphicsDevice, inverseBindPose, boneNames);
    }

    private getOrCreateSkinForGltfSkin(gltfSkin: any, resource: any, fallbackSkin: Skin): Skin {
        if (!resource) {
            return fallbackSkin;
        }

        if (!resource.__ifGltfSkinCache) {
            resource.__ifGltfSkinCache = new WeakMap<any, Skin>();
        }

        const skinCache = resource.__ifGltfSkinCache as WeakMap<any, Skin>;
        if (!resource.__ifGltfSkinStats) {
            resource.__ifGltfSkinStats = {
                exact: 0,
                fallback: 0,
            };
        }

        let skin = skinCache.get(gltfSkin);
        if (!skin) {
            const exactSkin = this.createSkinFromGltfSkin(gltfSkin, resource);
            if (exactSkin) {
                skin = exactSkin;
                resource.__ifGltfSkinStats.exact++;
            } else {
                skin = fallbackSkin;
                resource.__ifGltfSkinStats.fallback++;
            }
            skinCache.set(gltfSkin, skin);
        }

        return skin;
    }

    private getGltfSkinForSkinInstance(skinInstance: any, resource: any): any {
        const data = resource?.data;
        const skins = data?.skins;
        const gltfSkins = data?.gltf?.skins;
        if (!skinInstance || !skins || !gltfSkins) {
            return null;
        }

        const skinIndex = skins.indexOf(skinInstance.skin);
        return skinIndex >= 0 ? gltfSkins[skinIndex] : null;
    }

    private buildCloneNodeToGltfIndexMap(cloneNodeMap: Map<number, GraphNode>) {
        const result = new Map<GraphNode, number>();
        cloneNodeMap.forEach((cloneNode, gltfNodeIndex) => {
            result.set(cloneNode, gltfNodeIndex);
        });
        return result;
    }

    private getGltfSkinForRenderNode(
        render: RenderComponent,
        resource: any,
        cloneNodeToGltfIndex: Map<GraphNode, number>
    ): any {
        const renderEntity = (render as any).entity as GraphNode;
        const taggedNodeIndex = renderEntity ? (renderEntity as any).__ifGltfNodeIndex : undefined;
        const gltfNodeIndex =
            typeof taggedNodeIndex === 'number'
                ? taggedNodeIndex
                : renderEntity
                    ? cloneNodeToGltfIndex.get(renderEntity)
                    : undefined;
        const gltfNode =
            gltfNodeIndex !== undefined ? resource?.data?.gltf?.nodes?.[gltfNodeIndex] : null;
        const skinIndex = gltfNode?.skin;
        return typeof skinIndex === 'number' ? resource?.data?.gltf?.skins?.[skinIndex] : null;
    }

    private getGltfSkinForMeshInstance(
        renderGltfSkin: any,
        meshInstance: MeshInstance,
        meshToGltfSkin: WeakMap<Mesh, any>,
        resource: any,
        sourceCounts?: {
            mesh: number;
            render: number;
            fallback: number;
            ambiguousMesh: number;
        }
    ): any {
        const mesh = meshInstance.mesh;
        if (mesh && meshToGltfSkin.has(mesh)) {
            const meshGltfSkin = meshToGltfSkin.get(mesh);
            if (meshGltfSkin) {
                if (sourceCounts) {
                    sourceCounts.mesh++;
                }
                return meshGltfSkin;
            }
            if (sourceCounts) {
                sourceCounts.ambiguousMesh++;
            }
        }

        if (renderGltfSkin) {
            if (sourceCounts) {
                sourceCounts.render++;
            }
            return renderGltfSkin;
        }

        const fallbackSkin = this.getGltfSkinForSkinInstance(meshInstance.skinInstance, resource);
        if (fallbackSkin && sourceCounts) {
            sourceCounts.fallback++;
        }
        return fallbackSkin;
    }

    private configureSkinnedMeshInstances(root: Entity, resource?: any) {
        const renderComponents = root.findComponents('render') as RenderComponent[];
        const cloneNodeMap = this.buildGltfCloneNodeMap(resource, root);
        const cloneNodeToGltfIndex = this.buildCloneNodeToGltfIndexMap(cloneNodeMap);
        const meshToGltfSkin = this.buildMeshToGltfSkinMap(resource);
        let configuredCount = 0;
        let configuredBoneCount = 0;
        let disabledCount = 0;
        const skinSourceCounts = {
            mesh: 0,
            render: 0,
            fallback: 0,
            ambiguousMesh: 0,
        };

        for (const render of renderComponents) {
            const skinRoot = (render.rootBone as GraphNode) || null;
            if (!skinRoot || !render.meshInstances) {
                continue;
            }

            const renderGltfSkin = this.getGltfSkinForRenderNode(
                render,
                resource,
                cloneNodeToGltfIndex
            );

            for (const meshInstance of render.meshInstances) {
                if (!meshInstance.skinInstance || !meshInstance.node) {
                    continue;
                }

                if (!(meshInstance as any).__ifOriginalNode) {
                    (meshInstance as any).__ifOriginalNode = meshInstance.node;
                }

                const gltfSkin = this.getGltfSkinForMeshInstance(
                    renderGltfSkin,
                    meshInstance,
                    meshToGltfSkin,
                    resource,
                    skinSourceCounts
                );
                if (gltfSkin?.joints?.length > maxSkinJoints) {
                    meshInstance.skinInstance = null;
                    meshInstance.node = (meshInstance as any).__ifOriginalNode;
                    meshInstance.clearShaders();
                    disabledCount++;
                    continue;
                }

                if (gltfSkin) {
                    const skin = this.getOrCreateSkinForGltfSkin(
                        gltfSkin,
                        resource,
                        meshInstance.skinInstance.skin
                    );
                    if (meshInstance.mesh) {
                        (meshInstance.mesh as any).skin = skin;
                        (meshInstance.mesh as any).boneAabb = null;
                        (meshInstance.mesh as any).boneUsed = null;
                    }
                    const skinInstance = new SkinInstance(skin);
                    skinInstance.rootBone = skinRoot;
                    meshInstance.skinInstance = skinInstance;
                    meshInstance.clearShaders();

                    if (
                        this.configureSkinBonesFromGltfJoints(
                            meshInstance.skinInstance,
                            gltfSkin,
                            cloneNodeMap
                        )
                    ) {
                        configuredBoneCount++;
                    }
                    configuredCount++;
                }
            }
        }

        if (configuredCount > 0) {
            console.log(
                `[SKINNING] Configured ${configuredCount} skinned mesh instance(s) with per-mesh skin instances`
            );
            console.log(
                `[SKINNING] Skin sources: mesh=${skinSourceCounts.mesh}, renderNode=${skinSourceCounts.render}, fallback=${skinSourceCounts.fallback}, ambiguousMesh=${skinSourceCounts.ambiguousMesh}`
            );
            console.log(
                `[SKINNING] Skin resources: exact=${resource?.__ifGltfSkinStats?.exact || 0}, fallback=${resource?.__ifGltfSkinStats?.fallback || 0}`
            );
        }

        if (configuredBoneCount > 0) {
            console.log(
                `[SKINNING] Resolved ${configuredBoneCount} mesh skin instance(s) from glTF joint indices`
            );
        }

        if (disabledCount > 0) {
            console.log(
                `[SKINNING] Disabled ${disabledCount} mesh instance(s) with more than ${maxSkinJoints} joints to match the app renderer fallback`
            );
        }
    }

    // Apply altitude offset from aircraft configuration
    private applyAltitudeOffset() {
        // Check if altitude offset has already been applied
        if (this.altitudeOffsetApplied) {
            console.log(`Altitude offset already applied, skipping`);
            return;
        }

        const aircraftConfig = this.observer.get('aircraftConfig.configuration');
        if (!aircraftConfig) {
            return;
        }

        // Try both camelCase and PascalCase naming conventions
        const altitudeOffset =
            aircraftConfig.resetAltitudeOffset || aircraftConfig.ResetAltitudeOffset;

        if (altitudeOffset !== undefined && altitudeOffset !== null) {
            console.log(`Applying altitude offset: ${altitudeOffset}`);

            // Check if scene is centered - if so, we need to apply the offset differently
            const isCentered = this.observer.get('centerScene');

            if (isCentered) {
                // Store original scene root position if not already stored
                if (this.originalSceneRootPosition === null) {
                    this.originalSceneRootPosition = this.sceneRoot.getLocalPosition().clone();
                    console.log(
                        `Stored original scene root position:`,
                        this.originalSceneRootPosition
                    );
                }

                // When centered, the model's bottom is at Y=0, so we just add the offset
                console.log(`Scene is centered - applying offset to scene root`);
                const newSceneRootPosition = new Vec3(
                    this.originalSceneRootPosition.x,
                    this.originalSceneRootPosition.y + altitudeOffset,
                    this.originalSceneRootPosition.z
                );
                this.sceneRoot.setLocalPosition(newSceneRootPosition);
                console.log(`Scene root Y position after offset: ${newSceneRootPosition.y}`);
            } else {
                // Store original entity transforms if not already stored
                if (this.originalEntityTransforms.length === 0) {
                    this.entities.forEach((entity) => {
                        this.originalEntityTransforms.push({
                            position: entity.getLocalPosition().clone(),
                            rotation: entity.getLocalEulerAngles().clone(),
                        });
                    });
                    console.log(
                        `Stored original transforms for ${this.originalEntityTransforms.length} entities`
                    );
                }

                // When not centered, apply to individual entities from their original positions
                console.log(`Scene is not centered - applying offset to individual entities`);
                this.entities.forEach((entity, index) => {
                    const originalPos = this.originalEntityTransforms[index].position;
                    const newPosition = new Vec3(
                        originalPos.x,
                        originalPos.y + altitudeOffset,
                        originalPos.z
                    );
                    entity.setLocalPosition(newPosition);
                    console.log(`Entity ${index} - Y: ${originalPos.y} -> ${newPosition.y}`);
                });
            }

            // Mark that altitude offset has been applied
            this.altitudeOffsetApplied = true;

            // Mark bounds as dirty since we've moved the model
            this.dirtyBounds = true;
        }
    }

    // Hide high-speed spinning nodes (propeller blurs, etc.) for screenshots
    private hideHighSpeedSpinningNodes() {
        const aircraftConfig = this.observer.get('aircraftConfig.configuration');
        if (!aircraftConfig) {
            console.log('No aircraft configuration found');
            return;
        }

        // Get engine definitions
        const engineDefinitions =
            aircraftConfig.engineDefinitions || aircraftConfig.EngineDefinitions;
        if (!engineDefinitions || !Array.isArray(engineDefinitions)) {
            console.log('No engine definitions found');
            return;
        }

        // Clear any previously hidden meshes
        this.hiddenMeshInstancesForScreenshot.clear();

        let hiddenCount = 0;

        for (const engine of engineDefinitions) {
            const highSpeedNodes = engine.highSpeedSpinningNodes || engine.HighSpeedSpinningNodes;
            if (highSpeedNodes && Array.isArray(highSpeedNodes)) {
                console.log(`  Engine has ${highSpeedNodes.length} high-speed spinning nodes`);

                for (const nodeName of highSpeedNodes) {
                    // Find mesh instances with this node name
                    for (const meshInstance of this.meshInstances) {
                        const meshNode = this.getMeshInstanceDisplayNode(meshInstance);
                        if (meshNode && meshNode.name === nodeName) {
                            // Hide the mesh instance
                            meshInstance.visible = false;
                            this.hiddenMeshInstancesForScreenshot.add(meshInstance);
                            hiddenCount++;
                        }
                    }
                }
            }
        }

        if (hiddenCount > 0) {
            console.log(`Hidden ${hiddenCount} high-speed spinning mesh instance(s)`);
        } else {
        }
    }

    // Restore visibility of high-speed spinning nodes after screenshots
    private showHighSpeedSpinningNodes() {
        console.log('Restoring visibility of high-speed spinning nodes...');

        let restoredCount = 0;
        for (const meshInstance of this.hiddenMeshInstancesForScreenshot) {
            meshInstance.visible = true;
            restoredCount++;
        }

        this.hiddenMeshInstancesForScreenshot.clear();

        if (restoredCount > 0) {
            console.log(`Restored visibility of ${restoredCount} mesh instance(s)`);
        }
    }

    // Remove altitude and pitch offsets temporarily for screenshots
    private removeOffsetsForScreenshot() {
        const isCentered = this.observer.get('centerScene');

        if (isCentered) {
            // Restore scene root to original position and rotation
            if (this.originalSceneRootPosition !== null) {
                this.sceneRoot.setLocalPosition(this.originalSceneRootPosition);
                console.log(`Restored scene root position to:`, this.originalSceneRootPosition);
            }
            if (this.originalSceneRootRotation !== null) {
                this.sceneRoot.setLocalEulerAngles(
                    this.originalSceneRootRotation.x,
                    this.originalSceneRootRotation.y,
                    this.originalSceneRootRotation.z
                );
                console.log(`Restored scene root rotation to:`, this.originalSceneRootRotation);
            }
        } else {
            // Restore entities to original transforms
            if (this.originalEntityTransforms.length > 0) {
                this.entities.forEach((entity, index) => {
                    if (index < this.originalEntityTransforms.length) {
                        const originalTransform = this.originalEntityTransforms[index];
                        entity.setLocalPosition(originalTransform.position);
                        entity.setLocalEulerAngles(
                            originalTransform.rotation.x,
                            originalTransform.rotation.y,
                            originalTransform.rotation.z
                        );
                        console.log(
                            `Entity ${index} restored to: pos=${originalTransform.position}, rot=${originalTransform.rotation}`
                        );
                    }
                });
            }
        }

        // Mark bounds as dirty
        this.dirtyBounds = true;
    }

    // Restore altitude and pitch offsets after screenshots
    private restoreOffsetsAfterScreenshot() {
        console.log('Restoring altitude and pitch offsets after screenshot...');

        // Simply re-apply the offsets from the original base transforms
        // We don't need to check if they were applied, we just reapply from scratch
        const wasAltitudeApplied = this.altitudeOffsetApplied;
        const wasPitchApplied = this.pitchOffsetApplied;

        // Temporarily reset flags so the apply functions will run
        this.altitudeOffsetApplied = false;
        this.pitchOffsetApplied = false;

        // Reapply offsets from original base transforms
        if (wasAltitudeApplied) {
            this.applyAltitudeOffset();
        }
        if (wasPitchApplied) {
            this.applyPitchOffset();
        }

        // Mark bounds as dirty
        this.dirtyBounds = true;
        console.log('Offsets restored after screenshot');
    }

    // Apply pitch offset from aircraft configuration (for taildragger aircraft)
    private applyPitchOffset() {
        // Check if pitch offset has already been applied
        if (this.pitchOffsetApplied) {
            console.log(`Pitch offset already applied, skipping`);
            return;
        }

        const aircraftConfig = this.observer.get('aircraftConfig.configuration');
        if (!aircraftConfig) {
            return;
        }

        // Try both camelCase and PascalCase naming conventions
        const pitchOffset = aircraftConfig.resetPitchOffset || aircraftConfig.ResetPitchOffset;

        if (pitchOffset !== undefined && pitchOffset !== null) {
            console.log(`Applying pitch offset: ${pitchOffset} degrees (pitching up)`);

            // Check if scene is centered - if so, we need to apply the offset differently
            const isCentered = this.observer.get('centerScene');

            if (isCentered) {
                // Store original scene root rotation if not already stored
                if (this.originalSceneRootRotation === null) {
                    this.originalSceneRootRotation = this.sceneRoot.getLocalEulerAngles().clone();
                    console.log(
                        `Stored original scene root rotation:`,
                        this.originalSceneRootRotation
                    );
                }

                // When centered, apply rotation to the scene root
                console.log(`Scene is centered - applying pitch offset to scene root`);
                const newSceneRootRotation = new Vec3(
                    this.originalSceneRootRotation.x + pitchOffset, // Already in degrees
                    this.originalSceneRootRotation.y,
                    this.originalSceneRootRotation.z
                );
                this.sceneRoot.setLocalEulerAngles(
                    newSceneRootRotation.x,
                    newSceneRootRotation.y,
                    newSceneRootRotation.z
                );
                console.log(
                    `Scene root pitch rotation after offset: ${newSceneRootRotation.x} degrees`
                );
            } else {
                // Original entity transforms already stored in applyAltitudeOffset
                // When not centered, apply to individual entities from their original rotations
                console.log(`Scene is not centered - applying pitch offset to individual entities`);
                this.entities.forEach((entity, index) => {
                    const originalRot = this.originalEntityTransforms[index].rotation;
                    const newRotation = new Vec3(
                        originalRot.x + pitchOffset, // Already in degrees
                        originalRot.y,
                        originalRot.z
                    );
                    entity.setLocalEulerAngles(newRotation.x, newRotation.y, newRotation.z);
                    console.log(
                        `Entity ${index} - pitch: ${originalRot.x} -> ${newRotation.x} degrees`
                    );
                });
            }

            // Mark that pitch offset has been applied
            this.pitchOffsetApplied = true;

            // Mark bounds as dirty since we've rotated the model
            this.dirtyBounds = true;
        }
    }

    // Animation type enum values (matching C# AnimationType enum)
    private readonly AnimationType = {
        LeftAileron: 0,
        RightAileron: 1,
        Elevator: 2,
        Rudder: 3,
        Spoilers: 4,
        LandingGear: 5,
        Reverse: 6,
        LeftFlap: 7,
        RightFlap: 8,
        Throttle1: 9,
        Throttle2: 10,
        Throttle3: 11,
        Throttle4: 12,
        WingFlexRight: 13,
        WingFlexLeft: 14,
    };

    private getAnimationTrackByName(animationName: string): AnimTrack | null {
        return (
            this.animTracks.find((track: any) => (track._name || track.name) === animationName) ||
            null
        );
    }

    private getAnimationSnapshot(track: AnimTrack): AnimSnapshot {
        let snapshot = this.animationSnapshots.get(track);
        if (!snapshot) {
            snapshot = new AnimSnapshot(track);
            this.animationSnapshots.set(track, snapshot);
        }
        return snapshot;
    }

    private getAnimationTrackTime(track: AnimTrack, progress: number): number {
        const inputs = ((track as any)._inputs || track.inputs || []) as Array<any>;
        let start = Number.POSITIVE_INFINITY;
        let end = Number.NEGATIVE_INFINITY;

        for (const input of inputs) {
            const data = input?._data || input?.data;
            if (!data || data.length === 0) {
                continue;
            }

            start = Math.min(start, data[0]);
            end = Math.max(end, data[data.length - 1]);
        }

        if (!Number.isFinite(start) || !Number.isFinite(end)) {
            start = 0;
            end = (track as any)._duration || track.duration || 0;
        }

        return start + (end - start) * progress;
    }

    private findAnimationTargetNode(entity: Entity, pathData: any): GraphNode | null {
        const entityPath = pathData?.entityPath;
        if (!Array.isArray(entityPath) || entityPath.length === 0) {
            return null;
        }

        return (
            (entity.findByPath(entityPath) as GraphNode) ||
            (entityPath.length > 1
                ? (entity.findByPath(entityPath.slice(1)) as GraphNode)
                : null) ||
            (entity.findByName(entityPath[entityPath.length - 1]) as GraphNode) ||
            null
        );
    }

    private applySampledTransformValue(
        node: GraphNode,
        propertyPath: string,
        value: number[],
        touchedPaths: Map<GraphNode, number>,
        sourceAnimation: string,
        touchedSources: Map<GraphNode, Map<string, string>>
    ): boolean {
        const rest = this.restLocalTransforms.get(node);
        if (!rest) {
            return false;
        }

        // A clip and one of its own per-channel tracks sample the same keyframes, so the
        // second one replaces the first instead of adding its delta on top of it.
        const nodeSources = touchedSources.get(node) || new Map<string, string>();
        const sameSource = nodeSources.get(propertyPath) === sourceAnimation;
        nodeSources.set(propertyPath, sourceAnimation);
        touchedSources.set(node, nodeSources);

        const touched = touchedPaths.get(node) || 0;
        switch (propertyPath) {
            case 'localPosition':
                if ((touched & touchedTranslation) === 0 || sameSource) {
                    node.setLocalPosition(value[0], value[1], value[2]);
                } else {
                    const position = node.getLocalPosition();
                    node.setLocalPosition(
                        position.x + value[0] - rest.position.x,
                        position.y + value[1] - rest.position.y,
                        position.z + value[2] - rest.position.z
                    );
                }
                touchedPaths.set(node, touched | touchedTranslation);
                return true;

            case 'localScale':
                if ((touched & touchedScale) === 0 || sameSource) {
                    node.setLocalScale(value[0], value[1], value[2]);
                } else {
                    const scale = node.getLocalScale();
                    node.setLocalScale(
                        scale.x + value[0] - rest.scale.x,
                        scale.y + value[1] - rest.scale.y,
                        scale.z + value[2] - rest.scale.z
                    );
                }
                touchedPaths.set(node, touched | touchedScale);
                return true;

            case 'localRotation': {
                const sampled = new Quat(value[0], value[1], value[2], value[3]);
                if ((touched & touchedRotation) === 0 || sameSource) {
                    node.setLocalRotation(sampled);
                } else {
                    const delta = new Quat().invert(rest.rotation).mul(sampled);
                    const rotation = node.getLocalRotation().clone().mul(delta).normalize();
                    node.setLocalRotation(rotation);
                }
                touchedPaths.set(node, touched | touchedRotation);
                return true;
            }
        }

        return false;
    }

    // ContentBuilder keeps only the keyframes a slerp needs, so a control surface clip can be
    // two keys 60° apart. AnimTrack.eval blends quaternions component by component and does
    // not normalize: halfway between those keys it returns a quaternion of length 0.966,
    // which shrinks and shears the node about its origin (the A321neo rudder in the batch
    // thumbnails). Linear rotation curves are sampled with a shortest-arc slerp instead, so
    // keys stored with opposite signs also blend correctly; other rotation samples are
    // normalized.
    private sampleRotationCurve(
        track: AnimTrack,
        curve: any,
        time: number,
        evaluated: number[]
    ): number[] {
        const inputs = ((track as any)._inputs || track.inputs || []) as Array<any>;
        const outputs = ((track as any)._outputs || track.outputs || []) as Array<any>;
        const input = inputs[curve._input ?? curve.input];
        const output = outputs[curve._output ?? curve.output];
        const times = input?._data || input?.data;
        const values = output?._data || output?.data;
        const interpolation = curve._interpolation ?? curve.interpolation;

        if (
            interpolation === INTERPOLATION_LINEAR &&
            times?.length > 1 &&
            values?.length >= times.length * 4
        ) {
            let key = 0;
            while (key < times.length - 2 && time >= times[key + 1]) {
                key++;
            }
            const span = times[key + 1] - times[key];
            const t = span > 0 ? Math.min(Math.max((time - times[key]) / span, 0), 1) : 0;
            const a = key * 4;
            const b = a + 4;
            let dot = 0;
            for (let i = 0; i < 4; i++) {
                dot += values[a + i] * values[b + i];
            }
            const sign = dot < 0 ? -1 : 1;
            const from = new Quat(values[a], values[a + 1], values[a + 2], values[a + 3]);
            const to = new Quat(
                sign * values[b],
                sign * values[b + 1],
                sign * values[b + 2],
                sign * values[b + 3]
            );
            const sampled = new Quat().slerp(from.normalize(), to.normalize(), t);
            return [sampled.x, sampled.y, sampled.z, sampled.w];
        }

        const length = Math.hypot(evaluated[0], evaluated[1], evaluated[2], evaluated[3]);
        return length > 0
            ? [
                evaluated[0] / length,
                evaluated[1] / length,
                evaluated[2] / length,
                evaluated[3] / length
            ]
            : evaluated;
    }

    private applyAdditiveAnimationProgressForTracks(
        tracks: Array<{ name: string; track: AnimTrack; progress: number }>
    ): boolean {
        let appliedAny = false;

        this.entities.forEach((entity) => {
            const touchedPaths = new Map<GraphNode, number>();
            const touchedSources = new Map<GraphNode, Map<string, string>>();
            for (const { track, progress } of tracks) {
                const sourceAnimation =
                    this.individualAnimationChannels.get(track.name)?.sourceAnimation ??
                    track.name;
                const snapshot = this.getAnimationSnapshot(track);
                const time = this.getAnimationTrackTime(track, progress);
                track.eval(time, snapshot);

                const curves = (track as any)._curves || track.curves || [];
                const results = (snapshot as any)._results || [];
                for (let curveIndex = 0; curveIndex < curves.length; curveIndex++) {
                    const curve = curves[curveIndex];
                    const result = results[curveIndex];
                    if (!result) {
                        continue;
                    }

                    const paths = curve._paths || curve.paths || [];
                    for (const pathData of paths) {
                        const propertyPath =
                            pathData?.propertyPath?.[0] || pathData?.propertyPath;
                        const node = this.findAnimationTargetNode(entity, pathData);
                        const value =
                            propertyPath === 'localRotation'
                                ? this.sampleRotationCurve(track, curve, time, result)
                                : result;
                        if (
                            node &&
                            this.applySampledTransformValue(
                                node,
                                propertyPath,
                                value,
                                touchedPaths,
                                sourceAnimation,
                                touchedSources
                            )
                        ) {
                            appliedAny = true;
                        }
                    }
                }
            }
        });

        if (appliedAny) {
            this.renderNextFrame();
        }

        return appliedAny;
    }

    private applyActiveAnimationProgressPose(): boolean {
        const activeTracks: Array<{ name: string; track: AnimTrack; progress: number }> = [];

        this.animationProgresses.forEach((progress, name) => {
            const track = this.getAnimationTrackByName(name);
            if (track) {
                activeTracks.push({ name, track, progress });
            }
        });

        activeTracks.sort(
            (a, b) => this.animTracks.indexOf(a.track) - this.animTracks.indexOf(b.track)
        );

        return activeTracks.length > 0
            ? this.applyAdditiveAnimationProgressForTracks(activeTracks)
            : false;
    }

    private setAnimationProgressState(animationNames: string[], value: number) {
        const animations = this.observer.get('animations') || {};
        Object.keys(animations).forEach((key) => {
            if (animationNames.includes(key)) {
                animations[key].progress = value;
            }
            animations[key].playing = false;
        });

        this.observer.suspendEvents = true;
        this.observer.set('animations', animations);
        this.observer.suspendEvents = false;
    }

    private getAnimationNamesFromDefinition(animationDefinition: any): string[] {
        const animationNames =
            animationDefinition.animationNames ?? animationDefinition.AnimationNames;
        if (Array.isArray(animationNames)) {
            return animationNames.filter(
                (name) => typeof name === 'string' && name.length > 0
            );
        }

        const animationName =
            animationDefinition.animationName ?? animationDefinition.AnimationName;
        return typeof animationName === 'string' && animationName.length > 0
            ? [animationName]
            : [];
    }

    private setAnimationProgressForNames(animationNames: string[], value: number): number {
        let matchedCount = 0;
        const matchedNames: string[] = [];

        for (const animationName of animationNames) {
            const trackPath = this.animationMap[animationName];
            if (trackPath) {
                const track = this.getAnimationTrackByName(animationName);
                if (track) {
                    this.animationProgresses.set(animationName, value);
                    matchedNames.push(animationName);
                }
                matchedCount++;
            } else {
                console.log(`    ✗ Animation "${animationName}" NOT found in animationMap`);
            }
        }

        if (matchedNames.length > 0) {
            this.applyActiveAnimationProgressPose();
            this.setAnimationProgressState(matchedNames, value);
            this.refreshSelectedNodeTransform();
        }

        return matchedCount;
    }

    // Set animations by group for easy UI control
    setAnimationGroup(group: string, value: number) {
        const aircraftConfig = this.observer.get('aircraftConfig.configuration');
        if (!aircraftConfig) {
            console.log('No aircraft configuration found');
            return;
        }

        const animationDefinitions =
            aircraftConfig.animationDefinitions || aircraftConfig.AnimationDefinitions;
        const groupLower = group.toLowerCase();
        let matchedCount = 0;

        // Special handling for ailerons: they should move in opposite directions
        // When rolling left (value < 0.5): left aileron up, right aileron down
        // When rolling right (value > 0.5): left aileron down, right aileron up
        if (groupLower === 'ailerons') {
            // Map value (0-1, 0.5=neutral) to opposite aileron positions
            // Left aileron: 0.0 (roll left) -> 1.0 (up), 1.0 (roll right) -> 0.0 (down)
            // Right aileron: 0.0 (roll left) -> 0.0 (down), 1.0 (roll right) -> 1.0 (up)
            const leftAileronValue = value; // Direct: low value = left down, high value = left up
            const rightAileronValue = 1.0 - value; // Inverted: low value = right up, high value = right down

            // Set left aileron
            this.setAnimationGroup('leftaileron', leftAileronValue);
            // Set right aileron
            this.setAnimationGroup('rightaileron', rightAileronValue);
            return;
        }

        // First, try matching by AnimationType enum from animation definitions
        if (animationDefinitions && Array.isArray(animationDefinitions)) {
            for (const animationDefinition of animationDefinitions) {
                const animationType =
                    animationDefinition.animationType ?? animationDefinition.AnimationType;
                const animationNames = this.getAnimationNamesFromDefinition(animationDefinition);

                if (animationNames.length === 0) continue;

                let shouldSet = false;

                // Match by group name
                switch (groupLower) {
                    case 'ailerons':
                        shouldSet =
                            animationType === this.AnimationType.LeftAileron ||
                            animationType === this.AnimationType.RightAileron;
                        break;
                    case 'leftaileron':
                    case 'left_aileron':
                        shouldSet = animationType === this.AnimationType.LeftAileron;
                        break;
                    case 'rightaileron':
                    case 'right_aileron':
                        shouldSet = animationType === this.AnimationType.RightAileron;
                        break;
                    case 'elevator':
                        shouldSet = animationType === this.AnimationType.Elevator;
                        break;
                    case 'rudder':
                        shouldSet = animationType === this.AnimationType.Rudder;
                        break;
                    case 'gear':
                    case 'landinggear':
                        shouldSet = animationType === this.AnimationType.LandingGear;
                        break;
                    case 'flaps':
                        shouldSet =
                            animationType === this.AnimationType.LeftFlap ||
                            animationType === this.AnimationType.RightFlap;
                        break;
                    case 'spoilers':
                        shouldSet = animationType === this.AnimationType.Spoilers;
                        break;
                    case 'throttle':
                    case 'engines':
                        shouldSet =
                            animationType === this.AnimationType.Throttle1 ||
                            animationType === this.AnimationType.Throttle2 ||
                            animationType === this.AnimationType.Throttle3 ||
                            animationType === this.AnimationType.Throttle4;
                        break;
                    case 'wingflex':
                        shouldSet =
                            animationType === this.AnimationType.WingFlexLeft ||
                            animationType === this.AnimationType.WingFlexRight;
                        break;
                }

                if (shouldSet) {
                    matchedCount += this.setAnimationProgressForNames(animationNames, value);
                }
            }
        }

        // Also search by animation name pattern for cases where AnimationType might not be set
        // This catches animations like "Flap_Detail", "Flap_Actuators", etc.
        // For flaps, always run pattern matching to catch detail animations
        // For left/right ailerons, only run if AnimationType matching didn't find anything
        if (
            matchedCount === 0 ||
            groupLower === 'flaps' ||
            ((groupLower === 'leftaileron' || groupLower === 'rightaileron') && matchedCount === 0)
        ) {
            const namePatterns: Record<string, string[]> = {
                flaps: ['flap', 'flap_', 'flaplever', 'flap_actuator', 'flap_detail'],
                ailerons: ['aileron', 'rollaxis'],
                leftaileron: ['aileron', 'rollaxis'],
                left_aileron: ['aileron', 'rollaxis'],
                rightaileron: ['aileron', 'rollaxis'],
                right_aileron: ['aileron', 'rollaxis'],
                elevator: ['elevator', 'pitchaxis'],
                rudder: ['rudder', 'yawaxis', 'steering'],
                gear: ['gear', 'landinggear', 'landing_gear'],
                spoilers: ['spoiler'],
                throttle: ['throttle'],
                engines: ['throttle', 'engine'],
                wingflex: ['wingflex', 'wing_flex'],
            };

            const patterns = namePatterns[groupLower];
            if (patterns) {
                // Search through all available animations
                for (const animationName in this.animationMap) {
                    if (this.individualAnimationChannels.has(animationName)) {
                        continue;
                    }

                    const animationNameLower = animationName.toLowerCase();
                    let matches = patterns.some((pattern) => animationNameLower.includes(pattern));

                    // For left/right ailerons, filter by left/right indicators in the name
                    if (
                        matches &&
                        (groupLower === 'leftaileron' || groupLower === 'left_aileron')
                    ) {
                        // Must contain left indicator and NOT contain right indicator
                        matches =
                            (animationNameLower.includes('left') ||
                                animationNameLower.includes('l_')) &&
                            !animationNameLower.includes('right') &&
                            !animationNameLower.includes('r_');
                    } else if (
                        matches &&
                        (groupLower === 'rightaileron' || groupLower === 'right_aileron')
                    ) {
                        // Must contain right indicator and NOT contain left indicator
                        matches =
                            (animationNameLower.includes('right') ||
                                animationNameLower.includes('r_')) &&
                            !animationNameLower.includes('left') &&
                            !animationNameLower.includes('l_');
                    }

                    if (matches) {
                        this.setIndividualAnimationProgress(animationName, value);
                        matchedCount++;
                    }
                }
            }
        }

        // Also check cockpit UI items for control surfaces
        if (groupLower === 'ailerons' || groupLower === 'elevator' || groupLower === 'rudder') {
            const cockpitUIItems = aircraftConfig.cockpitUIItems || aircraftConfig.CockpitUIItems;
            if (cockpitUIItems && Array.isArray(cockpitUIItems)) {
                for (const cockpitUIItem of cockpitUIItems) {
                    const bindingName = (
                        cockpitUIItem.bindingName ||
                        cockpitUIItem.BindingName ||
                        ''
                    ).toLowerCase();
                    let isMatch = false;

                    if (
                        groupLower === 'ailerons' &&
                        (bindingName.includes('aileron') || bindingName.includes('rollaxis'))
                    ) {
                        isMatch = true;
                    } else if (
                        groupLower === 'elevator' &&
                        (bindingName.includes('elevator') || bindingName.includes('pitchaxis'))
                    ) {
                        isMatch = true;
                    } else if (
                        groupLower === 'rudder' &&
                        (bindingName.includes('rudder') || bindingName.includes('yawaxis'))
                    ) {
                        isMatch = true;
                    }

                    if (isMatch) {
                        const animationBindings =
                            cockpitUIItem.animationBindings || cockpitUIItem.AnimationBindings;
                        if (animationBindings && Array.isArray(animationBindings)) {
                            for (const binding of animationBindings) {
                                const animName = binding.animationName || binding.AnimationName;
                                if (animName) {
                                    this.setIndividualAnimationProgress(animName, value);
                                    matchedCount++;
                                }
                            }
                        }
                    }
                }
            }
        }

        // Handle nose wheel steering separately
        if (
            groupLower === 'nosewheel' ||
            groupLower === 'nose_wheel' ||
            groupLower === 'steering'
        ) {
            const wheelDefinitions =
                aircraftConfig.wheelDefinitions || aircraftConfig.WheelDefinitions;
            if (wheelDefinitions && Array.isArray(wheelDefinitions)) {
                for (const wheelDef of wheelDefinitions) {
                    const steeringBindings =
                        wheelDef.steeringAnimationBindings || wheelDef.SteeringAnimationBindings;
                    if (steeringBindings && Array.isArray(steeringBindings)) {
                        for (const binding of steeringBindings) {
                            const animationName = binding.animationName || binding.AnimationName;
                            if (animationName) {
                                this.setIndividualAnimationProgress(animationName, value);
                                matchedCount++;
                            }
                        }
                    }
                }
            }

            // Also search by name pattern for nose wheel steering
            const steeringKeywords = ['nosewheel', 'nose_wheel', 'steering', 'nosewheelsteering'];
            for (const animationName in this.animationMap) {
                if (this.individualAnimationChannels.has(animationName)) {
                    continue;
                }

                const animationNameLower = animationName.toLowerCase();
                const isSteering = steeringKeywords.some((keyword) =>
                    animationNameLower.includes(keyword)
                );

                if (isSteering) {
                    this.setIndividualAnimationProgress(animationName, value);
                    matchedCount++;
                }
            }
        }

        // Handle suspension separately
        if (groupLower === 'suspension') {
            const wheelDefinitions =
                aircraftConfig.wheelDefinitions || aircraftConfig.WheelDefinitions;
            if (wheelDefinitions && Array.isArray(wheelDefinitions)) {
                for (const wheelDef of wheelDefinitions) {
                    const suspensionBindings =
                        wheelDef.suspensionAnimationBindings ||
                        wheelDef.SuspensionAnimationBindings;
                    if (suspensionBindings && Array.isArray(suspensionBindings)) {
                        for (const binding of suspensionBindings) {
                            const animationName = binding.animationName || binding.AnimationName;
                            if (animationName) {
                                this.setIndividualAnimationProgress(animationName, value);
                                matchedCount++;
                            }
                        }
                    }
                }
            }

            // Also search by name pattern for suspension animations
            const suspensionKeywords = ['suspension', 'shock', 'strut', 'oleo', 'damper'];
            for (const animationName in this.animationMap) {
                if (this.individualAnimationChannels.has(animationName)) {
                    continue;
                }

                const animationNameLower = animationName.toLowerCase();
                const isSuspension = suspensionKeywords.some((keyword) =>
                    animationNameLower.includes(keyword)
                );

                if (isSuspension) {
                    this.setIndividualAnimationProgress(animationName, value);
                    matchedCount++;
                }
            }
        }

        // Handle special actions (doors, hatches, etc.)
        if (groupLower === 'doors' || groupLower === 'door') {
            const doorKeywords = ['door', 'hatch', 'cargo', 'canopy', 'ramp', 'gate'];
            for (const animationName in this.animationMap) {
                // Per-channel tracks ("clip › node · channel") are covered by their source
                // clip. Scrubbing one isolates it by clearing every other pose, and a
                // channel that is active together with its own clip would be added on top
                // of it (0 + 0 - rest = -rest, a mirrored node).
                if (this.individualAnimationChannels.has(animationName)) {
                    continue;
                }

                const animationNameLower = animationName.toLowerCase();
                const isDoor = doorKeywords.some((keyword) => animationNameLower.includes(keyword));

                if (isDoor) {
                    this.setIndividualAnimationProgress(animationName, value);
                    matchedCount++;
                }
            }

            // Also check removable objects animation bindings
            const removableObjectsBindings =
                aircraftConfig.removableObjectsAnimationBindings ||
                aircraftConfig.RemovableObjectsAnimationBindings;
            if (removableObjectsBindings && Array.isArray(removableObjectsBindings)) {
                for (const binding of removableObjectsBindings) {
                    const animationName = binding.animationName || binding.AnimationName;
                    if (animationName) {
                        this.setIndividualAnimationProgress(animationName, value);
                        matchedCount++;
                    }
                }
            }
        }

        console.log(
            `Set ${matchedCount} animation(s) for group "${group}" to ${(value * 100).toFixed(0)}%`
        );
    }

    // Apply animation settings for screenshot based on aircraft configuration
    applyScreenshotAnimationSettings() {
        const aircraftConfig = this.observer.get('aircraftConfig.configuration');
        if (!aircraftConfig) {
            console.log('No aircraft configuration found, skipping screenshot animation setup');
            return;
        }

        const gearExtendedRaw = this.observer.get('screenshot.gearExtended');
        const shouldExtendGear = gearExtendedRaw !== false;
        // In the current aircraft animation convention, landing gear progress is inverted:
        // 0.0 = gear extended, 1.0 = gear retracted.
        const gearProgress = shouldExtendGear ? 0.0 : 1.0;
        // Suspension progress is inverted for these bindings:
        // lower values are more extended, higher values are more compressed.
        const suspensionProgress = shouldExtendGear ? 0.5 : 1.0;

        // Get animation definitions (try both camelCase and PascalCase)
        const animationDefinitions =
            aircraftConfig.animationDefinitions || aircraftConfig.AnimationDefinitions;

        if (animationDefinitions && Array.isArray(animationDefinitions)) {
            console.log(`Processing ${animationDefinitions.length} animation definitions...`);

            for (const animationDefinition of animationDefinitions) {
                let percentage = 0.0;
                // Try both camelCase and PascalCase
                const animationType =
                    animationDefinition.animationType ?? animationDefinition.AnimationType;

                // Set percentage based on animation type (matching C# logic)
                switch (animationType) {
                    case this.AnimationType.LeftAileron:
                    case this.AnimationType.RightAileron:
                    case this.AnimationType.Elevator:
                    case this.AnimationType.Rudder:
                        percentage = 0.5;
                        break;
                    case this.AnimationType.Spoilers:
                        percentage = 0.0;
                        break;
                    case this.AnimationType.LandingGear:
                        percentage = gearProgress;
                        break;
                    case this.AnimationType.Reverse:
                    case this.AnimationType.LeftFlap:
                    case this.AnimationType.RightFlap:
                    case this.AnimationType.Throttle1:
                    case this.AnimationType.Throttle2:
                    case this.AnimationType.Throttle3:
                    case this.AnimationType.Throttle4:
                        percentage = 0.0;
                        break;
                    case this.AnimationType.WingFlexRight:
                    case this.AnimationType.WingFlexLeft:
                        percentage = 0.5;
                        break;
                    default:
                        percentage = 0.0;
                        break;
                }

                const animationNames = this.getAnimationNamesFromDefinition(animationDefinition);

                console.log(
                    `  Animation definition - Names: "${animationNames.join(
                        ', '
                    )}", Type: ${animationType}, Target percentage: ${
                        percentage * 100
                    }%`
                );

                if (animationNames.length > 0) {
                    const matchedCount = this.setAnimationProgressForNames(
                        animationNames,
                        percentage
                    );
                    if (matchedCount !== animationNames.length) {
                        console.log('    Available animations:', Object.keys(this.animationMap));
                    }
                }
            }
        }

        // Process cockpit UI items for control surfaces
        const cockpitUIItems = aircraftConfig.cockpitUIItems || aircraftConfig.CockpitUIItems;

        if (cockpitUIItems && Array.isArray(cockpitUIItems)) {
            console.log(
                `Processing ${cockpitUIItems.length} cockpit UI items for control surfaces...`
            );

            for (const cockpitUIItem of cockpitUIItems) {
                // Try both camelCase and PascalCase for bindingName
                const bindingName = (
                    cockpitUIItem.bindingName ||
                    cockpitUIItem.BindingName ||
                    ''
                ).toLowerCase();

                // Check if this binding is for a control surface we want to set
                const isControlSurface =
                    bindingName.includes('control_surfaces') ||
                    bindingName.includes('elevator') ||
                    bindingName.includes('aileron') ||
                    bindingName.includes('rudder') ||
                    bindingName.includes('steering') ||
                    bindingName.includes('rollaxis') || // Roll axis control (ailerons)
                    bindingName.includes('pitchaxis') || // Pitch axis control (elevator)
                    bindingName.includes('yawaxis'); // Yaw axis control (rudder)

                if (isControlSurface) {
                    console.log(`  Cockpit UI item - Binding: "${bindingName}"`);

                    // Get animationBindings array from the cockpit UI item
                    const animationBindings =
                        cockpitUIItem.animationBindings || cockpitUIItem.AnimationBindings;

                    if (animationBindings && Array.isArray(animationBindings)) {
                        console.log(`    Found ${animationBindings.length} animation bindings`);

                        for (const binding of animationBindings) {
                            // Get the animation name from the binding
                            const animationName = binding.animationName || binding.AnimationName;

                            if (animationName) {
                                const trackPath = this.animationMap[animationName];
                                if (trackPath) {
                                    // Determine percentage based on binding type
                                    let percentage = 0.5; // Default neutral position

                                    if (bindingName.includes('spoiler')) {
                                        percentage = 0.0; // Spoilers retracted
                                    } else if (bindingName.includes('flap')) {
                                        percentage = 0.0; // Flaps retracted
                                    } else if (bindingName.includes('gear')) {
                                        percentage = gearProgress;
                                    }

                                    console.log(
                                        `    ✓ Setting "${animationName}" to ${(
                                            percentage * 100
                                        ).toFixed(0)}% (from binding: ${bindingName})`
                                    );
                                    this.setIndividualAnimationProgress(animationName, percentage);
                                } else {
                                    console.log(
                                        `    ✗ Animation "${animationName}" NOT found in animationMap`
                                    );
                                }
                            }
                        }
                    }
                }
            }
        }

        // Process wheel definitions for steering and suspension animations
        const wheelDefinitions = aircraftConfig.wheelDefinitions || aircraftConfig.WheelDefinitions;

        if (wheelDefinitions && Array.isArray(wheelDefinitions)) {
            console.log(`Processing ${wheelDefinitions.length} wheel definitions...`);

            for (const wheelDef of wheelDefinitions) {
                const wheelName = wheelDef.name || wheelDef.Name || 'Unknown';

                // Process steering animation bindings
                const steeringBindings =
                    wheelDef.steeringAnimationBindings || wheelDef.SteeringAnimationBindings;
                if (steeringBindings && Array.isArray(steeringBindings)) {
                    for (const binding of steeringBindings) {
                        const animationName = binding.animationName || binding.AnimationName;
                        if (animationName) {
                            const trackPath = this.animationMap[animationName];
                            if (trackPath) {
                                console.log(
                                    `  ✓ Setting steering animation "${animationName}" to 50% (wheel: ${wheelName})`
                                );
                                this.setIndividualAnimationProgress(animationName, 0.5);
                            }
                        }
                    }
                }

                // Process suspension animation bindings - set to fully extended
                const suspensionBindings =
                    wheelDef.suspensionAnimationBindings || wheelDef.SuspensionAnimationBindings;
                if (suspensionBindings && Array.isArray(suspensionBindings)) {
                    for (const binding of suspensionBindings) {
                        const animationName = binding.animationName || binding.AnimationName;
                        if (animationName) {
                            const trackPath = this.animationMap[animationName];
                            if (trackPath) {
                                console.log(
                                    `  ✓ Setting suspension animation "${animationName}" to ${(
                                        suspensionProgress * 100
                                    ).toFixed(0)}% (wheel: ${wheelName})`
                                );
                                this.setIndividualAnimationProgress(
                                    animationName,
                                    suspensionProgress
                                );
                            }
                        }
                    }
                }
            }
        }

        // Process all animations and set suspension-related ones - catch any not in wheelDefinitions
        console.log('Checking for additional suspension animations...');
        const suspensionKeywords = ['suspension', 'shock', 'strut', 'oleo', 'damper'];
        let suspensionCount = 0;

        for (const animationName in this.animationMap) {
            // Per-channel tracks ("clip › node · channel") are covered by their source
            // clip, and scrubbing one of them isolates it by clearing every other pose,
            // which would undo the landing gear and control surfaces set above.
            if (this.individualAnimationChannels.has(animationName)) {
                continue;
            }

            const animationNameLower = animationName.toLowerCase();
            const isSuspension = suspensionKeywords.some((keyword) =>
                animationNameLower.includes(keyword)
            );

            if (isSuspension) {
                console.log(
                    `  ✓ Found suspension animation: "${animationName}", setting to ${(
                        suspensionProgress * 100
                    ).toFixed(0)}%`
                );
                this.setIndividualAnimationProgress(animationName, suspensionProgress);
                suspensionCount++;
            }
        }

        if (suspensionCount > 0) {
            console.log(`Applied 100% to ${suspensionCount} additional suspension animation(s)`);
        } else {
            console.log('No additional suspension animations found');
        }
    }

    // Import package manifest from JSON file
    importManifest() {
        console.log('Importing package manifest...');

        // Create file input element
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json';

        input.onchange = (event: Event) => {
            const target = event.target as HTMLInputElement;
            const file = target.files?.[0];

            if (!file) {
                console.error('No file selected');
                return;
            }

            const reader = new FileReader();

            reader.onload = (e: ProgressEvent<FileReader>) => {
                try {
                    const content = e.target?.result as string;
                    const manifest = JSON.parse(content);

                    // Validate the manifest structure
                    if (!manifest || typeof manifest !== 'object') {
                        throw new Error('Invalid manifest format: not a valid JSON object');
                    }

                    // Create manifest data object
                    const manifestData = {
                        loaded: true,
                        manifest: manifest,
                        error: null as string | null,
                        importedAt: new Date().toISOString(),
                    };

                    // Save to localStorage
                    if ((window as any).saveManifest) {
                        (window as any).saveManifest(manifestData);
                    }

                    // Update observer to trigger UI update
                    this.observer.set('manifestData', manifestData);

                    console.log('Package manifest imported successfully');
                    console.log('  Models:', manifest.modelPackages?.length || 0);
                    console.log('  Liveries:', manifest.liveryPackages?.length || 0);
                    console.log('  Configurations:', manifest.configurationPackages?.length || 0);
                    console.log('  Sounds:', manifest.soundPackages?.length || 0);
                    console.log('  Shared:', manifest.sharedPackages?.length || 0);

                    // Trigger livery detection now that manifest is loaded
                    // Use setTimeout to ensure observer has updated
                    setTimeout(() => {
                        this.detectLiveries();
                    }, 0);
                } catch (err) {
                    console.error('Failed to import manifest:', err);
                    const errorMessage = err instanceof Error ? err.message : 'Unknown error';
                    this.observer.set('manifestData', {
                        loaded: false,
                        manifest: null,
                        error: errorMessage,
                        importedAt: null as string | null,
                    });
                    this.observer.set('ui.error', `Failed to import manifest: ${errorMessage}`);
                }
            };

            reader.onerror = () => {
                console.error('Failed to read file');
                this.observer.set('ui.error', 'Failed to read manifest file');
            };

            reader.readAsText(file);
        };

        // Trigger file selection
        input.click();
    }

    // perform post-load operations on the scene
    private postSceneLoad() {
        // Immediate setup: construct a list of meshInstances so we can quickly access them when configuring wireframe rendering etc.
        this.meshInstances = this.entities
            .map((entity) => {
                return this.collectMeshInstances(entity);
            })
            .flat();

        // Apply altitude and pitch offsets from aircraft configuration if available
        this.applyAltitudeOffset();
        this.applyPitchOffset();

        // Only enable skeleton if we actually expect no meshes based on the asset data
        // This prevents skeleton from being auto-enabled due to timing/race conditions
        const hasMeshData = this.entityAssets.some((ea) => {
            const resource = ea.asset?.resource as any;
            return resource && resource.renders && resource.renders.length > 0;
        });

        if (this.meshInstances.length === 0 && !hasMeshData) {
            this.observer.set('debug.skeleton', true);
        }

        // Update scene stats immediately
        this.updateSceneStats();

        // Defer heavy operations (normal maps, material mapping, animations) to keep UI responsive
        // Show loading indicator during heavy work
        this.observer.set('ui.loadingMessage', 'Processing scene...');
        
        setTimeout(() => {
            // Apply normal map settings based on observer state
            const normalMapsEnabled = this.observer.get('debug.normalMaps');
            if (normalMapsEnabled) {
                this.applyXXXYNormalMapDecoding();
            } else {
                this.setNormalMapsEnabled(false);
            }

            // build material texture map for the materials panel
            this.buildMaterialTextureMap();

            // rebuild the anim state graph
            if (this.animTracks.length > 0) {
                this.rebuildAnimTracks();
            }

            // make a list of all the morph instance target names
            const morphs: Record<
                string,
                { name: string; targets: Record<string, MorphTargetData> }
            > = {};
            const morphInstances: Record<string, MorphInstance> = {};

            // get all morph targets
            this.meshInstances.forEach((meshInstance, i) => {
                if (meshInstance.morphInstance) {
                    const morphInstance = meshInstance.morphInstance;
                    morphInstances[i] = morphInstance;

                    // mesh name line
                    const meshNode = this.getMeshInstanceDisplayNode(meshInstance);
                    const meshName = (meshInstance && meshNode && meshNode.name) || `Mesh ${i}`;
                    morphs[i] = {
                        name: meshName,
                        targets: {},
                    };

                    // morph targets
                    morphInstance.morph.targets.forEach((target: MorphTarget, targetIndex: number) => {
                        morphs[i].targets[targetIndex] = {
                            name: target.name,
                            targetIndex: targetIndex,
                        };
                        this.observer.on(
                            `morphs.${i}.targets.${targetIndex}.weight:set`,
                            (weight: number) => {
                                morphInstances[i].setWeight(targetIndex, weight);
                                this.dirtyNormals = true;
                                this.renderNextFrame();
                            }
                        );
                    });
                }
            });

            this.observer.suspendEvents = true;
            this.observer.set('morphs', morphs);
            this.observer.suspendEvents = false;

            // handle animation update
            const observer = this.observer;
            observer.on('animationUpdate', () => {
                // set progress for main animation system
                for (let i = 0; i < this.entities.length; ++i) {
                    const entity = this.entities[i];
                    if (entity && entity.anim && entity.anim.baseLayer) {
                        const baseLayer = entity.anim.baseLayer;
                        const progress =
                            baseLayer.activeStateCurrentTime / baseLayer.activeStateDuration;
                        this.suppressAnimationProgressUpdate = true;
                        observer.set('animation.progress', progress === 1 ? progress : progress % 1);
                        this.suppressAnimationProgressUpdate = false;

                        // Update individual animation states - but only update progress for active animation
                        // Don't override playing states that were set manually
                        const activeState = baseLayer.activeState;
                        const animations = observer.get('animations') || {};
                        let hasUpdate = false;

                        Object.keys(animations).forEach((animationName) => {
                            const trackPath = this.animationMap[animationName];
                            if (trackPath === activeState && baseLayer.playing && entity.anim.playing) {
                                // This is the currently active and playing animation - update its progress
                                const currentProgress = progress === 1 ? progress : progress % 1;
                                if (animations[animationName].progress !== currentProgress) {
                                    animations[animationName].progress = currentProgress;
                                    hasUpdate = true;
                                }
                                // Only update playing state if it's not already true
                                if (!animations[animationName].playing) {
                                    animations[animationName].playing = true;
                                    hasUpdate = true;
                                }
                            }
                        });

                        // Only update if there are actual changes to avoid unnecessary re-renders
                        if (hasUpdate) {
                            observer.suspendEvents = true;
                            observer.set('animations', animations);
                            observer.suspendEvents = false;
                        }
                        break;
                    }
                }
            });

            // dirty everything
            this.dirtyWireframe = this.dirtyBounds = this.dirtySkeleton = this.dirtyGrid = this.dirtyNormals = true;

            this.renderNextFrame();

            // we perform some special processing on the first frame
            this.firstFrame = true;
        }, 50); // Defer heavy operations by 50ms to keep UI responsive
    }

    private initSceneBounds() {
        this.setCenterScene(this.observer.get('centerScene'));

        // set projective skybox radius
        this.setSkyboxDomeRadius(this.observer.get('skybox.domeProjection.domeRadius'));

        // focus the camera on the scene
        this.focusSelection();
        
        // Ensure camera is at a reasonable zoom distance after focus
        // This prevents the camera from being too close on initial load
        if (this.cameraControls) {
            const sceneSize = this.cameraControls.sceneSize || 1;
            // Set a reasonable initial zoom distance (2x scene size)
            const initialZoomDist = sceneSize * 2;
            // @ts-ignore
            if (this.cameraControls._zoomDist !== undefined && this.cameraControls._zoomDist < initialZoomDist) {
                // @ts-ignore
                this.cameraControls._zoomDist = initialZoomDist;
                console.log(`[initSceneBounds] Set initial zoom distance to ${initialZoomDist} (scene size: ${sceneSize})`);
            }
            
            // Also reset the zoom to ensure proper calculation
            // @ts-ignore - resetZoom may not exist on all versions
            if (typeof this.cameraControls.resetZoom === 'function') {
                this.cameraControls.resetZoom(this.getZoomDist());
            }
            console.log(`[initSceneBounds] Camera focused. Scene size: ${sceneSize}, zoom dist: ${this.getZoomDist()}`);
        }

        // Update orbit distance for new scene (will be applied when setUICameraMode is called)
        const uiCameraMode = this.observer.get('camera.uiCameraMode') || 'orbit';
        if (uiCameraMode === 'orbit') {
            const sceneSize = this.cameraControls?.sceneSize || 10;
            this.observer.set('camera.orbitDistance', sceneSize * 2.5);
            console.log(`[initSceneBounds] Updated orbit distance to ${sceneSize * 2.5} for scene size ${sceneSize}`);
        }
    }

    // rebuild the animation state graph
    private rebuildAnimTracks() {
        this.entities.forEach((entity) => {
            // create the anim component if there isn't one already
            if (!entity.anim) {
                entity.addComponent('anim', {
                    activate: true,
                    speed: this.animSpeed,
                });
                entity.anim.rootBone = entity;
            } else {
                // clean up any previous animations
                entity.anim.removeStateGraph();
            }

            this.animTracks.forEach((t: any, i: number) => {
                // add an event to each track which transitions to the next track when it ends
                t.events = new AnimEvents([
                    {
                        name: 'transition',
                        time: t.duration,
                        nextTrack: `track_${i === this.animTracks.length - 1 ? 0 : i + 1}`,
                    },
                ]);
                const path = `track_${i}`;
                entity.anim.assignAnimation(path, t);
                this.animationMap[t.name] = path;
            });
            // if the user has selected to play all tracks in succession, then transition to the next track after a set amount of loops
            entity.anim.on('transition', (e) => {
                const animationName: string = this.observer.get('animation.selectedTrack');
                if (
                    animationName === 'ALL_TRACKS' &&
                    entity.anim.baseLayer.activeStateProgress >= this.animLoops
                ) {
                    entity.anim.baseLayer.transition(e.nextTrack, this.animTransition);
                }
            });
        });

        // Create individual animation states
        const individualAnimations: Record<string, any> = {};
        this.animTracks.forEach((track: any) => {
            const metadata = this.individualAnimationChannels.get(track.name);
            individualAnimations[track.name] = {
                name: track.name,
                displayName: metadata?.displayName || track.name,
                sourceAnimation: metadata?.sourceAnimation,
                targetProperty: metadata?.targetProperty,
                isIndividualChannel: Boolean(metadata),
                playing: false,
                progress: 0,
                duration: track.duration,
            };
        });

        // let the controls know about the new animations, set the selected track and immediately start playing the animation
        const animationState = this.observer.get('animation');
        const animationKeys = Object.keys(this.animationMap);
        animationState.list = JSON.stringify(animationKeys);
        animationState.selectedTrack = animationKeys[0];
        animationState.playing = false; // Don't auto-play animations on load

        this.observer.suspendEvents = true;
        this.observer.set('animation', animationState);
        this.observer.set('animations', individualAnimations);
        this.observer.suspendEvents = false;

        // Make sure all animations are paused on load
        this.entities.forEach((entity) => {
            if (entity.anim) {
                entity.anim.playing = false;
                if (entity.anim.baseLayer) {
                    entity.anim.baseLayer.pause();
                }
            }
        });

        // Reset all animations to neutral positions after load
        this.resetAnimationsToNeutral();
    }

    // Reset all animation groups to neutral positions
    resetAnimationsToNeutral() {
        // Only reset if we have aircraft configuration
        const aircraftConfig = this.observer.get('aircraftConfig.configuration');
        if (!aircraftConfig) {
            return;
        }

        const gearExtendedRaw = this.observer.get('screenshot.gearExtended');
        const shouldExtendGear = gearExtendedRaw !== false;
        const gearProgress = shouldExtendGear ? 0.0 : 1.0;
        const suspensionProgress = shouldExtendGear ? 0.15 : 1.0;

        // Set control surfaces to neutral (0.5)
        this.setAnimationGroup('ailerons', 0.5);
        this.setAnimationGroup('elevator', 0.5);
        this.setAnimationGroup('rudder', 0.5);
        this.setAnimationGroup('nosewheel', 0.5);

        // Set flaps and spoilers to retracted (0)
        this.setAnimationGroup('flaps', 0);
        this.setAnimationGroup('spoilers', 0);

        // Set throttle to idle (0)
        this.setAnimationGroup('throttle', 0);

        // Set wing flex to neutral (0.5)
        this.setAnimationGroup('wingflex', 0.5);

        // Respect screenshot gear mode when neutralizing animations.
        this.setAnimationGroup('gear', gearProgress);

        // Keep suspension mostly extended when gear is down.
        this.setAnimationGroup('suspension', suspensionProgress);

        // Set doors to closed (0)
        this.setAnimationGroup('doors', 0);
    }

    private calcSceneBounds(result: BoundingBox, root: Entity | null = null) {
        const meshInstances = root ? this.collectMeshInstances(root) : this.meshInstances;
        if (meshInstances.length) {
            Viewer.calcMeshBoundingBox(result, meshInstances);
        } else {
            root = root ?? this.sceneRoot;
            if (root.children.length) {
                Viewer.calcHierBoundingBox(result, root);
            } else {
                result.copy(defaultSceneBounds);
            }
        }
    }

    private resetWireframeMeshes() {
        this.app.scene.layers
            .getLayerByName('World')
            .removeMeshInstances(this.wireframeMeshInstances);
        this.wireframeMeshInstances.forEach((mi) => {
            mi.clearShaders();
        });
        this.wireframeMeshInstances = [];
    }

    private buildWireframeMeshes() {
        this.wireframeMeshInstances = this.getSelectedMeshInstances().map((mi) => {
            const meshInstance = new MeshInstance(mi.mesh, this.wireframeMaterial, mi.node);
            meshInstance.renderStyle = PRIMITIVE_LINES;
            meshInstance.skinInstance = mi.skinInstance;
            meshInstance.morphInstance = mi.morphInstance;
            return meshInstance;
        });

        this.app.scene.layers.getLayerByName('World').addMeshInstances(this.wireframeMeshInstances);
    }

    private onFrameRender() {
        // Skip automatic resolution sync during screenshot capture (batch mode)
        // @ts-ignore
        if (!this.isCapturingScreenshot) {
            if (this.canvasResize) {
                const { width, height } = this.getCanvasSize();
                const pixelScale = this.observer.get('camera.pixelScale');
                const widthPixels = Math.floor((width * window.devicePixelRatio) / pixelScale);
                const heightPixels = Math.floor((height * window.devicePixelRatio) / pixelScale);
                this.app.graphicsDevice.setResolution(widthPixels, heightPixels);
                this.observer.set('runtime.viewportWidth', widthPixels);
                this.observer.set('runtime.viewportHeight', heightPixels);
                this.canvasResize = false;
            }

            // rebuild render targets
            this.rebuildRenderTargets();
        }
    }

    // generate and render debug elements on prerender
    private onPrerender() {
        if (this.firstFrame) {
            return;
        }

        // wireframe
        if (this.dirtyWireframe) {
            this.dirtyWireframe = false;

            this.resetWireframeMeshes();
            // Show wireframes if debug wireframe is enabled OR if any object is selected (single or multi)
            const multiSelectedNodes: Array<any> =
                this.observer.get('scene.multiSelectedNodes') || [];
            const hasMultiSelection = multiSelectedNodes.length > 0;
            const shouldShowWireframes =
                this.showWireframe || this.selectedNode || hasMultiSelection;
            if (shouldShowWireframes) {
                this.buildWireframeMeshes();
            }

            this.getSelectedMeshInstances().forEach((mi) => {
                mi.material.depthBias = shouldShowWireframes ? -1.0 : 0.0;
                mi.material.slopeDepthBias = shouldShowWireframes ? 1.0 : 0.0;
            });
        }

        // debug bounds
        if (this.dirtyBounds || this.xrMode?.active) {
            this.dirtyBounds = false;

            // calculate bounds
            this.calcSceneBounds(this.dynamicSceneBounds);

            this.debugBounds.clear();
            if (this.showBounds) {
                this.calcSceneBounds(bbox, this.selectedNode as Entity);
                this.debugBounds.box(bbox.getMin(), bbox.getMax());
            }
            this.debugBounds.update();

            const v = new Vec3(
                this.dynamicSceneBounds.halfExtents.x * 2,
                this.dynamicSceneBounds.halfExtents.y * 2,
                this.dynamicSceneBounds.halfExtents.z * 2
            );
            this.observer.set('scene.bounds', v.toString());
        }

        // debug normals
        if (this.dirtyNormals) {
            this.dirtyNormals = false;
            this.debugNormals.clear();

            if (this.normalLength > 0) {
                for (let i = 0; i < this.meshInstances.length; ++i) {
                    const meshInstance = this.meshInstances[i];

                    const vertexBuffer = meshInstance.morphInstance // @ts-ignore TODO not defined in pc
                        ? meshInstance.morphInstance._vertexBuffer
                        : meshInstance.mesh.vertexBuffer;

                    if (vertexBuffer) {
                        const skinMatrices = meshInstance.skinInstance
                            ? meshInstance.skinInstance.matrices
                            : null;

                        // if there is skinning we need to manually update matrices here otherwise
                        // our normals are always a frame behind
                        if (skinMatrices) {
                            // @ts-ignore TODO not defined in pc
                            meshInstance.skinInstance.updateMatrices(meshInstance.node);
                        }

                        this.debugNormals.generateNormals(
                            vertexBuffer,
                            meshInstance.node.getWorldTransform(),
                            this.normalLength,
                            skinMatrices
                        );
                    }
                }
            }
            this.debugNormals.update();
        }

        // debug skeleton
        if (this.dirtySkeleton) {
            this.dirtySkeleton = false;
            this.debugSkeleton.clear();

            if (this.showSkeleton || this.showAxes) {
                this.entities.forEach((entity) => {
                    if (this.meshInstances.length === 0 || entity.findComponent('render')) {
                        this.debugSkeleton.generateSkeleton(
                            entity,
                            this.showSkeleton,
                            this.showAxes,
                            this.selectedNode
                        );
                    }
                });
            }

            this.debugSkeleton.update();
        }

        // debug grid
        if (this.sceneBounds && this.dirtyGrid) {
            this.dirtyGrid = false;

            this.debugGrid.clear();
            if (this.showGrid) {
                const v0 = new Vec3(0, 0, 0);
                const v1 = new Vec3(0, 0, 0);
                const y = 0;

                // Grid extent: 50 meters in each direction
                const gridExtent = 50;

                // Draw grid lines every 1 meter (gray) and every 5 meters (white)
                for (let x = -gridExtent; x <= gridExtent; ++x) {
                    const isMajorLine = x % 5 === 0;
                    const color = isMajorLine ? 0x80ffffff >>> 0 : 0x80808080 >>> 0; // White for 5m, gray for 1m

                    v0.set(-gridExtent, y, x);
                    v1.set(gridExtent, y, x);
                    this.debugGrid.line(v0, v1, color);

                    v0.set(x, y, -gridExtent);
                    v1.set(x, y, gridExtent);
                    this.debugGrid.line(v0, v1, color);
                }
            }
            this.debugGrid.update();
        }

        // fit camera planes to the scene
        this.fitCameraClipPlanes();

        this.shadowCatcher.onUpdate(this.dynamicSceneBounds);
    }

    private onPostrender() {
        if (this.firstFrame) {
            this.firstFrame = false;

            // reinit scene bounds after first render in order to get accurate morph target and skinned bounds
            this.initSceneBounds();

            // Re-initialize UI camera mode after first render to ensure proper setup
            // (scene bounds are now accurate, so orbit camera can position correctly)
            const uiCameraMode = this.observer.get('camera.uiCameraMode') || 'orbit';
            console.log('[onPostrender] First frame - re-initializing UI camera mode:', uiCameraMode);
            this.setUICameraMode(uiCameraMode);

            // initialize livery system after everything is fully loaded
            this.detectLiveries();
            this.categorizeTextures();
            this.buildMaterialTextureMap();

            // Preload all livery textures in the background for instant switching
            this.preloadAllLiveryTextures().catch((error) => {
                console.error('[LIVERY PRELOAD] Error preloading textures:', error);
            });

            // After liveries are detected, ensure selected livery is valid and applied
            setTimeout(() => {
                const availableLiveries = this.normalizeLiveryList(
                    this.observer.get('liveries.available') || []
                );
                if (availableLiveries.length > 0) {
                    const currentLivery = this.resolveLiveryAlias(this.observer.get('liveries.selected') || '');
                    const liveryToSelect = availableLiveries.includes(currentLivery)
                        ? currentLivery
                        : (availableLiveries.includes('Generic') ? 'Generic' : (availableLiveries[0] || 'Generic'));

                    console.log(
                        '[Viewer.onPostrender] Ensuring livery is selected after detection. Available:',
                        availableLiveries,
                        '| Current:',
                        currentLivery,
                        '| Selecting:',
                        liveryToSelect
                    );

                    this.observer.set('liveries.selected', liveryToSelect);
                    if (liveryToSelect && this.setSelectedLivery) {
                        console.log(
                            '[Viewer.onPostrender] Selecting livery to apply configurations:',
                            liveryToSelect
                        );
                        this.setSelectedLivery(liveryToSelect);
                    }
                    
                    // Hide loading overlay
                    this.observer.set('ui.loadingOverlay', false);
                    this.observer.set('ui.loadingProgress', undefined);
                }
            }, 100); // Small delay to ensure detectLiveries has completed
        }

        // resolve the (possibly multisampled) render target
        const rt = this.camera.camera.renderTarget;
        if (rt && rt.samples > 1) {
            rt.resolve();
        }

        // perform multiframe update. returned flag indicates whether more frames
        // are needed.
        // Skip multiframe update if render target is null (e.g., during screenshot setup)
        if (rt) {
            this.multiframeBusy = this.multiframe.update();
        }
    }

    private onFrameend() {
        if (this.loadTimestamp !== null) {
            this.observer.set('scene.loadTime', `${Date.now() - this.loadTimestamp}ms`);
            this.loadTimestamp = null;
        }

        // CRITICAL: Don't trigger additional renders during screenshot capture
        // This prevents the render loop from running during GPU sync delays,
        // which could move the camera and overwrite the render target with wrong data
        // @ts-ignore
        if (this.isCapturingScreenshot) {
            return;
        }

        if (this.multiframeBusy) {
            this.app.renderNextFrame = true;
        }
    }
}

export default Viewer;
